import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import LeftNav from './components/LeftNav';
import AuditLog from './components/AuditLog';
import './App.css';
import { rateVisible, deltaChip } from './utils/dashboardFormat';
import { parseDeepLink, hashForIntent, phaseFromHash, hashPhaseSegment } from './utils/deepLink';
import Login from './components/Login';
import SignupStep1 from './components/SignupStep1';
import SignupStep2 from './components/SignupStep2';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import ShareViewer from './components/ShareViewer';
import AskWinter from './components/AskWinter';
import BuyerSpacesPhase from './components/BuyerSpacesPhase';
import CallsPhase from './components/CallsPhase';
import ImpactReport from './components/ImpactReport';
import OutcomeControl from './components/OutcomeControl';
import AttentionCurve from './components/AttentionCurve';
import DealCycleDashboard from './components/DealCycleDashboard';
import TeamPerformanceDashboard from './components/TeamPerformanceDashboard';
import PasswordStrengthHint, { isPasswordStrong } from './components/PasswordStrengthHint';
import TwoFactorChallenge from './components/TwoFactorChallenge';
import TwoFactorSetup from './components/TwoFactorSetup';
import SecuritySettingsCard from './components/SecuritySettingsCard';
import SuppressionListCard from './components/SuppressionListCard';
import MfaEnrollmentGate, { isMfaRestricted } from './components/MfaEnrollmentGate';
import ProfileMenu from './components/ProfileMenu';
import CrmIntegrationTiles from './components/CrmIntegrationTiles';
import DocuSignSettings from './components/DocuSignSettings';
import ApiAccessSettings from './components/ApiAccessSettings';
import SalesLibraryPhase from './components/SalesLibraryPhase';
import QbrBriefsPhase from './components/QbrBriefsPhase';
import CoachingPhase from './components/CoachingPhase';
// RecordTrailerModal is loaded lazily by the standalone /record-trailer
// hash route in Root() — not imported here. See the IS_RECORDING_TAB
// branch at the bottom of this file.
import { PermissionEditorModal, TransferGlobalAdminModal } from './components/SecurityTab';
import TopBar from './components/TopBar';
import HelpMenu, { HelpProvider, useHelp } from './components/HelpMenu';
import TopicChapterStrip from './components/TopicChapterStrip';
import InternalSharePanel from './components/InternalSharePanel';
import CommentsThread from './components/CommentsThread';
import AskWinterCallTab from './components/AskWinterCallTab';
import {
  PillSwitcher as ReviewPillSwitcher,
  TalkingTimeline as ReviewTalkingTimeline,
  TranscriptPanel as ReviewTranscriptPanel,
  computeSpeakerStats as reviewComputeSpeakerStats,
  getSpeakerColor as reviewGetSpeakerColor,
  applySpeakerNameLayers as reviewApplySpeakerNameLayers,
} from './components/CallReviewSections';
import redditPixel from './utils/redditPixel';
import googleAds from './utils/googleAds';
import { displayCompanyName, isPlaceholderCompanyName } from './utils/displayCompanyName';
import { checkForNewerBuild, reloadOnceFor } from './utils/buildFreshness';
import InlineMarkdown from './components/InlineMarkdown';
import BrandSettings from './components/BrandSettings';
import FormsPhase from './components/FormsPhase';
import ProductsSettings from './components/ProductsSettings';

// When the URL is /share/:token we bypass the auth+dashboard app entirely
// and render the public ShareViewer. Tokens are base64url (`A-Za-z0-9_-`).
// Parsed at module load (before App runs) so nothing about the share
// landing page depends on auth state initialization.
const SHARE_TOKEN_MATCH = typeof window !== 'undefined'
  ? window.location.pathname.match(/^\/share\/([A-Za-z0-9_-]{16,})\/?$/)
  : null;
const SHARE_TOKEN = SHARE_TOKEN_MATCH ? SHARE_TOKEN_MATCH[1] : null;

// Same idea for /space/:slug — Decision Room prospect view. Token rides
// in the query (?t=<token>); the slug is human-readable so we accept
// hyphenated lowercase. Match parsed once at module load so the
// public surface never touches auth state.
const SPACE_SLUG_MATCH = typeof window !== 'undefined'
  ? window.location.pathname.match(/^\/space\/([a-z0-9][a-z0-9-]{0,80})\/?$/i)
  : null;
const SPACE_SLUG = SPACE_SLUG_MATCH ? SPACE_SLUG_MATCH[1] : null;

// /space/:slug/doc/:docId — ONE Decision Room document, read on its own
// page (View from a document card opens this in a new tab). Same
// ?t=<token> credential as the room itself, so the URL survives being
// pasted fresh into any browser. Parsed BEFORE the room match is
// consulted in Root() — the room regex above is anchored so it can't
// swallow this path, but order makes the precedence explicit.
const ROOM_DOC_MATCH = typeof window !== 'undefined'
  ? window.location.pathname.match(/^\/space\/([a-z0-9][a-z0-9-]{0,80})\/doc\/([A-Za-z0-9-]{6,64})\/?$/i)
  : null;
const ROOM_DOC_SLUG = ROOM_DOC_MATCH ? ROOM_DOC_MATCH[1] : null;
const ROOM_DOC_ID = ROOM_DOC_MATCH ? ROOM_DOC_MATCH[2] : null;

// /i/:token — INTERNAL share viewer (2026-08-23). Colleague-facing,
// seat-optional, visually distinct from every buyer surface. Same
// module-load parse pattern as SHARE_TOKEN.
const INTERNAL_TOKEN_MATCH = typeof window !== 'undefined'
  ? window.location.pathname.match(/^\/i\/([A-Za-z0-9_-]{16,})\/?$/)
  : null;
const INTERNAL_TOKEN = INTERNAL_TOKEN_MATCH ? INTERNAL_TOKEN_MATCH[1] : null;

// The internal viewer is an INTERNAL (colleague / seller) surface, so
// it follows the app's theme rather than pinning light the way every
// buyer surface does. It reads the theme through the --room-* tokens
// already — but Root() renders it INSTEAD of <App/>, and the effect
// that stamps data-theme onto <html> lives inside App. Without this,
// every one of those tokens resolved to its light fallback and a
// dark-mode seller opening their own document got a white page with
// dark-mode chrome around it.
//
// At module scope, not in an effect, so the attribute is set before
// first paint — an effect would repaint the page light-then-dark.
// Deliberately scoped to INTERNAL_TOKEN: /d/, /space/ and /share/ are
// buyer surfaces and must stay light whatever the seller prefers. A
// colleague with no seat has no stored preference and gets light,
// which is the right default for a link they were sent.
if (INTERNAL_TOKEN && typeof document !== 'undefined') {
  try {
    if (localStorage.getItem('trailercst_theme') === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
    }
  } catch (_) { /* storage blocked (private mode, embedded) — light is fine */ }
}

// /d/:token — public tracked DOCUMENT share viewer (doc share links,
// pairs with backend routes/docShares.js). Buyer-facing, no auth. Same
// module-load parse pattern as SHARE_TOKEN / INTERNAL_TOKEN.
const DOC_TOKEN_MATCH = typeof window !== 'undefined'
  ? window.location.pathname.match(/^\/d\/([A-Za-z0-9_-]{16,})\/?$/)
  : null;
const DOC_TOKEN = DOC_TOKEN_MATCH ? DOC_TOKEN_MATCH[1] : null;

// /form/:token — public form fill page (2026-09-07, flag forms). A
// per-recipient link minted from the Forms page; token is a
// distributions row exactly like /share and /d, so the same base64url
// shape applies. Buyer surface: parsed at module load, never touches
// auth state.
const FORM_TOKEN_MATCH = typeof window !== 'undefined'
  ? window.location.pathname.match(/^\/form\/([A-Za-z0-9_-]{16,})\/?$/)
  : null;
const FORM_TOKEN = FORM_TOKEN_MATCH ? FORM_TOKEN_MATCH[1] : null;

// Production API URL - change this to your Railway backend URL
const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';
// Shown when the backend answers 402 card_required (trial over, no
// plan yet). No self-serve checkout exists in the app any more.
const PLAN_REQUIRED_MESSAGE = 'Your free trial has ended. Get in touch and we\'ll get your team set up on a plan.';

// Mirror of backend/utils/quota.js (2026-05-15 single-tier migration):
//   TrailerCast — one per-seat tier; prices are quoted by the team, not
//                 shown in the app (2026-09-17)
//   Enterprise  — sales-led add-ons (SSO, SLA, white-label, etc.) on top
//
// Internal tier codes `starter` and `pro` both map to TrailerCast for
// display. Legacy starter (1-seat) subscriptions are honored at their
// existing Stripe price until Daniel migrates them; new signups land on
// 'pro' as the paid tier code. `free` is the trial state, capped at
// 10 demos in 14 days.
//
// Paid-tier limits are HIDDEN safety caps — marketing + in-app UI both
// say "unlimited demos." The numbers below exist only as a margin
// guardrail against a single power-user account torching unit
// economics. See backend/utils/quota.js for the authoritative cap.
const TIER_LIMITS = {
  free: 3,
  starter: 50,  // hidden cap — UI advertises "unlimited"
  pro: 100,     // hidden cap — UI advertises "unlimited"
  enterprise: null,
};
const TIER_DISPLAY = {
  free: 'Free trial',
  starter: 'TrailerCast',
  pro: 'TrailerCast',
  enterprise: 'Enterprise',
  // Beta — hand-assigned to early adopters by the founder. No
  // billing, no trial, no cap. Promote/demote via the backend
  // scripts/set-company-tier.js. Customers see "Beta" wherever
  // their tier is shown so they know they're on a special plan.
  beta: 'Beta',
};

// Jurisdiction copy mirrored from backend/utils/consentCopy.js.
// Keep the label/description strings in sync with the backend when
// either side changes — the backend's ALL_JURISDICTIONS is the
// authoritative enum.
const CONSENT_JURISDICTIONS = [
  {
    id: 'us_two_party',
    label: 'United States — two-party consent state',
    desc: 'CA, FL, IL, MD, MA, MT, NV, NH, PA, WA (and similar). All participants must consent to recording.',
  },
  {
    id: 'us_one_party',
    label: 'United States — one-party consent state',
    desc: 'Most US states. Federal and state law allow recording when at least one participant (you) consents.',
  },
  {
    id: 'canada',
    label: 'Canada',
    desc: 'PIPEDA governs commercial recording. Single-party consent is generally permitted; BC has additional PIPA rules.',
  },
  {
    id: 'eu_uk',
    label: 'European Union / United Kingdom',
    desc: 'GDPR / UK-GDPR applies. Recording typically requires a lawful basis — usually explicit participant consent.',
  },
  {
    id: 'other',
    label: 'Other / not listed',
    desc: 'Conservative default: treat as requiring explicit consent from all participants.',
  },
];
const CONSENT_JURISDICTION_LABEL = Object.fromEntries(CONSENT_JURISDICTIONS.map((j) => [j.id, j.label]));

// Append a short-lived signed playback token (minted from
// /api/demos/:id/playback-token) to a media-stream URL. The public stream
// routes serve recordings by bare UUID today; once is_private enforcement is
// enabled they will require this token for PRIVATE recordings. Appending it is
// harmless for non-private demos (the route ignores it), so callers don't need
// to branch on privacy. No token → the bare URL is returned unchanged.
function withPlaybackToken(url, token) {
  if (!token) return url;
  return url + (url.includes('?') ? '&' : '?') + 't=' + encodeURIComponent(token);
}

// ═══════════════════════════════════════════════════════════════
// REEL THUMBNAIL — shared across library sidebar, library page
// cards, dashboard recent-demo tiles, and top-performing rows.
// ═══════════════════════════════════════════════════════════════
// Points a <video preload=metadata> at the public /api/stream/:id
// endpoint. Once the browser has enough bytes to decode, we seek
// to duration/2 so the poster is a halfway-through frame (more
// representative of the trailer than the intro slide, which is the
// same branded card on every demo). On hover we autoplay-preview
// Gong-style; on leave we pause and reset to the halfway poster.
// Muted + playsInline is required for autoplay under modern
// browser policies. The parent is responsible for sizing the
// element and handling placeholder/fallback for non-completed
// demos — this component assumes the trailer is playable.
// ═══════════════════════════════════════════════════════════════
// TRAILERS SCREEN — shared constants
// ═══════════════════════════════════════════════════════════════

// The two halves of the Trailers screen. A trailer is either tied to a
// specific deal (the retrospective archive) or it is a reusable asset
// that gets sent to many prospects. Same recording table, two very
// different jobs, and reps were previously scrolling past one to find
// the other.
const TRAILER_TABS = [
  {
    value: 'deal',
    label: 'Deal',
    subtitle: 'Trailers built from a specific call, kept with the deal they belong to. Reopen one to copy its share link, check engagement, or regenerate a follow-up.',
    emptyTitle: 'No deal trailers yet',
    emptyBody: 'Upload a demo recording and the AI builds a trailer from the moments that mattered.',
  },
  {
    value: 'reusable',
    label: 'Reusable',
    subtitle: 'Micro demos, product overviews, and explainers you record once and reuse across every deal. Drop them into any Decision Room or follow-up.',
    emptyTitle: 'Record a micro demo once, reuse it in every deal',
    emptyBody: 'A two-minute walkthrough of one feature, an integration explainer, a pricing overview — record it in the browser and it is ready to drop into any deal, forever.',
  },
];

// Sort keys are the API's, verbatim — see the SORT_SQL allowlist in
// backend/db/demos.js. Sending a key the server does not know is not a
// silent no-op there (it falls back to created_desc), but keeping the
// two lists literally identical is what makes that never matter.
const TRAILER_SORTS = [
  { value: 'created_desc',  label: 'Newest first' },
  { value: 'created_asc',   label: 'Oldest first' },
  { value: 'updated_desc',  label: 'Recently updated' },
  { value: 'name_asc',      label: 'Name A–Z' },
  { value: 'name_desc',     label: 'Name Z–A' },
  { value: 'duration_desc', label: 'Longest first' },
  { value: 'duration_asc',  label: 'Shortest first' },
];

// How many rows a Trailers page pulls. Matches the API default and the
// previous hard-coded value, so an existing workspace sees exactly the
// same first screen — the difference is that row 51 is now reachable.
const TRAILER_PAGE_SIZE = 50;

// "4m 12s" / "38s". Returns null for anything unusable so callers can
// skip the badge entirely rather than rendering "0s" or "NaNs".
function formatTrailerDuration(seconds) {
  const total = Math.round(Number(seconds));
  if (!Number.isFinite(total) || total <= 0) return null;
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return mm > 0 ? `${mm}m ${ss}s` : `${ss}s`;
}

// Trailer length. trailerDurationSeconds is the composed reel's own
// length (2026-09-05). durationSeconds is the RECORDING's — the whole
// call for a demo — and this used to badge it as if it were the
// trailer's, so a 4-minute reel read "47m". It is only the trailer's
// length when the file IS the trailer (browser recording, uploaded
// video). Rows that predate both fall back to summing the moments the
// way this screen always did — including the end-minus-start fallback
// for TIER 2 moments, which do not carry `duration` and would
// otherwise make the badge vanish on a subset of trailers.
function trailerDurationSeconds(d) {
  if (!d) return null;
  const fromTrailer = Number(d.trailerDurationSeconds);
  if (Number.isFinite(fromTrailer) && fromTrailer > 0) return fromTrailer;
  const fileIsTrailer = d.recordingKind === 'browser_recording' || d.recordingKind === 'uploaded_video';
  const fromColumn = Number(d.durationSeconds);
  if (fileIsTrailer && Number.isFinite(fromColumn) && fromColumn > 0) return fromColumn;
  const momentSeconds = (m) => {
    const dur = Number(m.duration);
    if (Number.isFinite(dur) && dur > 0) return dur;
    const start = Number(m.startSeconds);
    const end = Number(m.endSeconds);
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) return end - start;
    return 0;
  };
  const summed = (d.moments || []).reduce((sum, m) => sum + momentSeconds(m), 0);
  return summed > 0 ? summed : null;
}

// Trailer-class rows only. Qualifying calls and meetings share the
// demos table but run a lighter pipeline and live in the Calls inbox.
// Sent to the API as ?recordingKind= rather than filtered out of the
// response, because the API's `total` is what these screens render as
// their denominator — narrowing after the fetch made it count rows they
// never show. Legacy NULL recordingKind is folded into 'demo'
// server-side (see NULL_RECORDING_KIND in backend/db/demos.js).
// 'uploaded_video' (finished_video_upload, 2026-09-04) is a finished
// file stored as-is — trailer-class, exactly like a browser recording.
const TRAILER_RECORDING_KINDS = ['demo', 'browser_recording', 'uploaded_video'];

// Recording kinds that open in the trailer review surface. Anything
// else (qualifying calls, meetings) belongs to the Calls inbox.
const TRAILER_CLASS_KINDS = new Set(TRAILER_RECORDING_KINDS);

// Sprite grid shape — must match backend/utils/generateThumbnailSprite.js.
// Changing these without a matching backend deploy will render a
// stretched / misaligned scrub.
const REEL_SPRITE_FRAMES = 10;

// Stable pseudo-random poster frame derived from the demo id. Keeps
// the library visually varied across tiles (critical when multiple
// demos share a cloned sprite sheet — seeded demos, rebuilds) while
// staying deterministic so the same demo shows the same poster on
// every page load. djb2-style hash, good enough for this.
function posterFrameIndex(demoId) {
  if (!demoId || typeof demoId !== 'string') return Math.floor(REEL_SPRITE_FRAMES / 2);
  let h = 5381;
  for (let i = 0; i < demoId.length; i++) {
    h = ((h << 5) + h + demoId.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % REEL_SPRITE_FRAMES;
}

// Same hashing idea for the video-fallback path — seeks to a stable
// but varied position in the [10%, 90%] window of the trailer's
// duration. Avoids always showing the middle frame on demos that
// share a cloned blob_key.
function posterSeekFraction(demoId) {
  if (!demoId || typeof demoId !== 'string') return 0.5;
  let h = 5381;
  for (let i = 0; i < demoId.length; i++) {
    h = ((h << 5) + h + demoId.charCodeAt(i)) | 0;
  }
  // 0.10 … 0.90 so we skip the branded intro/outro cards that hang
  // around the edges on most trailers.
  return 0.1 + (Math.abs(h) % 81) / 100;
}

// The API now scores each trailer's own sprite strip and returns the
// frame worth resting on as `posterFrameIndex`. That is strictly
// better than hashing the UUID, which only ever guaranteed "stable and
// varied" — never "a frame worth looking at". The hash below stays as
// the fallback for rows the backend has not scored (legacy trailers)
// and for any window where this build is ahead of the API, where the
// field is simply absent and reads as undefined.
function resolvePosterFrame(explicit, demoId) {
  if (Number.isInteger(explicit) && explicit >= 0 && explicit < REEL_SPRITE_FRAMES) {
    return explicit;
  }
  return posterFrameIndex(demoId);
}

// Same idea for the <video> fallback path, which needs a fraction of
// the runtime rather than a frame index. Frame N covers
// [N/10, (N+1)/10] of the trailer, so aim at the middle of its bucket.
function resolveSeekFraction(explicit, demoId) {
  if (Number.isInteger(explicit) && explicit >= 0 && explicit < REEL_SPRITE_FRAMES) {
    return (explicit + 0.5) / REEL_SPRITE_FRAMES;
  }
  return posterSeekFraction(demoId);
}

// ═══════════════════════════════════════════════════════════════
// DEAL STATUS PILL — fictitious data until CRM integration lands.
// ═══════════════════════════════════════════════════════════════
//
// Once the CRM (Salesforce / HubSpot / Pipedrive) is wired up, each
// demo row will carry a real `dealStatus` field set by a backend
// sync. In the meantime we derive a deterministic status from the
// demo id so every render is stable and the distribution across a
// list of demos reads as realistic (60% pending, 25% won, 15% lost).
//
// Swap-over plan: when the backend ships the real field, the consumer
// sites below (library card, recent-demo tile, top-performing row)
// will prefer `d.dealStatus` over the fake derivation. The shared
// component here doesn't need to change.
const DEAL_STATUSES = {
  won:     { label: 'Closed Won',        className: 'deal-status-pill--won' },
  lost:    { label: 'Closed Lost',       className: 'deal-status-pill--lost' },
  pending: { label: 'Pending Decision',  className: 'deal-status-pill--pending' },
};

function fakeDealStatusForDemo(demo) {
  // Caller already has the real status via demo.dealStatus once the
  // CRM sync is up — short-circuit when that's set.
  if (demo && typeof demo.dealStatus === 'string' && DEAL_STATUSES[demo.dealStatus]) {
    return demo.dealStatus;
  }
  // djb2 hash on a stable id so the same demo always shows the same
  // status regardless of which surface renders it.
  const seed = demo && (demo.id || demo.demoId || demo.filename || '') + '';
  if (!seed) return 'pending';
  let h = 5381;
  for (const ch of seed) h = ((h << 5) + h + ch.charCodeAt(0)) | 0;
  const bucket = Math.abs(h) % 100;
  if (bucket < 25) return 'won';
  if (bucket < 40) return 'lost';
  return 'pending';
}

function DealStatusPill({ demo, compact = false }) {
  // Real CRM data ONLY (dashboards Tier-1, 2026-07-22). The pill used
  // to fall back to fakeDealStatusForDemo — a hash of the demo id —
  // which painted fabricated won/lost states on real deals. On a sales
  // dashboard that's worse than showing nothing: it either misleads or,
  // once spotted, poisons trust in every real number on the page.
  // No CRM-synced status → no pill.
  const status = demo && demo.dealStatus;
  const cfg = DEAL_STATUSES[status];
  if (!cfg) return null;
  return (
    <span
      className={`deal-status-pill ${cfg.className} ${compact ? 'deal-status-pill--compact' : ''}`}
      title={`Deal status: ${cfg.label} (from CRM)`}
    >
      <span className="deal-status-pill-dot" aria-hidden="true" />
      {cfg.label}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════
// ENGAGEMENT HEAT-STRIP — per-second watch density under the trailer
// player. Intensity 0..1 drives opacity of an indigo fill on each
// bucket. Gaps (never-watched seconds) render as near-transparent
// so the eye picks out which chunks actually got attention.
// ═══════════════════════════════════════════════════════════════

function EngagementHeatStrip({ heatmap }) {
  if (!heatmap || !Array.isArray(heatmap.buckets) || heatmap.buckets.length === 0) {
    return null;
  }
  // No views yet. This is now the NORMAL state for a fresh trailer —
  // the backend used to manufacture sample segments to fill the strip
  // and no longer does (2026-07-28), so an unwatched trailer honestly
  // renders empty. Not aria-hidden: this sentence is the only content
  // in this state, so it has to reach screen readers.
  if (!heatmap.totalSegments) {
    return (
      <div className="engagement-heatstrip engagement-heatstrip--empty">
        <span className="engagement-heatstrip-label">Views appear here once prospects watch</span>
      </div>
    );
  }
  return (
    <div
      className={`engagement-heatstrip ${heatmap.synthetic ? 'engagement-heatstrip--synthetic' : ''}`}
      role="img"
      aria-label={`Engagement heat-strip — ${heatmap.totalSegments} playback segments across this trailer`}
    >
      {heatmap.buckets.map((b, i) => (
        <span
          key={i}
          className={`engagement-heatstrip-bar ${b.watches === 0 ? 'is-cold' : ''}`}
          style={{
            // Minimum baseline so cold regions are still visible as
            // "nobody's here" rather than invisible. Hot regions saturate.
            opacity: b.watches === 0 ? 0.08 : 0.3 + b.intensity * 0.7,
          }}
          title={`${Math.floor(b.startSeconds / 60)}:${String(Math.round(b.startSeconds % 60)).padStart(2, '0')} — watched ${b.watches}×`}
        />
      ))}
      {heatmap.synthetic && (
        <span
          className="engagement-heatstrip-synthetic-label"
          title="This trailer has no real views yet — showing sample engagement so you can see the layout. Live data replaces this the moment a prospect watches."
        >
          Sample
        </span>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// DEAL BRIEF — AI-generated post-demo debrief card
// ═══════════════════════════════════════════════════════════════
//
// Renders the structured brief produced by backend/utils/generateDealBrief.js.
// Each section gracefully collapses when its array is empty so the
// card doesn't render "No competitors mentioned" filler. "Generating
// deal brief..." state shows when a demo is fresh and the brief hasn't
// landed yet (fire-and-forget pipeline — can arrive after the user
// already opened the trailer).
const DEAL_BRIEF_OBJECTION_SEVERITY_LABEL = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

function DealBrief({ brief, demoCompletedAt, defaultOpen = false }) {
  // Collapsed by default — these briefs get long (4 questions, 3
  // objections, 4 next-actions can run past a viewport's worth of
  // content) and the seller has the trailer + chapters above to digest
  // first. The chevron in the header makes the click target obvious.
  // When rendered inside a fixed-pill container (the trailer-review
  // section), pass defaultOpen=true so the panel doesn't appear to
  // shrink the moment the pill becomes active.
  const [open, setOpen] = useState(defaultOpen);

  // "Generating…" heuristic — show the pending state for demos
  // completed in the last 10 minutes. Older demos with no brief are
  // probably pre-feature (legacy) or had a generator failure; either
  // way a pending state would be misleading.
  const showPending = !brief
    && demoCompletedAt
    && (Date.now() - new Date(demoCompletedAt).getTime()) < 10 * 60 * 1000;

  if (!brief && !showPending) return null;

  if (!brief && showPending) {
    return (
      <section className="deal-brief deal-brief--pending">
        <div className="deal-brief-head">
          <div className="deal-brief-title-group">
            <span className="deal-brief-eyebrow">AI Deal Brief</span>
            <h3>Analyzing this demo…</h3>
          </div>
        </div>
        <p className="deal-brief-pending-msg">
          We're building your post-demo notes — questions asked, objections
          raised, what excited them, and suggested next steps. Usually ready
          within a minute of the trailer completing.
        </p>
      </section>
    );
  }

  const hasQuestions = Array.isArray(brief.questions) && brief.questions.length > 0;
  const hasObjections = Array.isArray(brief.objections) && brief.objections.length > 0;
  const hasExcited = Array.isArray(brief.excitedAbout) && brief.excitedAbout.length > 0;
  const hasCompetitors = Array.isArray(brief.competitors) && brief.competitors.length > 0;
  const hasNextActions = Array.isArray(brief.nextActions) && brief.nextActions.length > 0;

  // Small rollup for the collapsed-card header so the seller sees
  // how much content is waiting behind the chevron without expanding.
  const rollupParts = [];
  if (hasNextActions) rollupParts.push(`${brief.nextActions.length} next action${brief.nextActions.length === 1 ? '' : 's'}`);
  if (hasQuestions) rollupParts.push(`${brief.questions.length} question${brief.questions.length === 1 ? '' : 's'}`);
  if (hasObjections) rollupParts.push(`${brief.objections.length} objection${brief.objections.length === 1 ? '' : 's'}`);
  if (hasExcited) rollupParts.push(`${brief.excitedAbout.length} excited`);
  if (hasCompetitors) rollupParts.push(`${brief.competitors.length} competitor${brief.competitors.length === 1 ? '' : 's'}`);
  const rollup = rollupParts.join(' · ');

  return (
    <section className={`deal-brief ${open ? 'is-open' : 'is-collapsed'}`}>
      <button
        type="button"
        className="deal-brief-head deal-brief-head--clickable"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="deal-brief-body"
      >
        <div className="deal-brief-title-group">
          <span className="deal-brief-eyebrow">AI Deal Brief</span>
          <h3>Post-demo notes</h3>
        </div>
        <div className="deal-brief-head-right">
          {rollup && <span className="deal-brief-rollup">{rollup}</span>}
          <span className="deal-brief-chevron" aria-hidden="true">
            {open ? '▲' : '▼'}
          </span>
        </div>
      </button>

      {!open && brief.summary && (
        <InlineMarkdown
          tag="p"
          text={brief.summary}
          className="deal-brief-summary deal-brief-summary--peek"
        />
      )}

      {open && (
      <div id="deal-brief-body" className="deal-brief-body">

      {brief.summary && (
        <InlineMarkdown
          tag="p"
          text={brief.summary}
          className="deal-brief-summary"
        />
      )}

      {hasNextActions && (
        <div className="deal-brief-section deal-brief-next">
          <h4>Next best actions</h4>
          <ol>
            {brief.nextActions.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ol>
        </div>
      )}

      {hasExcited && (
        <div className="deal-brief-section deal-brief-excited">
          <h4>What excited them</h4>
          <ul>
            {brief.excitedAbout.map((e, i) => (
              <li key={i}>
                <strong>{e.topic}</strong>
                {e.evidence && <> — <span className="deal-brief-evidence">"{e.evidence}"</span></>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasObjections && (
        <div className="deal-brief-section deal-brief-objections">
          <h4>Objections raised</h4>
          <ul>
            {brief.objections.map((o, i) => (
              <li key={i}>
                <span className={`deal-brief-severity deal-brief-severity--${o.severity}`}>
                  {DEAL_BRIEF_OBJECTION_SEVERITY_LABEL[o.severity] || 'Medium'}
                </span>
                {o.objection}
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasQuestions && (
        <div className="deal-brief-section deal-brief-questions">
          <h4>Questions they asked</h4>
          <ul>
            {brief.questions.map((q, i) => (
              <li key={i}>
                <span className="deal-brief-q-text">{q.question}</span>
                {(q.askedBy || q.timestamp) && (
                  <span className="deal-brief-q-meta">
                    {q.askedBy && <span>{q.askedBy}</span>}
                    {q.askedBy && q.timestamp && <span> · </span>}
                    {q.timestamp && <span className="deal-brief-q-ts">{q.timestamp}</span>}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasCompetitors && (
        <div className="deal-brief-section deal-brief-competitors">
          <h4>Competitors mentioned</h4>
          <ul>
            {brief.competitors.map((c, i) => (
              <li key={i}>
                <strong>{c.name}</strong>
                {c.context && <> — {c.context}</>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="deal-brief-footer">
        Generated by AI. Double-check key details against the recording before forwarding.
      </div>

      </div>
      )}
    </section>
  );
}

// Shown in thumbnail placeholder slots (library sidebar rows, dashboard
// recent-demo tiles, library grid cards) while a trailer is still
// generating. Reuses the same five-bar wave animation as the upload
// processing view so the "we're working on it" affordance is
// consistent across surfaces, and overlays a thin progress bar when
// the caller has a percent to show.
const ProcessingWave = React.memo(function ProcessingWave({ progress = null, size = 'md' }) {
  const hasPct = typeof progress === 'number' && Number.isFinite(progress) && progress > 0;
  return (
    <div className={`processing-wave processing-wave--${size}`}>
      <div className="processing-wave-bars">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="processing-wave-bar"
            style={{ animationDelay: `${i * 0.1}s` }}
          />
        ))}
      </div>
      {hasPct && (
        <>
          <div className="processing-wave-bar-track">
            <div
              className="processing-wave-bar-fill"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
          </div>
          <div className="processing-wave-pct">{Math.round(progress)}%</div>
        </>
      )}
    </div>
  );
});

// Memoized so App state updates (toast open/close, modal open/close,
// typing anywhere on the page) don't re-render every thumbnail on
// the dashboard. Props (demoId, hasSprite, className) are primitives
// so the default shallow-compare is exactly what we want.
const ReelThumb = React.memo(function ReelThumb({ demoId, hasSprite, className, posterFrame }) {
  // Two render paths:
  //   (1) hasSprite === true  → show the pre-generated JPEG sprite as
  //       a CSS background-image. Poster = middle frame; on hover we
  //       shift background-position-x based on cursor X for a Gong-
  //       style scrub preview. Instant load, zero network on hover.
  //   (2) hasSprite === false → fall back to the older <video
  //       preload="metadata"> approach that lazily pulls the first
  //       keyframe when the element scrolls into view. Slower on
  //       first paint, but keeps legacy demos (pre-sprite-gen) and
  //       the rare sprite-gen failures working.
  if (hasSprite) {
    return <ReelThumbSprite demoId={demoId} className={className} posterFrame={posterFrame} />;
  }
  return <ReelThumbVideo demoId={demoId} className={className} posterFrame={posterFrame} />;
});

const ReelThumbSprite = React.memo(function ReelThumbSprite({ demoId, className, posterFrame }) {
  const ref = useRef(null);
  // Cached bounding rect — set on mouseenter, read on mousemove.
  // Moves the layout-invalidating getBoundingClientRect() call out
  // of the 60Hz mousemove path. Invalidated on mouseleave so a
  // subsequent enter re-reads the rect (handles scroll + resize).
  const rectRef = useRef(null);
  // rAF handle + latest pending cursor X. Coalesces mousemove bursts
  // into one paint per animation frame — setFrame was otherwise firing
  // dozens of times per second, saturating React's scheduler.
  const rafRef = useRef(0);
  const pendingXRef = useRef(0);

  // Frame index drives background-position. Prefer the backend's
  // content-scored choice; fall back to the stable pseudo-random
  // frame per demoId, which gives the library visual variety even
  // when multiple demos share a cloned sprite (seed data, rebuilds)
  // and reads more natural than always-middle.
  const midIndex = resolvePosterFrame(posterFrame, demoId);
  const [frame, setFrame] = useState(midIndex);
  // The resting frame can change after first paint — the list renders
  // from whatever is already in state, then a refetch fills
  // posterFrameIndex in. Re-seat it when that happens, but never
  // mid-scrub: rectRef is non-null exactly while the cursor is over
  // the thumbnail, and stomping `frame` there would fight the user.
  useEffect(() => {
    if (rectRef.current) return;
    setFrame(midIndex);
  }, [midIndex]);

  const applyPendingFrame = () => {
    rafRef.current = 0;
    const rect = rectRef.current;
    if (!rect || rect.width <= 0) return;
    const ratio = Math.max(0, Math.min(1, pendingXRef.current / rect.width));
    const next = Math.min(
      REEL_SPRITE_FRAMES - 1,
      Math.max(0, Math.floor(ratio * REEL_SPRITE_FRAMES))
    );
    setFrame((prev) => (prev === next ? prev : next));
  };

  const handleEnter = (e) => {
    rectRef.current = e.currentTarget.getBoundingClientRect();
  };
  const handleMove = (e) => {
    // Lazily grab the rect if somehow we got a move without a
    // preceding enter (touchscreens + edge cases).
    if (!rectRef.current) {
      rectRef.current = e.currentTarget.getBoundingClientRect();
    }
    pendingXRef.current = e.clientX - rectRef.current.left;
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(applyPendingFrame);
    }
  };
  const handleLeave = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    rectRef.current = null;
    setFrame(midIndex);
  };

  // background-size: N00% 100% means the sprite sheet is N times the
  // container width (one "page" per frame). background-position-x as
  // a percentage then slides by whole frames: 0% = frame 0, 100% =
  // last frame. Formula: (frame / (N-1)) * 100%.
  const bgStyle = {
    backgroundImage: `url(${API_BASE}/api/thumbnails/${demoId})`,
    backgroundSize: `${REEL_SPRITE_FRAMES * 100}% 100%`,
    backgroundPosition: `${(frame / (REEL_SPRITE_FRAMES - 1)) * 100}% 0`,
    backgroundRepeat: 'no-repeat',
  };

  return (
    <div
      ref={ref}
      className={`${className || ''} reel-thumb-sprite`}
      style={bgStyle}
      onMouseEnter={handleEnter}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      aria-hidden="true"
    />
  );
});

const ReelThumbVideo = React.memo(function ReelThumbVideo({ demoId, className, posterFrame }) {
  const ref = useRef(null);
  // Seek to a pseudo-random position derived from demoId (stable per
  // trailer, varied across tiles). Wrapped in try/catch because some
  // browsers throw NotSupportedError if the Range bytes for that
  // position haven't loaded yet — we'll just stay at the default
  // first-frame poster, which is still fine.
  const seekToMid = () => {
    const v = ref.current;
    if (!v) return;
    const dur = v.duration;
    if (!isFinite(dur) || dur <= 0) return;
    try { v.currentTime = dur * resolveSeekFraction(posterFrame, demoId); } catch { /* noop */ }
  };
  const handleEnter = () => {
    const v = ref.current;
    if (!v) return;
    // .play() returns a Promise that rejects on autoplay blocks.
    // Muted + playsInline satisfies Chrome/Safari/Firefox policies
    // in practice, but swallow the rejection defensively.
    v.play().catch(() => {});
  };
  const handleLeave = () => {
    const v = ref.current;
    if (!v) return;
    v.pause();
    seekToMid();
  };
  return (
    <video
      ref={ref}
      src={`${API_BASE}/api/stream/${demoId}`}
      preload="metadata"
      muted
      playsInline
      className={className}
      onLoadedMetadata={seekToMid}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    />
  );
});

// ═══════════════════════════════════════════════════════════════
// DEMO-ROW KEBAB MENU — portal + fixed positioning
// ═══════════════════════════════════════════════════════════════
// The dropdown renders through React.createPortal into document.body,
// with inline position:fixed coordinates computed from the kebab
// button's bounding rect. That lets the menu escape every ancestor's
// overflow-hidden / overflow-auto (which was clipping the menu to the
// sidebar + library-list on the dashboard). Closes on scroll/resize
// because fixed coords don't track layout changes — a seller who
// scrolls the library wants the stale-position menu gone anyway.
function DemoKebabMenu({ isOpen, onOpenChange, variant = 'default', menuItems }) {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  // Ref-mirror of onOpenChange so the scroll/resize close handlers can
  // read the latest function without us having to put `onOpenChange`
  // in the position-effect's deps. Parents pass an inline arrow on
  // every render — including it in deps re-fires the effect on every
  // render, which resets `placed` faster than the flip logic can
  // settle, leaving the menu stuck in `visibility: hidden`.
  const onOpenChangeRef = useRef(onOpenChange);
  useEffect(() => { onOpenChangeRef.current = onOpenChange; });

  // `coords` carries either { top, right } (open below) or { bottom, right }
  // (open above) so the menu's anchor edge is always pinned to the kebab,
  // regardless of which direction we're flipping. When the menu first
  // mounts we render it below with `visibility: hidden`, measure its
  // height, then flip if it would clip the viewport bottom. The second
  // setCoords reveals it in its final position with no visible flicker.
  const [coords, setCoords] = useState(null);
  // Tracks whether the menu has been measured + finalized. Until true,
  // we keep `visibility: hidden` so users don't see the pre-flip frame.
  const [placed, setPlaced] = useState(false);

  useLayoutEffect(() => {
    if (!isOpen) { setCoords(null); setPlaced(false); return; }
    const r = buttonRef.current && buttonRef.current.getBoundingClientRect();
    if (!r) return;
    // Initial guess: open below. The post-render effect below measures
    // and flips if the menu would overflow the viewport bottom.
    setCoords({
      top: Math.round(r.bottom + 4),
      right: Math.round(window.innerWidth - r.right),
    });
    setPlaced(false);

    const closeIt = () => onOpenChangeRef.current(false);
    // Capture-phase scroll catches scrolls in any ancestor — the
    // library-list scroll in the sidebar would otherwise move the
    // kebab out from under the (still-fixed-position) menu.
    window.addEventListener('scroll', closeIt, { capture: true, passive: true });
    window.addEventListener('resize', closeIt, { passive: true });
    return () => {
      window.removeEventListener('scroll', closeIt, { capture: true });
      window.removeEventListener('resize', closeIt);
    };
  }, [isOpen]);

  // Second pass: after the portal mounts the menu, measure its actual
  // rendered height and clamp the position so the menu always stays
  // fully on-screen.
  //
  // Three cases:
  //   1. Menu fits below the kebab → keep original position.
  //   2. Menu doesn't fit below but fits above → flip up.
  //   3. Menu doesn't fit either way → clamp to viewport bottom (best
  //      effort: top items visible, scroll-into-view if needed). The
  //      previous version fell through case 3 with the menu still
  //      anchored below the kebab, which positioned the entire menu
  //      below the viewport edge — visually nothing rendered.
  //
  // Always positioning with `top` (never `bottom`) keeps the math
  // simple and means React doesn't have to clear a stale `top` style
  // when transitioning between cases.
  useLayoutEffect(() => {
    if (!isOpen || !coords || placed) return;
    const menuEl = menuRef.current;
    const btnEl = buttonRef.current;
    if (!menuEl || !btnEl) return;

    const menuH = menuEl.offsetHeight;
    const btnRect = btnEl.getBoundingClientRect();
    const viewportH = window.innerHeight;
    // Margin of safety so we don't kiss the viewport edge.
    const MARGIN = 8;

    const belowTop = btnRect.bottom + 4;
    const aboveTop = btnRect.top - 4 - menuH;
    const fitsBelow = belowTop + menuH + MARGIN <= viewportH;
    const fitsAbove = aboveTop >= MARGIN;

    let nextTop;
    if (fitsBelow) {
      nextTop = belowTop;                                  // case 1
    } else if (fitsAbove) {
      nextTop = aboveTop;                                  // case 2
    } else {
      // Case 3 — neither side fits cleanly. Pin to the bottom of the
      // viewport with a margin; if the menu is taller than the viewport
      // the top items get clipped (acceptable degradation), but at
      // least SOMETHING is visible and the menu can be scrolled.
      nextTop = Math.max(MARGIN, viewportH - menuH - MARGIN);
    }

    if (nextTop !== coords.top) {
      setCoords({
        top: Math.round(nextTop),
        right: coords.right,
      });
    }
    setPlaced(true);
  }, [isOpen, coords, placed]);

  // Translate `coords` into the inline style the portal element uses.
  const portalStyle = coords ? {
    position: 'fixed',
    top: `${coords.top}px`,
    right: `${coords.right}px`,
    visibility: placed ? 'visible' : 'hidden',
    // High z-index so the portalled menu floats above any sibling card
    // with its own stacking context. The previous z-index of 50 was
    // beaten by sticky headers / modals on some surfaces, leaving the
    // menu visually behind them.
    zIndex: 9999,
  } : null;

  return (
    <div className="demo-action-menu-wrap">
      <button
        ref={buttonRef}
        type="button"
        className={`demo-action-kebab ${isOpen ? 'is-open' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          onOpenChange(!isOpen);
        }}
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title="More actions"
      >
        ⋮
      </button>
      {isOpen && coords && createPortal(
        <div
          ref={menuRef}
          className={`demo-action-menu demo-action-menu--portal demo-action-menu--${variant}`}
          role="menu"
          style={portalStyle}
          // Stop clicks from reaching the window outside-click handler
          // while still letting item onClicks fire normally.
          onClick={(e) => e.stopPropagation()}
        >
          {menuItems}
        </div>,
        document.body
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ONBOARDING FORM — 5-question personalization, shared between the
// first-visit modal and the Settings > AI Personalization tab.
// ═══════════════════════════════════════════════════════════════
// Extracted from the App component so keystrokes update state
// locally here instead of triggering a full App re-render. With
// App.jsx approaching 7,000 lines, re-rendering on every character
// typed into a textarea was producing a visible lag. This
// component owns its own form state + submit state + error state,
// seeds from `initialAnswers` on mount, and reports save results
// via the async `onSave` prop (returns promise — parent throws on
// error, component catches + displays inline).
function OnboardingForm({
  initialAnswers,
  isReadOnly,
  isFirstAdmin = false, // when true, render the team-size + tier-pref
                        // question that auto-routes the post-trial
                        // pricing modal. Driven by the `isAdmin` flag
                        // on authUser.company at signup time so invited
                        // members (whose tier is inherited) don't see it.
  onSave,          // async (answers) => void — throws on error
  onCancel,        // optional () => void; shows a secondary button when provided
  submitLabel = 'Save personalization',
  cancelLabel = 'Cancel',
}) {
  const [form, setForm] = React.useState(() => ({
    companyDescription: (initialAnswers && initialAnswers.companyDescription) || '',
    targetBuyer:        (initialAnswers && initialAnswers.targetBuyer) || '',
    industry:           (initialAnswers && initialAnswers.industry) || '',
    valueProps:         (initialAnswers && initialAnswers.valueProps) || '',
    painPoints:         (initialAnswers && initialAnswers.painPoints) || '',
    teamSize:           (initialAnswers && initialAnswers.teamSize) || '',
    tierPreference:     (initialAnswers && initialAnswers.tierPreference) || '',
  }));
  const [error, setError] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  const updateField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSave(form);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  // Map team size → suggested tier. Solo + 2-5 → Starter; 6-15 → Pro;
  // 15+ → Enterprise. Used to auto-fill tierPreference whenever the
  // user picks a team-size button so the post-trial pricing modal can
  // pre-select the right tier without a second decision.
  const teamSizeToTier = (size) => {
    if (size === 'just-me') return 'starter';
    if (size === '2-5') return 'starter';
    if (size === '6-15') return 'pro';
    if (size === '15+') return 'enterprise';
    return '';
  };
  const handleTeamSize = (size) => {
    setForm((prev) => ({
      ...prev,
      teamSize: size,
      tierPreference: teamSizeToTier(size),
    }));
  };

  return (
    <form className="onboarding-form" onSubmit={handleSubmit}>
      {isFirstAdmin && (
        <div className="auth-field">
          <label>How big is your sales team?</label>
          <div className="onboarding-team-size-grid">
            {[
              { id: 'just-me',  label: 'Just me',     hint: 'Solo founder or AE' },
              { id: '2-5',      label: '2 – 5',       hint: 'Small team, single pod' },
              { id: '6-15',     label: '6 – 15',      hint: 'Multi-pod / multi-product' },
              { id: '15+',      label: '15+',         hint: 'Larger org, talk to sales' },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={`onboarding-team-size-btn ${form.teamSize === opt.id ? 'is-selected' : ''}`}
                onClick={() => handleTeamSize(opt.id)}
                disabled={isReadOnly}
              >
                <span className="onboarding-team-size-label">{opt.label}</span>
                <span className="onboarding-team-size-hint">{opt.hint}</span>
              </button>
            ))}
          </div>
          <span className="auth-field-hint">
            We'll suggest the right plan based on your answer. You can switch later — every plan ships every product feature.
          </span>
        </div>
      )}

      <div className="auth-field">
        <label>What does your company do?</label>
        <textarea
          rows={3}
          maxLength={1000}
          placeholder="We're an AI-powered sales engagement platform that turns demo recordings into personalized trailers..."
          value={form.companyDescription}
          onChange={updateField('companyDescription')}
          disabled={isReadOnly}
        />
        <span className="auth-field-hint">1-2 sentences. What you sell and who it's for.</span>
      </div>

      <div className="auth-field">
        <label>Who's your typical buyer?</label>
        <select
          value={form.targetBuyer}
          onChange={updateField('targetBuyer')}
          disabled={isReadOnly}
        >
          <option value="">Select one…</option>
          <option value="Sales leader (VP Sales / CRO)">Sales leader (VP Sales / CRO)</option>
          <option value="RevOps leader">RevOps leader</option>
          <option value="SDR / BDR manager">SDR / BDR manager</option>
          <option value="Account Executive">Account Executive</option>
          <option value="Sales Engineer">Sales Engineer</option>
          <option value="Founder / CEO">Founder / CEO</option>
          <option value="Marketing leader">Marketing leader</option>
          <option value="IT / Operations">IT / Operations</option>
          <option value="Other">Other</option>
        </select>
        <span className="auth-field-hint">The job title of the person who signs the contract.</span>
      </div>

      <div className="auth-field">
        <label>Industry</label>
        <select
          value={form.industry}
          onChange={updateField('industry')}
          disabled={isReadOnly}
        >
          <option value="">Select one…</option>
          <option value="SaaS / Software">SaaS / Software</option>
          <option value="Fintech">Fintech</option>
          <option value="Healthcare / HealthTech">Healthcare / HealthTech</option>
          <option value="Manufacturing / Industrial">Manufacturing / Industrial</option>
          <option value="Retail / E-commerce">Retail / E-commerce</option>
          <option value="Professional Services">Professional Services</option>
          <option value="Education / EdTech">Education / EdTech</option>
          <option value="Media / Entertainment">Media / Entertainment</option>
          <option value="Other">Other</option>
        </select>
        <span className="auth-field-hint">Your company's primary industry, not your buyer's.</span>
      </div>

      <div className="auth-field">
        <label>Your top value propositions</label>
        <textarea
          rows={4}
          maxLength={1000}
          placeholder="- Cut demo follow-up time from hours to minutes&#10;- 3x higher reply rate on personalized follow-ups&#10;- See exactly who opened the trailer and how far they watched"
          value={form.valueProps}
          onChange={updateField('valueProps')}
          disabled={isReadOnly}
        />
        <span className="auth-field-hint">What outcomes do customers buy you for? 2-4 bullet points is plenty.</span>
      </div>

      <div className="auth-field">
        <label>Pain points your product solves</label>
        <textarea
          rows={4}
          maxLength={1000}
          placeholder="- Generic follow-up emails that don't reflect the actual call&#10;- No visibility into whether a prospect engaged post-demo&#10;- Champions struggling to sell the deal internally from memory"
          value={form.painPoints}
          onChange={updateField('painPoints')}
          disabled={isReadOnly}
        />
        <span className="auth-field-hint">What problems do prospects describe when they come to you?</span>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="onboarding-actions">
        {onCancel && (
          <button
            type="button"
            className="onboarding-skip"
            onClick={onCancel}
            disabled={submitting}
          >
            {cancelLabel}
          </button>
        )}
        {!isReadOnly && (
          <button type="submit" className="generate-btn ready" disabled={submitting}>
            <span className="btn-text">{submitting ? 'Saving…' : submitLabel}</span>
          </button>
        )}
      </div>
    </form>
  );
}

// Maps the App's `phase` value to the matching docs slug. Used by
// PhaseSlugRegistrar below to drive the "Help on this page" deep-
// link in the TopBar Help menu. Returning '' falls back to the docs
// home (introduction page). Per-detail surfaces (call detail, demo
// detail) can override with more specific slugs via useHelp() inside
// their own components.
function phaseToHelpSlug(phase) {
  switch (phase) {
    case 'dashboard':         return '';
    case 'upload':            return 'recording';
    case 'completed':         return 'trailers/reviewing';
    case 'library':           return 'trailers/reviewing';
    case 'calls':             return 'calls';
    case 'buyer-spaces':      return 'buyer-spaces';
    case 'conversations':     return 'conversations';
    case 'reports':           return 'reports';
    case 'settings':          return 'setup/account';
    case 'security':          return 'roles-and-permissions';
    default:                  return '';
  }
}

// Tiny inner component that lives inside HelpProvider so it can
// call useHelp(). Watches the phase prop and pushes the right slug
// into the context. Mounted with no DOM output.
function PhaseSlugRegistrar({ phase }) {
  const { setSlug } = useHelp();
  useEffect(() => {
    setSlug(phaseToHelpSlug(phase));
    // We don't clear on unmount — there's always a phase, so the
    // slug is always defined while the app is alive.
  }, [phase, setSlug]);
  return null;
}

function App() {
  // ═══════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════
  const [selectedFile, setSelectedFile] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [trailerPath, setTrailerPath] = useState(null);
  // Race-window guard: backend sometimes flips status→completed a beat before
  // the composed trailer is streamable (R2 propagation / stream endpoint cold
  // path). Without this, the <video> element spins forever with no surface
  // for the user. onError flips this true; we render a "still processing"
  // panel with a Retry button instead.
  const [videoLoadError, setVideoLoadError] = useState(false);
  // 20-second watchdog on the <video> element. onLoadStart starts it;
  // onLoadedMetadata clears it. If it fires, treat the stream as stalled
  // (connection hung / R2 unreachable / backend cold-start) and flip to
  // the error panel so the user has a Retry button instead of an
  // infinite browser buffer spinner.
  const videoWatchdogRef = useRef(null);
  // Which demo's kebab action-menu is open. `null` = none. Only one open at a
  // time across the whole dashboard — clicking another kebab closes the prior.
  const [openActionMenuId, setOpenActionMenuId] = useState(null);

  // Rebuild-with-different-clips modal state. Open on the completed view
  // when the user wants to reshape the trailer without re-uploading.
  const [rebuildModalOpen, setRebuildModalOpen] = useState(false);
  const [rebuildSelected, setRebuildSelected] = useState(() => new Set());
  // 'replace' (default — overwrite this trailer) | 'new' (keep the
  // original and save the rebuilt version as its own trailer).
  const [rebuildMode, setRebuildMode] = useState('replace');
  const [rebuildSubmitting, setRebuildSubmitting] = useState(false);
  // Segment narration regen (2026-08-12): which chapter is mid-rewrite
  // (null = none) + the English scripts (gates the affordance).
  const [regenNarrationBusy, setRegenNarrationBusy] = useState(null);
  const [demoNarrationTexts, setDemoNarrationTexts] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildError, setRebuildError] = useState(null);

  // Custom-trailer modal state (hand-pick clips from the source recording).
  // Opens against a completed parent demo; submitting kicks off a NEW
  // demo row (parent_demo_id = source) so the original AI-auto trailer
  // stays intact. See backend/utils/customTrailer.js.
  const [customTrailerOpen, setCustomTrailerOpen] = useState(false);
  const [customTrailerParent, setCustomTrailerParent] = useState(null); // the demo we're branching from
  const [customTrailerToken, setCustomTrailerToken] = useState(null); // signed playback token — required for private parents
  const [customTrailerClips, setCustomTrailerClips] = useState([]);     // [{startMs, endMs, customLabel}]
  const [customTrailerInPoint, setCustomTrailerInPoint] = useState(null); // null or ms
  const [customTrailerCurrentMs, setCustomTrailerCurrentMs] = useState(0);
  const [customTrailerSubmitting, setCustomTrailerSubmitting] = useState(false);
  const [customTrailerError, setCustomTrailerError] = useState(null);
  const customTrailerVideoRef = useRef(null);
  // Building flag — true while a submitted custom trailer is being
  // rendered. Mirrors the `rebuilding` flag's role for the rebuild
  // flow; renders a processing panel on the parent demo's view with
  // a progress bar + "go to your new trailer" deep link when done.
  const [customTrailerBuilding, setCustomTrailerBuilding] = useState(false);
  const [customTrailerBuildingId, setCustomTrailerBuildingId] = useState(null);
  const [customTrailerBuildingProgress, setCustomTrailerBuildingProgress] = useState(0);
  // V3 additions: transcript-click selection + AI suggestions + timeline.
  // Transcript + AI moments come from the parent demo (lazy-loaded when
  // modal opens) so big payloads don't get pulled until needed.
  const [customTrailerTranscript, setCustomTrailerTranscript] = useState(null); // { segments: [{start, end, text}] }
  const [customTrailerSuggestions, setCustomTrailerSuggestions] = useState([]); // AI-detected moments from parent
  const [customTrailerLoadingDetails, setCustomTrailerLoadingDetails] = useState(false);
  const [customTrailerSelStart, setCustomTrailerSelStart] = useState(null);     // first-clicked transcript segment idx
  const [customTrailerSourceDuration, setCustomTrailerSourceDuration] = useState(0);
  // Timeline drag state — which clip + which edge (in/out) is being dragged.
  const [customTrailerDragging, setCustomTrailerDragging] = useState(null); // { index, edge: 'start'|'end' } | null
  const customTrailerTimelineRef = useRef(null);
  // V4.1: ref to the transcript scroll container + the currently active
  // segment's element, so we can scrollIntoView as the video plays.
  const customTrailerTranscriptListRef = useRef(null);
  const customTrailerActiveSegRef = useRef(null);
  // V4.2: undo state for the most-recently-removed clip. The seller
  // gets a 6-second window to restore. Tracked as a single-slot
  // history (last delete only) — covers the common "oh wait, I didn't
  // mean to delete that" case without complicating the UX with a full
  // undo stack.
  const [customTrailerUndo, setCustomTrailerUndo] = useState(null); // { clip, index, expiresAt } | null

  // Notifications bell — polled every 30s when a user is logged in and
  // the tab is visible. Unread count drives the red dot on the bell
  // icon; full list is shown in the dropdown panel.
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // Prospect-engagement threads on the completed-demo view (seller
  // side). V2: each thread is a series of messages between one
  // prospect email and the seller on a specific distribution. The
  // list shows threads; opening one reveals the full convo + a
  // reply textarea.
  const [demoThreads, setDemoThreads] = useState([]);
  const [demoMessagesLoading, setDemoMessagesLoading] = useState(false);
  const [openThreadKey, setOpenThreadKey] = useState(null);
  const [threadReplyBody, setThreadReplyBody] = useState('');
  const [threadReplySubmitting, setThreadReplySubmitting] = useState(false);
  const [threadReplyError, setThreadReplyError] = useState(null);

  // Conversations inbox — all prospect threads across every demo owned
  // by this user. Loaded when the Conversations phase opens. Each row
  // includes a signed thread-share URL so clicking "Open" takes the
  // seller straight to the conversation hub, no dashboard hop.
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);

  // ─── Upcoming calendar meetings (shown in LeftNav) ──────────────
  // Real data from /api/qualifying-calls/upcoming — bots scheduled by
  // the calendar poller in `status IN ('scheduled','joining','recording')`.
  // Empty by default; the widget in LeftNav is gated on length > 0 so
  // it stays hidden until real calls land. A beta customer reported
  // seeing the previous seed data (Acme Corp / Northwind / Meridian /
  // Vireo) on their dashboard, which was confusing — that fictitious
  // data has been removed entirely. Future: provider detection from
  // meetingUrl hostname; right now we default to the generic glyph.
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  // upcoming_calls_join only: calendar connection state for the widget's
  // empty/reconnect states. null = flag off (legacy widget behavior:
  // bot rows only, hidden when empty).
  const [upcomingCalendarMeta, setUpcomingCalendarMeta] = useState(null);
  // loadUpcomingMeetings is defined further down — after authToken +
  // authFetch are declared — so its useCallback dependency array
  // doesn't trip the temporal-dead-zone (ReferenceError: Cannot
  // access 'authToken' before initialization). Previously sat right
  // here, which crashed the entire React tree on first render.
  // Conversations phase filters. Kept client-side — the conversations
  // list is bounded (one row per unique prospect thread, capped server-
  // side) so filtering in the browser is cheaper than round-tripping.
  //   convFilterDemo      substring match on the demo title
  //   convFilterProspect  substring match on prospect name OR email
  //                       (CRM integration will populate richer
  //                        prospect context in a future commit)
  //   convFilterDateFrom  ISO yyyy-mm-dd lower bound (inclusive)
  //   convFilterDateTo    ISO yyyy-mm-dd upper bound (inclusive, end-of-day)
  const [convFilterDemo, setConvFilterDemo] = useState('');
  const [convFilterProspect, setConvFilterProspect] = useState('');
  const [convFilterDateFrom, setConvFilterDateFrom] = useState('');
  const [convFilterDateTo, setConvFilterDateTo] = useState('');

  // Full-recording modal — surfaces the raw demo recording (the user's
  // original upload, persisted to R2 via Phase 2A). Opened from the
  // completed-demo view button, the kebab menu on any demo tile/card,
  // or the library row. Null = closed; set to a demoId to open.
  const [fullRecordingDemoId, setFullRecordingDemoId] = useState(null);
  const [fullRecordingDuration, setFullRecordingDuration] = useState(0);
  // Short-lived signed playback token for the full original recording, minted
  // when the modal opens (see effect below). Appended to the source-stream URL
  // so playback keeps working once the stream route begins requiring a token
  // for is_private recordings. Null until minted / for the bare-URL fallback.
  const [fullRecordingToken, setFullRecordingToken] = useState(null);
  // Optional seek-target (seconds) for the full-recording modal. Set by
  // chapter-row "Watch full segment" buttons so the modal opens at the
  // clip's end-point in the original recording, mirroring ShareViewer's
  // per-chapter deep-link. Null = play from 0:00.
  const [fullRecordingSeekAt, setFullRecordingSeekAt] = useState(null);
  const fullRecordingVideoRef = useRef(null);
  const openFullRecording = (demoId, seekSeconds = null) => {
    if (!demoId) return;
    setFullRecordingDemoId(demoId);
    setFullRecordingDuration(0);
    setFullRecordingSeekAt(typeof seekSeconds === 'number' && Number.isFinite(seekSeconds) ? seekSeconds : null);
  };
  const closeFullRecording = () => {
    setFullRecordingDemoId(null);
    setFullRecordingDuration(0);
    setFullRecordingSeekAt(null);
  };

  // ─── Trailer player chapter jump ───────────────────────────────────
  // The completed-demo view renders a chapters-list + timeline-tick
  // strip mirroring ShareViewer's prospect-facing UX. Both surfaces
  // need a ref to the <video> element and the loaded metadata
  // duration so ticks can position proportionally.
  const reelVideoRef = useRef(null);
  const [reelVideoDuration, setReelVideoDuration] = useState(0);
  // Best seek target for a moment — prefer reelOffsetSeconds if the
  // composer embedded it; fall back to startSeconds clamped to the
  // trailer's duration so pre-Phase-2D demos without offsets don't bounce
  // off the end. Matches ShareViewer.seekTargetFor().
  const reelSeekTargetFor = (m) => {
    if (typeof m?.reelOffsetSeconds === 'number' && Number.isFinite(m.reelOffsetSeconds)) {
      return Math.max(0, m.reelOffsetSeconds);
    }
    const fallback = typeof m?.startSeconds === 'number' ? m.startSeconds : 0;
    if (reelVideoDuration > 0) return Math.max(0, Math.min(fallback, reelVideoDuration - 0.5));
    return Math.max(0, fallback);
  };
  const reelJumpTo = (m) => {
    const v = reelVideoRef.current;
    if (!v) return;
    try { v.currentTime = reelSeekTargetFor(m); } catch (_) { /* noop */ }
    v.play().catch(() => { /* user-gesture policy — ignore */ });
  };
  const formatClipTime = (seconds) => {
    if (seconds == null || !Number.isFinite(seconds)) return '';
    const mm = Math.floor(seconds / 60);
    const ss = Math.floor(seconds % 60);
    return `${mm}:${ss.toString().padStart(2, '0')}`;
  };

  // Close the full-recording modal on Escape — matches the rebuild
  // modal + ShareViewer source modal patterns. Backdrop click is
  // handled inline on the overlay element.
  useEffect(() => {
    if (!fullRecordingDemoId) return;
    // Mint a short-lived signed playback token for this recording. Harmless
    // for non-private demos (the stream route ignores it); required once
    // is_private enforcement lands on the stream routes. Best-effort — on
    // failure we fall back to the bare URL, which still serves non-private
    // recordings, so this can never regress current playback.
    let cancelled = false;
    setFullRecordingToken(null);
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/demos/${encodeURIComponent(fullRecordingDemoId)}/playback-token`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (!cancelled && res.ok) {
          const data = await res.json();
          if (data && data.token) setFullRecordingToken(data.token);
        }
      } catch (_) { /* non-fatal — bare-URL fallback */ }
    })();
    const onKey = (e) => { if (e.key === 'Escape') closeFullRecording(); };
    window.addEventListener('keydown', onKey);
    return () => { cancelled = true; window.removeEventListener('keydown', onKey); };
  }, [fullRecordingDemoId]);
  // Unread count mirrors notifications kind='prospect_message' but
  // scoped to the conversations list. Drives the sidebar pill.
  const conversationsUnreadCount = Array.isArray(conversations)
    ? conversations.filter((c) => c.awaitingSellerReply).length
    : 0;
  const [moments, setMoments] = useState([]);
  // Full detected-moment pool (demos.all_moments — write-once at first
  // compose). `moments` narrows to the composed selection, so the rebuild
  // modal reads THIS list to let users restore clips they previously
  // excluded. Null on legacy demos → modal falls back to `moments`.
  const [allMoments, setAllMoments] = useState(null);
  const [selectedMoments, setSelectedMoments] = useState([]);
  // Whether the current completed-phase demo has its ORIGINAL recording
  // persisted to R2 (Phase 2A source_key). Drives the "Watch full
  // segment" chapter-row button + the top-level "Watch full recording"
  // action. False for pre-Phase-2A demos that never had the source
  // uploaded alongside the trailer.
  const [currentDemoSourceAvailable, setCurrentDemoSourceAvailable] = useState(false);
  // Deal brief for the currently-loaded demo. Structured JSON from the
  // backend AI pass (utils/generateDealBrief.js). Null means either
  // (a) legacy demo pre-brief feature, or (b) the generator hasn't
  // finished yet — it's fire-and-forget so it can land after the trailer
  // is already viewable. Frontend renders a "generating…" state in
  // case (b), nothing in case (a).
  const [currentDemoDealBrief, setCurrentDemoDealBrief] = useState(null);
  // Multi-track Recall data (real participant names) + diarized
  // transcript for the trailer-review's Talking Timeline + Transcript
  // pills. Both null on manual uploads and pre-multi-track demos —
  // pills handle the empty state inline.
  const [currentDemoTranscript, setCurrentDemoTranscript] = useState(null);
  const [currentDemoParticipantTalkTime, setCurrentDemoParticipantTalkTime] = useState(null);
  // Three speaker-name resolution layers (see backend ALTER TABLE in
  // db/index.js for shapes). Used by the Talking-Timeline pill in the
  // completed-trailer review view to render real names instead of
  // "Speaker 3 / Speaker 4". applySpeakerNameLayers in
  // CallReviewSections.jsx is the single source of priority logic.
  const [currentDemoSpeakerNameOverrides, setCurrentDemoSpeakerNameOverrides] = useState(null);
  const [currentDemoCalendarAttendees, setCurrentDemoCalendarAttendees] = useState(null);
  const [currentDemoExtractedNames, setCurrentDemoExtractedNames] = useState(null);
  const [trailerReviewPill, setTrailerReviewPill] = useState('summary');
  // Engagement heat-map for the currently-loaded demo. Fetched from
  // /api/demos/:id/engagement-heatmap whenever the seller opens a
  // demo, and re-fetched when they manually Refresh the stats. Null
  // = not loaded yet; `{ buckets: [], moments: [], totalSegments: 0 }`
  // = loaded but no engagement data (brand-new share with no views).
  const [currentDemoHeatmap, setCurrentDemoHeatmap] = useState(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  // Uploaded filename for the currently-open completed demo. Used as a
  // fallback title when prospectName wasn't set at generation time.
  const [currentDemoFilename, setCurrentDemoFilename] = useState('');
  // Phase state is persisted to the URL hash (#/library, #/security, etc.)
  // so F5 / deep-links / external links land on the right page. Initial
  // value priority:
  //   1. Valid hash → use it (deep-link / refresh on a page).
  //   2. Stored auth token → assume 'dashboard' (avoids the
  //      container narrow-flash: without this, phase='login' on first
  //      render → `.has-left-nav` is off → container clamps to 860px →
  //      /me resolves → class flips on → container snaps to 1440px).
  //   3. No token → 'login'.
  const [phase, setPhase] = useState(() => {
    if (typeof window === 'undefined') return 'login';
    // hashPhaseSegment strips the subpath/query so
    // '#/buyer-spaces/<id>/<section>' still resolves to phase
    // 'buyer-spaces'. Subpath parsing happens in the BuyerSpacesPhase
    // component itself.
    const phasePart = hashPhaseSegment(window.location.hash);
    // 'login' / 'signup' are PUBLIC phases — always honor regardless
    // of auth state. Renders the right screen for someone landing on
    // /#/login from a marketing CTA. They're deliberately absent from
    // phaseFromHash's allowlist: they're auth screens, not deep-link
    // destinations.
    if (phasePart === 'login' || phasePart === 'signup') return phasePart;

    // Authenticated phases — only honor the hash if we have a token.
    // Without this token check, a logged-out user refreshing on
    // /#/dashboard would land on phase='dashboard' (because the hash
    // names a real phase), bootstrapping would flip to false, and the
    // login JSX (gated on phase==='login') would never render —
    // leaving the user staring at the empty page shell.
    try {
      const hasToken = !!(window.localStorage && window.localStorage.getItem('trailercst_token'));
      if (hasToken) return phaseFromHash(window.location.hash) || 'dashboard';
    } catch (_) { /* private-mode localStorage may throw */ }
    return 'login';
  });
  const [previewingMomentIndex, setPreviewingMomentIndex] = useState(null);
  // Internal share modal target (2026-08-23): jobId of the trailer being
  // shared internally, or null. Renders InternalSharePanel.
  const [internalShareJobId, setInternalShareJobId] = useState(null);
  const [prospectName, setProspectName] = useState('');
  // Finished-video upload (finished_video_upload flag, 2026-09-04). When
  // on, the upload screen stores the file AS-IS as a completed trailer
  // instead of running the AI pipeline. Scope is the Deal/Reusable tab
  // the card lands on; the title becomes the card name.
  const [uploadAsIs, setUploadAsIs] = useState(false);
  const [uploadAsIsScope, setUploadAsIsScope] = useState('reusable');
  const [uploadTitle, setUploadTitle] = useState('');
  const finishedUploadXhrRef = useRef(null);
  const [toast, setToast] = useState(null); // { message, action?: { label, onClick } } | null
  // Share-link URL cache keyed by jobId. We create the distribution once
  // per trailer (lazy, on first "Copy share link" click) and reuse the URL
  // for subsequent clicks — this avoids spamming the distributions table
  // with a new row every time the user copies.
  const [shareLinkCache, setShareLinkCache] = useState({}); // { [jobId]: url }
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const abortControllerRef = useRef(null);
  const previewPlayerRef = useRef(null);
  const previewStopAtRef = useRef(null);
  const toastTimerRef = useRef(null);
  const browserNotificationRef = useRef(null);

  // Minimize-to-floating-widget state for the upload-in-progress UI.
  // When true + the upload is still running, the full processing view
  // hides and a small fixed-position pill renders in the bottom-right
  // showing % progress, so the seller can navigate around the app
  // while transcription/compose finishes. Resets to false whenever a
  // new upload starts or the job hits a terminal state.
  const [uploadMinimized, setUploadMinimized] = useState(false);

  // Auth State
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('trailercst_token'));
  const [authUser, setAuthUser] = useState(null);
  const [authView, setAuthView] = useState('login'); // 'login' | 'signup1' | 'signup2' | 'forgot' | 'reset' | 'twoFactor'
  // 2FA challenge state — populated when /login returns a
  // challenge: '2fa_required' response. Cleared on successful verify
  // or cancel-back-to-login.
  const [twoFactorChallenge, setTwoFactorChallenge] = useState(null); // { token, email } | null
  // Invite state — populated when the user arrives at /?invite=<token>.
  // Token is passed to the signup call so the backend can attach the new
  // user to the right company instead of seeding a new one. Metadata
  // (company name, inviter) drives banners in SignupStep1 + Step2.
  const [pendingInvite, setPendingInvite] = useState(null); // { token, email, companyName, inviterName } | null
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signupForm1, setSignupForm1] = useState({ email: '', password: '' });
  const [signupForm2, setSignupForm2] = useState({ fullName: '', companyName: '', role: '', companySize: '', useCase: '', joinCode: '' });
  const [forgotForm, setForgotForm] = useState({ email: '' });
  const [resetForm, setResetForm] = useState({ newPassword: '', confirmPassword: '' });
  const [resetToken, setResetToken] = useState(null);
  const [forgotSubmitted, setForgotSubmitted] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);

  // Settings Panel State
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileTab, setProfileTab] = useState('profile'); // 'profile' | 'narration' | 'appearance' | 'password' | 'notifications'
  // "Sign out everywhere" in-flight guard (Password & security tab).
  const [logoutAllBusy, setLogoutAllBusy] = useState(false);
  // Admin Settings phase — which of the admin surfaces is active inside
  // the Settings phase: 'brand' | 'personalization' | 'bot' | 'consent' | 'billing'.
  // (Per-user Notifications lives in the profile modal, not here.)
  const [settingsPhaseTab, setSettingsPhaseTab] = useState('brand');

  // Plan modal — opens from the Billing tab and the dashboard tier
  // badge. 2026-09-17: the self-serve pricing modal (Stripe Checkout,
  // seat stepper, monthly/annual toggle) was replaced by a contact-us
  // space. No plan prices are shown anywhere in the app; customers
  // reach the team by email to get set up or change plans. Existing
  // subscribers can still open the Stripe Customer Portal for card,
  // invoices, seat changes, and cancellation.
  const PLAN_CONTACT_EMAIL = 'hello@trailercast.io';
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [pricingBusy, setPricingBusy] = useState(null); // null | 'portal'
  const [pricingError, setPricingError] = useState(null);
  // Single entry point for every "you need a plan" moment: the Billing
  // tab, the dashboard tier badge, the post-trial banner, and the 402
  // card_required responses from upload / trailer / rebuild / bots.
  // Opens the contact space instead of bouncing to Stripe Checkout.
  const openPlanContact = () => {
    setPricingError(null);
    setPricingModalOpen(true);
    refreshAuthUser();
  };

  // Notifications tab state — chat integrations (Slack/Teams webhooks) +
  // per-event channel matrix. Fetched lazily when the Notifications tab
  // becomes active; null means "not loaded yet" so we can distinguish an
  // in-flight load from an empty response.
  const [notifIntegrations, setNotifIntegrations] = useState(null);
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [notifMeta, setNotifMeta] = useState(null);
  const [notifLoadError, setNotifLoadError] = useState(null);
  const [notifBusy, setNotifBusy] = useState(false);
  // Inline per-channel form state — which channel's connect form is open,
  // the URL being typed, whether help is expanded, per-channel status msg.
  const [notifFormOpen, setNotifFormOpen] = useState(null); // 'slack' | 'teams' | null
  const [notifFormUrl, setNotifFormUrl] = useState('');
  const [notifHelpOpen, setNotifHelpOpen] = useState(null); // 'slack' | 'teams' | null
  const [notifChannelMsg, setNotifChannelMsg] = useState({}); // { slack: {kind:'ok'|'err', text} }

  // Profile section (PATCH /api/auth/profile)
  const [profileForm, setProfileForm] = useState({ firstName: '', lastName: '', jobTitle: '', profilePictureUrl: null });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState(null);
  // True for ~3 seconds after a successful save so the button can render
  // a "Saved ✓" confirmation in-form. The transient toast was too easy
  // to miss — beta testers were unsure whether their click registered.
  const [profileSaved, setProfileSaved] = useState(false);

  // Brand section (PATCH /api/auth/brand — company-wide brand config)
  const [brandForm, setBrandFormRaw] = useState({ companyName: '', primaryColor: '#6366f1', secondaryColor: '#8b5cf6', logoUrl: null, slideTheme: null });
  // Tracks whether the user has edited the brand form since it was last
  // synced from authUser. Prevents a background authUser refresh (e.g.
  // from a teammate invite acceptance triggering /auth/me) from
  // stomping work-in-progress edits on the Brand tab. Reset to false
  // after every successful save or hydration.
  const brandFormDirtyRef = useRef(false);
  // Drop-in replacement for setBrandForm that also marks the form as
  // dirty, so the hydration useEffect doesn't re-sync mid-edit. Anywhere
  // the UI updates brandForm in response to a user action should use
  // this — the underlying setBrandFormRaw is reserved for hydration
  // paths that explicitly clear the dirty flag themselves.
  const setBrandForm = (updater) => {
    brandFormDirtyRef.current = true;
    setBrandFormRaw(updater);
  };
  const [brandSaving, setBrandSaving] = useState(false);
  const [brandError, setBrandError] = useState(null);
  // Bumped on every successful brand save (2026-09-04). BrandSettings
  // keys its engine-rendered previews on this so the gallery refreshes
  // with the just-saved brand instead of showing stale renders.
  const [brandSavedVersion, setBrandSavedVersion] = useState(0);
  // Slide-deck template upload state (admin-only; handles its own
  // submit independent of saveBrand because the payload is a
  // multipart file, not the JSON the /brand PATCH accepts).
  // [REMOVED 2026-05-02] slideTemplate* state — was for the .pptx
  // corporate-deck upload feature. Superseded by the per-slide-type
  // custom-backgrounds feature below.

  // Custom-slide-backgrounds state (Phase 1 of slide-templates feature).
  // Per-slide-type uploads to /api/slide-templates. Map keyed on
  // slide_type so the 4 tiles render independently.
  const [customSlideTemplates, setCustomSlideTemplates] = useState({});
  const [customSlideUploading, setCustomSlideUploading] = useState(null); // slide_type currently uploading
  const [customSlideError, setCustomSlideError] = useState(null);
  const [customSlideLoaded, setCustomSlideLoaded] = useState(false);
  const customSlideInputRefs = {
    intro: useRef(null),
    agenda: useRef(null),
    transition: useRef(null),
    outro: useRef(null),
  };

  // Phase 2: text-zone editor state. Modal opens against a single
  // template; seller drags zones over the image, edits per-zone
  // styling, and saves zones back to /api/slide-templates/:id.
  const [zoneEditorOpen, setZoneEditorOpen] = useState(false);
  const [zoneEditorTemplate, setZoneEditorTemplate] = useState(null); // { id, slideType, imageUrl, ... }
  const [zoneEditorZones, setZoneEditorZones] = useState([]);
  const [zoneEditorSelectedId, setZoneEditorSelectedId] = useState(null);
  const [zoneEditorSaving, setZoneEditorSaving] = useState(false);
  const [zoneEditorError, setZoneEditorError] = useState(null);
  // Drag state — { zoneId, mode: 'move' | 'resize', startX, startY, startZone }
  const [zoneDrag, setZoneDrag] = useState(null);
  const zoneEditorImageRef = useRef(null);

  // Integrations section — calendar OAuth + meeting bots (Recall.ai).
  // oauthStatus: { providers: {...}, connections: [{provider, accountEmail, expiresAt}] }
  // userBots: [{recall_bot_id, meeting_url, status, demo_id, ...}]
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [oauthStatus, setOauthStatus] = useState(null);
  // CRM connection status — { providers: { hubspot: { connected, configured, ... }, salesforce: { ... } } }
  // Loaded by loadIntegrations alongside oauthStatus. Drives the
  // setup-checklist "Connect CRM" item + the toast that fires when
  // the OAuth callback round-trip lands.
  const [crmStatus, setCrmStatus] = useState(null);
  const [userBots, setUserBots] = useState([]);
  // recordingKind default mirrors the backend default ('qualifying_call')
  // so an untouched picker schedules exactly like the pre-picker flow.
  const [scheduleForm, setScheduleForm] = useState({ meetingUrl: '', meetingTitle: '', recordingKind: 'qualifying_call', joinAt: '' });
  // Recording-eligibility checklist (2026-08-27, the OHM case): the
  // poll worker applies gates the user cannot see, so an account can
  // look fully connected while nothing ever schedules. Mirrors the
  // server-side checklist so the banner can name the failing gate.
  const [recordingEligibility, setRecordingEligibility] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [botNameInput, setBotNameInput] = useState('');
  const [botNameSaving, setBotNameSaving] = useState(false);

  // Theme — 'light' (default) or 'dark'. Persists via localStorage so the
  // user's choice survives refresh. Backend persistence can come later.
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return localStorage.getItem('trailercst_theme') === 'dark' ? 'dark' : 'light';
  });
  // Sidebar collapse (2026-07-26 collapsible sidebar). Same localStorage
  // pattern as theme; read SYNCHRONOUSLY so the saved state applies on
  // first render — no width flash before hydration. With no stored
  // preference, tablets (768-1023px) default collapsed; an explicit
  // user choice always wins afterwards.
  const [navCollapsed, setNavCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const stored = localStorage.getItem('trailercst_nav_collapsed');
      if (stored === '1') return true;
      if (stored === '0') return false;
      return window.innerWidth >= 768 && window.innerWidth < 1024;
    } catch (_) { return false; }
  });
  const toggleNavCollapsed = () => {
    setNavCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('trailercst_nav_collapsed', next ? '1' : '0'); } catch (_) {}
      return next;
    });
  };
  // Ctrl/Cmd+B toggles the sidebar (VS Code convention). Guarded so it
  // never fires while the user is typing in a field.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'b') return;
      const t = e.target;
      const tag = t && t.tagName ? t.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (t && t.isContentEditable)) return;
      e.preventDefault();
      toggleNavCollapsed();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // toggleNavCollapsed uses the functional-updater form, so the
    // first-render closure is never stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [demoHistory, setDemoHistory] = useState([]);
  // Library sidebar state — separate from demoHistory so the Recent-demos
  // tile and the sidebar can request different scopes (personal vs team)
  // without stepping on each other. Team scope is Pro+ only; backend 403s
  // on free/starter so the frontend just hides the tab.
  const [libraryScope, setLibraryScope] = useState('personal'); // 'personal' | 'team'
  const [libraryDemos, setLibraryDemos] = useState([]);
  const [libraryQuery, setLibraryQuery] = useState('');
  const [libraryLoading, setLibraryLoading] = useState(false);
  // Set when the library fetch fails (audit Med, 2026-07-17): before
  // this, a failed load silently rendered the "No trailers yet" empty
  // state — indistinguishable from genuinely having no trailers.
  const [libraryError, setLibraryError] = useState(null);
  const [libraryStatusFilter, setLibraryStatusFilter] = useState('all'); // full library page only

  // ── Trailers screen: content split, sort, view mode, pagination ──
  //
  // The Trailers screen used to filter a loaded 50-row array in the
  // browser, which meant search / creator / date controls physically
  // could not see row 51 and the result count was a lie. All of these
  // now go to the API as query params (see loadLibraryDemos) and the
  // screen renders `libraryTotal` — the unpaginated match count — so
  // "50 of 214" is honest and "Load more" can reach the rest.
  //
  // Tab, sort, and view mode persist in localStorage. Same lazy-init +
  // try/catch shape as trailercst_theme / trailercst_nav_collapsed:
  // private-browsing modes throw on access rather than returning null,
  // so an unguarded read here would white-screen the whole app.
  const [libraryContentScope, setLibraryContentScope] = useState(() => {
    try {
      return localStorage.getItem('trailercst_trailers_tab') === 'reusable' ? 'reusable' : 'deal';
    } catch (_) { return 'deal'; }
  });
  const [librarySort, setLibrarySort] = useState(() => {
    try {
      const stored = localStorage.getItem('trailercst_trailers_sort');
      return TRAILER_SORTS.some((s) => s.value === stored) ? stored : 'created_desc';
    } catch (_) { return 'created_desc'; }
  });
  const [libraryViewMode, setLibraryViewMode] = useState(() => {
    try {
      return localStorage.getItem('trailercst_trailers_view') === 'list' ? 'list' : 'grid';
    } catch (_) { return 'grid'; }
  });
  // Folder filter (2026-08-24). Three values, matching the API:
  //   'all'  → no folder filter
  //   'none' → Unfiled only
  //   <uuid> → that folder
  //
  // Deliberately NOT persisted, unlike tab/sort/view. A remembered
  // folder is a remembered EMPTY SCREEN: come back tomorrow, land in
  // "Q3 Onboarding", see nothing, and conclude your trailers are gone.
  // Tab and sort re-frame the same list; a folder hides most of it.
  const [libraryFolderId, setLibraryFolderId] = useState('all');
  // Workspace folders, with counts. Loaded once per visit to Trailers.
  const [trailerFolders, setTrailerFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  // Demo id whose folder PATCH is in flight, so its own row can show
  // the pending state without freezing the grid — same shape as
  // reclassifyingId below.
  const [filingId, setFilingId] = useState(null);
  // Debounced mirror of libraryQuery. The raw value drives the input
  // (so typing stays instant); this one drives the fetch. 250ms matches
  // SalesLibraryPhase, which is the only other server-side search box.
  const [libraryQueryDebounced, setLibraryQueryDebounced] = useState('');
  // Unpaginated match count from the API. Nothing is dropped from the
  // response any more (recording kind is a query param now), so
  // libraryDemos.length is both the pagination offset and the numerator
  // `total` is an honest denominator for.
  const [libraryTotal, setLibraryTotal] = useState(0);
  const [libraryLoadingMore, setLibraryLoadingMore] = useState(false);
  // Demo id whose Deal/Reusable reclassify PATCH is in flight, so the
  // row can disable its own control without freezing the whole grid.
  const [reclassifyingId, setReclassifyingId] = useState(null);
  // "+ New trailer" dropdown on the Trailers phase header. Two options:
  // upload existing recording (legacy flow → setPhase('upload')) or
  // record a new trailer in the browser (opens a SEPARATE TAB at
  // #/record-trailer — the RecordTrailerPage standalone surface).
  // Spec'd 2026-05-09 in conversation; backend at /api/recordings/browser.
  const [newTrailerMenuOpen, setNewTrailerMenuOpen] = useState(false);

  // Per-demo engagement state — populated when the seller opens a
  // completed demo and expands the "Who's watched this?" section.
  // Separate from the global Reports state so opening a demo doesn't
  // clobber the Reports page's current dataset.
  const [engagementData, setEngagementData] = useState(null); // { aggregate, recipients } | null
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [engagementError, setEngagementError] = useState(null); // audit Med 2026-07-17: failed ≠ empty
  const [showEngagement, setShowEngagement] = useState(false);
  // Whether the Highlight Summary card is expanded on the completed-
  // demo view. Defaults open — it's the primary "what's in this trailer"
  // orientation for the seller — but collapsible for sellers who
  // prefer a quieter layout.
  const [summaryOpen, setSummaryOpen] = useState(true);

  // Active tab for the demo-detail right rail (2026-05-08 rebuild).
  // Five tabs:
  //   - 'engagement'   — prospect threads + demo stats card (default;
  //                      this is the post-share signal sellers want
  //                      to see first when reopening a trailer)
  //   - 'highlights'   — AI-derived feature/benefit summary
  //   - 'comments'     — internal team comments + @mentions
  //   - 'ask-winter'   — chat with Winter grounded on this demo's
  //                      transcript + summary
  // Right-rail tab. 2026-05-29: adaptive default — a fresh trailer opens
  // on 'highlights' (the clips you just made) instead of 'engagement'
  // (which shows all-zeros stats and reads as "nothing here"). Once
  // engagement data loads WITH views, we auto-flip to 'engagement' so a
  // seller returning to a watched trailer lands on the numbers — UNLESS
  // they've already picked a tab this session (reelTabUserPickedRef),
  // which locks their choice so the auto-flip never yanks the tab out
  // from under them.
  const [demoRightTab, setDemoRightTab] = useState('highlights');
  const reelTabUserPickedRef = React.useRef(false);

  // Comment count for the Comments-tab badge on the demo detail.
  // Pre-fetched on demo load + bubbled from CommentsThread once it
  // mounts (so post / delete keep the badge in sync without
  // requiring the user to re-open the demo).
  const [demoCommentCount, setDemoCommentCount] = useState(0);

  // Reports page state — three datasets loaded on demand when the user
  // opens the Reports phase or switches tabs within it.
  const [reportsTab, setReportsTab] = useState('top-demos'); // 'top-demos' | 'by-user' | 'activity'
  const [reportsScope, setReportsScope] = useState('personal'); // 'personal' | 'team' (tier-gated)
  const [reportsTopDemos, setReportsTopDemos] = useState([]);
  const [reportsByUser, setReportsByUser] = useState([]);
  const [reportsActivity, setReportsActivity] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState(null); // string | null — non-403 load failures
  const [reportsSort, setReportsSort] = useState({ col: null, dir: 'desc' }); // client-side table sort
  // Monotonic id per loadReportsTab call — stale responses (an older
  // fetch resolving after a newer scope/tab flip) are dropped instead
  // of overwriting the fresher data. Same pattern as the engagement
  // loader's job guard.
  const reportsReqSeqRef = React.useRef(0);
  const [demoStats, setDemoStats] = useState({ total: 0, completed: 0 });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null); // audit Med 2026-07-17: failed ≠ empty
  // Dashboard view switcher — pills at the top of the dashboard let
  // the seller flip between Overview / Deal Cycle / Team Performance.
  // Persisted to URL hash (?dashboard=deal-cycle) so refresh stays.
  // Team Performance is admin-only; non-admins see the pill but can't
  // open it (server returns 403, the dashboard renders the message).
  const [dashboardView, setDashboardView] = useState(() => {
    if (typeof window === 'undefined') return 'overview';
    const m = window.location.hash.match(/dashboard=([a-z-]+)/);
    return m ? m[1] : 'overview';
  });
  // User filter — admins only. Drives a ?userId=N query on the
  // dashboard endpoints to scope the data to a single rep. Null =
  // company-wide / self for non-admins. The dropdown roster reuses
  // the existing `teamMembers` state (loaded by loadTeamMembers()).
  const [dashboardUserFilter, setDashboardUserFilter] = useState(null);
  // Dashboard insights — funnel + needs-attention queue, fetched
  // from GET /api/dashboard/insights when the seller hits the dashboard
  // phase. null while loading/never-fetched; { funnel, needsAttention,
  // quarterStart } once loaded. Keeps the widget rendering simple
  // (just check if loaded; if so use real data, else show empty state).
  const [dashboardInsights, setDashboardInsights] = useState(null);
  const [dashboardInsightsLoading, setDashboardInsightsLoading] = useState(false);
  const [dashboardInsightsError, setDashboardInsightsError] = useState(null); // failed ≠ empty
  // Tier-2 (2026-07-22): cross-surface buyer-activity feed from the
  // unified engagement_events log, + the period selector the child
  // dashboards' docblocks always promised ('7d'|'30d'|'90d'|'quarter').
  const [dashboardActivity, setDashboardActivity] = useState([]);
  const [dashboardActivityError, setDashboardActivityError] = useState(null);
  const [dashboardPeriod, setDashboardPeriod] = useState('30d');
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Brand Settings State
  // (dead showBrandSettings state removed 2026-07-25 — vestige of the
  // never-imported BrandSettings.jsx component, deleted same day)

  // Unsaved-changes guard (settings audit fast-win): the brand form
  // tracks dirtiness in brandFormDirtyRef but nothing warned on
  // reload — a refresh silently discarded edits.
  useEffect(() => {
    const warn = (e) => {
      if (brandFormDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);
  const [brandConfig, setBrandConfig] = useState({
    companyName: '',
    primaryColor: '#3b82f6',
    secondaryColor: '#8b5cf6',
    logoUrl: null
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);
  
  // Voice Picker State
  const [availableVoices, setAvailableVoices] = useState([]);
  const [defaultVoiceId, setDefaultVoiceId] = useState(null);
  const [selectedVoiceId, setSelectedVoiceId] = useState(null);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [voicesError, setVoicesError] = useState(null); // audit Med 2026-07-17: failed ≠ empty
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  // Voice Cloning (Enterprise only)
  const [cloneFile, setCloneFile] = useState(null);
  const [cloneName, setCloneName] = useState('');
  const [cloneLoading, setCloneLoading] = useState(false);
  const [cloneError, setCloneError] = useState(null);
  const cloneInputRef = useRef(null);

  // Onboarding personalization form state. 5-question form answers get
  // injected into the AI moment-detection prompt so the trailer editing is
  // specific to the seller's product + ICP. Modal auto-opens on first
  // dashboard visit when company.onboarded is false; dismissable with
  // a "remind me later" → reopenable via a persistent banner until
  // completed. See backend/routes/auth.js /api/auth/onboarding.
  // Feedback modal state. Floating button bottom-right of every
  // authed screen; clicking opens the modal with category + message +
  // submit. POSTs to /api/feedback which persists the row + fires an
  // email to the founder. Success toast + auto-close, no response
  // UX yet (replies happen via the founder inbox, reply_to bridged).
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ category: 'feature_request', message: '' });
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);

  // Recording-consent acknowledgment modal. Auto-opens when the seller
  // tries to dispatch a bot without having clicked through the
  // consent rules (or when the admin has changed jurisdictions since
  // the seller last acknowledged). See backend/routes/auth.js
  // POST /consent/acknowledge + the 403 code=consent_not_acknowledged
  // path on /integrations/recall/schedule.
  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const [consentCheckboxes, setConsentCheckboxes] = useState({ authority: false, inform: false, jurisdiction: false });
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  const [consentError, setConsentError] = useState(null);

  // Admin's jurisdiction selector form (in Settings > Integrations).
  // Seeded from authUser.company.consentJurisdiction on tab open.
  const [jurisdictionForm, setJurisdictionForm] = useState({ jurisdiction: '', privacyPolicyUrl: '' });
  const [jurisdictionSaving, setJurisdictionSaving] = useState(false);
  const [jurisdictionError, setJurisdictionError] = useState(null);

  // Onboarding modal visibility. Form state (answers + submit status +
  // error) lives inside the OnboardingForm component — extracted out
  // of App so keystrokes don't re-render the whole dashboard tree.
  // onboardingDismissed is a session flag: once the user closes the
  // modal without completing, we stop auto-opening it this session
  // (banner still available to re-enter).
  const [onboardingModalOpen, setOnboardingModalOpen] = useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  // Persistent dismissal of the dashboard "Finish setting up TrailerCast"
  // checklist card. Stored in localStorage so the choice survives across
  // sessions — once the user clicks Hide they don't want to see it
  // again, even if they leave items incomplete. Re-surfaces only via
  // a manual reset (not currently exposed in UI; clear the key to
  // force-show again).
  const [setupChecklistHidden, setSetupChecklistHidden] = useState(() => {
    try {
      return typeof window !== 'undefined' && window.localStorage.getItem('tc_setup_checklist_hidden') === '1';
    } catch (_) { return false; }
  });
  const dismissSetupChecklist = () => {
    setSetupChecklistHidden(true);
    try { window.localStorage.setItem('tc_setup_checklist_hidden', '1'); } catch (_) { /* no-op */ }
  };

  // Follow-Up Generator State
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followUpTone, setFollowUpTone] = useState('professional');
  const [followUpContext, setFollowUpContext] = useState('');
  const [followUpMessage, setFollowUpMessage] = useState('');
  const [generatingFollowUp, setGeneratingFollowUp] = useState(false);
  // Share URL returned alongside the generated message — shown as a chip
  // under the output so the seller can confirm the link made it into the body.
  const [followUpShareUrl, setFollowUpShareUrl] = useState('');
  // "Preview as {name}" field — lets the seller see what the email will look
  // like after {{first_name}} is substituted without having to wire a
  // recipient to preview against. Empty string means show the raw template.
  const [followUpPreviewName, setFollowUpPreviewName] = useState('');
  // Attendees (recipients for the follow-up email) — shape: [{ id, firstName,
  // lastName, email, lastSendStatus, lastSentAt }]. Loaded from the backend
  // for the currently-active demo; persisted across sessions so sellers don't
  // retype addresses every time.
  const [attendees, setAttendees] = useState([]);
  const [newAttendee, setNewAttendee] = useState({ firstName: '', email: '' });
  const [sendingFollowUp, setSendingFollowUp] = useState(false);
  const [lastSendResult, setLastSendResult] = useState(null); // { sent: [...], failed: [...] }
  const [includeTimestamps, setIncludeTimestamps] = useState(true);

  // ═══════════════════════════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Listen for save events from a separately-opened recording tab
  // (RecordTrailerPage at #/record-trailer). When that tab uploads a
  // new browser-recorded trailer, it posts on the shared
  // BroadcastChannel; we refresh the library so the new tile shows up
  // here without the user having to come back and click anything.
  // Channel name matches RecordTrailerPage's BROADCAST_CHANNEL constant.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.BroadcastChannel) return;
    const bc = new BroadcastChannel('tc-recordings');
    bc.onmessage = (e) => {
      if (e.data && e.data.type === 'saved') {
        // Best-effort refresh — only call when we have an auth token,
        // and silently swallow errors so a closed-tab race doesn't
        // throw on this side.
        if (!authToken) return;
        try { loadLibraryDemos(); } catch (_) {}
      }
    };
    return () => { try { bc.close(); } catch (_) {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // Apply theme to <html data-theme="..."> so :root + [data-theme="dark"]
  // overrides kick in. Persists the choice for next session.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('trailercst_theme', theme); } catch (_) {}
  }, [theme]);

  // Body scroll lock while the voice modal is open. Without this, the
  // mouse wheel scrolls both the modal content AND the page behind it
  // once the modal's scroll reaches its limits ("scroll chaining"),
  // which feels broken. We restore the previous overflow on close so we
  // don't clobber any other scroll-management the app may pick up later.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!showVoiceSettings) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showVoiceSettings]);

  // ─── Phase ↔ URL hash sync ───────────────────────────────────────
  // Keep window.location.hash in sync with the current phase so refresh
  // stays on the same page and deep-links work. The login/signup phases
  // are intentionally NOT hash-persisted — they're transient auth screens
  // reached by unauthenticated visitors; dropping them in the URL would
  // confuse returning sellers who land on #/login after a refresh when
  // their session is still valid. Same for 'upload' which is an
  // in-progress wizard (losing state on refresh is expected).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Deliberately NOT deepLink's PHASES set: this answers a different
    // question ("which phases get WRITTEN to the URL"), and it is that
    // set minus 'upload' for the reason in the comment above. Reading a
    // phase back OUT of a hash is phaseFromHash's job — don't merge them.
    const PERSISTABLE = new Set([
      'dashboard', 'library', 'reports', 'conversations',
      'buyer-spaces', 'calls', 'sales-library', 'qbr', 'coaching',
      'security', 'integrations', 'settings', 'audit-log',
      'forms',
    ]);
    if (!PERSISTABLE.has(phase)) return;
    // DON'T clobber an in-flight `#demo=xyz` restoration hash when
    // phase is 'dashboard'. Refresh on the trailer page lands with
    // hash=#demo=xyz and phase=dashboard (initializer defaults) —
    // the demoHashRestoredRef effect below then re-opens the demo.
    // If we overwrite the hash to '#/dashboard' here before that
    // effect fires, the demo id is lost and the seller bounces back
    // to the dashboard every refresh.
    if (phase === 'dashboard' && window.location.hash.startsWith('#demo=')) {
      return;
    }
    // Same protection for Decision Rooms subpath hashes
    // (#/buyer-spaces/<spaceId>/<sectionId>) — BuyerSpacesPhase owns
    // the subpath and we must not flatten it back to #/buyer-spaces.
    if (phase === 'buyer-spaces' && window.location.hash.startsWith('#/buyer-spaces/')) {
      return;
    }
    // Same protection for the Calls subpath hashes — the
    // CallsPhase owns #/calls/<id> for the detail view restoration
    // on refresh.
    if (phase === 'calls' && window.location.hash.startsWith('#/calls/')) {
      return;
    }
    // Bootstrap-window guard: if phase is 'dashboard' but the hash
    // points at a persistable phase we're about to rehydrate to,
    // don't overwrite it. The bootstrap effect force-sets phase to
    // 'dashboard' after /me resolves; this effect would then write
    // '#/dashboard' before the rehydrate effect (which depends on
    // `bootstrapping`) gets a chance to re-apply the hash. That race
    // is what was bouncing refresh-on-/calls back to the dashboard.
    //
    // Critical: this guard MUST be scoped to the bootstrap window
    // only (`bootstrapping === true`). Once bootstrap is done, any
    // phase change is user-driven (e.g. clicking "Dashboard" in the
    // left nav from inside a Buyer Space) and we DO want to overwrite
    // the hash so refresh lands on the dashboard, not back where they
    // last were. Without the bootstrapping check, the guard fired on
    // every nav click and the hash never updated — refresh always
    // restored the previous detail view.
    if (phase === 'dashboard' && bootstrapping) {
      const currentHashPhase = hashPhaseSegment(window.location.hash);
      if (currentHashPhase && currentHashPhase !== 'dashboard' && PERSISTABLE.has(currentHashPhase)) {
        return;
      }
    }
    const target = `#/${phase}`;
    if (window.location.hash !== target) {
      // replaceState so we don't pollute the browser history with every
      // nav click — refresh restores state but Back button still works
      // through real navigations (login flow, external links).
      window.history.replaceState(null, '', window.location.pathname + window.location.search + target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Rehydrate phase from the hash after auth finishes bootstrapping.
  // The useState initializer already parsed the hash on first mount, but
  // the bootstrapping branch below force-sets phase to 'dashboard' when
  // /me resolves. This effect re-applies the hash once bootstrapping
  // clears so a refresh on #/library lands on library, not dashboard.
  useEffect(() => {
    if (bootstrapping) return;
    if (typeof window === 'undefined') return;
    // Same resolver as the initializer above, so a refresh on
    // #/buyer-spaces/<id>/<section> rehydrates to the buyer-spaces phase.
    const phasePart = phaseFromHash(window.location.hash);
    if (!phasePart || phase === phasePart) return;
    // Auth guard (2026-05-30 blank-screen fix): every routable phase
    // requires a session, and the useState initializer already checks
    // for a token before honoring the hash — but this rehydrate twin
    // didn't. The gap: an EXPIRED session's boot briefly enters
    // phase='dashboard', the mirror effect above writes '#/dashboard'
    // into the URL, /auth/me 401s and drops the user to the login
    // phase — then this effect fired and stomped phase back to
    // 'dashboard' while logged out. phase='dashboard' with
    // authUser=null renders NEITHER the dashboard NOR the login form:
    // the recurring "blank TrailerCast page when I click Sign in".
    let hasToken = false;
    try { hasToken = !!window.localStorage.getItem('trailercst_token'); } catch (_) { /* private mode */ }
    if (!hasToken) {
      // Scrub the dead session's hash so refreshes and the mirror
      // effect can't resurrect this dead-end after the user logs in.
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      return;
    }
    setPhase(phasePart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapping]);

  // Load stats + recent demos on landing phases; voices when the user
  // actually needs them (upload screen); library rows on dashboard +
  // library phases. Loaders are no-op when authToken is missing.
  // Waits out the bootstrap window: authToken is seeded straight from
  // localStorage BEFORE /auth/me validates it, so firing here during
  // bootstrap sprays 401s from every loader when the stored token is
  // stale (and authFetch's logout-on-401 then clears the token, which
  // is why the console spam appeared to come from a logged-out page).
  // When bootstrap resolves, the flip of `bootstrapping` re-runs this
  // effect and the loads fire with a validated session.
  useEffect(() => {
    if (bootstrapping) return;
    if ((phase === 'dashboard' || phase === 'upload') && authToken) {
      loadDemoHistory();
    }
    if (phase === 'upload' && authToken) {
      loadAvailableVoices();
    }
    // 'library' is deliberately absent: the Trailers screen has its own
    // loader effect that carries the tab / sort / filter state. Firing
    // here too would issue a second, unfiltered request per visit.
    if (phase === 'dashboard' && authToken) {
      loadLibraryDemos();
    }
    // Top-performing dashboard tile piggybacks on the Reports personal-
    // scope top-demos endpoint so sellers see a preview without visiting
    // the full Reports page. Limited to 3 rows in the tile; reports page
    // re-fires its own load on tab switch.
    if (phase === 'dashboard' && authToken) {
      loadReportsTab('top-demos', 'personal');
    }
    // OAuth status drives the Setup Checklist's "Connect your calendar"
    // checkmark. Previously only loaded when the Integrations tab was
    // opened, which meant the checklist always rendered as
    // "unconnected" until the user visited that tab — a bit misleading.
    // Now we pre-fetch on dashboard mount so the checklist is accurate
    // on first paint. Cheap call; single Promise.all of two
    // endpoints.
    if (phase === 'dashboard' && authToken) {
      loadIntegrations();
    }
    // Funnel + needs-attention widgets data. Single round-trip to
    // /api/dashboard/insights; result drives both widgets. Renders an
    // empty state until the response lands or if the call fails.
    if (phase === 'dashboard' && authToken) {
      loadDashboardInsights();
      loadDashboardActivity();
    }
    // Team roster for the dashboard user-filter dropdown — admin only.
    // Same /api/company/members endpoint used by the Security tab, so
    // we just trigger an extra load when admins hit the dashboard.
    if (phase === 'dashboard' && authToken && authUser?.company?.isAdmin && teamMembers.length === 0) {
      loadTeamMembers();
    }
    // Phase-specific lazy-loads that previously only fired via the
    // LeftNav click handler also need to fire when the user reloads
    // on a deep-linked phase (e.g. F5 on #/conversations needs to
    // fetch conversations, not just render an empty list).
    if (phase === 'conversations' && authToken) {
      loadConversations();
    }
    // NOTE: no reports load here — the dedicated [phase, reportsTab,
    // reportsScope] effect below covers deep-link entry too, and this
    // one hardcoded 'top-demos' (wrong when the user left Reports on
    // another tab) while double-fetching alongside it.
    if (phase === 'security' && authToken) {
      loadTeamMembers();
      if (authUser?.company?.isAdmin) loadTeamInvites();
    }
    // When we arrive on Security from the top-bar search, scroll to and
    // flash-highlight the searched-for teammate's row. We wait for the
    // roster to render before scrolling — teamMembers is empty on first
    // render, so this effect re-runs once the roster loads (the second
    // useEffect below watches the roster length too).
    if (phase === 'integrations' && authToken) {
      loadIntegrations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, authToken, authUser?.company?.isAdmin, bootstrapping]);

  // NOTE: The "scroll-and-highlight team member after search click"
  // useEffect lives much further down in this file, immediately after
  // the highlightedMemberId + teamMembers useState declarations
  // (around line ~4800). It was originally placed here but hit the
  // same TDZ ReferenceError pattern called out in the comment below —
  // App.jsx is one giant function body and hooks that read `let`/`const`
  // -declared state must come AFTER those declarations in source order,
  // even though all hooks fire after the function body returns.

  // loadUpcomingMeetings — fetch the user's scheduled bots from the
  // real backend endpoint and shape for the LeftNav widget. Defined
  // here (not next to the useState declaration above) so the
  // useCallback dependency array doesn't reference `authToken` before
  // it's been declared further down in this function — that ordering
  // bug triggered a TDZ ReferenceError that crashed every render and
  // left users staring at a blank page after the previous deploy.
  const loadUpcomingMeetings = useCallback(async () => {
    if (!authToken) return;
    // upcoming_calls_join (2026-09-02): the widget shows the seller's
    // whole calendar — every meeting for today + the next two days,
    // recorded or not — each with a Join link that opens the real
    // Zoom/Meet/Teams URL. Backed by the live-calendar endpoint; the
    // legacy bot-rows path below stays byte-identical for workspaces
    // where the flag is off.
    if (authUser?.company?.featureFlags?.upcoming_calls_join) {
      try {
        const r = await fetch(`${API_BASE}/api/integrations/calendar/upcoming`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!r.ok) {
          // Soft-fail (including the 404 the backend serves while its
          // flag cache lags ours by up to a minute) — widget hides.
          setUpcomingMeetings([]);
          setUpcomingCalendarMeta(null);
          return;
        }
        const data = await r.json();
        // Widget window: today + 2 days (the endpoint itself returns
        // the poller's full 7). In-progress meetings stay listed until
        // they END — "join late" is the moment the link matters most.
        const now = Date.now();
        const windowEnd = new Date();
        windowEnd.setDate(windowEnd.getDate() + 2);
        windowEnd.setHours(23, 59, 59, 999);
        const events = (Array.isArray(data.events) ? data.events : [])
          .filter((e) => new Date(e.endAt).getTime() > now
            && new Date(e.startAt).getTime() <= windowEnd.getTime())
          .map((e) => ({
            id: e.id,
            title: e.title || '(untitled meeting)',
            startAt: e.startAt,
            meetingUrl: e.meetingUrl || null,
            zoomPasscode: e.zoomPasscode || null,
            notetaker: e.notetaker || null,
            provider: e.meetingProvider === 'teams' ? 'microsoft-teams'
              : e.meetingProvider === 'zoom' ? 'zoom'
              : 'google-meet',
          }));
        setUpcomingMeetings(events);
        setUpcomingCalendarMeta({
          connected: !!data.connected,
          reconnectRequired: Array.isArray(data.reconnectRequired) ? data.reconnectRequired : [],
        });
      } catch (err) {
        console.warn('[upcoming-meetings] calendar load failed (non-fatal):', err.message);
        setUpcomingMeetings([]);
        setUpcomingCalendarMeta(null);
      }
      return;
    }
    try {
      const r = await fetch(`${API_BASE}/api/qualifying-calls/upcoming`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) {
        // Soft-fail — sidebar widget stays hidden on auth/server errors
        // rather than rendering noise or a broken state.
        setUpcomingMeetings([]);
        return;
      }
      const data = await r.json();
      const rows = Array.isArray(data.upcoming) ? data.upcoming : [];
      setUpcomingMeetings(rows.map((b) => ({
        id: b.id,
        title: b.meetingTitle || '(untitled meeting)',
        startAt: b.meetingStartAt,
        // Provider detection from the meeting URL where the row carries
        // one; the generic glyph covers the rest.
        provider: b.meetingUrl && b.meetingUrl.includes('zoom.us') ? 'zoom'
          : b.meetingUrl && b.meetingUrl.includes('teams.microsoft.com') ? 'microsoft-teams'
          : 'google-meet',
      })));
    } catch (err) {
      console.warn('[upcoming-meetings] load failed (non-fatal):', err.message);
      setUpcomingMeetings([]);
    }
  }, [authToken, authUser?.company?.featureFlags?.upcoming_calls_join]);

  // Sidebar "Upcoming calls" widget lives in the LeftNav, which is
  // visible across every phase. So we drive its data load off the auth
  // boundary (not the phase boundary) and refresh on a 5-minute timer
  // so a newly-scheduled bot appears without a page reload. Each tick
  // is cheap (single SELECT against recall_bots scoped to the user)
  // so the interval has minimal cost.
  useEffect(() => {
    if (!authToken) {
      setUpcomingMeetings([]);
      setUpcomingCalendarMeta(null);
      return undefined;
    }
    loadUpcomingMeetings();
    const handle = setInterval(loadUpcomingMeetings, 5 * 60 * 1000);
    return () => clearInterval(handle);
  }, [authToken, loadUpcomingMeetings]);

  // When the admin flips scope (Personal ↔ Team) or the library-page
  // status filter changes, re-fetch. Kept out of the phase useEffect so
  // we don't re-fire every dashboard mount.
  // The !bootstrapping check keeps the mount-time run from firing with
  // the not-yet-validated localStorage token (stale token → 401 spam);
  // the initial library load is the phase effect's job, and scope/
  // filter flips can only happen post-bootstrap anyway. Deliberately
  // NOT in the deps — this effect must not re-fire on bootstrap
  // completion, or it would duplicate the phase effect's load.
  useEffect(() => {
    // Trailers-screen flips are handled by that screen's own effect,
    // which also carries the tab / sort / search / date params. This one
    // now only covers the dashboard's recent-trailer surfaces.
    if (phase === 'dashboard' && authToken && !bootstrapping) {
      loadLibraryDemos(libraryScope, libraryStatusFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryScope, libraryStatusFilter]);

  // Reset the video error flag whenever the active demo changes so a stale
  // "still processing" panel from a previous demo doesn't mask a new one.
  // Also clears the buffering watchdog so a previous demo's timer can't
  // fire on the new one.
  useEffect(() => {
    setVideoLoadError(false);
    if (videoWatchdogRef.current) {
      clearTimeout(videoWatchdogRef.current);
      videoWatchdogRef.current = null;
    }
    // Reset the demo-detail right-rail tab when switching demos.
    // Default to 'highlights' (the clips) — a freshly-loaded trailer
    // most often has no engagement yet, so leading with the zeros panel
    // reads as empty. loadEngagementForDemo auto-flips to 'engagement'
    // when this demo actually has views. Reset the user-picked lock so
    // that auto-flip is allowed again for the new demo.
    setDemoRightTab('highlights');
    reelTabUserPickedRef.current = false;
    setDemoCommentCount(0);
  }, [jobId]);

  // Pre-fetch comment count for the demo-detail Comments tab badge.
  // One-shot on jobId change; CommentsThread bubbles updates from
  // there once the user opens the tab.
  useEffect(() => {
    if (!jobId || !authToken) return undefined;
    let cancelled = false;
    fetch(`${API_BASE}/api/demos/${encodeURIComponent(jobId)}/comments`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => r.ok ? r.json() : { comments: [] })
      .then((d) => {
        if (cancelled) return;
        setDemoCommentCount(Array.isArray(d.comments) ? d.comments.length : 0);
      })
      .catch(() => { /* badge stays at 0 — non-fatal */ });
    return () => { cancelled = true; };
  }, [jobId, authToken]);

  // Billing return handler — Stripe Checkout redirects users back to
  // /billing/success?session_id=cs_... on payment + /billing/canceled
  // when they bail. We don't render a dedicated route; instead we
  // detect the path on boot, show a toast, refetch /auth/me to pick up
  // the new tier (the webhook should have already written it), then
  // clean the URL so a refresh doesn't re-fire the toast. Skipped while
  // auth is still resolving so the toast doesn't fire before the user
  // sees the dashboard. Runs once per session.
  useEffect(() => {
    if (!authUser) return;
    const path = window.location.pathname || '';
    if (path !== '/billing/success' && path !== '/billing/canceled') return;
    if (path === '/billing/success') {
      // Tailored toast for the signup-flow vs mid-trial-upgrade variant.
      // signup=1 query param is set on the success_url for the
      // /signup-checkout-session endpoint specifically; everything else
      // is a normal upgrade through the in-app pricing modal.
      const params0 = new URLSearchParams(window.location.search);
      const isSignupFlow = params0.get('signup') === '1';
      if (isSignupFlow) {
        showToast('Subscription active — welcome aboard!');
      } else {
        showToast('Subscription active — welcome aboard!');
      }
      // Ad-platform Purchase events. We pull two signals out of the
      // post-Stripe redirect:
      //   1. The user's tier from authUser.company (refreshed by
      //      /auth/me; the webhook should have flipped it before this
      //      runs). Worst case: race condition leaves it at the prior
      //      tier; we under-attribute slightly.
      //   2. The billing period from the success URL's billing_period
      //      query param (the backend bakes this in when it constructs
      //      the success_url for Checkout). Annual is 10x monthly value
      //      so getting it right materially affects ad-attribution
      //      ROI math. Defaults to monthly if missing.
      const params = new URLSearchParams(window.location.search);
      const billingPeriodParam = params.get('billing_period') === 'annual' ? 'annual' : 'monthly';
      const tierForPixel = authUser?.company?.tier;
      // USD-denominated attribution values mirroring the Stripe Price
      // IDs (2026-05-15 single-tier model), per seat: monthly list vs
      // annual list × 12. Kept for ad-pixel ROI math only — no price
      // is shown in-app. At conversion time we don't know the seat count without a /me
      // round-trip, so use a single-seat attribution value. Pixel
      // events undercount multi-seat purchases but never overcount;
      // safer for ad-attribution math.
      const valueUsd = (() => {
        if (tierForPixel === 'pro' || tierForPixel === 'starter') {
          return billingPeriodParam === 'annual' ? 708 : 79;
        }
        return billingPeriodParam === 'annual' ? 708 : 79;
      })();
      redditPixel.trackPurchase({ value: valueUsd, currency: 'USD' });
      googleAds.trackPurchase({ value: valueUsd, currency: 'USD' });
      // Refetch /auth/me so the tier badge + Settings → Billing tab reflect
      // the new state immediately. Webhook is async but Stripe's redirect
      // happens after webhook delivery in practice.
      refreshAuthUser();
      setPhase('settings');
      setSettingsPhaseTab('billing');
    } else {
      // /billing/canceled. If this was a signup-flow checkout (signup=1
      // query param on the cancel_url), the user came back without
      // leaving a card on file — they need to either retry or live with
      // the fact that they can't create demos until they do. Show a
      // distinct toast so they know what's expected, but still drop them
      // on the dashboard where the persistent banner picks up the nudge.
      const params0 = new URLSearchParams(window.location.search);
      const isSignupFlow = params0.get('signup') === '1';
      if (isSignupFlow) {
        showToast('No card on file — your trial isn\'t active yet. Click "Activate trial" anytime.');
      } else {
        showToast('Checkout canceled — no charge was made.');
      }
      setPhase('dashboard');
    }
    window.history.replaceState({}, '', '/');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  // OAuth return handler — backend redirects here after the user connects
  // (or fails to connect) a calendar provider. Reads the query params,
  // shows a toast, auto-opens the settings modal on the Integrations tab,
  // then strips the params so a page reload doesn't re-fire the toast.
  useEffect(() => {
    if (!authUser) return; // wait for auth to resolve
    const params = new URLSearchParams(window.location.search);
    const provider = params.get('oauth');
    const status = params.get('status');
    if (!provider) return;
    if (status === 'ok') {
      showToast(`${provider === 'google' ? 'Google Calendar' : 'Microsoft 365'} connected.`);
    } else {
      const reason = params.get('reason') || 'unknown';
      showToast(`Couldn't connect ${provider}: ${reason}`);
    }
    // Integrations is a top-level phase now (post-UI-restructure), not
    // a profile-modal tab. OAuth-callback returns land the user on
    // that phase so they can see the connected calendar immediately.
    setPhase('integrations');
    loadIntegrations();
    // Clean the URL so refresh doesn't reshow the toast.
    const clean = window.location.pathname + window.location.hash;
    window.history.replaceState({}, '', clean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  // Least-privilege guard: non-admin members never see admin-only
  // phases. If phase somehow lands on one of them (stale state after a
  // demotion, deep link, back-forward to an admin URL the user used
  // before they were a member), bounce to the dashboard silently. The
  // LeftNav already hides these items for non-admins; this effect
  // covers every other path in.
  useEffect(() => {
    if (!authUser) return;
    const isAdmin = !!(authUser?.company?.isAdmin);
    const ADMIN_ONLY_PHASES = new Set(['settings']);
    if (ADMIN_ONLY_PHASES.has(phase) && !isAdmin) {
      setPhase('dashboard');
    }
  }, [phase, authUser]);

  // Hydrate the Brand form from authUser whenever authUser's brand
  // payload changes — on first login, on refresh, after a save, etc.
  // Previously brandForm only hydrated when the user opened the
  // profile modal via the avatar; anyone who navigated straight to
  // Settings → Brand saw an empty form even though their brand was
  // saved server-side.
  //
  // Skip the sync if the user has unsaved edits in the form, to avoid
  // a background /auth/me refresh stomping their work-in-progress.
  useEffect(() => {
    if (!authUser?.company?.brand) return;
    if (brandFormDirtyRef.current) return;
    const brand = authUser.company.brand;
    // Use the raw setter — hydration intentionally does NOT mark the
    // form dirty, so subsequent authUser refreshes keep re-syncing
    // until the user actually edits something.
    setBrandFormRaw({
      companyName: brand.companyName || '',
      primaryColor: brand.primaryColor || '#6366f1',
      secondaryColor: brand.secondaryColor || '#8b5cf6',
      logoUrl: brand.logoUrl || null,
      slideTheme: brand.slideTheme || null,
    });
  }, [
    authUser?.company?.brand?.companyName,
    authUser?.company?.brand?.primaryColor,
    authUser?.company?.brand?.secondaryColor,
    authUser?.company?.brand?.logoUrl,
    authUser?.company?.brand?.slideTheme,
  ]);

  // Close the open kebab menu on outside click or Escape. Menu content stops
  // its own click events; anything reaching window closes.
  useEffect(() => {
    if (!openActionMenuId) return;
    const onClick = () => setOpenActionMenuId(null);
    const onKey = (e) => { if (e.key === 'Escape') setOpenActionMenuId(null); };
    window.addEventListener('click', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [openActionMenuId]);

  // Reports: load whenever user opens the Reports phase, or switches tab,
  // or flips scope. Separate from library so the two surfaces don't
  // interfere.
  // Reports now lives as a Dashboards tab (2026-08-06 nav merge) but the
  // legacy `phase === 'reports'` deep-links keep working — both routes
  // render the same block below.
  const reportsActive = phase === 'reports' || (phase === 'dashboard' && dashboardView === 'reports');
  useEffect(() => {
    // !bootstrapping: don't fire with the not-yet-validated localStorage
    // token (a stale token on a #/reports deep-link would 401-spam the
    // login screen). `bootstrapping` stays in the deps so the flip to
    // false re-runs this effect and the deep-link load still happens.
    if (reportsActive && authToken && !bootstrapping) {
      loadReportsTab(reportsTab, reportsScope);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportsActive, reportsTab, reportsScope, authToken, bootstrapping]);

  // Team invites are loaded imperatively from the Team-tab click handler
  // (see the settings-sidebar button in the JSX below). A useEffect here
  // would need to reference loadTeamInvites + isCompanyAdmin, both of
  // which are declared further down in the component body — hitting
  // their temporal dead zone on first render. Imperative call sidesteps
  // the whole issue and matches what we actually want UX-wise (fetch
  // once when the admin clicks into the tab, not every time authUser
  // changes for an unrelated reason).

  // On mount: check for password reset link first, otherwise restore session.
  useEffect(() => {
    // Detect ?reset=1&token=... from a password reset email link
    const params = new URLSearchParams(window.location.search);

    // Google Sign-On callback handoff. Backend redirects here with
    // ?google_auth_token=<JWT>&new_user=0|1 after the OAuth round-trip.
    // Move the token into localStorage + auth state, surface profile
    // completion for new users, and strip the params so a refresh
    // doesn't replay them. If the token's bad, the GET /auth/me below
    // will catch it and bounce the user back to login.
    // Google sign-in for a 2FA-enrolled account: the backend hands back a
    // 5-minute CHALLENGE token (not a session) and we run the identical
    // second-factor step the password path uses. Before 2026-07-27 the
    // Google path skipped 2FA entirely.
    const google2faChallenge = params.get('google_2fa_challenge');
    if (google2faChallenge) {
      setTwoFactorChallenge({ token: google2faChallenge, email: params.get('email') || '' });
      setAuthView('twoFactor');
      setPhase('login');
      window.history.replaceState({}, '', window.location.pathname);
      setBootstrapping(false);
      return;
    }

    const googleToken = params.get('google_auth_token');
    if (googleToken) {
      const isNewUser = params.get('new_user') === '1';
      localStorage.setItem('trailercst_token', googleToken);
      // Fetch the user profile to populate authUser before clearing
      // bootstrapping — same pattern as the normal session-restore
      // path below, just with the token from URL instead of storage.
      fetch(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${googleToken}` },
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('token expired'))))
        .then((data) => {
          setAuthToken(googleToken);
          setAuthUser(data.user);
          setBrandConfig((prev) => ({
            ...prev,
            companyName: prev.companyName || displayCompanyName(data.user.companyName, ''),
          }));
          if (isNewUser || data.user.profileCompleted === false) {
            setShowProfileCompletion(true);
          }
          setPhase('dashboard');
        })
        .catch(() => {
          localStorage.removeItem('trailercst_token');
          setAuthView('login');
          setTimeout(() => showToast('Google sign-in succeeded but the session expired immediately. Please try again.'), 300);
        })
        .finally(() => {
          // Strip both params so a refresh doesn't loop.
          window.history.replaceState({}, '', window.location.pathname);
          setBootstrapping(false);
        });
      return;
    }

    // Google Sign-On error redirect. Same code path as success but the
    // backend redirects with ?auth_error=<reason> when the round-trip
    // fails. Surface a friendly toast + drop to login.
    const authErr = params.get('auth_error');
    if (authErr) {
      const friendly = {
        company_exists: `Someone from your domain already uses TrailerCast. Ask them for an invite.`,
        email_unverified: `Google says that email isn't verified. Verify it in your Google account, then retry.`,
        account_disabled: `That account has been disabled. Contact your admin.`,
        not_configured: `Google sign-in isn't set up on this environment yet.`,
        invalid_state: `Sign-in session expired. Try again.`,
        token_exchange: `Couldn't complete sign-in with Google. Try again.`,
        token_fetch: `Couldn't reach Google. Try again.`,
        id_token_decode: `Couldn't read your Google account. Try again.`,
        missing_params: `Sign-in was cancelled. Try again.`,
        server_error: `Server hiccup during sign-in. Try again.`,
      }[authErr] || `Sign-in failed: ${authErr}. Try again.`;
      setAuthView('login');
      setTimeout(() => showToast(friendly), 300);
      window.history.replaceState({}, '', window.location.pathname);
      setBootstrapping(false);
      return;
    }

    if (params.get('reset') === '1' && params.get('token')) {
      setResetToken(params.get('token'));
      setAuthView('reset');
      setBootstrapping(false);
      return;
    }

    // Detect ?invite=<token> from a team-invite email. Resolve it to get
    // company metadata + the invited email, pre-fill the signup form, and
    // jump straight to signup step 1 — no session restore, no login prompt.
    // If they already have a session, we still honor the invite flow so
    // the invite doesn't silently drop.
    const inviteTok = params.get('invite');
    if (inviteTok) {
      fetch(`${API_BASE}/api/auth/invite/${encodeURIComponent(inviteTok)}`)
        .then((r) => r.ok ? r.json() : Promise.reject(new Error(`status ${r.status}`)))
        .then((data) => {
          const inv = data.invite;
          setPendingInvite({
            token: inviteTok,
            email: inv.email,
            companyName: inv.companyName,
            inviterName: inv.inviterName,
          });
          setSignupForm1((prev) => ({ ...prev, email: inv.email }));
          setAuthView('signup1');
          setBootstrapping(false);
        })
        .catch(() => {
          // Invalid / expired invite — fall through to normal login with a
          // toast so the user isn't silently redirected.
          setTimeout(() => showToast('That invite link is invalid or expired. Ask your admin to send a fresh one.'), 500);
          setAuthView('login');
          setBootstrapping(false);
        });
      // Strip the invite param so a refresh doesn't re-fire (pendingInvite
      // state keeps the flow intact).
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    // Detect ?verify=1&token=... from a verification email link. Fire and
    // forget — call the verify endpoint, strip the token from the URL so
    // refreshes don't re-fire it, then continue with the normal session
    // restoration below. Toast surfaces the success/failure once we're
    // loaded. Fallback to the generic "verified!" toast even if the session
    // can't be restored (brand-new user finishing signup, but their JWT
    // was already lost somehow — they'll just need to log in after).
    if (params.get('verify') === '1' && params.get('token')) {
      const token = params.get('token');
      fetch(`${API_BASE}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
        .then(async (r) => {
          const data = await r.json().catch(() => ({}));
          if (r.ok) {
            // setTimeout so the toast lands AFTER bootstrap finishes and
            // the toast container is actually mounted.
            setTimeout(() => showToast('Email verified — you can now upload and send follow-ups.'), 500);
          } else {
            setTimeout(() => showToast(`Couldn't verify: ${data.error || 'Invalid or expired link.'}`), 500);
          }
        })
        .catch(() => {
          setTimeout(() => showToast('Network error while verifying email. Try the Resend button on your dashboard.'), 500);
        })
        .finally(() => {
          // Strip the query params so a refresh doesn't re-fire.
          window.history.replaceState({}, '', window.location.pathname);
        });
      // Fall through — proceed with normal session-restore logic below.
    }

    // Marketing site CTAs pass ?view=signup or ?view=login so the user
    // lands on the right auth screen. If a valid session exists we still
    // auto-login them — they probably clicked "Sign in" without realizing
    // they were already logged in.
    const viewParam = params.get('view');
    if (viewParam === 'signup' || viewParam === 'login') {
      const saved = localStorage.getItem('trailercst_token');
      if (!saved) {
        // Fire the Reddit "Lead" event when a fresh visitor arrives at
        // the signup view from a marketing CTA. This is the top-of-
        // funnel conversion signal Reddit's algo optimizes against —
        // without it, "Lowest Cost / Lead" campaigns serve cheap broad
        // impressions because the algo has no signal of what a
        // converting Reddit user looks like. Only fires for signup
        // (not login) and only when there's no existing session, so
        // returning users auto-logging-in don't pollute the conversion
        // count.
        if (viewParam === 'signup') {
          redditPixel.trackLead();
        }
        setAuthView(viewParam === 'signup' ? 'signup1' : 'login');
        setBootstrapping(false);
        return;
      }
      // If logged in, fall through to the normal /auth/me flow below.
    }

    const saved = localStorage.getItem('trailercst_token');
    if (!saved) {
      // Belt-and-suspenders: force the auth screen to render. The
      // initial phase useState SHOULD have already returned 'login'
      // when there's no token, but this guards against a stale hash
      // (e.g. logged out without clearing #/dashboard from the URL)
      // sneaking past the token check and stranding the user on a
      // blank page.
      setPhase('login');
      setAuthView('login');
      // Drop that stale hash too — the rehydrate effect now also
      // auth-guards, but a clean URL means nothing else can trip on it.
      try {
        if (window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      } catch (_) { /* non-fatal */ }
      setBootstrapping(false);
      return;
    }
    // Bootstrap timeout (2026-05-29 blank-page fix): the session-restore
    // fetch had NO timeout, so a hung /api/auth/me (cold Railway dyno,
    // network stall, proxy black hole) left `bootstrapping` stuck at true
    // forever → the loading shell renders with no content and no login
    // form = the "blank TrailerCast page on login" report. AbortController
    // fires the .catch after 12s, which forces the login screen; a
    // belt-and-suspenders hard timer flips bootstrapping to false even if
    // the fetch somehow neither resolves, rejects, nor aborts.
    const bootstrapController = new AbortController();
    const abortTimer = setTimeout(() => bootstrapController.abort(), 12000);
    const hardStop = setTimeout(() => {
      setPhase('login'); setAuthView('login'); setBootstrapping(false);
    }, 15000);
    fetch(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${saved}` },
      signal: bootstrapController.signal,
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('token expired')))
      .then(data => {
        setAuthToken(saved);
        setAuthUser(data.user);
        // Auto-fill brand config with the user's company name
        setBrandConfig(prev => ({ ...prev, companyName: prev.companyName || displayCompanyName(data.user.companyName, '') }));
        // Show profile completion banner if they skipped step 2
        if (data.user && data.user.profileCompleted === false) {
          setShowProfileCompletion(true);
        }
        // Respect an already-set hash-based phase — a refresh on
        // #/library shouldn't bounce the user back to the dashboard.
        // The hash-rehydrate effect re-applies this after bootstrap too,
        // but setting it here avoids a brief flash of dashboard content.
        //
        // This is the DESTRUCTIVE site, so it uses the same shared
        // resolver as the other two: whatever we set here, the phase↔hash
        // mirror effect immediately writes into the URL. Getting it wrong
        // doesn't just flash the dashboard, it OVERWRITES the deep link
        // before the rehydrate effect can read it. The hand-rolled copy
        // that used to live here had lost 'buyer-spaces' and 'calls' from
        // its allowlist and compared the raw hash without stripping the
        // subpath, so every cold load on '#/buyer-spaces/<id>/documents'
        // or '#/calls/<id>' landed on the dashboard (2026-08-24).
        const hash = (typeof window !== 'undefined' && window.location.hash) || '';
        setPhase(phaseFromHash(hash) || 'dashboard');
      })
      .catch((err) => {
        // Timeout/abort keeps the token (it may be valid — the backend
        // was just slow); expired/invalid clears it. Either way we fall
        // to the login screen rather than stranding on a blank shell.
        const isTimeout = err && (err.name === 'AbortError');
        if (!isTimeout) {
          localStorage.removeItem('trailercst_token');
          setAuthToken(null);
          setAuthUser(null);
          // Scrub any auth-phase hash the mirror effect wrote during
          // this failed boot (e.g. '#/dashboard'). Leaving it in the
          // URL fed the rehydrate effect a stale deep-link on the next
          // load — the "blank page after session expiry" loop.
          try {
            if (window.location.hash) {
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }
          } catch (_) { /* non-fatal */ }
        }
        // Session restore failed (expired/invalid token, slow backend, or
        // a network blip). Force the auth screen. Without this, `phase`
        // stays at its initial value — which is 'dashboard' whenever a
        // token was present at mount — and with authUser null NEITHER the
        // dashboard branch NOR the login branch renders → blank shell
        // (logo + footer only). Mirrors the no-token path above.
        setPhase('login');
        setAuthView('login');
      })
      .finally(() => {
        clearTimeout(abortTimer);
        clearTimeout(hardStop);
        setBootstrapping(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore the previously-viewed demo on page refresh. When
  // loadCompletedDemo succeeds it writes #demo=<id> to the URL hash;
  // on a fresh page load (auth just resolved, phase === 'dashboard')
  // we read that hash and re-invoke the loader so F5 keeps the
  // seller on the same recording instead of bouncing back to the
  // dashboard. Runs once per auth resolution — the restoredRef
  // guards against re-firing if authUser changes later in the
  // session (e.g. after editing profile).
  const demoHashRestoredRef = useRef(false);
  useEffect(() => {
    if (bootstrapping || !authUser || !authToken) return;
    if (demoHashRestoredRef.current) return;
    if (phase !== 'dashboard') return;
    if (typeof window === 'undefined') return;
    const match = window.location.hash.match(/^#demo=([^&]+)/);
    if (!match) { demoHashRestoredRef.current = true; return; }
    const demoId = decodeURIComponent(match[1]);
    if (!demoId) { demoHashRestoredRef.current = true; return; }
    demoHashRestoredRef.current = true;
    // Fire-and-forget; loadCompletedDemo handles its own errors +
    // toasts if the demo is gone or not yet completed.
    loadCompletedDemo(demoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapping, authUser, authToken, phase]);

  // Clear the #demo=... hash whenever the seller navigates AWAY from
  // the completed view (to dashboard / reports / library / wherever).
  // Centralized here so we don't have to remember to call clearDemoHash
  // from every back-button handler individually. Also covers the
  // refresh-then-restore dance — once loadCompletedDemo fires on
  // mount and flips phase back to 'completed', this effect is a no-op.
  useEffect(() => {
    if (bootstrapping) return;
    if (phase === 'completed') return;
    if (typeof window === 'undefined') return;
    if (window.location.hash.startsWith('#demo=')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [phase, bootstrapping]);

  // Restore an in-progress (#job=...) demo on cold mount. Mirrors the
  // demoHashRestoredRef pattern above but for the analyze + compose
  // phases. Without this, refreshing between upload and clip-selection
  // dumped the seller back to the dashboard with no way to find the
  // pending job.
  const jobHashRestoredRef = useRef(false);
  useEffect(() => {
    if (bootstrapping || !authUser || !authToken) return;
    if (jobHashRestoredRef.current) return;
    if (phase !== 'dashboard') return;
    if (typeof window === 'undefined') return;
    const match = window.location.hash.match(/^#job=([^&]+)/);
    if (!match) { jobHashRestoredRef.current = true; return; }
    const id = decodeURIComponent(match[1]);
    if (!id) { jobHashRestoredRef.current = true; return; }
    jobHashRestoredRef.current = true;
    loadInProgressDemo(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapping, authUser, authToken, phase]);

  // Write/clear #job=<id> as the seller moves through select + generating.
  // Survives refresh, lets us share a deep link to a pending demo if ever
  // useful. Cleared the moment the seller leaves these phases so the URL
  // stays clean on the dashboard.
  useEffect(() => {
    if (bootstrapping) return;
    if (typeof window === 'undefined') return;
    const inResumablePhase = (phase === 'select' || phase === 'generating' || phase === 'analyzing') && jobId;
    const currentHash = window.location.hash;
    if (inResumablePhase) {
      const target = `#job=${encodeURIComponent(jobId)}`;
      if (currentHash !== target) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search + target);
      }
    } else if (currentHash.startsWith('#job=')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [phase, jobId, bootstrapping]);

  // ═══════════════════════════════════════════════════════════════
  // AUTH HELPERS
  // ═══════════════════════════════════════════════════════════════

  // Wrapper around fetch that attaches the Authorization header when we have a token.
  //
  // Central 401 handling (audit Med, 2026-07-17): a 401 on an authed
  // request means the token is expired or the account was deleted —
  // the backend only 401s auth failures (permission denials are 403s).
  // Before this, an expired session left the app half-alive: every
  // loader silently failed into its empty state until the user gave up
  // and refreshed. Now the first 401 logs them out to the login screen
  // with an explanation. Login/signup/2FA use raw fetch, so a wrong
  // password can never trip this. (handleLogout is declared later in
  // the component body — safe here because this closure only runs at
  // request time, long after the body has executed. Don't convert this
  // to a call during render.)
  const authFetch = async (url, options = {}) => {
    const headers = { ...(options.headers || {}) };
    if (authToken) headers.Authorization = `Bearer ${authToken}`;
    const res = await fetch(url, { ...options, headers });
    if (res.status === 401 && authToken) {
      handleLogout();
      setAuthError('Your session has expired. Please sign in again.');
    }
    return res;
  };

  // Google Sign-On — kicks off the redirect-based OAuth flow. The backend
  // mints a state JWT, builds the Google authorize URL, and returns it as
  // JSON; we then redirect the browser to Google. After the user grants
  // access, Google redirects to our backend /callback, which redirects
  // back here with ?google_auth_token=…&new_user=0|1 in the URL — handled
  // in the bootstrap useEffect a few hundred lines below.
  const handleGoogleSignIn = async () => {
    setAuthError(null);
    setAuthLoading(true);
    try {
      // Preserve any ?invite=TOKEN currently in the URL so invited users
      // can join via Google without losing the invite attachment.
      let inviteQs = '';
      try {
        const inviteToken = new URLSearchParams(window.location.search).get('invite');
        if (inviteToken) inviteQs = `?invite=${encodeURIComponent(inviteToken)}`;
      } catch (_) { /* SSR-safe fallback */ }

      const res = await fetch(`${API_BASE}/api/auth/google/start${inviteQs}`);
      const data = await res.json();
      if (!res.ok || !data.authorizeUrl) {
        throw new Error(data.error || 'Could not start Google sign-in');
      }
      // Hand off to Google. The button shows a loading state until this
      // navigation happens — the redirect itself is instant.
      window.location.href = data.authorizeUrl;
    } catch (err) {
      setAuthError(err.message);
      setAuthLoading(false);
    }
  };

  // Login — POST /api/auth/login with { email, password }.
  // If the account has 2FA enabled, the backend returns
  // { challenge: '2fa_required', challengeToken } instead of a session
  // token. We stash the challenge token + flip authView to 'twoFactor'
  // so Login.jsx hands off to TwoFactorChallenge.jsx for the second step.
  const handleLogin = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setAuthError(null);
    setAuthLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: loginForm.email.trim(),
          password: loginForm.password,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');

      if (data.challenge === '2fa_required') {
        setTwoFactorChallenge({ token: data.challengeToken, email: loginForm.email.trim() });
        setAuthView('twoFactor');
        setLoginForm({ email: '', password: '' });
        return;
      }

      localStorage.setItem('trailercst_token', data.token);
      setAuthToken(data.token);
      setAuthUser(data.user);
      setBrandConfig(prev => ({ ...prev, companyName: prev.companyName || displayCompanyName(data.user.companyName, '') }));
      if (data.user && data.user.profileCompleted === false) {
        setShowProfileCompletion(true);
      }
      setLoginForm({ email: '', password: '' });
      setPhase('dashboard');
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  // Second step of 2FA login. Caller already passed password; we now
  // take the 6-digit TOTP code (or backup code) and exchange it +
  // the challenge token for the real session JWT.
  const handleTwoFactorVerify = async (code) => {
    if (!twoFactorChallenge) return;
    setAuthError(null);
    setAuthLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login/2fa-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challengeToken: twoFactorChallenge.token,
          code: String(code || '').trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '2FA verification failed');

      localStorage.setItem('trailercst_token', data.token);
      setAuthToken(data.token);
      setAuthUser(data.user);
      setBrandConfig(prev => ({ ...prev, companyName: prev.companyName || displayCompanyName(data.user.companyName, '') }));
      if (data.user && data.user.profileCompleted === false) {
        setShowProfileCompletion(true);
      }
      setTwoFactorChallenge(null);
      setAuthView('login');
      setPhase('dashboard');
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const cancelTwoFactor = () => {
    setTwoFactorChallenge(null);
    setAuthError(null);
    setAuthView('login');
  };

  // Signup Step 1 — creates account, transitions to step 2
  const handleSignupStep1 = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setAuthError(null);
    setAuthLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: signupForm1.email.trim(),
          password: signupForm1.password,
          // Invite token attaches the new user to the invited company
          // with role='member'. Null for fresh-team signups.
          inviteToken: pendingInvite ? pendingInvite.token : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Signup failed');

      // Store token and user — we're authenticated now, but still need to complete profile
      localStorage.setItem('trailercst_token', data.token);
      setAuthToken(data.token);
      setAuthUser(data.user);
      setAuthView('signup2');
      // Ad-platform conversion events — fire on step 1 (account row
      // created), not step 2. Step 2 is profile enrichment; both
      // optimizers want the cleanest "form submitted + account
      // created" signal. Both functions no-op when their pixel/tag
      // env vars are unset, so calling unconditionally is safe.
      redditPixel.trackSignUp();
      googleAds.trackTrialStarted();
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  // Signup Step 2 — completes profile, transitions to dashboard.
  // Supports two paths:
  //   - "Start a new team" — companyName required, a join code is minted
  //   - "Join an existing team" — joinCode required, user inherits company
  // Backend issues a fresh JWT because companyId + tier can change here.
  const handleSignupStep2 = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setAuthError(null);
    setAuthLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/complete-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          fullName: signupForm2.fullName.trim(),
          companyName: signupForm2.companyName ? signupForm2.companyName.trim() : null,
          role: signupForm2.role || null,
          companySize: signupForm2.companySize || null,
          useCase: signupForm2.useCase || null,
          joinCode: signupForm2.joinCode ? signupForm2.joinCode.trim() : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save profile');

      // Swap in the refreshed token so subsequent requests carry the
      // correct companyId + tier. Old placeholder-company JWT is now stale.
      if (data.token) {
        localStorage.setItem('trailercst_token', data.token);
        setAuthToken(data.token);
      }

      setAuthUser(data.user);
      setBrandConfig(prev => ({ ...prev, companyName: prev.companyName || displayCompanyName(data.user.companyName, '') }));
      setSignupForm1({ email: '', password: '' });
      setSignupForm2({ fullName: '', companyName: '', role: '', companySize: '', useCase: '', joinCode: '' });
      setShowProfileCompletion(false);

      // Trial flow (changed 2026-05-07): no credit card required at
      // signup. New users go straight to the dashboard and get full
      // feature access. The card-collection gate now only fires when
      // the trial expires OR the demo cap is reached — backend returns
      // 402 + code='card_required' on that specific upload, and the
      // frontend's existing 402-handler bounces them through Stripe
      // Checkout at the moment-of-impact (see App.jsx upload flow).
      //
      // We deliberately skip the post-signup Stripe Checkout redirect
      // that lived here previously. The marketing site promises "Get
      // started for free, no credit card required" and we must honor
      // that on the very next click.
      setPhase('dashboard');
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  // User clicks "Skip for now" on signup step 2
  const handleSignupSkip = () => {
    setAuthError(null);
    setSignupForm1({ email: '', password: '' });
    setShowProfileCompletion(true); // remind them once they're in
    setPhase('dashboard');
  };

  // Forgot password — POST /api/auth/forgot-password
  const handleForgotPassword = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setAuthError(null);
    setAuthLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotForm.email.trim() }),
      });
      // Backend always returns 200 to prevent email enumeration
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong');
      }
      setForgotSubmitted(true);
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  // Reset password — POST /api/auth/reset-password
  const handleResetPassword = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setAuthError(null);

    if (resetForm.newPassword !== resetForm.confirmPassword) {
      setAuthError("Passwords don't match");
      return;
    }

    setAuthLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: resetToken,
          newPassword: resetForm.newPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Reset failed');

      setResetSuccess(true);
      setResetForm({ newPassword: '', confirmPassword: '' });
      // Clean up the URL params
      window.history.replaceState({}, '', window.location.pathname);
      setResetToken(null);
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  // Helpers for view transitions
  const goToLogin = () => {
    setAuthError(null);
    setAuthView('login');
    setForgotSubmitted(false);
    setResetSuccess(false);
  };
  const goToSignup = () => {
    setAuthError(null);
    setAuthView('signup1');
  };
  const goToForgot = () => {
    setAuthError(null);
    setAuthView('forgot');
    setForgotSubmitted(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('trailercst_token');
    setAuthToken(null);
    setAuthUser(null);
    setProfileOpen(false);
    setPhase('login');
    setAuthView('login');
    // Clear the URL hash so a refresh doesn't try to restore a
    // post-login deep-link (e.g. #/dashboard). The phase useState
    // initializer reads the hash on next mount; without this, a
    // refresh after logout strands the user on a blank dashboard
    // because phase becomes 'dashboard' while authToken is null.
    if (typeof window !== 'undefined' && window.location.hash) {
      try {
        window.history.replaceState({}, '', window.location.pathname + window.location.search);
      } catch (_) { /* SSR-safe fallback */ }
    }
    setShowProfileCompletion(false);
    // Reset everything else too
    setSelectedFile(null);
    setJobId(null);
    setTrailerPath(null);
    setMoments([]);
    setAllMoments(null);
    setSelectedMoments([]);
    setProgress(0);
    setStatus(null);
    setError(null);
  };

  // ═══════════════════════════════════════════════════════════════
  // PROFILE DROPDOWN HELPERS
  // ═══════════════════════════════════════════════════════════════

  const loadAvailableVoices = async () => {
    setVoicesLoading(true);
    setVoicesError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/voices`);
      if (!res.ok) throw new Error('Failed to load voices');
      const data = await res.json();
      setAvailableVoices(data.voices || []);
      setDefaultVoiceId(data.defaultVoiceId || null);
      setAvailableLanguages(data.languages || []);

      // Preference-priority seeding: user's saved preferredVoiceId wins
      // if it's still in the allowed set (tier change, voice retired,
      // etc. can invalidate an old preference). Otherwise fall through
      // to whatever's in state, else first available.
      const allowedVoices = new Set((data.voices || []).map(v => v.id));
      const savedVoice = authUser && authUser.preferredVoiceId;
      setSelectedVoiceId((prev) => {
        if (savedVoice && allowedVoices.has(savedVoice)) return savedVoice;
        if (prev && allowedVoices.has(prev)) return prev;
        return (data.voices && data.voices.length > 0) ? data.voices[0].id : null;
      });

      const allowedCodes = new Set((data.languages || []).map(l => l.code));
      const savedLang = authUser && authUser.preferredLanguage;
      setSelectedLanguage((prev) => {
        if (savedLang && allowedCodes.has(savedLang)) return savedLang;
        if (prev && allowedCodes.has(prev)) return prev;
        return data.defaultLanguage || 'en';
      });
    } catch (err) {
      console.error('Voice load error:', err);
      setAvailableVoices([]);
      setAvailableLanguages([]);
      setVoicesError('Couldn’t load voices.');
    } finally {
      setVoicesLoading(false);
    }
  };

  // Clone-your-own-voice section is collapsed by default inside the
  // voice modal — most users won't touch it daily, and its form
  // elements (name + file picker + submit) dominate the modal
  // vertically when expanded. Collapsed by default leaves more room
  // for the voice grid users ARE picking from. (Voice cloning was
  // tier-gated to Enterprise pre-2026-04-21; now universal under the
  // single TrailerCast tier.)
  const [showCloneSection, setShowCloneSection] = useState(false);

  // Voice modal: snapshot the selection at open-time so Cancel can
  // revert back. The modal is now an explicit Save-or-Cancel surface
  // (not the old auto-save-on-close) — users asked for a clear Save
  // button since "click outside / × to dismiss" felt ambiguous.
  const voiceSnapshotRef = useRef(null);

  const openVoiceModal = () => {
    voiceSnapshotRef.current = {
      voiceId: selectedVoiceId,
      language: selectedLanguage,
    };
    setShowVoiceSettings(true);
  };

  const saveVoiceAndClose = async () => {
    setShowVoiceSettings(false);
    await saveNarrationPrefs({
      voiceId: selectedVoiceId,
      language: selectedLanguage,
    });
    showToast('Narration preferences saved.');
  };

  const cancelVoiceModal = () => {
    // Revert UI state to what it was when the modal opened so the upload
    // form's voice trigger (and the in-flight upload if any) reflects
    // the pre-modal choice.
    if (voiceSnapshotRef.current) {
      setSelectedVoiceId(voiceSnapshotRef.current.voiceId);
      setSelectedLanguage(voiceSnapshotRef.current.language);
    }
    setShowVoiceSettings(false);
  };

  // Auto-save narration preferences to the backend. Fire-and-forget —
  // the seller expects the modal to feel immediate, so we don't block
  // their selection on the network round-trip. If the save fails we log
  // it; next page load they'll just re-seed to defaults.
  const saveNarrationPrefs = async ({ voiceId, language }) => {
    if (!authToken) return;
    try {
      const res = await authFetch(`${API_BASE}/api/auth/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preferredVoiceId: voiceId || null,
          preferredLanguage: language || null,
        }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      // Keep authUser in sync so other code paths see the fresh prefs.
      if (data && data.user) setAuthUser(data.user);
    } catch (err) {
      console.error('[voice] pref save failed:', err);
    }
  };

  // ─── VOICE CLONING (Enterprise) ────────────────────────────────
  const handleCloneFileSelect = (file) => {
    if (!file) return;
    // Quick client-side validation — backend re-validates.
    const ok = /\.(mp3|wav|m4a|mp4|webm)$/i.test(file.name);
    if (!ok) {
      setCloneError('Sample must be MP3, WAV, M4A, or WebM.');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setCloneError('Sample is too large (max 15 MB).');
      return;
    }
    setCloneError(null);
    setCloneFile(file);
  };

  const submitClone = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setCloneError(null);
    if (!cloneFile) {
      setCloneError('Pick an audio sample first.');
      return;
    }
    if (!cloneName.trim()) {
      setCloneError('Give your voice a name.');
      return;
    }

    setCloneLoading(true);
    try {
      const fd = new FormData();
      fd.append('name', cloneName.trim());
      fd.append('sample', cloneFile);

      const res = await authFetch(`${API_BASE}/api/voices/clone`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Voice clone failed');

      // Refresh the user DTO so the Narration Voice panel shows the new clone.
      setAuthUser((prev) => prev ? {
        ...prev,
        clonedVoiceId: data.clonedVoiceId,
        clonedVoiceName: data.clonedVoiceName,
      } : prev);

      // Reset form + reload the catalogue so the new voice appears in the grid.
      setCloneFile(null);
      setCloneName('');
      if (cloneInputRef.current) cloneInputRef.current.value = '';
      await loadAvailableVoices();
      // Auto-select the newly cloned voice.
      setSelectedVoiceId(data.clonedVoiceId);
      showToast(`✓ Voice "${data.clonedVoiceName}" is ready to use.`);
    } catch (err) {
      setCloneError(err.message);
    } finally {
      setCloneLoading(false);
    }
  };

  const deleteClone = async () => {
    if (!window.confirm('Delete your cloned voice? This removes it permanently and cannot be undone.')) return;
    setCloneLoading(true);
    setCloneError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/voices/clone`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to remove cloned voice');

      setAuthUser((prev) => prev ? { ...prev, clonedVoiceId: null, clonedVoiceName: null } : prev);
      await loadAvailableVoices();
      showToast('Cloned voice removed.');
    } catch (err) {
      setCloneError(err.message);
    } finally {
      setCloneLoading(false);
    }
  };

  const loadDemoHistory = async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      // Trailer-class recordings only — qualifying calls + meetings run
      // a lighter pipeline (no trailer rendering) and live in the Calls
      // inbox. 'browser_recording' belongs here because those ARE final
      // trailers (recorded in the browser, no AI pipeline). Asking the
      // server for them keeps the page we get back from being diluted by
      // rows this screen would only throw away.
      const historyParams = new URLSearchParams();
      for (const kind of TRAILER_RECORDING_KINDS) historyParams.append('recordingKind', kind);
      const res = await authFetch(`${API_BASE}/api/demos?${historyParams.toString()}`);
      if (!res.ok) throw new Error('Failed to load history');
      const data = await res.json();
      setDemoHistory(data.demos || []);
      setDemoStats(data.stats || { total: 0, completed: 0 });
    } catch (err) {
      console.error('History load error:', err);
      setHistoryError('Couldn’t load your recent trailers.');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Load per-demo engagement on expand. Fire-and-forget — the
  // collapsible uncovers the panel immediately, data arrives shortly
  // after. Called from the completed-phase view.
  const loadEngagementForDemo = async (demoId) => {
    if (!demoId || !authToken) return;
    setEngagementLoading(true);
    setEngagementError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/demos/${demoId}/engagement`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      setEngagementData(data);
      // Adaptive default-tab: if this trailer already has views and the
      // seller hasn't manually picked a tab this session, surface the
      // engagement numbers (the reason they'd return to a shared trailer).
      // A no-op if they're already on engagement or picked another tab.
      if (data && data.aggregate && (data.aggregate.views || 0) > 0 && !reelTabUserPickedRef.current) {
        setDemoRightTab('engagement');
      }
    } catch (err) {
      console.error('[engagement] load failed:', err);
      setEngagementData(null);
      setEngagementError('Couldn’t load engagement data.');
    } finally {
      setEngagementLoading(false);
    }
  };

  // Legacy — kept as a no-op so any stale callers don't throw. The
  // engagement panel is now always-open (Commit "trailer page stats +
  // follow-up modal"), so toggling it is no longer meaningful.
  const toggleEngagement = () => {};

  // Close the follow-up modal on Escape, matching the rebuild +
  // full-recording modal patterns.
  useEffect(() => {
    if (!showFollowUp) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setShowFollowUp(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showFollowUp]);

  // Auto-load the engagement panel on completed-phase mount so fresh
  // numbers paint without the seller pressing refresh. The 20s
  // auto-poll was removed per user request — it was distracting and
  // the manual Refresh button in the panel header is sufficient for
  // sellers watching a test view roll in.
  useEffect(() => {
    if (phase !== 'completed' || !jobId) return;
    loadEngagementForDemo(jobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, jobId]);

  // Re-fetch engagement when the seller tabs back into the window.
  // Sellers commonly check their share-link engagement by clicking
  // the email link to themselves in another tab, then switching back
  // to the dashboard expecting "opened" to flip — without this, the
  // panel stays cached on the original "Not yet opened" state until
  // they manually click Refresh. (Bug observed 2026-04-26.)
  useEffect(() => {
    if (phase !== 'completed' || !jobId) return;
    if (typeof window === 'undefined') return;
    const onFocus = () => loadEngagementForDemo(jobId);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, jobId]);

  // Clear engagement state when switching demos so an old demo's numbers
  // don't briefly flash in the panel while a new one loads.
  useEffect(() => {
    setShowEngagement(false);
    setEngagementData(null);
    setEngagementError(null);
  }, [jobId]);

  // ═══════════════════════════════════════════════════════════════
  // REPORTS
  // ═══════════════════════════════════════════════════════════════

  // CSV download for the two tabular Reports tabs (2026-07-26 roadmap
  // quick-win). Mirrors the loader's URL + format=csv; authFetch → blob
  // → synthetic <a download> click, objectURL revoked after.
  const exportReportsCsv = async () => {
    try {
      const url = reportsTab === 'by-user'
        ? `${API_BASE}/api/reports/by-user?format=csv`
        : `${API_BASE}/api/reports/top-demos?scope=${reportsScope}&limit=50&format=csv`;
      const res = await authFetch(url);
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = reportsTab === 'by-user' ? 'trailercast-by-user.csv' : 'trailercast-top-demos.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (err) {
      showToast(`CSV export failed: ${err.message}`);
    }
  };

  const loadReportsTab = async (tab = reportsTab, scope = reportsScope) => {
    if (!authToken) return;
    // Claim a sequence id. Any response arriving after a newer call has
    // started is stale — its writes (data, loading, error) are dropped.
    const seq = ++reportsReqSeqRef.current;
    const isCurrent = () => seq === reportsReqSeqRef.current;
    setReportsLoading(true);
    setReportsError(null);
    try {
      if (tab === 'top-demos') {
        const res = await authFetch(`${API_BASE}/api/reports/top-demos?scope=${scope}&limit=50`);
        if (!res.ok) {
          if (res.status === 403 && scope === 'team') {
            if (!isCurrent()) return;
            setReportsScope('personal');
            return loadReportsTab(tab, 'personal');
          }
          throw new Error(`status ${res.status}`);
        }
        const data = await res.json();
        if (!isCurrent()) return;
        setReportsTopDemos(data.demos || []);
      } else if (tab === 'by-user') {
        const res = await authFetch(`${API_BASE}/api/reports/by-user`);
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = await res.json();
        if (!isCurrent()) return;
        setReportsByUser(data.users || []);
      } else if (tab === 'activity') {
        const res = await authFetch(`${API_BASE}/api/reports/activity?scope=${scope}&limit=50`);
        if (!res.ok) {
          if (res.status === 403 && scope === 'team') {
            if (!isCurrent()) return;
            setReportsScope('personal');
            return loadReportsTab(tab, 'personal');
          }
          throw new Error(`status ${res.status}`);
        }
        const data = await res.json();
        if (!isCurrent()) return;
        setReportsActivity(data.activity || []);
      }
    } catch (err) {
      console.error('[reports] load failed:', err);
      // Surface it — before this, a 500 fell through to the empty state
      // ("No demos with views yet"), indistinguishable from real-empty
      // and with no retry short of leaving the page and coming back.
      if (isCurrent()) setReportsError('Couldn’t load reports. Check your connection and retry.');
    } finally {
      if (isCurrent()) setReportsLoading(false);
    }
  };

  // Small client-side sort helper for the tables. Most datasets top out at
  // ~50-100 rows so sorting in JS is fine — avoids server round-trips when
  // users toggle columns.
  const sortReportRows = (rows, col, dir) => {
    if (!col) return rows;
    // Direction lives in the comparator (not a post-sort reverse) so
    // equal values keep their original relative order either way.
    const mult = dir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const av = a[col];
      const bv = b[col];
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * mult;
      }
      return String(av || '').localeCompare(String(bv || '')) * mult;
    });
  };

  // Snapshot of the Trailers screen's active query, refreshed on every
  // render so imperative "just refresh the list" callers (delete, rename,
  // the record-tab BroadcastChannel, the left-nav click) reload what the
  // user is actually looking at instead of resetting them to an
  // unfiltered first page.
  // Populated further down, right after the creator/date filter state it
  // reads is declared. Until then it is null, and every caller falls back
  // to a plain scope+status fetch — which is the correct behaviour for a
  // render that has not reached the Trailers screen's state yet anyway.
  const trailersQueryRef = useRef(null);

  // Monotonic request id. The Trailers screen can have several loads in
  // flight at once — a debounced search landing while a tab switch is
  // still resolving, or a dashboard drill-in that sets filters and
  // navigates in the same tick — and without this the slower, older
  // response wins and the grid contradicts its own controls.
  const libraryReqRef = useRef(0);

  // Library loader — backs the dashboard's recent-trailer surfaces AND
  // the full Trailers screen.
  //
  // Three call shapes, all supported on purpose:
  //   loadLibraryDemos()                  → refresh what is on screen
  //   loadLibraryDemos(scope, status)     → legacy positional
  //   loadLibraryDemos({ ...queryOpts })  → explicit, used by the screen
  //
  // A bare refresh re-issues the Trailers screen's current query when
  // that screen is what the user is on, and a plain scope+status fetch
  // otherwise — so the dashboard's tiles are never narrowed by a filter
  // the user set on a different page.
  const loadLibraryDemos = async (optsOrScope, statusArg) => {
    if (!authToken) return;

    let opts;
    if (optsOrScope && typeof optsOrScope === 'object') {
      opts = optsOrScope;
    } else if (optsOrScope === undefined && statusArg === undefined) {
      opts = phase === 'library' && trailersQueryRef.current
        ? { ...trailersQueryRef.current }
        : {};
    } else {
      opts = { scope: optsOrScope, status: statusArg };
    }

    const scope = opts.scope || libraryScope;
    const status = opts.status !== undefined ? opts.status : libraryStatusFilter;
    const offset = Number(opts.offset) || 0;
    const append = !!opts.append && offset > 0;

    const reqId = ++libraryReqRef.current;
    if (append) setLibraryLoadingMore(true); else setLibraryLoading(true);
    setLibraryError(null);
    try {
      const params = new URLSearchParams();
      params.set('scope', scope);
      if (status && status !== 'all') params.set('status', status);
      // Everything below used to be applied to the loaded array in the
      // browser, over a fixed window — which is why the search box could
      // not see row 51. It is all server-side now.
      if (opts.contentScope === 'deal' || opts.contentScope === 'reusable') {
        params.set('contentScope', opts.contentScope);
      }
      if (opts.q && opts.q.trim()) params.set('q', opts.q.trim());
      if (opts.sort) params.set('sort', opts.sort);
      if (opts.creatorUserId) params.set('creatorUserId', String(opts.creatorUserId));
      // 'all' is the absence of a filter, so it is never sent. 'none'
      // is the Unfiled sentinel and MUST be sent — omitting it would
      // silently widen Unfiled to the whole library.
      if (opts.folderId && opts.folderId !== 'all') params.set('folderId', String(opts.folderId));
      if (opts.createdFrom) params.set('createdFrom', opts.createdFrom);
      if (opts.createdTo) params.set('createdTo', opts.createdTo);
      // Trailer kinds have to go to the server too: `total` below is
      // rendered as "showing N of M", and a client-side kind filter left
      // M counting the Calls rows it dropped.
      for (const kind of TRAILER_RECORDING_KINDS) params.append('recordingKind', kind);
      params.set('limit', String(TRAILER_PAGE_SIZE));
      if (offset) params.set('offset', String(offset));

      const res = await authFetch(`${API_BASE}/api/demos?${params.toString()}`);
      if (!res.ok) {
        // Tier gate: free/starter trying to hit team scope. Fall back to
        // personal silently so the sidebar never shows "403" to the user.
        if (res.status === 403 && scope === 'team') {
          setLibraryScope('personal');
          return loadLibraryDemos({ ...opts, scope: 'personal', offset: 0, append: false });
        }
        throw new Error(`status ${res.status}`);
      }
      const data = await res.json();
      // A newer request has been issued since this one left, so its
      // answer is the current one. Drop this response rather than let it
      // overwrite fresher rows.
      if (reqId !== libraryReqRef.current) return;

      const rows = data.demos || [];
      setLibraryDemos((prev) => (append ? [...prev, ...rows] : rows));
      setLibraryTotal(Number.isFinite(Number(data.total)) ? Number(data.total) : rows.length);
    } catch (err) {
      if (reqId !== libraryReqRef.current) return;
      console.error('[library] load failed:', err);
      if (!append) {
        setLibraryDemos([]);
        setLibraryTotal(0);
      }
      setLibraryError('Couldn’t load your trailers.');
    } finally {
      if (reqId === libraryReqRef.current) {
        if (append) setLibraryLoadingMore(false); else setLibraryLoading(false);
      }
    }
  };

  // "Load more" — same query, next page, appended. Guarded against
  // double-fire: the button disables, but a fast double-click can still
  // land two events before React re-renders.
  const loadMoreTrailers = () => {
    if (libraryLoading || libraryLoadingMore) return;
    if (libraryDemos.length >= libraryTotal) return;
    loadLibraryDemos({ ...trailersQueryRef.current, offset: libraryDemos.length, append: true });
  };

  // Move a trailer between the Deal and Reusable tabs. Optimistic: the
  // row leaves the current tab immediately and comes back if the PATCH
  // fails. A reclassify is a judgement the rep has already made — making
  // them wait on a round-trip to see it land reads as lag, not safety.
  const reclassifyDemo = async (demoId, nextScope) => {
    if (!demoId || reclassifyingId) return;
    const label = nextScope === 'reusable' ? 'Reusable' : 'Deal';
    const previousRows = libraryDemos;
    const previousTotal = libraryTotal;
    setReclassifyingId(demoId);
    // The row no longer matches the open tab, so it leaves the list.
    setLibraryDemos((rows) => rows.filter((r) => r.id !== demoId));
    setLibraryTotal((t) => Math.max(0, t - 1));
    try {
      const res = await authFetch(`${API_BASE}/api/demos/${demoId}/content-scope`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentScope: nextScope }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `status ${res.status}`);
      }
      // The toast renderer calls dismissToast() after the action, so
      // the handler only has to switch tabs.
      showToast(`Moved to ${label}.`, {
        label: `View ${label}`,
        onClick: () => setLibraryContentScope(nextScope),
      });
    } catch (err) {
      console.error('[library] reclassify failed:', err);
      setLibraryDemos(previousRows);
      setLibraryTotal(previousTotal);
      showToast(`Couldn’t move that trailer to ${label}. Nothing changed.`);
    } finally {
      setReclassifyingId(null);
    }
  };

  // ── Trailer folders (2026-08-24) ──
  // Flat, workspace-shared filing. See the backend notes for why the
  // shape is one folder per recording rather than many-to-many.

  const loadTrailerFolders = async () => {
    if (!authToken) return;
    setFoldersLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/trailer-folders`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      setTrailerFolders(Array.isArray(data.folders) ? data.folders : []);
    } catch (err) {
      // A folder rail that fails to load must not take the Trailers
      // screen with it — the list below is the thing the user came for.
      // Empty rail, no error banner: "all" still shows everything.
      console.error('[folders] load failed:', err);
      setTrailerFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  };

  // File a recording into a folder, or out of one. `folderId` null
  // removes it. Not optimistic, unlike reclassifyDemo: the row usually
  // STAYS visible (filing does not change which tab it is on), so
  // there is no jarring disappearance to pre-empt — and the counts on
  // the rail have to come back from the server anyway.
  const setDemoFolder = async (demoId, folderId, folderName) => {
    if (!demoId || filingId) return;
    setFilingId(demoId);
    try {
      const res = await authFetch(`${API_BASE}/api/demos/${demoId}/folder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderId: folderId || null }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `status ${res.status}`);
      }
      setLibraryDemos((rows) => rows.map((r) => (
        r.id === demoId ? { ...r, folderId: folderId || null } : r
      )));
      // Counts moved; the rail is now stale.
      loadTrailerFolders();
      // If the user is looking AT a folder and just moved something
      // out of it, the row no longer belongs on screen.
      if (libraryFolderId !== 'all' && libraryFolderId !== (folderId || 'none')) {
        setLibraryDemos((rows) => rows.filter((r) => r.id !== demoId));
        setLibraryTotal((t) => Math.max(0, t - 1));
      }
      showToast(folderId ? `Filed in ${folderName || 'folder'}.` : 'Removed from its folder.');
    } catch (err) {
      console.error('[folders] file failed:', err);
      showToast('Couldn’t move that recording. Nothing changed.');
    } finally {
      setFilingId(null);
    }
  };

  const createTrailerFolder = async (name) => {
    const clean = String(name || '').trim();
    if (!clean) return null;
    try {
      const res = await authFetch(`${API_BASE}/api/trailer-folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: clean }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.error || `status ${res.status}`);
      await loadTrailerFolders();
      // `created` is false when a folder with that name already
      // existed. Saying "created" then would be a small lie the user
      // can see through the moment they look at the rail.
      showToast(payload.created ? `Folder “${payload.folder.name}” created.` : `“${payload.folder.name}” already exists.`);
      return payload.folder || null;
    } catch (err) {
      console.error('[folders] create failed:', err);
      showToast('Couldn’t create that folder.');
      return null;
    }
  };

  const deleteTrailerFolder = async (folder) => {
    if (!folder || !folder.id) return;
    const n = Number(folder.trailerCount) || 0;
    const warning = n > 0
      ? `Delete “${folder.name}”?\n\n${n} recording${n === 1 ? '' : 's'} will move to Unfiled. Nothing is deleted.`
      : `Delete “${folder.name}”?`;
    if (!window.confirm(warning)) return;
    try {
      const res = await authFetch(`${API_BASE}/api/trailer-folders/${folder.id}`, { method: 'DELETE' });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.error || `status ${res.status}`);
      // Looking at the folder that just went away — fall back to all,
      // which also refetches. Otherwise the screen keeps requesting a
      // folder id that no longer resolves.
      if (libraryFolderId === folder.id) setLibraryFolderId('all');
      await loadTrailerFolders();
      const moved = Number(payload.unfiled) || 0;
      showToast(moved > 0
        ? `Folder deleted. ${moved} recording${moved === 1 ? '' : 's'} moved to Unfiled.`
        : 'Folder deleted.');
    } catch (err) {
      console.error('[folders] delete failed:', err);
      showToast(err.message || 'Couldn’t delete that folder.');
    }
  };

  const renameTrailerFolder = async (folder) => {
    if (!folder || !folder.id) return;
    const next = window.prompt('Rename folder', folder.name);
    if (next === null) return;
    const clean = String(next).trim();
    if (!clean || clean === folder.name) return;
    try {
      const res = await authFetch(`${API_BASE}/api/trailer-folders/${folder.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: clean }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.error || `status ${res.status}`);
      await loadTrailerFolders();
      showToast('Folder renamed.');
    } catch (err) {
      console.error('[folders] rename failed:', err);
      showToast(err.message || 'Couldn’t rename that folder.');
    }
  };

  // Open the personal settings modal on a specific tab, with the same
  // transient-state reset + form hydration the old avatar-click did.
  // Called from the ProfileMenu dropdown items (2026-08-12) — the
  // avatar itself now opens the MENU, and this is what menu items use
  // to reach the modal. Also used by SecuritySettingsCard's deep-link.
  const openProfileSettings = (tab = 'profile') => {
    toggleProfile(true);
    setProfileTab(tab);
  };

  const toggleProfile = (forceOpen = null) => {
    const willOpen = forceOpen === true ? true : !profileOpen;
    setProfileOpen(willOpen);
    if (willOpen) {
      // Reset transient state when opening + hydrate editable forms from authUser
      setProfileTab('profile');
      setPasswordError(null);
      setPasswordSuccess(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setProfileError(null);
      setBrandError(null);
      if (authUser) {
        setProfileForm({
          firstName: authUser.firstName || '',
          lastName: authUser.lastName || '',
          jobTitle: authUser.jobTitle || '',
          profilePictureUrl: authUser.profilePictureUrl || null,
        });
        const brand = (authUser.company && authUser.company.brand) || {};
        // Reset path — use the raw setter + clear dirty so this is
        // treated as a fresh hydration, not a user edit.
        brandFormDirtyRef.current = false;
        setBrandFormRaw({
          companyName: brand.companyName || '',
          primaryColor: brand.primaryColor || '#6366f1',
          secondaryColor: brand.secondaryColor || '#8b5cf6',
          logoUrl: brand.logoUrl || null,
          slideTheme: brand.slideTheme || null,
        });
      }
      loadDemoHistory();
    }
  };

  // ─── IMAGE RESIZE + SETTINGS HANDLERS ─────────────────────────
  /**
   * Draw `file` onto a canvas, fit within `maxSize` x `maxSize`, export as
   * JPEG data URL. Keeps payloads small and compresses away transparency
   * (profile pics and logos don't need alpha for our use case).
   */
  // Resize an uploaded image to a max edge length and encode as a
  // data URL. opts.preserveTransparency:
  //   - false (default): output JPEG. Transparent regions painted white.
  //                       Right call for profile pictures (small, photo-like).
  //   - true:            output PNG. Preserves alpha. Right call for
  //                       company logos — JPEG conversion was crushing
  //                       transparent edges into a white halo on the
  //                       new dark TopBar render.
  // 2026-05-08: brand logos now take preserveTransparency=true so the
  // alpha channel survives the round-trip from disk → canvas → server.
  const resizeImageToDataUrl = (file, maxSize = 256, quality = 0.85, opts = {}) => {
    return new Promise((resolve, reject) => {
      if (!file) return reject(new Error('No file provided'));
      if (!/^image\//.test(file.type || '')) return reject(new Error('File must be an image'));
      const preserveTransparency = !!opts.preserveTransparency;
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Could not read file'));
      reader.onload = () => {
        const img = new window.Image();
        img.onload = () => {
          const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
          const w = Math.max(1, Math.round(img.width * scale));
          const h = Math.max(1, Math.round(img.height * scale));
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!preserveTransparency) {
            // JPEG can't represent transparency — paint white behind
            // so transparent PNGs render cleanly when re-encoded.
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, w, h);
          }
          // Higher-quality scaling for the canvas downsample. Default
          // is browser-dependent; explicit 'high' gives sharper logos.
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, w, h);
          resolve(preserveTransparency
            ? canvas.toDataURL('image/png')
            : canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = () => reject(new Error('Could not decode image'));
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleProfilePictureSelect = async (file) => {
    if (!file) return;
    setProfileError(null);
    try {
      const dataUrl = await resizeImageToDataUrl(file, 256, 0.85);
      setProfileForm((prev) => ({ ...prev, profilePictureUrl: dataUrl }));
    } catch (err) {
      setProfileError(err.message || 'Could not process image');
    }
  };

  const handleBrandLogoSelect = async (file) => {
    if (!file) return;
    setBrandError(null);
    try {
      // Logos: 1024px max edge, PNG output to preserve transparency.
      // Renders crisply at every surface size — TopBar 40px, slide
      // intros 200px, follow-up email signature 60px — without the
      // white-fill halo JPEG conversion was leaving on transparent
      // logos. Bumped 512 → 1024 because the TopBar render at 2x
      // retina (80px) was visibly soft at the lower source size.
      const dataUrl = await resizeImageToDataUrl(file, 1024, 0.92, { preserveTransparency: true });
      setBrandForm((prev) => ({ ...prev, logoUrl: dataUrl }));
    } catch (err) {
      setBrandError(err.message || 'Could not process logo');
    }
  };

  const saveProfile = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setProfileError(null);
    setProfileSaved(false);
    setProfileSaving(true);
    // Snapshot what we're sending so it's visible in dev tools / Sentry if
    // a user reports "save did nothing" — the previous version had no
    // breadcrumb so we couldn't tell whether the request fired at all.
    const payload = {
      firstName: (profileForm.firstName || '').trim(),
      lastName: (profileForm.lastName || '').trim(),
      jobTitle: (profileForm.jobTitle || '').trim(),
    };
    // Only include profilePictureUrl when the user actually changed it.
    // Why this matters: users who signed in via Google have their picture
    // column populated with a Google CDN URL (e.g.
    // https://lh3.googleusercontent.com/...) — not a data URL. The
    // backend validates the field as `isReasonableDataUrl` and returns
    // 413 "must be an image data URL" if it sees a plain HTTPS URL.
    // Echoing the loaded value back would fail every save attempt for
    // every Google-signed-in user who hasn't uploaded their own photo
    // (the "it's making me add a profile picture" report). Three real
    // change cases:
    //   - new upload   → profilePictureUrl is a fresh data: URL  (changed)
    //   - removed pic  → profilePictureUrl is null + previously had one (changed)
    //   - untouched    → profilePictureUrl === authUser.profilePictureUrl (skip)
    const currentPic = authUser?.profilePictureUrl ?? null;
    const formPic = profileForm.profilePictureUrl ?? null;
    if (currentPic !== formPic) {
      payload.profilePictureUrl = formPic;
    }
    try {
      const res = await authFetch(`${API_BASE}/api/auth/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Profile update failed (${res.status})`);
      setAuthUser(data.user);
      // Re-hydrate the form from the server's canonical response so the
      // user sees exactly what was persisted (handles backend trimming /
      // null-coercion / character limits). Without this rehydrate, a
      // user who hits Save → reopens the modal → types again would see
      // their typed value persist visually even if the server rejected
      // it silently.
      if (data.user) {
        setProfileForm({
          firstName: data.user.firstName || '',
          lastName: data.user.lastName || '',
          jobTitle: data.user.jobTitle || '',
          profilePictureUrl: data.user.profilePictureUrl || null,
        });
      }
      setProfileSaved(true);
      showToast('Profile updated.');
      // Clear the inline "Saved ✓" badge after a few seconds so the
      // button returns to a neutral state on subsequent edits.
      setTimeout(() => setProfileSaved(false), 3000);
    } catch (err) {
      console.error('[profile] save failed:', err, 'payload:', payload);
      setProfileError(err.message);
    } finally {
      setProfileSaving(false);
    }
  };

  const removeProfilePicture = () => {
    setProfileForm((prev) => ({ ...prev, profilePictureUrl: null }));
  };

  // Onboarding save — thin async wrapper the OnboardingForm component
  // calls with the validated answers object. Returns a promise that
  // resolves on success or throws with the backend error message so
  // the form can display it inline. Merges the new state into
  // authUser.company so the checklist row + banner + Personalize
  // setting hide instantly, no /me round-trip required.
  const saveOnboardingAnswers = async (answers) => {
    const res = await authFetch(`${API_BASE}/api/auth/onboarding`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answers }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to save onboarding');
    setAuthUser((prev) => prev ? {
      ...prev,
      company: {
        ...(prev.company || {}),
        onboarded: true,
        onboardedAt: data.onboardedAt,
        onboardingAnswers: data.answers,
      },
    } : prev);
    setOnboardingModalOpen(false);
    showToast('Thanks — your personalization is saved. Future demos will reflect it.');
  };

  // Feedback form submit. Sends category + message + context
  // breadcrumbs (current phase, URL, user-agent, completed-demo
  // count) to POST /api/feedback. Clears the form + closes the
  // modal on success; error stays on the panel for retry.
  const submitFeedback = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!feedbackForm.message.trim()) {
      setFeedbackError('Please describe your feedback.');
      return;
    }
    setFeedbackError(null);
    setFeedbackSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: feedbackForm.category,
          message: feedbackForm.message.trim(),
          context: {
            phase: typeof phase === 'string' ? phase : null,
            path: window.location.pathname + window.location.hash,
            userAgent: navigator.userAgent,
            demoCount: demoStats?.completed || 0,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send feedback');
      setFeedbackModalOpen(false);
      setFeedbackForm({ category: 'feature_request', message: '' });
      showToast('Thanks — your feedback was sent. We read every one.');
    } catch (err) {
      setFeedbackError(err.message);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  // ── Recording-consent handlers ─────────────────────────────
  // Acknowledge consent (seller-level). Snapshots the company's
  // current jurisdiction onto the user row. Closes the modal and
  // updates authUser optimistically so the bot-schedule form
  // unlocks without waiting for a /me round-trip.
  const acknowledgeConsent = async () => {
    setConsentError(null);
    setConsentSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE}/api/auth/consent/acknowledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to record acknowledgment');
      setAuthUser((prev) => prev ? {
        ...prev,
        consentAcknowledgedAt: data.consentAcknowledgedAt,
        consentAcknowledgedJurisdiction: data.consentAcknowledgedJurisdiction,
      } : prev);
      setConsentModalOpen(false);
      setConsentCheckboxes({ authority: false, inform: false, jurisdiction: false });
      showToast('Thanks — you can now schedule meeting bots.');
    } catch (err) {
      setConsentError(err.message);
    } finally {
      setConsentSubmitting(false);
    }
  };

  // Admin saves the company-wide jurisdiction + optional privacy URL.
  const saveCompanyConsent = async () => {
    setJurisdictionError(null);
    if (!jurisdictionForm.jurisdiction) {
      setJurisdictionError('Please pick a jurisdiction.');
      return;
    }
    setJurisdictionSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/api/auth/company-consent`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jurisdiction: jurisdictionForm.jurisdiction,
          privacyPolicyUrl: jurisdictionForm.privacyPolicyUrl || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save jurisdiction');
      setAuthUser((prev) => prev ? {
        ...prev,
        company: {
          ...(prev.company || {}),
          consentJurisdiction: data.consentJurisdiction,
          privacyPolicyUrl: data.privacyPolicyUrl,
        },
      } : prev);
      showToast('Jurisdiction saved.');
    } catch (err) {
      setJurisdictionError(err.message);
    } finally {
      setJurisdictionSaving(false);
    }
  };

  // Auto-open the onboarding modal on first dashboard visit after
  // signup. Only fires for company admins (owners included) whose
  // company hasn't completed onboarding yet AND who haven't
  // dismissed the modal this session. The form component seeds
  // itself from authUser.company.onboardingAnswers on mount, so no
  // prefill state lives at App level anymore.
  useEffect(() => {
    if (!authUser) return;
    if (phase !== 'dashboard') return;
    const company = authUser.company || {};
    if (company.onboarded) return;
    if (onboardingDismissed) return;
    if (!company.isAdmin) return;
    setOnboardingModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, phase, onboardingDismissed]);

  const saveBrand = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setBrandError(null);
    setBrandSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/api/auth/brand`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: brandForm.companyName,
          primaryColor: brandForm.primaryColor,
          secondaryColor: brandForm.secondaryColor,
          logoUrl: brandForm.logoUrl,
          slideTheme: brandForm.slideTheme || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Brand update failed');
      // Clear the dirty flag BEFORE setAuthUser so the hydration
      // effect that fires on authUser change will re-sync the form
      // from the server's canonical copy. Eliminates the edge case
      // where server-side normalization (e.g. color hex casing or
      // trimming) differs from what the user typed.
      brandFormDirtyRef.current = false;
      setAuthUser(data.user);
      showToast('Brand settings saved.');
      // Refresh the engine-rendered previews with the just-saved brand.
      setBrandSavedVersion((v) => v + 1);
    } catch (err) {
      setBrandError(err.message);
    } finally {
      setBrandSaving(false);
    }
  };

  const removeBrandLogo = () => {
    setBrandForm((prev) => ({ ...prev, logoUrl: null }));
  };

  // [REMOVED 2026-05-02] uploadSlideTemplate / removeSlideTemplate /
  // renderSlideTemplateSection were for the .pptx corporate-deck
  // upload feature. Superseded by the per-slide-type custom-backgrounds
  // section below, which is wired into the actual render pipeline.

  // ─── Custom slide backgrounds (Phase 1 of slide-templates feature) ─

  const SLIDE_TYPE_LABELS = {
    intro: 'Intro slide',
    agenda: 'Agenda slide',
    transition: 'Transition card',
    outro: 'Outro slide',
  };
  const SLIDE_TYPE_DESCS = {
    intro: 'First slide. Your headline + theme cards on top.',
    agenda: 'What\'s inside. Numbered list of moments on top.',
    transition: 'Between each clip. Section title + timestamp on top.',
    outro: 'Final slide. Your contact info on top.',
  };
  const SLIDE_TYPES_ORDER = ['intro', 'agenda', 'transition', 'outro'];

  const fetchCustomSlideTemplates = async () => {
    if (customSlideLoaded) return;
    try {
      const res = await authFetch(`${API_BASE}/api/slide-templates`);
      if (!res.ok) {
        // 403 = tier-locked (Starter); leave empty + we render the upgrade UI
        if (res.status === 403) {
          setCustomSlideLoaded(true);
          return;
        }
        throw new Error('Failed to load templates');
      }
      const data = await res.json();
      const map = {};
      (data.templates || []).forEach((tpl) => {
        map[tpl.slideType] = tpl;
      });
      setCustomSlideTemplates(map);
      setCustomSlideLoaded(true);
    } catch (err) {
      setCustomSlideError(err.message);
    }
  };

  const uploadCustomSlide = async (slideType, file) => {
    if (!file) return;
    setCustomSlideUploading(slideType);
    setCustomSlideError(null);
    try {
      const fd = new FormData();
      fd.append('slide_type', slideType);
      fd.append('file', file);
      const res = await authFetch(`${API_BASE}/api/slide-templates`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 403 && data.code === 'tier_locked') {
          // Backend may still emit tier_locked during the grace window
          // for legacy free-tier accounts that haven't been migrated to
          // the unified TrailerCast tier yet. Surface a generic
          // "subscribe to unlock" message instead of mentioning Pro.
          throw new Error('Custom slide backgrounds unlock with an active TrailerCast subscription.');
        }
        throw new Error(data.error || `Upload failed (${res.status})`);
      }
      // Replace the slot with the new template; bust the imageUrl cache by appending updatedAt.
      setCustomSlideTemplates((prev) => ({
        ...prev,
        [slideType]: data.template,
      }));
    } catch (err) {
      setCustomSlideError(err.message);
    } finally {
      setCustomSlideUploading(null);
      // Clear the file input so re-uploading the same file works
      if (customSlideInputRefs[slideType] && customSlideInputRefs[slideType].current) {
        customSlideInputRefs[slideType].current.value = '';
      }
    }
  };

  // Toggle a template's text theme (dark text vs light text). Quick
  // override for sellers whose template's background color clashes
  // with the default. Persists via PATCH /api/slide-templates/:id
  // with just { textTheme }.
  const toggleSlideTextTheme = async (slideType, nextTheme) => {
    const tpl = customSlideTemplates[slideType];
    if (!tpl) return;
    const prev = tpl.textTheme || 'dark';
    if (prev === nextTheme) return;
    // Optimistic update so the UI feels instant.
    setCustomSlideTemplates((s) => ({
      ...s, [slideType]: { ...tpl, textTheme: nextTheme },
    }));
    try {
      const res = await authFetch(`${API_BASE}/api/slide-templates/${tpl.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ textTheme: nextTheme }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save text theme');
      setCustomSlideTemplates((s) => ({ ...s, [slideType]: data.template }));
    } catch (err) {
      // Revert on failure
      setCustomSlideTemplates((s) => ({
        ...s, [slideType]: { ...tpl, textTheme: prev },
      }));
      setCustomSlideError(err.message);
    }
  };

  const deleteCustomSlide = async (slideType) => {
    const tpl = customSlideTemplates[slideType];
    if (!tpl) return;
    if (!window.confirm(`Remove the custom ${slideType} slide background? Future trailers will use the default again.`)) return;
    setCustomSlideUploading(slideType);
    try {
      const res = await authFetch(`${API_BASE}/api/slide-templates/${tpl.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || 'Delete failed');
      }
      setCustomSlideTemplates((prev) => {
        const next = { ...prev };
        delete next[slideType];
        return next;
      });
    } catch (err) {
      setCustomSlideError(err.message);
    } finally {
      setCustomSlideUploading(null);
    }
  };

  // Lazy-load custom templates when the Brand tab activates. Cheap
  // GET, only fires once per session unless customSlideLoaded resets.
  React.useEffect(() => {
    if (phase === 'settings' && settingsPhaseTab === 'brand' && !customSlideLoaded) {
      fetchCustomSlideTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, settingsPhaseTab, customSlideLoaded]);

  // ─── Zone editor (Phase 2) ─────────────────────────────────────────

  // Per-slide-type known zone ids. The renderer maps these to actual
  // text content (see backend/utils/generateTransitionSlides.js
  // zoneContent maps). Anything not in this list still saves but
  // won't render in the trailer.
  const ZONE_PRESETS = {
    intro:      ['eyebrow', 'title', 'subtitle', 'themes', 'footer'],
    agenda:     ['eyebrow', 'title', 'items'],
    transition: ['number', 'title', 'subtitle', 'timestamp'],
    outro:      ['title', 'subtitle', 'name', 'email'],
  };

  // Type scale for new zones, in 1920x1080 slide coordinates.
  //
  // Every new zone used to start at 48px regardless of role, which is
  // why hand-built templates came out looking flat: a title rendered at
  // the same size as its footer, and 48px on a 1920px-wide canvas reads
  // small — sellers correctly perceived it as "not bold/thick enough"
  // even though the weight was right (Inter 700 measures ~48% more ink
  // than Inter 400, so bold was always applying). These match the
  // server-side DEFAULT_ZONES_BY_SLIDE_TYPE in satoriSlideRenderer.js,
  // so a hand-placed zone now starts at the same size the AI-generated
  // slides use for the same role.
  const ZONE_TYPE_SCALE = {
    title:     { fontSize: 84, fontWeight: 700, fontFamily: 'Space Grotesk' },
    name:      { fontSize: 40, fontWeight: 700, fontFamily: 'Space Grotesk' },
    number:    { fontSize: 140, fontWeight: 700, fontFamily: 'Space Grotesk' },
    eyebrow:   { fontSize: 28, fontWeight: 700, fontFamily: 'Inter' },
    subtitle:  { fontSize: 32, fontWeight: 500, fontFamily: 'Inter' },
    themes:    { fontSize: 28, fontWeight: 600, fontFamily: 'Inter' },
    items:     { fontSize: 36, fontWeight: 500, fontFamily: 'Inter' },
    timestamp: { fontSize: 24, fontWeight: 600, fontFamily: 'Inter' },
    email:     { fontSize: 28, fontWeight: 500, fontFamily: 'Inter' },
    footer:    { fontSize: 22, fontWeight: 600, fontFamily: 'Inter' },
  };

  // Sensible default rectangle when adding a new zone. Centered,
  // 30% width, 12% height. Seller drags from there. Type styling comes
  // from the zone's ROLE (see ZONE_TYPE_SCALE) rather than one flat size.
  const defaultZoneRect = (slideType, zoneId) => {
    const scale = ZONE_TYPE_SCALE[zoneId] || { fontSize: 48, fontWeight: 700, fontFamily: 'Inter' };
    return {
      x: Math.floor(1920 * 0.2),
      y: Math.floor(1080 * 0.45),
      width: Math.floor(1920 * 0.6),
      height: Math.floor(1080 * 0.1),
      fontColor: '#ffffff',
      alignment: 'center',
      ...scale,
    };
  };

  // Sample preview text per zone id — used by the live-preview overlay
  // in the editor so the seller sees what each zone will contain.
  const ZONE_SAMPLE_TEXT = {
    // Mixed case on purpose — the pipeline sends 'Demo Highlights'
    // (generateTransitionSlides introZoneContent). The old ALL-CAPS
    // sample read visually heavier than the real render at the same
    // weight, so the editor oversold the boldness of the final video.
    eyebrow:   'Demo Highlights',
    title:     'Prepared for Acme Corp',
    subtitle:  'Three key themes',
    themes:    'Pricing · Onboarding · ROI',
    footer:    'Your Company',
    items:     '1. Pricing  ·  2. Onboarding  ·  3. ROI',
    number:    '01',
    timestamp: '2:30',
    name:      'Daniel Fellows',
    email:     'daniel@trailercast.io',
  };

  const openZoneEditor = (template) => {
    if (!template) return;
    setZoneEditorTemplate(template);
    // Hydrate from saved zones, else seed with one default zone using
    // the first known preset id for the slide type.
    const existing = Array.isArray(template.textZones) ? template.textZones : [];
    if (existing.length > 0) {
      setZoneEditorZones(existing.map((z, i) => ({ ...z, id: z.id || `zone_${i}` })));
      setZoneEditorSelectedId(existing[0].id || 'zone_0');
    } else {
      const presets = ZONE_PRESETS[template.slideType] || [];
      const initialId = presets[0] || 'title';
      setZoneEditorZones([{ id: initialId, ...defaultZoneRect(template.slideType, initialId) }]);
      setZoneEditorSelectedId(initialId);
    }
    setZoneEditorError(null);
    setZoneEditorOpen(true);
  };

  const closeZoneEditor = () => {
    if (zoneEditorSaving) return;
    setZoneEditorOpen(false);
    setZoneDrag(null);
  };

  const addZone = (zoneId) => {
    if (!zoneEditorTemplate) return;
    if (zoneEditorZones.some((z) => z.id === zoneId)) {
      setZoneEditorError(`Zone "${zoneId}" already exists. Pick a different one or remove the existing.`);
      return;
    }
    setZoneEditorZones((prev) => [...prev, { id: zoneId, ...defaultZoneRect(zoneEditorTemplate.slideType, zoneId) }]);
    setZoneEditorSelectedId(zoneId);
    setZoneEditorError(null);
  };

  const removeZone = (zoneId) => {
    setZoneEditorZones((prev) => prev.filter((z) => z.id !== zoneId));
    if (zoneEditorSelectedId === zoneId) setZoneEditorSelectedId(null);
  };

  const updateZone = (zoneId, patch) => {
    setZoneEditorZones((prev) => prev.map((z) => (z.id === zoneId ? { ...z, ...patch } : z)));
  };

  // Convert mouse event coords to slide coords (1920×1080 reference).
  const eventToSlideCoords = (e) => {
    const img = zoneEditorImageRef.current;
    if (!img) return null;
    const rect = img.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 1920;
    const y = ((e.clientY - rect.top) / rect.height) * 1080;
    return { x, y };
  };

  const startZoneDrag = (zoneId, mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const zone = zoneEditorZones.find((z) => z.id === zoneId);
    if (!zone) return;
    const coords = eventToSlideCoords(e);
    if (!coords) return;
    setZoneEditorSelectedId(zoneId);
    setZoneDrag({
      zoneId,
      mode,
      startX: coords.x,
      startY: coords.y,
      startZone: { ...zone },
    });
  };

  React.useEffect(() => {
    if (!zoneDrag) return undefined;
    const onMove = (e) => {
      const coords = eventToSlideCoords(e);
      if (!coords) return;
      const dx = coords.x - zoneDrag.startX;
      const dy = coords.y - zoneDrag.startY;
      if (zoneDrag.mode === 'move') {
        const newX = Math.max(0, Math.min(1920 - zoneDrag.startZone.width, zoneDrag.startZone.x + dx));
        const newY = Math.max(0, Math.min(1080 - zoneDrag.startZone.height, zoneDrag.startZone.y + dy));
        updateZone(zoneDrag.zoneId, { x: Math.round(newX), y: Math.round(newY) });
      } else if (zoneDrag.mode === 'resize') {
        const newW = Math.max(80, Math.min(1920 - zoneDrag.startZone.x, zoneDrag.startZone.width + dx));
        const newH = Math.max(30, Math.min(1080 - zoneDrag.startZone.y, zoneDrag.startZone.height + dy));
        updateZone(zoneDrag.zoneId, { width: Math.round(newW), height: Math.round(newH) });
      }
    };
    const onUp = () => setZoneDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneDrag]);

  const saveZones = async () => {
    if (!zoneEditorTemplate) return;
    setZoneEditorSaving(true);
    setZoneEditorError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/slide-templates/${zoneEditorTemplate.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ textZones: zoneEditorZones }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      // Update the templates map with the new zones
      setCustomSlideTemplates((prev) => ({
        ...prev,
        [zoneEditorTemplate.slideType]: data.template,
      }));
      setZoneEditorOpen(false);
      showToast('Slide template saved.');
    } catch (err) {
      setZoneEditorError(err.message);
    } finally {
      setZoneEditorSaving(false);
    }
  };

  // Reset zones to "no custom positions" — sends empty array to the
  // backend, renderer falls back to default tree for this slide type.
  const clearAllZones = async () => {
    if (!zoneEditorTemplate) return;
    if (!window.confirm('Clear all zones? Trailers will use the default text positioning over your background image.')) return;
    setZoneEditorZones([]);
    setZoneEditorSelectedId(null);
  };

  // Authed image loader — fetches the slide-template image via authFetch
  // (sends Authorization header), converts to a blob, and returns an
  // object URL safe to drop in <img src>. Browsers don't send Bearer
  // tokens on direct <img src> requests, so naive img tags 401 against
  // the proxy endpoint. This sub-component handles that for us.
  const AuthedSlideImage = ({ template }) => {
    const [src, setSrc] = React.useState(null);
    const [errorMsg, setErrorMsg] = React.useState(null);
    React.useEffect(() => {
      if (!template) return undefined;
      // Reset state on template change so the previous image's
      // "Loading…" doesn't carry over visually if the new fetch
      // hasn't started yet.
      setSrc(null);
      setErrorMsg(null);
      if (!template.imageUrl) {
        setErrorMsg('No image associated with this template.');
        return undefined;
      }
      let cancelled = false;
      let createdUrl = null;
      (async () => {
        try {
          const res = await authFetch(`${API_BASE}${template.imageUrl}`);
          if (cancelled) return;
          if (!res.ok) {
            // Distinguish common failure modes so the UI can guide the
            // user rather than spin forever. 503 = R2 not configured
            // on the backend; 404 = image_key gone (template row in DB
            // but R2 object missing — usually because R2 was reset);
            // anything else = backend error.
            const detail =
              res.status === 503
                ? 'Image storage not configured on the server.'
                : res.status === 404
                ? 'Image not found in storage — try re-uploading.'
                : res.status === 401
                ? 'Authentication expired — refresh the page.'
                : `Failed to load (HTTP ${res.status}).`;
            console.warn(
              `[AuthedSlideImage] ${template.slideType || 'unknown'}: ${detail}`
            );
            setErrorMsg(detail);
            return;
          }
          const blob = await res.blob();
          createdUrl = URL.createObjectURL(blob);
          if (!cancelled) setSrc(createdUrl);
        } catch (err) {
          console.warn(
            `[AuthedSlideImage] ${template.slideType || 'unknown'}: network error — ${err.message}`
          );
          if (!cancelled) setErrorMsg('Network error — check your connection.');
        }
      })();
      return () => {
        cancelled = true;
        if (createdUrl) URL.revokeObjectURL(createdUrl);
      };
      // Re-fetch when the underlying template changes (replace upload
      // changes updatedAt; we key on that). imageUrl is part of the
      // dep list so a backend that ever swaps the URL pattern will
      // also re-trigger.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template && template.id, template && template.updatedAt, template && template.imageUrl]);

    if (errorMsg) {
      return (
        <div
          className="custom-slide-tile-empty"
          style={{ color: '#dc2626', textAlign: 'center', padding: '12px' }}
          title={errorMsg}
        >
          <span style={{ fontWeight: 600 }}>Preview unavailable</span>
          <small style={{ display: 'block', fontSize: '10px', marginTop: 4, opacity: 0.85, lineHeight: 1.4 }}>
            {errorMsg}
          </small>
        </div>
      );
    }
    if (!src) {
      return (
        <div className="custom-slide-tile-empty">
          <span>Loading…</span>
        </div>
      );
    }
    return <img src={src} alt={`${template.slideType} background preview`} />;
  };

  // Same pattern for the zone editor's larger background image.
  // Separated from AuthedSlideImage because the editor needs a ref +
  // a specific className for the absolute-positioning of zone overlays.
  const ZoneEditorBg = ({ template, innerRef }) => {
    const [src, setSrc] = React.useState(null);
    React.useEffect(() => {
      if (!template) return undefined;
      let cancelled = false;
      let createdUrl = null;
      (async () => {
        try {
          const res = await authFetch(`${API_BASE}${template.imageUrl}`);
          if (!res.ok) return;
          const blob = await res.blob();
          createdUrl = URL.createObjectURL(blob);
          if (!cancelled) setSrc(createdUrl);
        } catch (_) { /* fail silently — background just won't render */ }
      })();
      return () => {
        cancelled = true;
        if (createdUrl) URL.revokeObjectURL(createdUrl);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template && template.id, template && template.updatedAt]);
    if (!src) {
      return (
        <div ref={innerRef} className="zone-editor-bg" style={{ background: '#000' }} />
      );
    }
    return (
      <img
        ref={innerRef}
        src={src}
        alt="Slide background"
        className="zone-editor-bg"
        draggable={false}
      />
    );
  };

  const renderCustomSlideBackgroundsSection = () => {
    const isCompanyAdmin = !!(authUser?.company?.isAdmin);

    // Custom slide backgrounds used to be gated on Pro+, but the
    // 2026-05-15 pricing migration collapsed everything into a single
    // TrailerCast tier where subscribers get every feature. Only the
    // admin-vs-member check remains — admins configure brand assets
    // for the whole team; members just see the read-only "ask an
    // admin" message.
    if (!isCompanyAdmin) {
      return (
        <div className="custom-slide-empty">
          <p className="profile-muted">Ask an admin to upload custom slide backgrounds for your team.</p>
        </div>
      );
    }

    return (
      <div className="custom-slide-grid">
        {SLIDE_TYPES_ORDER.map((slideType) => {
          const tpl = customSlideTemplates[slideType];
          const uploading = customSlideUploading === slideType;
          const inputRef = customSlideInputRefs[slideType];
          return (
            <div key={slideType} className="custom-slide-tile">
              <div className="custom-slide-tile-head">
                <strong>{SLIDE_TYPE_LABELS[slideType]}</strong>
                <span className="custom-slide-tile-desc">{SLIDE_TYPE_DESCS[slideType]}</span>
              </div>
              <div className="custom-slide-tile-preview">
                {tpl ? (
                  <AuthedSlideImage template={tpl} />
                ) : (
                  <div className="custom-slide-tile-empty">
                    <span>Default</span>
                  </div>
                )}
              </div>
              {tpl && !uploading && (
                <div className="custom-slide-theme-toggle" role="radiogroup" aria-label="Text color">
                  <span className="custom-slide-theme-label">Text:</span>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={(tpl.textTheme || 'dark') === 'dark'}
                    className={`custom-slide-theme-btn ${(tpl.textTheme || 'dark') === 'dark' ? 'is-active' : ''}`}
                    onClick={() => toggleSlideTextTheme(slideType, 'dark')}
                    title="Dark text — best for light/cream backgrounds"
                  >
                    Dark
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={tpl.textTheme === 'light'}
                    className={`custom-slide-theme-btn ${tpl.textTheme === 'light' ? 'is-active' : ''}`}
                    onClick={() => toggleSlideTextTheme(slideType, 'light')}
                    title="Light text — best for dark backgrounds"
                  >
                    Light
                  </button>
                </div>
              )}
              <div className="custom-slide-tile-actions">
                <button
                  type="button"
                  className="custom-slide-btn"
                  onClick={() => inputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading…' : tpl ? 'Replace' : 'Upload'}
                </button>
                {tpl && !uploading && (
                  <>
                    <button
                      type="button"
                      className="custom-slide-btn"
                      onClick={() => openZoneEditor(tpl)}
                      title="Define where AI text should appear on this slide"
                    >
                      {tpl.textZones && tpl.textZones.length > 0
                        ? `Edit zones (${tpl.textZones.length})`
                        : 'Edit zones'}
                    </button>
                    <button
                      type="button"
                      className="custom-slide-btn custom-slide-btn--danger"
                      onClick={() => deleteCustomSlide(slideType)}
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
              <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0];
                  if (f) uploadCustomSlide(slideType, f);
                }}
              />
            </div>
          );
        })}
        {customSlideError && (
          <div className="error-message custom-slide-error">{customSlideError}</div>
        )}
      </div>
    );
  };

  const handleChangePassword = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (!isPasswordStrong(passwordForm.newPassword)) {
      setPasswordError('New password must be at least 10 characters and include upper + lower case letters, a number, and a special character.');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to change password');

      setPasswordSuccess(true);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordError(err.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const formatDemoDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Human-friendly relative time ("3 minutes ago", "2 hours ago"). Used
  // for the calendar-poll heartbeat + potentially other fire-hose UI.
  // Deliberately not using Intl.RelativeTimeFormat — the output is
  // shorter and reads cleaner for dashboards than the polyfill format.
  const formatRelative = (date) => {
    if (!date) return '';
    const ms = Date.now() - date.getTime();
    if (ms < 0) return 'just now'; // clock drift / future date
    const s = Math.floor(ms / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d} day${d === 1 ? '' : 's'} ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  // ═══════════════════════════════════════════════════════════════
  // FILE HELPERS
  // ═══════════════════════════════════════════════════════════════
  const isVideoFile = (file) => {
    const videoTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska'];
    const videoExts = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];
    return videoTypes.includes(file.type) || videoExts.some(ext => file.name.toLowerCase().endsWith(ext));
  };

  const isAudioFile = (file) => {
    return file.type === 'audio/mpeg' || file.name.toLowerCase().endsWith('.mp3');
  };

  const handleFileSelect = (file) => {
    // A finished video is stored byte-for-byte, so audio has no meaning
    // here — the podcast path still lives behind the AI switch.
    if (uploadAsIs && file && !isVideoFile(file)) {
      setError('A finished video must be an MP4, MOV, or WebM file');
      return;
    }
    if (file && (isVideoFile(file) || isAudioFile(file))) {
      setSelectedFile(file);
      setError(null);
    } else {
      setError('Please select an MP4 video or MP3 audio file');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatTime = (seconds) => {
    if (typeof seconds !== 'number' || isNaN(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const getStatusInfo = (s) => {
    const statusMap = {
      queued: { icon: '', text: 'Queued — starting soon', color: '#fbbf24' },
      uploading: { icon: '', text: 'Uploading', color: '#fbbf24' },
      processing: { icon: '', text: 'Starting Pipeline', color: '#fbbf24' },
      transcribing: { icon: '', text: 'Transcribing Audio', color: '#60a5fa' },
      detecting: { icon: '', text: 'Finding Best Moments', color: '#a78bfa' },
      extracting: { icon: '', text: 'Extracting Clips', color: '#f472b6' },
      narrating: { icon: '', text: 'Generating Narration', color: '#fb923c' },
      voicing: { icon: '', text: 'Synthesizing Voice', color: '#4ade80' },
      composing: { icon: '', text: 'Composing Trailer', color: '#22d3ee' },
      completed: { icon: '', text: 'Trailer Ready!', color: '#10b981' },
      failed: { icon: '', text: 'Processing Failed', color: '#ef4444' }
    };
    return statusMap[s] || { icon: '', text: 'Processing…', color: '#9ca3af' };
  };

  // ═══════════════════════════════════════════════════════════════
  // BRAND SETTINGS HANDLERS
  // ═══════════════════════════════════════════════════════════════
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);  // Keep the actual File object for upload
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setBrandConfig(prev => ({ ...prev, logoUrl: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setLogoFile(null);
    setBrandConfig(prev => ({ ...prev, logoUrl: null }));
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  // ═══════════════════════════════════════════════════════════════
  // UPLOAD & PROCESSING
  // ═══════════════════════════════════════════════════════════════
  const finishedUploadEnabled = !!authUser?.company?.featureFlags?.finished_video_upload;

  // Store the selected file AS-IS as a completed trailer — no pipeline.
  // XHR rather than fetch because a 1–2 GB export deserves a real
  // progress bar, and fetch has no upload-progress events.
  const handleFinishedUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }
    if (!isVideoFile(selectedFile)) {
      setError('A finished video must be an MP4, MOV, or WebM file');
      return;
    }
    setLoading(true);
    setError(null);
    setProgress(0);
    setStatus('uploading');
    setPhase('analyzing');
    setUploadMinimized(false);

    const scope = uploadAsIsScope === 'deal' ? 'deal' : 'reusable';
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('contentScope', scope);
    if (uploadTitle && uploadTitle.trim()) formData.append('title', uploadTitle.trim());
    if (prospectName && prospectName.trim()) formData.append('prospectName', prospectName.trim());

    try {
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        finishedUploadXhrRef.current = xhr;
        xhr.open('POST', `${API_BASE}/api/upload/finished`);
        if (authToken) xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
        xhr.upload.onprogress = (e) => {
          // Hold at 99 until the server answers: the bytes are up but the
          // file is not stored yet, and 100% next to a spinner reads as
          // "stuck".
          if (e.lengthComputable) setProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
        };
        xhr.onload = () => {
          let data = {};
          try { data = JSON.parse(xhr.responseText || '{}'); } catch (_) { /* non-JSON error body */ }
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
        };
        xhr.onerror = () => reject(new Error('The upload was interrupted. Check your connection and try again.'));
        xhr.onabort = () => reject(Object.assign(new Error('Upload cancelled'), { name: 'AbortError' }));
        xhr.send(formData);
      });
      finishedUploadXhrRef.current = null;

      if (!result.ok) {
        if (result.status === 401 && authToken) {
          handleLogout();
          setAuthError('Your session has expired. Please sign in again.');
          return;
        }
        // Card required (trial over, no plan) → open the contact space.
        if (result.status === 402 && result.data.code === 'card_required') {
          openPlanContact();
          throw new Error(PLAN_REQUIRED_MESSAGE);
        }
        throw new Error(result.data.message || result.data.error || 'Upload failed');
      }

      setProgress(100);
      // Land on the tab the video was filed under. The Trailers loader
      // effect keys on phase + tab, so this is enough to show the new
      // card without an explicit refetch.
      setSelectedFile(null);
      setUploadTitle('');
      setProspectName('');
      setLoading(false);
      setStatus(null);
      setProgress(0);
      setLibraryContentScope(scope);
      setPhase('library');
      showToast(`Video added to Trailers (${scope === 'deal' ? 'Deal' : 'Reusable'}) — ready to share.`);
    } catch (err) {
      finishedUploadXhrRef.current = null;
      if (err.name === 'AbortError') return;
      console.error('Finished upload error:', err);
      setError(err.message);
      setLoading(false);
      setStatus(null);
      setPhase('upload');
    }
  };

  const handleUpload = async () => {
    if (uploadAsIs) return handleFinishedUpload();
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }
    // Kick off a notification permission prompt on this user gesture so we can
    // alert them when clips are ready if they've switched tabs. Fire-and-forget.
    ensureNotificationPermission();
    setLoading(true);
    setError(null);
    setProgress(0);
    setPhase('analyzing');
    // Start a fresh upload un-minimized — seller just clicked Upload,
    // their attention is on this view, so the full hero animation is
    // what they expect. They can minimize after.
    setUploadMinimized(false);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const mode = isVideoFile(selectedFile) ? 'demo' : 'podcast';
      formData.append('mode', mode);

      // Brand config is no longer sent per-upload — the backend fetches it
      // from the companies row. Configure once in Settings → Brand.

      // Prospect name for the intro slide ("Prepared for X").
      if (prospectName && prospectName.trim()) {
        formData.append('prospectName', prospectName.trim());
      }

      // Voice picker (Starter+ — backend falls back to default for Free tier)
      if (selectedVoiceId) {
        formData.append('voiceId', selectedVoiceId);
      }

      // Narration language (Pro+ for anything other than English)
      if (selectedLanguage && selectedLanguage !== 'en') {
        formData.append('language', selectedLanguage);
      }

      abortControllerRef.current = new AbortController();
      const response = await authFetch(`${API_BASE}/api/upload`, {
        method: 'POST',
        body: formData,
        signal: abortControllerRef.current.signal,
      });
      const data = await response.json();
      if (!response.ok) {
        // Backend returns 402 + code='card_required' when the company
        // has no Stripe subscription on file. Open the contact space so
        // the user can get set up, rather than show a generic upgrade
        // toast.
        if (response.status === 402 && data.code === 'card_required') {
          openPlanContact();
          throw new Error(PLAN_REQUIRED_MESSAGE);
        }
        throw new Error(data.error || 'Upload failed');
      }

      setJobId(data.jobId);
      setStatus('processing');
      pollForMoments(data.jobId);
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Upload cancelled by user');
        return;
      }
      console.error('Upload error:', err);
      setError(err.message);
      setLoading(false);
      setPhase('upload');
    }
  };

  const cancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (finishedUploadXhrRef.current) {
      try { finishedUploadXhrRef.current.abort(); } catch (_) { /* already done */ }
      finishedUploadXhrRef.current = null;
    }
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    // (A best-effort POST /api/cancel used to fire here — removed
    // 2026-07-26; the endpoint never existed on the backend.)
    setLoading(false);
    setProgress(0);
    setStatus(null);
    setJobId(null);
    setPhase('dashboard');
    setError(null);
  };

  const pollForMoments = (id) => {
    // Consecutive-failure counter + wall-clock start so we can surface
    // a failure in the UI when the backend becomes unreachable or the
    // job sits in 'processing' way past the plausible ceiling. Without
    // these two bailouts the user would see the analyzing spinner
    // forever, with no clue what went wrong.
    let consecutiveErrors = 0;
    const pollStartTs = Date.now();
    // Stall detection: trigger when status hasn't CHANGED for X
    // minutes (real stall) rather than when X minutes have ELAPSED
    // (which false-positives long-but-legitimate runs).
    //
    // We also keep an absolute ceiling as a final safety net so a
    // backend that holds the same status forever (e.g., a wedged
    // worker) eventually surfaces. Given the marketing promise of
    // "unlimited demo length" + 3-hour source videos can take
    // 45-60 min to fully process, the absolute ceiling is generous.
    const STALL_NO_CHANGE_MS = 15 * 60 * 1000;   // 15 min without a status change → surfaced as stall
    const STALL_ABSOLUTE_MS = 3 * 60 * 60 * 1000; // 3 hr absolute ceiling — backstop for truly hung pipelines
    let lastStatus = null;
    let lastStatusChangeTs = Date.now();

    pollRef.current = setInterval(async () => {
      try {
        // authFetch (not raw fetch): /api/status now requires auth +
        // ownership (H-1 fix) so the poll must carry the bearer token.
        const response = await authFetch(`${API_BASE}/api/status/${id}`);
        if (!response.ok) {
          // 404 after a job was created = the backend restarted or
          // garbage-collected the in-memory job record. Treat as a
          // failure — there's no way to recover the pipeline.
          if (response.status === 404) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setError('We lost track of this demo (the server restarted mid-pipeline). Try uploading again.');
            setLoading(false);
            setPhase('failed');
            return;
          }
          throw new Error(`status ${response.status}`);
        }
        const data = await response.json();
        consecutiveErrors = 0;

        setStatus(data.status);
        setProgress(data.progress || 0);

        // Track when status last changed so the stall guard below
        // measures inactivity (real stall) instead of total elapsed
        // time (false-positive on long-but-legitimate runs).
        if (data.status !== lastStatus) {
          lastStatus = data.status;
          lastStatusChangeTs = Date.now();
        }

        // Pause point — analyze phase finished, time for clip selection.
        // Behavior splits on uploadMinimized:
        //   - !uploadMinimized: auto-advance to the select view as before.
        //   - uploadMinimized: keep the seller on dashboard, leave loading
        //     true + jobId set so the floater stays visible. Update the
        //     status to 'analyzed' so the floater label reads "Clips
        //     ready — click to review." Clicking the floater drops them
        //     into 'select' (handled in the floater's onClick).
        if (data.status === 'analyzed' && data.moments && data.moments.length > 0 && phase !== 'select') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setMoments(data.moments);
          setSelectedMoments(data.moments.map((_, i) => i));
          // Always clear loading once moments are loaded — even when
          // minimized. The floater takes the user to 'select' on click,
          // and if loading stayed true the Generate button on that
          // screen would be disabled. (Bug observed 2026-04-26.)
          setLoading(false);
          if (uploadMinimized) {
            // Stay minimized; the floater takes the user to 'select' on click.
            // Still notify them that clips are ready.
            notifyClipsReady(data.moments.length);
            return;
          }
          setPhase('select');
          // Alert the user (toast + browser notification if tab is hidden)
          notifyClipsReady(data.moments.length);
          return;
        }

        // Terminal states
        if (data.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setTrailerPath(data.trailerPath);
          if (data.moments) setMoments(data.moments);
          if (data.allMoments) setAllMoments(data.allMoments);
          setLoading(false);
          setPhase('completed');
          return;
        }

        if (data.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          // Surface the backend's error message when present; fall
          // back to a generic but actionable line. The old default
          // ("Processing failed") gave the user nothing to do.
          setError(
            data.error
              || 'Something went wrong processing this demo. Please try uploading again — if it keeps failing, reach out on the feedback form.'
          );
          setLoading(false);
          setPhase('failed');
          return;
        }

        // Stall guard. Two triggers:
        //   1. Status hasn't changed in STALL_NO_CHANGE_MS (real stall —
        //      pipeline is wedged on a single step). Used to be a flat
        //      "30 min total elapsed" check which false-positived long
        //      legitimate runs (a 3-hour source video can take 60 min
        //      to process; killing at 30 min stranded the user even
        //      though the backend was making real progress).
        //   2. Absolute ceiling at STALL_ABSOLUTE_MS — backstop in case
        //      a worker holds the same status forever.
        const now = Date.now();
        if (now - lastStatusChangeTs > STALL_NO_CHANGE_MS || now - pollStartTs > STALL_ABSOLUTE_MS) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setError('Processing is taking unusually long. The pipeline may have stalled. Try uploading again.');
          setLoading(false);
          setPhase('failed');
          return;
        }
      } catch (err) {
        console.error('Status poll error:', err);
        consecutiveErrors += 1;
        // After ~30s of consecutive failed polls (at 2500ms cadence =
        // 12 tries), treat the connection as lost and fail out — better
        // than leaving the user on a frozen spinner.
        if (consecutiveErrors >= 12) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setError('Lost connection to the server. Refresh the page — if your demo processed successfully, it\'ll be waiting in your library.');
          setLoading(false);
          setPhase('failed');
        }
      }
    }, 2500);
  };

  // ═══════════════════════════════════════════════════════════════
  // CLIP SELECTION
  // ═══════════════════════════════════════════════════════════════

  // Every tier gets clip customization. canCustomizeClips is retained
  // as a constant (hardcoded true) so any remaining references in the
  // JSX below don't break while the locked-UI branches get pruned
  // organically.
  const userTier = (authUser && authUser.tier) || 'starter';
  const canCustomizeClips = true;

  const toggleMoment = (index) => {
    setSelectedMoments(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index].sort((a, b) => a - b)
    );
  };

  const selectAll = () => {
    setSelectedMoments(moments.map((_, i) => i));
  };
  const deselectAll = () => {
    setSelectedMoments([]);
  };

  const handleGenerate = async () => {
    if (selectedMoments.length === 0) {
      setError('Please select at least one clip');
      return;
    }
    setLoading(true);
    setError(null);
    setPhase('generating');

    try {
      const response = await authFetch(`${API_BASE}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobId,
          selectedIndices: selectedMoments,
          brandConfig: brandConfig.companyName || brandConfig.logoUrl ? brandConfig : undefined
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Generation failed');

      if (data.trailerPath || data.videoPath) {
        setTrailerPath(data.trailerPath || data.videoPath);
        setLoading(false);
        setPhase('completed');
      } else {
        pollForCompletion(jobId);
      }
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.message);
      setLoading(false);
      setPhase('select');
    }
  };

  const pollForCompletion = (id) => {
    // Mirrors pollForMoments safety rails — see comments there.
    // Stall logic: trigger on no-status-change-for-X-min OR an absolute
    // ceiling. The old elapsed-time check killed long-but-legitimate
    // runs (3-hour videos take 60 min on Railway).
    let consecutiveErrors = 0;
    const pollStartTs = Date.now();
    const STALL_NO_CHANGE_MS = 15 * 60 * 1000;
    const STALL_ABSOLUTE_MS = 3 * 60 * 60 * 1000;
    let lastStatus = null;
    let lastStatusChangeTs = Date.now();

    pollRef.current = setInterval(async () => {
      try {
        // authFetch (not raw fetch): /api/status now requires auth (H-1).
        const response = await authFetch(`${API_BASE}/api/status/${id}`);
        if (!response.ok) {
          if (response.status === 404) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setError('We lost track of this demo (the server restarted mid-pipeline). Try uploading again.');
            setLoading(false);
            setPhase('failed');
            return;
          }
          throw new Error(`status ${response.status}`);
        }
        const data = await response.json();
        consecutiveErrors = 0;

        setStatus(data.status);
        setProgress(data.progress || 0);

        if (data.status !== lastStatus) {
          lastStatus = data.status;
          lastStatusChangeTs = Date.now();
        }

        if (data.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setTrailerPath(data.trailerPath);
          setLoading(false);
          setPhase('completed');
        } else if (data.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setError(
            data.error
              || 'Something went wrong processing this demo. Please try uploading again — if it keeps failing, reach out on the feedback form.'
          );
          setLoading(false);
          setPhase('failed');
        } else {
          const now = Date.now();
          if (now - lastStatusChangeTs > STALL_NO_CHANGE_MS || now - pollStartTs > STALL_ABSOLUTE_MS) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setError('Processing is taking unusually long. The pipeline may have stalled. Try uploading again.');
            setLoading(false);
            setPhase('failed');
          }
        }
      } catch (err) {
        console.error('Poll error:', err);
        consecutiveErrors += 1;
        if (consecutiveErrors >= 12) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setError('Lost connection to the server. Refresh the page — if your demo processed successfully, it\'ll be waiting in your library.');
          setLoading(false);
          setPhase('failed');
        }
      }
    }, 3000);
  };

  // ═══════════════════════════════════════════════════════════════
  // FOLLOW-UP GENERATOR
  // ═══════════════════════════════════════════════════════════════
  const generateFollowUp = async () => {
    setGeneratingFollowUp(true);
    setFollowUpMessage('');
    
    try {
      const response = await authFetch(`${API_BASE}/api/generate-followup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobId,
          // Filter __pending__ at every layer so AI-generated email
          // copy never reads "Reaching out from __pending__email@..."
          // to a prospect. Falls back to 'our company' if every layer
          // is either empty or a placeholder.
          companyName: displayCompanyName(authUser?.company?.brand?.companyName, '')
            || displayCompanyName(brandConfig.companyName, '')
            || displayCompanyName(authUser?.companyName, '')
            || 'our company',
          moments: moments.length > 0 ? moments : [],
          tone: followUpTone,
          includeTimestamps,
          additionalContext: followUpContext
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate follow-up');
      }
      
      setFollowUpMessage(data.message || data.followUp || 'Generated message will appear here');
      setFollowUpShareUrl(data.shareUrl || '');
    } catch (err) {
      console.error('Follow-up generation error:', err);
      const template = generateLocalFollowUp();
      setFollowUpMessage(template);
      setFollowUpShareUrl('');
    } finally {
      setGeneratingFollowUp(false);
    }
  };

  // Pure helper — substitutes all three template tokens for the Preview-as
  // panel. Matches backend logic in utils/followupEmail.js so what the
  // seller sees in preview matches what recipients will actually receive.
  //   {{first_name}}  → previewName (or "there" fallback)
  //   {{share_link}}  → the actual share URL (so preview renders the URL
  //                     inline the way the plain-text email will)
  //   {{sender_name}} → the seller's full name (pulled from authUser)
  const substituteRecipientTokens = (template, firstName) => {
    if (!template) return '';
    const fn = (firstName && firstName.trim()) || 'there';
    const sn = [authUser?.firstName, authUser?.lastName].filter(Boolean).join(' ')
      || authUser?.fullName
      || authUser?.username
      || 'Your name';
    return template
      .replace(/\{\{\s*first_name\s*\}\}/g, fn)
      .replace(/\{\{\s*sender_name\s*\}\}/g, sn)
      .replace(/\{\{\s*share_link\s*\}\}/g, followUpShareUrl || '');
  };

  // ═══════════════════════════════════════════════════════════════
  // SECURITY & PERMISSIONS (team members + invites)
  // ═══════════════════════════════════════════════════════════════
  const [teamInvites, setTeamInvites] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);

  // Set by the top-bar search when a "Team" result is clicked. The
  // Security & Permissions phase reads this on render and scrolls to
  // the matching row + flashes a highlight. Cleared after the highlight
  // animation runs so re-opening the phase later doesn't re-flash.
  const [highlightedMemberId, setHighlightedMemberId] = useState(null);

  // Scroll-and-highlight a specific team member's row after the user
  // arrives via a top-bar Team search hit. Runs when:
  //  - we're on the Security phase
  //  - highlightedMemberId is set
  //  - the team roster has loaded (length > 0)
  // Uses requestAnimationFrame so the DOM has painted before we measure.
  // Clears highlightedMemberId after 2.5s so re-visiting the page later
  // doesn't keep re-flashing the same row. Lives HERE (not next to the
  // other phase useEffects up at ~line 1900) because referencing
  // highlightedMemberId/teamMembers up there would TDZ-error before
  // those useState declarations on the line above — and TDZ in App.jsx
  // = blank screen for everyone. Same pattern as loadUpcomingMeetings
  // below.
  useEffect(() => {
    if (phase !== 'security' || !highlightedMemberId || teamMembers.length === 0) return undefined;
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-team-member-id="${highlightedMemberId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('is-search-flash');
      }
    });
    const clear = setTimeout(() => {
      const el = document.querySelector(`[data-team-member-id="${highlightedMemberId}"]`);
      if (el) el.classList.remove('is-search-flash');
      setHighlightedMemberId(null);
    }, 2500);
    return () => { cancelAnimationFrame(raf); clearTimeout(clear); };
  }, [phase, highlightedMemberId, teamMembers.length]);

  // ─── Library + Reports shared filters ──────────────────────────
  // Creator (team member) + date-range filters that apply client-side
  // to both the Library grid and the Reports top-demos/activity tables.
  // Kept in module state so switching between the two phases preserves
  // the filters (most users want to see the same slice in both views).
  const [filterCreatorId, setFilterCreatorId] = useState('all'); // 'all' | userId
  const [filterDateFrom, setFilterDateFrom] = useState(''); // yyyy-mm-dd
  const [filterDateTo, setFilterDateTo] = useState('');     // yyyy-mm-dd

  // Snapshot of the Trailers screen's active query, refreshed on every
  // render so imperative "just refresh the list" callers (delete, rename,
  // privacy toggle, the record-tab BroadcastChannel, the left-nav click)
  // reload what the user is actually looking at, instead of resetting
  // them to an unfiltered first page. Declared as a ref up by
  // loadLibraryDemos; assigned here because the filter state below it is
  // what it reads.
  trailersQueryRef.current = {
    scope: libraryScope,
    status: libraryStatusFilter,
    contentScope: libraryContentScope,
    q: libraryQueryDebounced,
    sort: librarySort,
    // The creator filter only renders on team scope; carrying it into a
    // personal-scope request would AND a second user_id clause onto a
    // query already narrowed to the caller, and silently return nothing.
    creatorUserId: libraryScope === 'team' && filterCreatorId !== 'all' ? filterCreatorId : null,
    folderId: libraryFolderId,
    createdFrom: filterDateFrom || null,
    createdTo: filterDateTo || null,
  };

  // ── Trailers screen: persistence + debounce ──
  // Same try/catch shape as the theme writer: localStorage.setItem
  // throws in Safari private browsing and when a quota is exhausted, and
  // an unguarded write inside an effect takes the whole render down.
  useEffect(() => {
    try { localStorage.setItem('trailercst_trailers_tab', libraryContentScope); } catch (_) {}
  }, [libraryContentScope]);
  useEffect(() => {
    try { localStorage.setItem('trailercst_trailers_sort', librarySort); } catch (_) {}
  }, [librarySort]);
  useEffect(() => {
    try { localStorage.setItem('trailercst_trailers_view', libraryViewMode); } catch (_) {}
  }, [libraryViewMode]);

  // Search is a server round-trip now, so it gets a debounce. 250ms
  // matches SalesLibraryPhase, the app's only other server-side search
  // box. Clearing the box skips the delay — the user is asking to see
  // everything again and should not wait for it.
  useEffect(() => {
    const t = setTimeout(
      () => setLibraryQueryDebounced(libraryQuery),
      libraryQuery ? 250 : 0
    );
    return () => clearTimeout(t);
  }, [libraryQuery]);

  // The one loader for the Trailers screen. Every control on the page is
  // in this dep list, because every control is now a query param. Resets
  // to offset 0 on any change — a filter change makes the previously
  // loaded pages meaningless.
  useEffect(() => {
    if (phase !== 'library' || !authToken || bootstrapping) return;
    loadLibraryDemos({ ...trailersQueryRef.current, offset: 0 });
    // loadLibraryDemos is redefined every render; depending on it would
    // refetch on every keystroke anywhere in the app.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    phase, authToken, bootstrapping,
    libraryScope, libraryStatusFilter, libraryContentScope,
    libraryQueryDebounced, librarySort, libraryFolderId,
    filterCreatorId, filterDateFrom, filterDateTo,
  ]);

  // Folders are workspace-level, so they don't change with the tab,
  // the search box, or the scope — load them once on entering Trailers
  // rather than alongside every list refetch.
  useEffect(() => {
    if (phase !== 'library' || !authToken || bootstrapping) return;
    loadTrailerFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, authToken, bootstrapping]);

  // Auto-load the team roster whenever the user enters a phase where
  // the creator filter is exposed on team scope — without this, the
  // dropdown would be empty on first render until the user opened
  // Security & Permissions. Cheap call; safe on personal scope too.
  useEffect(() => {
    const needsRoster = (phase === 'library' && libraryScope === 'team')
      || (reportsActive && reportsScope === 'team')
      // By User exposes the creator dropdown regardless of scope, so it
      // needs the roster too — without this the control silently never
      // rendered until the user happened to visit team scope.
      || (reportsActive && reportsTab === 'by-user');
    if (needsRoster && authToken && teamMembers.length === 0) {
      loadTeamMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, libraryScope, reportsScope, reportsTab, authToken]);
  const [inviteForm, setInviteForm] = useState({ email: '' });
  const [sendingInvite, setSendingInvite] = useState(false);
  const [changingRoleId, setChangingRoleId] = useState(null);
  // Granular-permissions UI (Phase 3, integrated into the existing
  // Security & Permissions page on 2026-05-07): which member's
  // permissions modal is open + whether the Transfer Global Admin
  // modal is open. Both close on save.
  const [editingPermissionsUserId, setEditingPermissionsUserId] = useState(null);
  const [transferGlobalAdminOpen, setTransferGlobalAdminOpen] = useState(false);
  // Admin-triggered password-reset per-row spinner state. Value is the
  // member id whose reset is currently in flight (null when nothing's
  // loading). One-at-a-time matches the role-change pattern — admins
  // aren't batch-resetting so per-row loading is sufficient.
  const [resetingMemberId, setResetingMemberId] = useState(null);

  const isCompanyAdmin = !!(authUser && authUser.company && authUser.company.isAdmin);

  /**
   * Send a password-reset link to a teammate (admin-only). Hits the
   * admin-scoped POST /api/company/members/:id/send-password-reset
   * endpoint, which mints a fresh token + mails it via Resend. Owner
   * resets are rejected server-side (owner must self-serve).
   */
  const sendAdminPasswordReset = async (member) => {
    if (!isCompanyAdmin) return;
    if (member.isOwner) {
      showToast('The owner must reset their own password from the login screen.');
      return;
    }
    // Gentle confirm — this emails the teammate with a live link and
    // an undo isn't meaningful (they already got the email), so a one-
    // click reset feels risky even with the non-destructive copy below.
    const ok = window.confirm(
      `Send a password reset link to ${member.email}? They'll get an email with a 1-hour reset URL.`
    );
    if (!ok) return;
    setResetingMemberId(member.id);
    try {
      const res = await authFetch(
        `${API_BASE}/api/company/members/${member.id}/send-password-reset`,
        { method: 'POST' }
      );
      if (res.ok) {
        showToast(`Reset link sent to ${member.email}.`);
      } else {
        const data = await res.json().catch(() => ({}));
        showToast(data.error || 'Could not send the reset link.');
      }
    } catch (err) {
      console.error('[members] send reset failed:', err);
      showToast('Could not send the reset link.');
    } finally {
      setResetingMemberId(null);
    }
  };

  const loadTeamInvites = async () => {
    if (!authToken || !isCompanyAdmin) return;
    try {
      const res = await authFetch(`${API_BASE}/api/company/invites`);
      if (!res.ok) return;
      const data = await res.json();
      setTeamInvites(data.invites || []);
    } catch (err) {
      console.error('[invites] load failed:', err);
    }
  };

  // ─── INTEGRATIONS (calendar OAuth + Recall bots) ──────────────────
  // Loads on Integrations-tab open. Combines the per-user oauth_status
  // endpoint + the per-user bots list into one render. Also pulls
  // CRM status so the dashboard setup checklist can flag the CRM
  // step done without the user having to open the Integrations tab.
  const loadIntegrations = async () => {
    if (!authToken) return;
    setIntegrationsLoading(true);
    try {
      const [statusRes, botsRes, crmRes, eligRes] = await Promise.all([
        authFetch(`${API_BASE}/api/oauth/status`),
        authFetch(`${API_BASE}/api/integrations/bots`),
        authFetch(`${API_BASE}/api/integrations/crm/status`),
        // The eligibility explainer — a failed fetch hides the banner
        // rather than breaking the panel.
        authFetch(`${API_BASE}/api/integrations/recording-eligibility`).catch(() => null),
      ]);
      if (statusRes.ok) setOauthStatus(await statusRes.json());
      if (botsRes.ok) {
        const data = await botsRes.json();
        setUserBots(data.bots || []);
      }
      if (crmRes.ok) setCrmStatus(await crmRes.json());
      if (eligRes && eligRes.ok) setRecordingEligibility(await eligRes.json());
    } catch (err) {
      console.error('[integrations] load failed:', err);
    } finally {
      setIntegrationsLoading(false);
    }
  };

  // Connect a calendar provider. Backend returns an authorizeUrl we
  // hand to the browser via window.location — the user grants, then
  // Google/Microsoft redirects back to the backend callback which
  // exchanges the code and sends them to the app root with query params.
  const connectProvider = async (provider) => {
    try {
      const res = await authFetch(`${API_BASE}/api/oauth/${provider}/start`, {
        method: 'POST',
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `Failed to start OAuth (${res.status})`);
      }
      const { authorizeUrl } = await res.json();
      if (!authorizeUrl) throw new Error('No authorize URL returned');
      window.location.href = authorizeUrl;
    } catch (err) {
      showToast(`Couldn't connect ${provider}: ${err.message}`);
    }
  };

  // Flip the per-user auto-schedule opt-in. Backend gates on consent +
  // jurisdiction; on 403 with those codes we surface the same CTAs as
  // the manual-schedule path (open the consent modal, or nudge the
  // admin to set a jurisdiction). Enabling/disabling is instant — the
  // poller picks up the new flag on its next tick (<=10 min).
  const toggleAutoSchedule = async (nextEnabled) => {
    try {
      const res = await authFetch(`${API_BASE}/api/oauth/auto-schedule`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !!nextEnabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.code === 'consent_not_acknowledged') {
          setConsentModalOpen(true);
          setConsentError(null);
          return;
        }
        if (data.code === 'jurisdiction_not_set') {
          showToast('An admin needs to pick a consent jurisdiction before auto-scheduling can turn on.');
          return;
        }
        throw new Error(data.error || `Toggle failed (${res.status})`);
      }
      setOauthStatus((prev) => prev ? { ...prev, autoScheduleEnabled: !!data.autoScheduleEnabled } : prev);
      showToast(nextEnabled
        ? 'Auto-schedule on — TrailerCast will join matching meetings on your calendar.'
        : 'Auto-schedule off — you can still schedule bots manually.');
    } catch (err) {
      showToast(`Couldn't update auto-schedule: ${err.message}`);
    }
  };

  const disconnectProvider = async (provider) => {
    if (!window.confirm(`Disconnect ${provider === 'google' ? 'Google' : 'Microsoft'}? Auto-recording will stop for any upcoming meetings.`)) return;
    try {
      const res = await authFetch(`${API_BASE}/api/oauth/${provider}/disconnect`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error(`Disconnect failed (${res.status})`);
      showToast(`${provider === 'google' ? 'Google' : 'Microsoft'} disconnected.`);
      loadIntegrations();
    } catch (err) {
      showToast(`Couldn't disconnect: ${err.message}`);
    }
  };

  const scheduleManualBot = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const url = scheduleForm.meetingUrl.trim();
    if (!url) {
      setScheduleError('Meeting URL is required.');
      return;
    }
    setScheduleSaving(true);
    setScheduleError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/integrations/recall/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          meetingUrl: url,
          meetingTitle: scheduleForm.meetingTitle.trim() || null,
          recordingKind: scheduleForm.recordingKind || 'qualifying_call',
          // Empty joinAt keeps the old behavior — the bot joins the
          // moment you click. With a value, the bot waits: this is
          // the field whose ABSENCE sent a design partner's bot into
          // an empty Teams waiting room five hours early, where it
          // expired before the real meeting started (OHM, 2026-08-27).
          joinAt: scheduleForm.joinAt ? new Date(scheduleForm.joinAt).toISOString() : null,
        }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        // Consent gates — convert the specific 403 codes into
        // actionable UX. jurisdiction_not_set: admin needs to pick
        // one; show the form (already on this tab). consent_not_
        // acknowledged: open the acknowledgment modal.
        if (payload.code === 'consent_not_acknowledged') {
          setConsentModalOpen(true);
          throw new Error('Acknowledge the recording-consent rules to schedule a bot.');
        }
        if (payload.code === 'jurisdiction_not_set') {
          throw new Error('An admin needs to pick a consent jurisdiction above before bots can be scheduled.');
        }
        // Card-required gate (trial over, no plan). Open the contact
        // space rather than show a generic schedule-failed error.
        if (res.status === 402 && payload.code === 'card_required') {
          openPlanContact();
          throw new Error(PLAN_REQUIRED_MESSAGE);
        }
        throw new Error(payload.error || `Schedule failed (${res.status})`);
      }
      const data = await res.json();
      const kindLabel = { demo: 'Demo', qualifying_call: 'Qualifying Call', meeting: 'Meeting' }[scheduleForm.recordingKind] || 'Qualifying Call';
      showToast(`Bot scheduled as "${data.botName}" — will record a ${kindLabel}.`);
      setScheduleForm({ meetingUrl: '', meetingTitle: '', recordingKind: 'qualifying_call' });
      loadIntegrations();
    } catch (err) {
      setScheduleError(err.message);
    } finally {
      setScheduleSaving(false);
    }
  };

  const saveBotDisplayName = async () => {
    setBotNameSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/api/auth/brand`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ botDisplayName: botNameInput }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `Save failed (${res.status})`);
      }
      const data = await res.json();
      if (data.user) setAuthUser(data.user);
      showToast('Bot name saved.');
    } catch (err) {
      showToast(`Couldn't save: ${err.message}`);
    } finally {
      setBotNameSaving(false);
    }
  };

  // Members list — any member can load (they need to see who's on the
  // team). Admin actions (promote/demote) are gated on the backend and
  // conditionally rendered in the UI via isCompanyAdmin.
  const loadTeamMembers = async () => {
    if (!authToken) return;
    try {
      const res = await authFetch(`${API_BASE}/api/company/members`);
      if (!res.ok) return;
      const data = await res.json();
      setTeamMembers(data.members || []);
    } catch (err) {
      console.error('[members] load failed:', err);
    }
  };

  const changeMemberRole = async (userId, newRole) => {
    if (!isCompanyAdmin) return;
    setChangingRoleId(userId);
    try {
      const res = await authFetch(`${API_BASE}/api/company/members/${userId}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `status ${res.status}`);
      // Optimistic update — swap the role locally rather than re-fetching.
      setTeamMembers((prev) => prev.map((m) => (m.id === userId ? { ...m, role: newRole } : m)));
      showToast(`${newRole === 'admin' ? 'Promoted to admin' : 'Demoted to member'}.`);
    } catch (err) {
      console.error('[members] role change failed:', err);
      showToast(`Couldn't change role: ${err.message}`);
    } finally {
      setChangingRoleId(null);
    }
  };

  // Admin soft-deletes a teammate. Two-step confirm + warning copy
  // because the action revokes share-links + scrubs the user's PII
  // immediately. Their owned content (Decision Rooms, demos) survives
  // for the 30-day soft-delete window so admins can transfer it out
  // before hard-delete lands.
  const deleteTeamMember = async (member) => {
    if (!isCompanyAdmin) return;
    const label = [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email;
    if (!window.confirm(
      `Delete ${label}'s account?\n\n` +
      `This will:\n` +
      `  • sign them out and prevent future logins\n` +
      `  • revoke every share link they minted\n` +
      `  • scrub their name + email from audit logs and conversation threads\n\n` +
      `Their Decision Rooms and demos stay attached for 30 days so you can ` +
      `transfer ownership before the account is hard-deleted.\n\n` +
      `This cannot be undone.`
    )) return;
    try {
      const res = await authFetch(`${API_BASE}/api/company/members/${member.id}`, {
        method: 'DELETE',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `status ${res.status}`);
      setTeamMembers((prev) => prev.filter((m) => m.id !== member.id));
      showToast(`${label} deleted.`);
    } catch (err) {
      console.error('[members] delete failed:', err);
      showToast(`Couldn't delete: ${err.message}`);
    }
  };

  const sendTeamInvite = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const email = (inviteForm.email || '').trim();
    if (!email || !email.includes('@')) {
      return showToast('Enter a valid email address.');
    }
    setSendingInvite(true);
    try {
      const res = await authFetch(`${API_BASE}/api/company/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `status ${res.status}`);
      setTeamInvites((prev) => [data.invite, ...prev]);
      setInviteForm({ email: '' });
      showToast(` Invite sent to ${email}. They have 7 days to accept.`);
    } catch (err) {
      console.error('[invites] send failed:', err);
      showToast(`Couldn't send invite: ${err.message}`);
    } finally {
      setSendingInvite(false);
    }
  };

  const revokeTeamInvite = async (inviteId) => {
    try {
      const res = await authFetch(`${API_BASE}/api/company/invites/${inviteId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      setTeamInvites((prev) => prev.filter((inv) => inv.id !== inviteId));
      showToast('Invite revoked.');
    } catch (err) {
      console.error('[invites] revoke failed:', err);
      showToast(`Couldn't revoke: ${err.message}`);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // EMAIL VERIFICATION
  // ═══════════════════════════════════════════════════════════════

  // Resend the verification email. Backend always returns 200 (even if
  // email delivery fails internally) so we surface a generic "sent" toast
  // and trust the user's inbox. Also catches the "already verified" case.
  const [resendingVerification, setResendingVerification] = useState(false);
  const resendVerificationEmail = async () => {
    if (resendingVerification) return;
    setResendingVerification(true);
    try {
      const res = await authFetch(`${API_BASE}/api/auth/send-verification`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (data.alreadyVerified) {
        // Backend caught a state drift — refresh auth to flip the banner off.
        showToast('You\'re already verified. Refreshing status…');
        refreshAuthUser();
        return;
      }
      showToast(` Verification email sent to ${authUser?.email || 'your inbox'}. Check your spam folder if you don't see it.`);
    } catch (err) {
      console.error('[auth] resend verification failed:', err);
      showToast('Couldn\'t send verification email. Try again in a minute.');
    } finally {
      setResendingVerification(false);
    }
  };

  // Pull fresh auth state from the backend — used after a verification
  // mid-session so the banner disappears without requiring a re-login.
  const refreshAuthUser = async () => {
    if (!authToken) return null;
    try {
      const res = await authFetch(`${API_BASE}/api/auth/me`);
      if (!res.ok) return null;
      const data = await res.json();
      setAuthUser(data.user);
      return data.user;
    } catch (_) {
      // Ignore — stale state is fine.
      return null;
    }
  };

  // Auto-refresh authUser on window focus. Catches the very common
  // pattern: user clicks "Manage subscription" → opens Stripe Customer
  // Portal in a new tab → makes a plan change → tabs back to TrailerCast
  // → expects to see the new state immediately. Without this hook, they
  // see stale data until they manually hard-refresh.
  //
  // Also catches: subscription state changing while idle (renewal
  // webhook lands, payment_failed dunning, admin edits the row), then
  // user comes back to the tab and gets fresh state on first focus
  // without lifting a finger.
  React.useEffect(() => {
    if (!authToken) return undefined;
    const onFocus = () => { refreshAuthUser(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [authToken]);

  // Auto-refresh authUser when the Settings → Billing tab activates, and
  // self-heal a "charged but not linked" mismatch. Same staleness defense
  // as the focus hook, scoped to in-app navigation. If the company has a
  // Stripe customer on file but no linked subscription, a webhook was
  // almost certainly missed — POST /api/billing/sync pulls truth from
  // Stripe and backfills the row, then we refresh again so the tab shows
  // the real plan/status and unlocks the Manage-billing button. Only runs
  // on the mismatch, so healthy accounts pay just the cheap /me refresh.
  React.useEffect(() => {
    if (!(phase === 'settings' && settingsPhaseTab === 'billing')) return undefined;
    let cancelled = false;
    (async () => {
      const user = await refreshAuthUser();
      if (cancelled || !user) return;
      const co = user.company || {};
      if (co.hasStripeCustomer && !co.hasSubscription) {
        try {
          const res = await authFetch(`${API_BASE}/api/billing/sync`, { method: 'POST' });
          if (!cancelled && res.ok) await refreshAuthUser();
        } catch (_) {
          // Best-effort self-heal — the Manage-billing button is already
          // unlocked by hasStripeCustomer regardless of this call.
        }
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, settingsPhaseTab]);

  // ═══════════════════════════════════════════════════════════════
  // ATTENDEES (recipient list)
  // ═══════════════════════════════════════════════════════════════

  const loadAttendees = async (demoId) => {
    if (!demoId || !authToken) return;
    try {
      const res = await authFetch(`${API_BASE}/api/demos/${demoId}/attendees`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      setAttendees(data.attendees || []);
    } catch (err) {
      console.error('[attendees] load failed:', err);
      setAttendees([]);
    }
  };

  const addAttendee = async () => {
    const email = (newAttendee.email || '').trim();
    if (!jobId) return showToast('Open a completed trailer first.');
    if (!email || !email.includes('@')) {
      return showToast('Enter a valid email address.');
    }
    try {
      const res = await authFetch(`${API_BASE}/api/demos/${jobId}/attendees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: (newAttendee.firstName || '').trim() || null,
          email,
        }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `status ${res.status}`);
      }
      const data = await res.json();
      // If this email was already on the list the backend returns the
      // existing row (idempotent) — merge in place to avoid duplicates.
      setAttendees((prev) => {
        const withoutDup = prev.filter((a) => a.id !== data.attendee.id);
        return [...withoutDup, data.attendee];
      });
      setNewAttendee({ firstName: '', email: '' });
    } catch (err) {
      console.error('[attendees] add failed:', err);
      showToast(`Couldn't add recipient: ${err.message}`);
    }
  };

  const removeAttendee = async (attendeeId) => {
    if (!jobId || !attendeeId) return;
    try {
      const res = await authFetch(
        `${API_BASE}/api/demos/${jobId}/attendees/${attendeeId}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error(`status ${res.status}`);
      setAttendees((prev) => prev.filter((a) => a.id !== attendeeId));
    } catch (err) {
      console.error('[attendees] remove failed:', err);
      showToast(`Couldn't remove recipient: ${err.message}`);
    }
  };

  // Dispatch the follow-up to every attendee in the current list. Each
  // recipient gets their own personalized {{first_name}} substitution on
  // the server. Returns a per-recipient success/failure breakdown which
  // we surface as chips next to each row.
  const sendFollowUpToAttendees = async () => {
    if (!jobId) return showToast('Open a completed trailer first.');
    if (attendees.length === 0) {
      return showToast('Add at least one recipient before sending.');
    }
    if (!followUpMessage || !followUpMessage.trim()) {
      return showToast('Generate a follow-up message first.');
    }
    setSendingFollowUp(true);
    setLastSendResult(null);
    try {
      const res = await authFetch(`${API_BASE}/api/trailers/send-followup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobId,
          body: followUpMessage,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `status ${res.status}`);
      setLastSendResult({ sent: data.sent || [], failed: data.failed || [] });
      const sentCount = (data.sent || []).length;
      const failedCount = (data.failed || []).length;
      if (failedCount === 0) {
        showToast(`✓ Sent to ${sentCount} recipient${sentCount === 1 ? '' : 's'}.`);
      } else if (sentCount === 0) {
        showToast(`Couldn't send — ${failedCount} failed.`);
      } else {
        showToast(`Sent to ${sentCount}, ${failedCount} failed — see chips below.`);
      }
      // Reload attendees so last_sent_at / last_send_status chips refresh.
      loadAttendees(jobId);
    } catch (err) {
      console.error('[send-followup] failed:', err);
      showToast(`Send failed: ${err.message}`);
    } finally {
      setSendingFollowUp(false);
    }
  };

  const generateLocalFollowUp = () => {
    const company = displayCompanyName(authUser?.company?.brand?.companyName, '')
      || displayCompanyName(brandConfig.companyName, '')
      || displayCompanyName(authUser?.companyName, '')
      || 'our solution';
    const toneStyles = {
      professional: {
        greeting: 'Hello,',
        intro: `Thank you for taking the time to review ${company}. I wanted to follow up with a quick trailer showcasing the key features we discussed.`,
        closing: 'Please let me know if you have any questions or would like to schedule a deeper dive into any of these areas.',
        signoff: 'Best regards,'
      },
      friendly: {
        greeting: 'Hi there!',
        intro: `Thanks so much for your interest in ${company}! I put together a quick trailer of the best moments from our demo.`,
        closing: 'Would love to hear your thoughts! Feel free to reach out anytime.',
        signoff: 'Cheers,'
      },
      enthusiastic: {
        greeting: 'Hi!',
        intro: `I'm excited to share this trailer from our ${company} demo! These clips showcase exactly why our customers love what we've built.`,
        closing: `I'd love to show you even more - when works best for a follow-up call?`,
        signoff: 'Looking forward to connecting!'
      },
      concise: {
        greeting: 'Hi,',
        intro: `Attached: ${company} demo highlights.`,
        closing: 'Questions? Let me know.',
        signoff: 'Thanks,'
      }
    };
    
    const style = toneStyles[followUpTone] || toneStyles.professional;
    
    let momentsList = '';
    if (includeTimestamps && moments.length > 0) {
      momentsList = '\n\nKey moments covered:\n' + moments.slice(0, 5).map((m, i) => 
        `• ${m.feature || m.title || `Highlight ${i + 1}`}${m.startSeconds ? ` (${formatTime(m.startSeconds)})` : ''}`
      ).join('\n');
    }
    
    return `${style.greeting}

${style.intro}
${momentsList}

${style.closing}

${style.signoff}
[Your Name]`;
  };

  const copyFollowUp = () => {
    navigator.clipboard.writeText(followUpMessage);
  };

  // ═══════════════════════════════════════════════════════════════
  // DOWNLOAD & RESET
  // ═══════════════════════════════════════════════════════════════
  const handleDownload = () => {
    if (jobId) {
      window.open(`${API_BASE}/api/download/${jobId}`, '_blank');
    }
  };

  // ─── NOTIFICATIONS (in-app bell) ──────────────────────────────────
  const loadNotifications = async () => {
    if (!authToken) return;
    try {
      const res = await authFetch(`${API_BASE}/api/notifications`);
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnreadNotifications(data.unreadCount || 0);
    } catch (err) {
      console.error('[notifications] load failed:', err);
    }
  };

  const markNotificationRead = async (id) => {
    try {
      await authFetch(`${API_BASE}/api/notifications/${id}/read`, { method: 'POST' });
    } catch (_) { /* non-fatal */ }
  };

  const markAllNotificationsRead = async () => {
    try {
      const res = await authFetch(`${API_BASE}/api/notifications/read-all`, { method: 'POST' });
      if (res.ok) {
        setNotifications((prev) => prev.map((n) => ({ ...n, readAt: n.readAt || new Date().toISOString() })));
        setUnreadNotifications(0);
      }
    } catch (_) { /* non-fatal */ }
  };

  const openNotificationLink = async (notif) => {
    // Optimistic read-mark so the UI updates instantly.
    if (!notif.readAt) {
      setNotifications((prev) => prev.map((n) => n.id === notif.id ? { ...n, readAt: new Date().toISOString() } : n));
      setUnreadNotifications((n) => Math.max(0, n - 1));
      markNotificationRead(notif.id);
    }
    setNotificationsOpen(false);
    // Route to whatever the notification actually names.
    //
    // This used to match /[?&]demo=/ only, which handled exactly one of
    // the four link shapes in the notifications table — and notably did
    // NOT match a leading '#demo=', so @mention notifications navigated
    // nowhere. Room, call, and document links did nothing at all.
    navigateToDeepLink(notif.linkUrl);
  };

  /**
   * Navigate to a notification/email deep link without a page reload,
   * so in-app state and polling stay intact.
   */
  // While unverified, re-check on tab focus: the verification link
  // opens in another tab, and returning here is the moment the stale
  // banner should die on its own (2026-08-27).
  useEffect(() => {
    if (!authUser || authUser.emailVerified !== false) return undefined;
    const onFocus = () => refreshAuthUser();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.emailVerified]);

  // Direct hash edits (typed URLs, bookmarks, automation) never reached
  // the router: phase switches only happened via clicks or the boot
  // parse, so the URL changed while the old page stayed mounted — an
  // automated QA pass hit this three separate times (2026-08-27). One
  // listener, feeding the same navigateToDeepLink used everywhere else.
  // Loop-safe: internal navigation writes the hash via replaceState and
  // dispatches a synthetic hashchange; by the time either arrives here,
  // phase and hash already agree and navigateToDeepLink's own
  // hash-unchanged guard makes the re-entry a no-op.
  useEffect(() => {
    const onHashNav = () => {
      try { navigateToDeepLink(window.location.href); } catch (_) { /* bad hash: stay put */ }
    };
    window.addEventListener('hashchange', onHashNav);
    return () => window.removeEventListener('hashchange', onHashNav);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateToDeepLink = (linkUrl) => {
    const intent = parseDeepLink(linkUrl);
    if (!intent) return;
    // A demo opens through the existing loader rather than a phase
    // switch — it owns fetching the trailer and flipping to 'completed'.
    if (intent.demoId) {
      if (intent.tab) setDemoRightTab(intent.tab);
      const hash = hashForIntent(intent);
      if (hash && typeof window !== 'undefined') {
        window.history.replaceState(null, '', window.location.pathname + window.location.search + hash);
      }
      loadCompletedDemo(intent.demoId);
      return;
    }
    if (!intent.phase) return;
    // Set the hash FIRST: BuyerSpacesPhase and CallsPhase hydrate their
    // selected id from the URL on mount, so the phase switch has to see
    // the destination already in place.
    const hash = hashForIntent(intent);
    if (hash && typeof window !== 'undefined' && window.location.hash !== hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + hash);
      // Phase components listen for hashchange to resync; replaceState
      // does not emit one, so say it out loud.
      // Plain Event, not HashChangeEvent: the listeners only care that it
      // fired, and the HashChangeEvent constructor is not universally
      // supported.
      window.dispatchEvent(new Event('hashchange'));
    }
    setPhase(intent.phase);
  };

  // Poll notifications every 30s while the user is logged in + tab is
  // visible. First load fires as soon as authUser resolves.
  useEffect(() => {
    if (!authUser || !authToken) {
      setNotifications([]);
      setUnreadNotifications(0);
      return undefined;
    }
    loadNotifications();
    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      loadNotifications();
    };
    const interval = setInterval(tick, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, authToken]);

  // Close the notifications panel on outside click / Escape — same
  // pattern as the kebab menu.
  useEffect(() => {
    if (!notificationsOpen) return;
    const onClick = (e) => {
      if (e.target.closest && e.target.closest('.notifications-panel-wrap')) return;
      setNotificationsOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setNotificationsOpen(false); };
    window.addEventListener('click', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [notificationsOpen]);

  // ─── PROSPECT THREADS (seller side) ───────────────────────────────
  const loadDemoThreads = async (demoId) => {
    if (!demoId || !authToken) return;
    setDemoMessagesLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/demos/${demoId}/messages`);
      if (res.ok) {
        const data = await res.json();
        setDemoThreads(data.threads || []);
      } else {
        setDemoThreads([]);
      }
    } catch (err) {
      console.error('[threads] load failed:', err);
      setDemoThreads([]);
    } finally {
      setDemoMessagesLoading(false);
    }
  };

  // Delete an entire conversation thread (seller's replies + the
  // prospect's messages alike). Scoped server-side to threads on
  // demos this user owns — a crafted distributionId returns 0
  // deleted rather than an error. Optimistically removes the row
  // from the inbox, then reloads to stay in sync if anyone else
  // on the team changes state.
  const deleteConversationThread = async (conv) => {
    if (!conv || !conv.distributionId || !conv.prospectEmail) return;
    const label = conv.prospectName || conv.prospectEmail;
    const ok = window.confirm(
      `Delete the entire conversation with ${label}?\n\n`
      + `This removes the thread from your inbox AND from the share page — both you and the prospect will no longer see it. `
      + `This cannot be undone.`
    );
    if (!ok) return;
    try {
      const res = await authFetch(
        `${API_BASE}/api/conversations/${encodeURIComponent(conv.distributionId)}/messages?threadEmail=${encodeURIComponent(conv.prospectEmail)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Delete failed (${res.status})`);
      }
      setConversations((prev) => prev.filter((c) => c.threadKey !== conv.threadKey));
      showToast('Conversation deleted.');
    } catch (err) {
      showToast(`Couldn't delete: ${err.message}`);
    }
  };

  // Load the Conversations inbox — one row per prospect thread across
  // every demo the user owns. Fires on Conversations-phase mount.
  const loadConversations = async () => {
    if (!authToken) return;
    setConversationsLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/conversations`);
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      } else {
        setConversations([]);
      }
    } catch (err) {
      console.error('[conversations] load failed:', err);
      setConversations([]);
    } finally {
      setConversationsLoading(false);
    }
  };

  // Dashboard insights loader. Single round-trip to the new
  // /api/dashboard/insights aggregate. Resilient: errors leave
  // dashboardInsights null and the widget falls back to its empty
  // state rather than crashing the dashboard.
  const loadDashboardInsights = async (userIdScope = dashboardUserFilter) => {
    if (!authToken) return;
    setDashboardInsightsLoading(true);
    setDashboardInsightsError(null);
    try {
      const qs = userIdScope ? `?userId=${encodeURIComponent(userIdScope)}` : '';
      const res = await authFetch(`${API_BASE}/api/dashboard/insights${qs}`);
      if (res.ok) {
        const data = await res.json();
        setDashboardInsights(data);
      } else {
        console.warn('[dashboard/insights] non-OK response:', res.status);
        setDashboardInsights(null);
        setDashboardInsightsError('Couldn’t load pipeline insights.');
      }
    } catch (err) {
      console.error('[dashboard/insights] load failed:', err);
      setDashboardInsights(null);
      // Tier-1 (2026-07-22): a failed load used to be indistinguishable
      // from "no shares yet" — the funnel rendered its empty state over
      // a silent error. Same failed ≠ empty rule as the other loaders.
      setDashboardInsightsError('Couldn’t load pipeline insights.');
    } finally {
      setDashboardInsightsLoading(false);
    }
  };

  // Buyer-activity feed loader (Tier-2). Same failed ≠ empty contract
  // as every other loader.
  const loadDashboardActivity = async () => {
    if (!authToken) return;
    setDashboardActivityError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/dashboard/activity`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      setDashboardActivity(Array.isArray(data.activity) ? data.activity : []);
    } catch (err) {
      console.error('[dashboard/activity] load failed:', err);
      setDashboardActivityError('Couldn’t load buyer activity.');
    }
  };

  // Re-fire the insights aggregate when the dashboard user filter
  // changes. Drives the Pipeline funnel + Needs attention queue. The
  // initial load on dashboard mount already fires via the phase
  // effect; this one only handles the post-mount filter switch so we
  // don't double-fetch on first paint.
  useEffect(() => {
    // !bootstrapping: keep the mount-time run from firing with the
    // not-yet-validated localStorage token (stale token → 401 spam on
    // the login screen). Filter switches only happen post-bootstrap.
    if (phase !== 'dashboard' || !authToken || bootstrapping) return;
    loadDashboardInsights(dashboardUserFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardUserFilter]);

  // Load threads whenever the completed-phase view is mounted for a
  // specific demo. Runs after loadCompletedDemo sets jobId.
  useEffect(() => {
    if (phase === 'completed' && jobId && authToken) {
      loadDemoThreads(jobId);
    } else {
      setDemoThreads([]);
      setOpenThreadKey(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, jobId, authToken]);

  // Poll for the AI deal brief when it's missing on a freshly-loaded
  // completed demo. The backend generates the brief fire-and-forget
  // during the analyze phase, which means on a just-completed demo
  // the brief often lands ~10-30s after the seller hits the completed
  // view. Without this, they'd have to navigate away and back to see
  // it. Stops polling once the brief arrives OR after 5 minutes
  // (whichever comes first) so we don't hammer the backend.
  useEffect(() => {
    if (phase !== 'completed' || !jobId || !authToken) return undefined;
    if (currentDemoDealBrief) return undefined; // already have it
    const startedAt = Date.now();
    const POLL_CEILING_MS = 5 * 60 * 1000;
    const timer = setInterval(async () => {
      if (Date.now() - startedAt > POLL_CEILING_MS) {
        clearInterval(timer);
        return;
      }
      try {
        const res = await authFetch(`${API_BASE}/api/demos/${jobId}`);
        if (!res.ok) return;
        const data = await res.json();
        const d = data.demo || data;
        if (d && d.dealBrief) {
          setCurrentDemoDealBrief(d.dealBrief);
          clearInterval(timer);
        }
      } catch (_) { /* transient; keep polling */ }
    }, 15_000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, jobId, authToken, currentDemoDealBrief]);

  const submitThreadReply = async (threadKey) => {
    const body = threadReplyBody.trim();
    if (!body) {
      setThreadReplyError('Type a reply before sending.');
      return;
    }
    setThreadReplySubmitting(true);
    setThreadReplyError(null);
    try {
      const res = await authFetch(
        `${API_BASE}/api/demos/${jobId}/threads/${encodeURIComponent(threadKey)}/reply`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body }),
        }
      );
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `Reply failed (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      const saved = data && data.message;

      // Optimistic update: append the seller's new message into the
      // thread and flip off awaitingSellerReply so the "Awaiting reply"
      // pill clears instantly. The backend refresh below is purely
      // reconciliation — without this, there's a flicker where the
      // pill lingers until the GET round-trip lands.
      if (saved) {
        setDemoThreads((prev) => prev.map((t) => {
          if (t.threadKey !== threadKey) return t;
          const newMessages = [
            { ...saved }, // newest first (same order backend uses)
            ...t.messages,
          ];
          return {
            ...t,
            messages: newMessages,
            messageCount: newMessages.length,
            lastMessageAt: saved.createdAt,
            sellerHasReplied: true,
            awaitingSellerReply: false,
          };
        }));
      }

      setThreadReplyBody('');
      showToast('Reply sent — they\'ll get an email with a link back to the thread.');
      // Reconcile from the server so timestamps / ordering / any
      // other fields match the authoritative DB state.
      loadDemoThreads(jobId);
    } catch (err) {
      setThreadReplyError(err.message);
    } finally {
      setThreadReplySubmitting(false);
    }
  };

  const toggleThreadOpen = (threadKey) => {
    setThreadReplyError(null);
    setThreadReplyBody('');
    setOpenThreadKey((prev) => prev === threadKey ? null : threadKey);
  };

  // Open the ShareViewer in a new tab with the thread's signed resume
  // params so the seller can preview the exact conversation the
  // prospect sees (including the full transcript + reply box).
  const previewThreadAsProspect = async (threadKey) => {
    try {
      const res = await authFetch(
        `${API_BASE}/api/demos/${jobId}/threads/${encodeURIComponent(threadKey)}/share-url`
      );
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `Preview URL failed (${res.status})`);
      }
      const { url } = await res.json();
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      showToast(`Couldn't build preview URL: ${err.message}`);
    }
  };

  // ─── REBUILD WITH DIFFERENT CLIPS ─────────────────────────────────
  // Reshape a completed trailer without re-uploading. Requires Phase 2A
  // source persistence (backend enforces — 400 if source_key is null).
  //
  // The modal lists the FULL detected-moment pool (allMoments) so clips
  // excluded in an earlier compose can be brought back; legacy demos
  // without all_moments fall back to the current trailer's moments.
  // Indices sent to the backend index into this same pool — the backend
  // resolves it identically (all_moments || moments).
  const rebuildPool = (Array.isArray(allMoments) && allMoments.length > 0) ? allMoments : moments;

  const openRebuildModal = () => {
    if (!Array.isArray(rebuildPool) || rebuildPool.length === 0) {
      showToast("No clips were detected — nothing to rebuild from.");
      return;
    }
    // Default: pre-check the clips that are IN the current trailer
    // (matched by start time — pool entries and composed moments share
    // startSeconds). When the pool has no extras this selects everything,
    // matching the old behavior.
    const inTrailer = new Set((moments || []).map((m) => m.startSeconds));
    const preChecked = rebuildPool
      .map((m, i) => (inTrailer.has(m.startSeconds) ? i : null))
      .filter((i) => i !== null);
    setRebuildSelected(new Set(preChecked.length ? preChecked : rebuildPool.map((_, i) => i)));
    setRebuildMode('replace');
    setRebuildError(null);
    setRebuildModalOpen(true);
  };

  const closeRebuildModal = () => {
    setRebuildModalOpen(false);
    setRebuildError(null);
  };

  const toggleRebuildClip = (index) => {
    setRebuildSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // ─── Custom-trailer modal handlers ─────────────────────────────────
  // Open against any completed demo whose source_key is set. Pulls the
  // demo's metadata into customTrailerParent so the modal can validate
  // clip boundaries against duration_seconds. Also lazy-loads the
  // transcript + AI-detected moments for the V3 transcript-click +
  // suggestions UI; failures here degrade gracefully (the buttons-based
  // UX still works without transcript or suggestions).
  const openCustomTrailer = async (demo) => {
    if (!demo || !demo.id) return;
    setCustomTrailerParent(demo);
    setCustomTrailerClips([]);
    setCustomTrailerInPoint(null);
    setCustomTrailerCurrentMs(0);
    setCustomTrailerError(null);
    setCustomTrailerSelStart(null);
    setCustomTrailerTranscript(null);
    setCustomTrailerSuggestions([]);
    setCustomTrailerSourceDuration(demo.duration_seconds || 0);
    setCustomTrailerOpen(true);
    setCustomTrailerLoadingDetails(true);
    // Mint the signed playback token (best-effort). Source-stream now
    // REQUIRES it for private recordings; harmless for public ones.
    setCustomTrailerToken(null);
    (async () => {
      try {
        const tr = await authFetch(`${API_BASE}/api/demos/${demo.id}/playback-token`);
        if (tr.ok) {
          const td = await tr.json();
          if (td && td.token) setCustomTrailerToken(td.token);
        }
      } catch (_) { /* non-fatal — bare URL still serves public sources */ }
    })();
    try {
      // The demo full DTO carries transcript + moments. We hit
      // /api/demos/:id which returns shapeDemoFull (transcript inclusive).
      const res = await authFetch(`${API_BASE}/api/demos/${demo.id}`);
      if (res.ok) {
        const data = await res.json();
        const full = data.demo || {};
        if (full.transcript && Array.isArray(full.transcript.segments)) {
          setCustomTrailerTranscript(full.transcript);
        }
        if (Array.isArray(full.moments)) {
          // AI moments stored on the parent — these become "suggestions"
          // the seller can one-click into the clip list. Filter out any
          // already on the clip list (none on first open).
          setCustomTrailerSuggestions(full.moments);
        }
      }
    } catch (err) {
      // Non-fatal — buttons-based UX works without these.
      console.warn('[custom-trailer] details load failed:', err.message);
    } finally {
      setCustomTrailerLoadingDetails(false);
    }
  };

  const closeCustomTrailer = () => {
    if (customTrailerSubmitting) return; // don't close mid-submit
    // Guard against discarding in-progress work. The builder closes on an
    // OUTSIDE/backdrop click (and the × button) — so a fast or stray click
    // outside the modal was silently throwing away every clip the seller had
    // picked. Confirm first when there's work to lose; an empty builder still
    // closes freely. (Submit closes via a separate path, so a successful build
    // never hits this prompt.)
    if (customTrailerClips.length > 0) {
      const n = customTrailerClips.length;
      const ok = window.confirm(`Discard ${n} clip${n === 1 ? '' : 's'} and close the trailer builder?`);
      if (!ok) return;
    }
    setCustomTrailerOpen(false);
    setCustomTrailerError(null);
  };

  // Mark the current playhead as the in-point for the next clip.
  // Snapshots the player's currentTime in ms; the seller picks an
  // out-point next to capture the clip range.
  const markCustomIn = () => {
    const v = customTrailerVideoRef.current;
    if (!v) return;
    setCustomTrailerInPoint(Math.floor(v.currentTime * 1000));
    setCustomTrailerError(null);
  };

  // Clip-length limits, served by /auth/me from the same constants the
  // backend's validateClips enforces — the two layers cannot drift.
  // Fallbacks mirror the backend defaults for the moment between boot
  // and /me resolving. (2026-08-12: raised from a hardcoded 2 min.)
  const clipMinMs = (authUser?.limits?.customClipMinSeconds || 5) * 1000;
  const clipMaxMs = (authUser?.limits?.customClipMaxSeconds || 300) * 1000;
  const trailerMaxTotalMs = (authUser?.limits?.customTrailerMaxTotalSeconds || 1800) * 1000;
  const fmtClipLimit = (ms) => {
    if (ms < 60000) return `${Math.round(ms / 1000)} seconds`;
    const m = ms / 60000;
    return `${Number.isInteger(m) ? m : m.toFixed(1)} minute${m === 1 ? '' : 's'}`;
  };
  // Would adding a clip of `durationMs` blow the whole-trailer cap?
  // Checked at add time so the seller hears about it on the clip that
  // crosses the line, not as a 400 after submit.
  const wouldExceedTotal = (durationMs) => {
    const current = customTrailerClips.reduce((sum, c) => sum + (c.endMs - c.startMs), 0);
    return current + durationMs > trailerMaxTotalMs;
  };

  // Combine the previously-marked in-point with the current playhead
  // as the out-point and append the resulting clip to the list.
  // Validates the same rules the backend will so the seller gets
  // immediate feedback rather than a 400 after submit.
  const markCustomOutAndAdd = () => {
    const v = customTrailerVideoRef.current;
    if (!v) return;
    if (customTrailerInPoint == null) {
      setCustomTrailerError('Mark an in-point first.');
      return;
    }
    const startMs = customTrailerInPoint;
    const endMs = Math.floor(v.currentTime * 1000);
    if (endMs <= startMs) {
      setCustomTrailerError('Out-point must come after the in-point. Scrub forward and try again.');
      return;
    }
    const durationMs = endMs - startMs;
    if (durationMs < clipMinMs) {
      setCustomTrailerError(`Clip is too short — minimum ${fmtClipLimit(clipMinMs)}.`);
      return;
    }
    if (durationMs > clipMaxMs) {
      setCustomTrailerError(`Clip is too long — maximum ${fmtClipLimit(clipMaxMs)} per clip. Trim it down or split into two clips.`);
      return;
    }
    if (wouldExceedTotal(durationMs)) {
      setCustomTrailerError(`Adding this clip would push the trailer past its ${fmtClipLimit(trailerMaxTotalMs)} total limit. Shorten or remove another clip first.`);
      return;
    }
    // Overlap check against existing clips
    const overlaps = customTrailerClips.some((c) => startMs < c.endMs && endMs > c.startMs);
    if (overlaps) {
      setCustomTrailerError('This range overlaps an existing clip. Pick non-overlapping ranges.');
      return;
    }
    if (customTrailerClips.length >= 12) {
      setCustomTrailerError('Maximum 12 clips per custom trailer.');
      return;
    }
    setCustomTrailerClips((prev) => [...prev, { startMs, endMs, customLabel: '' }]);
    setCustomTrailerInPoint(null);
    setCustomTrailerError(null);
  };

  const removeCustomClip = (index) => {
    setCustomTrailerClips((prev) => {
      const removed = prev[index];
      if (removed) {
        // V4.2: save the deleted clip for a 6-second undo window. If
        // there's already an undo pending, replace it (single-slot).
        setCustomTrailerUndo({
          clip: removed,
          index,
          expiresAt: Date.now() + 6000,
        });
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  // Restore the most-recently-deleted clip. Re-inserts at the original
  // index so seller's intended ordering is preserved. Clears the undo
  // state after restoration.
  const undoRemoveCustomClip = () => {
    if (!customTrailerUndo) return;
    const { clip, index } = customTrailerUndo;
    setCustomTrailerClips((prev) => {
      const next = [...prev];
      // Re-insert at the original position (clamp if list shrunk).
      const insertAt = Math.min(index, next.length);
      next.splice(insertAt, 0, clip);
      return next;
    });
    setCustomTrailerUndo(null);
  };

  // Auto-expire the undo banner after its 6-second window.
  React.useEffect(() => {
    if (!customTrailerUndo) return undefined;
    const remaining = customTrailerUndo.expiresAt - Date.now();
    if (remaining <= 0) {
      setCustomTrailerUndo(null);
      return undefined;
    }
    const t = setTimeout(() => setCustomTrailerUndo(null), remaining);
    return () => clearTimeout(t);
  }, [customTrailerUndo]);

  const moveCustomClip = (index, direction) => {
    setCustomTrailerClips((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const updateCustomLabel = (index, value) => {
    setCustomTrailerClips((prev) => prev.map((c, i) => (i === index ? { ...c, customLabel: value.slice(0, 50) } : c)));
  };

  // Jump the player to a specific clip's start (preview affordance —
  // saves the seller from scrubbing back to verify their selection).
  const previewCustomClip = (clip) => {
    const v = customTrailerVideoRef.current;
    if (!v) return;
    try {
      v.currentTime = clip.startMs / 1000;
      v.play().catch(() => {});
    } catch (_) { /* noop */ }
  };

  // ─── V3 handlers: transcript click + AI suggestions + timeline drag ──

  // Transcript-segment click for clip selection.
  // First click = mark start of range (highlight the clicked segment).
  // Second click = mark end of range; if within bounds, add as clip.
  // Click outside the modal or "Cancel" resets the selection.
  // The result is the segment-start timestamp → clip-start, segment-end → clip-end.
  const handleTranscriptSegmentClick = (segmentIdx) => {
    if (!customTrailerTranscript || !customTrailerTranscript.segments) return;
    const segments = customTrailerTranscript.segments;
    const seg = segments[segmentIdx];
    if (!seg) return;

    // Jump player to the clicked segment for context.
    const v = customTrailerVideoRef.current;
    if (v) {
      try { v.currentTime = seg.start; } catch (_) { /* noop */ }
    }

    if (customTrailerSelStart == null) {
      // First click — mark the start.
      setCustomTrailerSelStart(segmentIdx);
      setCustomTrailerError(null);
      return;
    }

    // Second click — finalize the range. Use the EARLIER segment's
    // start as in-point and LATER segment's end as out-point so the
    // seller can click the segments in either order.
    const startIdx = Math.min(customTrailerSelStart, segmentIdx);
    const endIdx = Math.max(customTrailerSelStart, segmentIdx);
    const startMs = Math.floor(segments[startIdx].start * 1000);
    const endMs = Math.floor(segments[endIdx].end * 1000);

    setCustomTrailerSelStart(null);

    const durationMs = endMs - startMs;
    if (durationMs < clipMinMs) {
      setCustomTrailerError(`Selection is too short — pick more transcript text (minimum ${fmtClipLimit(clipMinMs)}).`);
      return;
    }
    if (durationMs > clipMaxMs) {
      setCustomTrailerError(`Selection is too long — maximum ${fmtClipLimit(clipMaxMs)} per clip. Pick less or split.`);
      return;
    }
    if (wouldExceedTotal(durationMs)) {
      setCustomTrailerError(`Adding this selection would push the trailer past its ${fmtClipLimit(trailerMaxTotalMs)} total limit. Shorten or remove another clip first.`);
      return;
    }
    const overlaps = customTrailerClips.some((c) => startMs < c.endMs && endMs > c.startMs);
    if (overlaps) {
      setCustomTrailerError('This range overlaps an existing clip.');
      return;
    }
    if (customTrailerClips.length >= 12) {
      setCustomTrailerError('Maximum 12 clips per custom trailer.');
      return;
    }
    setCustomTrailerClips((prev) => [...prev, { startMs, endMs, customLabel: '' }]);
    setCustomTrailerError(null);
  };

  // Add an AI-suggested moment as a clip in one click. The suggestion
  // comes from the parent demo's stored moments array (AI-detected).
  // Clamps duration to the validation window so a long suggestion
  // doesn't exceed the 2-min cap.
  const addSuggestion = (suggestion) => {
    if (!suggestion) return;
    if (customTrailerClips.length >= 12) {
      setCustomTrailerError('Maximum 12 clips per custom trailer.');
      return;
    }
    const startMs = Math.floor((suggestion.startSeconds || 0) * 1000);
    let endMs = Math.floor((suggestion.endSeconds || 0) * 1000);
    // Defensive — refuse suggestions whose timestamps fall past the
    // source duration. Some legacy demos have moment timestamps that
    // extend past the actual recording end (seed data / detection drift),
    // and ffmpeg silently produces empty clips when -ss extends past EOF.
    // If we know the source duration, hard-reject those rather than
    // letting a black-frame trailer go through.
    if (customTrailerSourceDuration > 0) {
      const sourceDurationMs = Math.floor(customTrailerSourceDuration * 1000);
      if (startMs >= sourceDurationMs) {
        setCustomTrailerError('This AI suggestion starts after the recording ends — its timestamp is out of bounds. Pick a different suggestion or use Mark In/Out manually.');
        return;
      }
      if (endMs > sourceDurationMs) {
        // Truncate to source end rather than reject so a near-miss still works.
        endMs = sourceDurationMs;
      }
    }
    if (endMs - startMs < clipMinMs) {
      setCustomTrailerError('AI suggestion is too short to use as a clip.');
      return;
    }
    let trimmedToCap = false;
    if (endMs - startMs > clipMaxMs) {
      // Trim to the cap rather than rejecting outright — but SAY SO.
      // This used to clamp silently, which meant a seller could ship a
      // shorter clip than the suggestion promised without ever knowing.
      endMs = startMs + clipMaxMs;
      trimmedToCap = true;
    }
    if (wouldExceedTotal(endMs - startMs)) {
      setCustomTrailerError(`Adding this suggestion would push the trailer past its ${fmtClipLimit(trailerMaxTotalMs)} total limit. Shorten or remove another clip first.`);
      return;
    }
    const overlaps = customTrailerClips.some((c) => startMs < c.endMs && endMs > c.startMs);
    if (overlaps) {
      setCustomTrailerError('This suggestion overlaps an existing clip — remove the conflict and try again.');
      return;
    }
    const label = suggestion.feature || suggestion.title || '';
    setCustomTrailerClips((prev) => [...prev, {
      startMs,
      endMs,
      customLabel: label.slice(0, 50),
    }]);
    setCustomTrailerError(trimmedToCap
      ? `Added — trimmed to the ${fmtClipLimit(clipMaxMs)} per-clip limit. Adjust the edges on the timeline if you'd rather keep a different part.`
      : null);
  };

  // Timeline drag — update a clip's start or end edge based on mouse
  // position over the timeline element. We listen to mousemove on the
  // timeline ref and convert pixel-x → ms based on element width and
  // source duration.
  const handleTimelineMouseDown = (clipIndex, edge) => (e) => {
    e.preventDefault();
    setCustomTrailerDragging({ index: clipIndex, edge });
  };
  React.useEffect(() => {
    if (!customTrailerDragging) return undefined;
    const onMove = (e) => {
      const t = customTrailerTimelineRef.current;
      if (!t || !customTrailerSourceDuration) return;
      const rect = t.getBoundingClientRect();
      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const ms = Math.floor((x / rect.width) * customTrailerSourceDuration * 1000);
      setCustomTrailerClips((prev) => prev.map((c, i) => {
        if (i !== customTrailerDragging.index) return c;
        if (customTrailerDragging.edge === 'start') {
          // Don't let start cross end - 5s minimum
          const newStart = Math.min(ms, c.endMs - 5000);
          return { ...c, startMs: Math.max(0, newStart) };
        }
        // edge === 'end'
        const maxEnd = customTrailerSourceDuration * 1000;
        const newEnd = Math.max(ms, c.startMs + 5000);
        return { ...c, endMs: Math.min(maxEnd, newEnd) };
      }));
    };
    const onUp = () => setCustomTrailerDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [customTrailerDragging, customTrailerSourceDuration]);

  // V4.1: auto-scroll the transcript so the active segment stays in
  // view as the video plays. Triggers when the active segment's index
  // changes (not on every currentMs tick) so we're not thrashing
  // scrollIntoView 30 times a second. Uses block:'nearest' so we only
  // nudge the list when the active segment leaves the visible window
  // — no jarring jumps if the seller has manually scrolled away.
  const customTrailerActiveSegIdx = React.useMemo(() => {
    if (!customTrailerTranscript || !customTrailerTranscript.segments) return -1;
    const currentSec = customTrailerCurrentMs / 1000;
    return customTrailerTranscript.segments.findIndex(
      (s) => s && currentSec >= s.start && currentSec < s.end
    );
  }, [customTrailerCurrentMs, customTrailerTranscript]);

  React.useEffect(() => {
    if (customTrailerActiveSegIdx < 0) return;
    const el = customTrailerActiveSegRef.current;
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } catch (_) { /* older browsers — ignore */ }
  }, [customTrailerActiveSegIdx]);

  // Click anywhere on the timeline (not a clip box) to scrub the player.
  const handleTimelineScrub = (e) => {
    if (customTrailerDragging) return; // don't scrub mid-drag
    const t = customTrailerTimelineRef.current;
    if (!t || !customTrailerSourceDuration) return;
    // Ignore clicks on clip boxes (they handle their own clicks).
    if (e.target !== t) return;
    const rect = t.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const seconds = (x / rect.width) * customTrailerSourceDuration;
    const v = customTrailerVideoRef.current;
    if (v) {
      try { v.currentTime = seconds; } catch (_) { /* noop */ }
    }
  };

  const submitCustomTrailer = async () => {
    if (customTrailerClips.length === 0) {
      setCustomTrailerError('Add at least one clip first.');
      return;
    }
    if (!customTrailerParent || !customTrailerParent.id) return;
    setCustomTrailerSubmitting(true);
    setCustomTrailerError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/demos/${customTrailerParent.id}/custom-trailer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clips: customTrailerClips.map((c) => ({
            startMs: c.startMs,
            endMs: c.endMs,
            customLabel: c.customLabel ? c.customLabel.trim() : undefined,
          })),
        }),
      });
      const payload = await res.json();
      if (!res.ok) {
        // 402 card-required → open the contact space (same pattern as upload).
        if (res.status === 402 && payload.code === 'card_required') {
          openPlanContact();
          throw new Error(PLAN_REQUIRED_MESSAGE);
        }
        throw new Error(payload.error || `Failed (${res.status})`);
      }
      // Close modal + show processing panel on the parent demo's view.
      // Mirrors the rebuild flow's UX: user stays on the demo they
      // were on, sees a "building..." panel with progress, and when
      // the new trailer's done we navigate to it via loadCompletedDemo.
      // We don't switch jobId immediately because loadCompletedDemo
      // refuses non-completed demos — polling decides when to switch.
      setCustomTrailerOpen(false);
      setCustomTrailerClips([]);
      setCustomTrailerInPoint(null);
      setCustomTrailerBuilding(true);
      setCustomTrailerBuildingId(payload.jobId);
      setCustomTrailerBuildingProgress(65);
      showToast('Building your custom trailer — this takes a few minutes.');

      // Poll the new demo's status until it's completed or failed.
      // Reuses the existing /api/status endpoint pattern. When done,
      // load the new demo into the main view so the user lands on
      // their finished trailer.
      const pollNewDemo = async () => {
        try {
          const sres = await authFetch(`${API_BASE}/api/status/${payload.jobId}`);
          if (!sres.ok) return; // transient — try again next tick
          const sdata = await sres.json();
          if (typeof sdata.progress === 'number') {
            setCustomTrailerBuildingProgress(sdata.progress);
          }
          if (sdata.status === 'completed') {
            setCustomTrailerBuilding(false);
            setCustomTrailerBuildingId(null);
            showToast('Your custom trailer is ready.');
            loadCompletedDemo(payload.jobId);
            return; // stop polling
          }
          if (sdata.status === 'failed') {
            setCustomTrailerBuilding(false);
            setCustomTrailerBuildingId(null);
            showToast(`Custom trailer build failed: ${sdata.error || 'unknown error'}`);
            return;
          }
        } catch (_) {
          // Non-fatal — keep polling.
        }
        // Schedule the next tick. 4-second cadence matches the upload
        // flow; balances perceived responsiveness vs server load.
        setTimeout(pollNewDemo, 4000);
      };
      pollNewDemo();
    } catch (err) {
      setCustomTrailerError(err.message);
    } finally {
      setCustomTrailerSubmitting(false);
    }
  };

  // Segment narration regen (2026-08-12): one clip's narration is
  // rewritten server-side (optionally steered by the instruction), then
  // the trailer recomposes with every other script untouched. Reuses
  // the rebuild polling machinery wholesale — from the player's point
  // of view this IS an in-place rebuild.
  const handleRegenerateNarration = async (momentIndex) => {
    const instruction = window.prompt(
      "Optional: tell the AI how to rewrite this clip's narration (e.g. \"shorter\", \"lead with the ROI math\"). Leave blank for a fresh take.",
      ''
    );
    if (instruction === null) return; // user cancelled
    setRegenNarrationBusy(momentIndex);
    try {
      const res = await authFetch(`${API_BASE}/api/demos/${jobId}/regenerate-narration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          momentIndex,
          ...(instruction.trim() ? { instruction: instruction.trim() } : {}),
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.error || `Couldn't regenerate narration (${res.status})`);
      showToast('New narration written — re-rendering the trailer…');
      setVideoLoadError(false);
      setRebuilding(true);
      setProgress(0);
      setStatus('composing');
      pollForCompletion(jobId);
    } catch (err) {
      showToast(err.message);
    } finally {
      setRegenNarrationBusy(null);
    }
  };

  const submitRebuild = async () => {
    if (rebuildSelected.size === 0) {
      setRebuildError('Pick at least one clip to include in the trailer.');
      return;
    }
    setRebuildSubmitting(true);
    setRebuildError(null);
    try {
      const selectedIndices = Array.from(rebuildSelected).sort((a, b) => a - b);
      const saveAsNew = rebuildMode === 'new';
      const res = await authFetch(`${API_BASE}/api/demos/${jobId}/rebuild`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selectedIndices, saveAsNew }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        // 402 card-required → open the contact space (same pattern as
        // upload + custom trailer; only reachable on the save-as-new path,
        // which is quota-charged like any new trailer).
        if (res.status === 402 && payload.code === 'card_required') {
          openPlanContact();
          throw new Error(PLAN_REQUIRED_MESSAGE);
        }
        throw new Error(payload.error || payload.message || `Rebuild failed (${res.status})`);
      }

      if (saveAsNew) {
        // New-trailer path: the original stays untouched; a NEW demo row
        // (payload.jobId) is composing. Mirror the custom-trailer UX —
        // stay on the current demo, poll the new job, land on it when done.
        setRebuildModalOpen(false);
        showToast('Building your new trailer — the original stays as is.');
        const newId = payload.jobId;
        const pollNewTrailer = async () => {
          try {
            const sres = await authFetch(`${API_BASE}/api/status/${newId}`);
            if (sres.status === 404) {
              // Backend cleaned up the half-built row after a failure.
              showToast('New trailer build failed — your original is untouched.');
              return;
            }
            if (sres.ok) {
              const sdata = await sres.json();
              if (sdata.status === 'completed') {
                showToast('Your new trailer is ready.');
                loadCompletedDemo(newId);
                loadDemoHistory();
                return;
              }
              if (sdata.status === 'failed') {
                showToast(`New trailer build failed: ${sdata.error || 'unknown error'} — your original is untouched.`);
                return;
              }
            }
          } catch (_) { /* transient — keep polling */ }
          setTimeout(pollNewTrailer, 4000);
        };
        pollNewTrailer();
        return;
      }

      setRebuildModalOpen(false);
      setRebuilding(true);
      setVideoLoadError(false);
      setProgress(0);
      setStatus('composing');
      showToast('Rebuilding your trailer…');
      // Reuse the existing status poller; when it flips back to 'completed'
      // we reload the demo to pick up the fresh trailer + moments.
      pollForCompletion(jobId);
    } catch (err) {
      setRebuildError(err.message);
    } finally {
      setRebuildSubmitting(false);
    }
  };

  // When a rebuild finishes (poller sets phase back to 'completed'),
  // reload the demo so the <video> element re-keys and the fresh trailer
  // plays instead of the cached old one.
  useEffect(() => {
    if (!rebuilding) return;
    if (status === 'completed') {
      setRebuilding(false);
      if (jobId) loadCompletedDemo(jobId);
    } else if (status === 'failed') {
      setRebuilding(false);
      showToast('Rebuild failed — your previous trailer is still intact.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, rebuilding]);

  const getVideoUrl = () => {
    if (!jobId) return null;
    // Use the dedicated streaming endpoint which supports HTTP Range requests
    // (required for <video> playback and seeking). The raw trailerPath returned
    // by the backend is a filesystem path that isn't publicly served.
    return `${API_BASE}/api/stream/${jobId}`;
  };

  // ═══════════════════════════════════════════════════════════════
  // SHARE LINKS — create-or-reuse a distribution token per trailer
  // ═══════════════════════════════════════════════════════════════
  // Returns a public share URL for the given jobId. Calls the backend
  // get-or-create endpoint so multiple UI surfaces (dashboard "Copy
  // share link", Follow-Up Generator, etc.) all resolve to the SAME
  // distribution — no duplicate rows, consistent URL. In-memory cache
  // skips the round-trip on subsequent calls within the session.
  // Throws on network / auth / ownership failure.
  const getOrCreateShareLink = async (targetJobId) => {
    if (!targetJobId) throw new Error('Missing jobId');
    if (shareLinkCache[targetJobId]) return shareLinkCache[targetJobId];
    if (!authToken) throw new Error('You need to be signed in');

    const res = await fetch(`${API_BASE}/api/trailers/for-demo/${targetJobId}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${authToken}` },
    });
    if (!res.ok) {
      const payload = await res.json().catch(() => ({}));
      throw new Error(payload.error || `Failed to resolve share link (status ${res.status})`);
    }
    const data = await res.json();
    if (!data.url) throw new Error('Share link response missing url');
    setShareLinkCache((prev) => ({ ...prev, [targetJobId]: data.url }));
    return data.url;
  };

  // Delete a demo. Shows a confirmation prompt first — destructive, can't
  // be undone. Removes from local state immediately for snappy UX; if the
  // backend rejects we show a toast and reload the library to resync.
  // Owner or company admin can delete any demo in their company; members
  // can delete their own (backend enforces).
  const deleteDemoById = async (demoId, title) => {
    if (!demoId) return;
    const label = title ? `"${title}"` : 'this trailer';
    if (!window.confirm(`Delete ${label}? This also removes its share links and recipient list. Can't be undone.`)) return;
    try {
      const res = await authFetch(`${API_BASE}/api/demos/${demoId}`, { method: 'DELETE' });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `status ${res.status}`);
      }
      // Pull from all three surfaces: library sidebar, dashboard recent
      // tiles, and the full library page (which mirrors libraryDemos).
      setLibraryDemos((prev) => prev.filter((d) => d.id !== demoId));
      setDemoHistory((prev) => prev.filter((d) => d.id !== demoId));
      showToast('Demo deleted.');
      // If we were viewing the just-deleted demo in the completed phase,
      // bounce back to the dashboard so we don't render a broken state.
      if (jobId === demoId && phase === 'completed') {
        setJobId(null);
        setTrailerPath(null);
        setMoments([]);
    setAllMoments(null);
        setPhase('dashboard');
      }
    } catch (err) {
      console.error('[demos] delete failed:', err);
      showToast(`Couldn't delete: ${err.message}`);
    }
  };

  // Toggle the Global-Admin-only privacy flag on a recording. Backend
  // re-checks the permission so the kebab item visibility is purely
  // UI sugar — flipping privacy without GA returns 403 there.
  // Optimistically updates local state, then re-fetches the list so
  // the lock-icon indicator + filtered visibility (for other users)
  // stay in sync with the server.
  const toggleDemoPrivacy = async (demoId, makePrivate) => {
    if (!demoId) return;
    try {
      const res = await authFetch(`${API_BASE}/api/demos/${demoId}/privacy`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPrivate: !!makePrivate }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `Privacy update failed (${res.status})`);
      }
      showToast(makePrivate
        ? 'Marked private. Only you and the owner can see it now.'
        : 'Marked public. Visible to teammates with team-library access.');
      // Refresh both library + dashboard surfaces so the lock icon /
      // filtered-out state propagates without a manual reload.
      if (typeof loadDemoHistory === 'function') loadDemoHistory();
      if (typeof loadLibraryDemos === 'function') loadLibraryDemos();
    } catch (err) {
      console.error('[demos] privacy toggle failed:', err);
      showToast(`Couldn't update privacy: ${err.message}`);
    }
  };

  // Rename a demo's display title. `field` is 'prospectName' (preferred
  // display) or 'filename' (fallback). Prompt-based UI — quick + works
  // on every surface without a separate modal. Backend PATCH enforces
  // the same owner/admin rule as delete.
  const renameDemoById = async (demoId, currentTitle) => {
    if (!demoId) return;
    const newTitle = window.prompt('Rename this trailer:', currentTitle || '');
    if (newTitle === null) return; // cancelled
    const trimmed = newTitle.trim();
    if (trimmed === (currentTitle || '').trim()) return; // no change
    try {
      const res = await authFetch(`${API_BASE}/api/demos/${demoId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prospectName: trimmed || null }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `status ${res.status}`);
      }
      // Optimistic swap in local state so the change shows immediately.
      const apply = (d) => d.id === demoId ? { ...d, prospectName: trimmed || null } : d;
      setLibraryDemos((prev) => prev.map(apply));
      setDemoHistory((prev) => prev.map(apply));
      showToast('Renamed.');
    } catch (err) {
      console.error('[demos] rename failed:', err);
      showToast(`Couldn't rename: ${err.message}`);
    }
  };

  // Can this user delete/rename the given demo?
  //   - Owner of the demo? Yes.
  //   - Company admin on the same company? Yes.
  //   - Otherwise no (button hidden — backend also enforces).
  const canManageDemo = (demo) => {
    if (!demo || !authUser) return false;
    // demo.creator only present on library-scope rows; otherwise the row
    // is from the caller's own list (listByUser) so by definition they
    // can manage it.
    if (!demo.creator) return true;
    if (demo.creator.userId === authUser.id) return true;
    return isCompanyAdmin; // same company enforced on the backend via row scope
  };

  // Render the standard demo-row action cluster: a primary "View" button and
  // a kebab (⋮) menu with Download / Copy share link / Rename / Delete.
  // Used in three places (library sidebar, recent-demo tiles, library grid)
  // so any action-model change lives in one place. `variant` tweaks sizing:
  // 'compact' for narrow sidebars, 'default' for tiles and grid cards.
  // `opts.reclassify` adds the Deal ⇄ Reusable move to the kebab. Only
  // the Trailers screen passes it — on a dashboard tile there is no
  // visible tab for the row to move between, so the action would have no
  // observable result.
  const renderDemoActions = (d, variant = 'default', opts = {}) => {
    const canManage = canManageDemo(d);
    const isCompleted = d.status === 'completed';
    // Compound key (variant + id) because the same demo can appear in two
    // surfaces simultaneously — e.g. the sidebar AND a recent-demo tile.
    // Keying by id alone would open both menus from a single click.
    const menuKey = `${variant}:${d.id}`;
    const menuOpen = openActionMenuId === menuKey;
    return (
      <div
        className={`demo-actions demo-actions--${variant}`}
        onClick={(e) => e.stopPropagation()}
      >
        {isCompleted && (
          <button
            type="button"
            className="demo-action-view"
            onClick={(e) => {
              e.stopPropagation();
              setOpenActionMenuId(null);
              loadCompletedDemo(d.id);
            }}
          >
            View
          </button>
        )}
        <DemoKebabMenu
          isOpen={menuOpen}
          variant={variant}
          onOpenChange={(next) => setOpenActionMenuId(next ? menuKey : null)}
          menuItems={
            <>
              {isCompleted && (
                <>
                  <a
                    className="demo-action-menu-item"
                    role="menuitem"
                    href={`${API_BASE}/api/download/${d.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpenActionMenuId(null)}
                  >
                    Download
                  </a>
                  <button
                    type="button"
                    className="demo-action-menu-item"
                    role="menuitem"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenActionMenuId(null);
                      copyShareLinkForJob(d.id);
                    }}
                  >
                    Copy share link
                  </button>
                  {/* Generate follow-up shortcut — jumps into the
                      completed-demo view AND auto-opens the follow-up
                      modal so the seller doesn't have to land, find
                      the button, and click again. loadCompletedDemo
                      awaits all the state (jobId, moments, attendees)
                      the modal reads from; setShowFollowUp(true) after
                      that fires on the same tick the modal mounts. */}
                  <button
                    type="button"
                    className="demo-action-menu-item"
                    role="menuitem"
                    onClick={async (e) => {
                      e.stopPropagation();
                      setOpenActionMenuId(null);
                      await loadCompletedDemo(d.id);
                      setShowFollowUp(true);
                    }}
                  >
                    Generate follow-up
                  </button>
                  {/* Phase 2A surfaced the full source recording in R2.
                      Exposed here so sellers can dive back into the
                      original meeting from any demo row without
                      opening the trailer first. Gated on sourceAvailable
                      so legacy pre-Phase-2A rows don't show a
                      broken link. */}
                  {d.sourceAvailable && (
                    <button
                      type="button"
                      className="demo-action-menu-item"
                      role="menuitem"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenActionMenuId(null);
                        openFullRecording(d.id);
                      }}
                    >
                      Watch full recording
                    </button>
                  )}
                </>
              )}
              {canManage && (
                <>
                  {/* Separator only makes sense when there are items above
                      it (Download / Share are completed-only). For
                      non-completed demos the menu is just Delete. */}
                  {isCompleted && <div className="demo-action-menu-sep" role="separator" />}
                  {isCompleted && (
                    <button
                      type="button"
                      className="demo-action-menu-item"
                      role="menuitem"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenActionMenuId(null);
                        renameDemoById(d.id, d.prospectName || d.filename);
                      }}
                    >
                      Rename
                    </button>
                  )}
                  {/* Deal ⇄ Reusable. The same recording legitimately
                      changes job over its life — a walkthrough recorded
                      for one prospect becomes the explainer you send
                      everyone — so this is a move, not a re-record. */}
                  {opts.reclassify && isCompleted && (() => {
                    const nextScope = (d.contentScope === 'reusable') ? 'deal' : 'reusable';
                    const nextLabel = nextScope === 'reusable' ? 'Reusable' : 'Deal';
                    return (
                      <button
                        type="button"
                        className="demo-action-menu-item"
                        role="menuitem"
                        disabled={reclassifyingId === d.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenActionMenuId(null);
                          reclassifyDemo(d.id, nextScope);
                        }}
                        title={nextScope === 'reusable'
                          ? 'Treat this as an asset you send across many deals'
                          : 'File this back with the deal it came from'}
                      >
                        {reclassifyingId === d.id ? 'Moving…' : `Move to ${nextLabel}`}
                      </button>
                    );
                  })()}
                  {/* Filing (2026-08-24). A native select inside the
                      kebab rather than a submenu: one folder per
                      recording means this is a single choice, and the
                      current one shows as selected without needing a
                      checkmark column. d.folderId is undefined on row
                      shapes that didn't select it — treat that as
                      unfiled rather than rendering a blank option. */}
                  {opts.reclassify && isCompleted && (
                    <div className="demo-action-menu-item" onClick={(e) => e.stopPropagation()}>
                      <label
                        htmlFor={`folder-${d.id}`}
                        style={{ display: 'block', fontSize: '0.75rem', opacity: 0.7, marginBottom: 2 }}
                      >
                        Folder
                      </label>
                      <select
                        id={`folder-${d.id}`}
                        value={d.folderId || ''}
                        disabled={filingId === d.id}
                        onChange={(e) => {
                          const next = e.target.value || null;
                          const picked = trailerFolders.find((f) => f.id === next);
                          setOpenActionMenuId(null);
                          setDemoFolder(d.id, next, picked && picked.name);
                        }}
                        style={{ width: '100%' }}
                      >
                        <option value="">No folder</option>
                        {trailerFolders.map((f) => (
                          <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {/* Global-Admin-only privacy toggle. Hidden for everyone
                      else (regular admins can see the rest of the kebab
                      but not this lever). Backend re-checks the permission
                      so the menu being shown isn't a security claim. */}
                  {isCompleted && authUser && authUser.company && authUser.company.isGlobalAdmin && (
                    <button
                      type="button"
                      className="demo-action-menu-item"
                      role="menuitem"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenActionMenuId(null);
                        toggleDemoPrivacy(d.id, !d.isPrivate);
                      }}
                      title={d.isPrivate
                        ? 'Currently visible only to the owner and you. Click to make it visible to all teammates with team-library access.'
                        : 'Hide this recording from every teammate except the owner and Global Admin (you).'}
                    >
                      {d.isPrivate ? '🔓 Mark public' : '🔒 Mark private'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="demo-action-menu-item demo-action-menu-item--danger"
                    role="menuitem"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenActionMenuId(null);
                      deleteDemoById(d.id, d.prospectName || d.filename);
                    }}
                  >
                    {isCompleted ? 'Delete' : 'Delete / Cancel'}
                  </button>
                </>
              )}
            </>
          }
        />
      </div>
    );
  };

  // Re-open a previously-completed demo in the full "completed" view so
  // the seller can preview the trailer, copy its share link, and (re-)run
  // the Follow-Up Generator. Called when the user clicks a recent-demo
  // tile on the dashboard. Only works for demos in the 'completed'
  // status — otherwise shows a toast explaining the state.
  const loadCompletedDemo = async (demoId) => {
    if (!demoId) return;
    if (!authToken) {
      showToast('You need to be signed in.');
      return;
    }
    try {
      const res = await authFetch(`${API_BASE}/api/demos/${demoId}`);
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `Failed to load demo (status ${res.status})`);
      }
      const body = await res.json();
      // Backend returns { demo: {...} } but be defensive for future routes.
      const d = body.demo || body;

      if (d.status === 'failed') {
        showToast('That demo failed to process.');
        return;
      }
      if (d.status !== 'completed') {
        // analyzed / generating / processing → hand off to the in-progress
        // restorer instead of dead-ending. For 'analyzed' this opens the
        // CLIP-SELECTION screen (moments come from the DB demo, not the
        // in-memory job), so it works even after a navigate/refresh/restart.
        // Previously this showed "still <status>. Hang tight." and stranded
        // the seller with NO way back into clip selection — the demo sat at
        // "3 clips ready for review" but clicking it never opened the selector.
        return loadInProgressDemo(demoId);
      }

      // Route non-demo recordings (qualifying calls + meetings) to
      // the Calls inbox instead of the trailer-review page. They run
      // a lighter pipeline (transcript + AI summary, no trailer
      // rendering), so opening them in the trailer view causes a
      // 404 on /api/stream and a misleading "Can't load trailer"
      // error. The Calls inbox is the right surface — it has the
      // transcript, the AI verdict, the talk-time timeline, and a
      // playback control for the source recording.
      //
      // 'demo' (and legacy NULL recordingKind) keeps the existing
      // trailer-review behavior. 'browser_recording' is also a
      // trailer (the recording IS the final trailer — no AI pipeline)
      // and routes to the same surface. Any other kind goes to Calls.
      if (d.recordingKind && !TRAILER_CLASS_KINDS.has(d.recordingKind)) {
        const kindLabel = d.recordingKind === 'qualifying_call'
          ? 'qualifying call'
          : d.recordingKind === 'meeting' ? 'meeting' : d.recordingKind;
        showToast(`That's a ${kindLabel} recording — opening in your Calls inbox.`);
        // Persist the call id in the hash format CallsPhase actually
        // reads (`#/calls/<id>` — see parseDetailHash in CallsPhase.jsx).
        // The previous `#demo=<id>` format was the trailer-review surface's
        // convention, which CallsPhase doesn't recognise — so the user
        // landed on the Calls list view (calendar) instead of the call
        // detail page. With the right hash the CallDetailDispatcher
        // mounts on first paint and the seller goes straight to the
        // transcript / AI summary.
        try {
          const hash = `#/calls/${encodeURIComponent(d.id)}`;
          if (typeof window !== 'undefined' && window.location.hash !== hash) {
            window.history.replaceState(null, '', window.location.pathname + window.location.search + hash);
          }
        } catch (_) { /* non-fatal */ }
        setPhase('calls');
        return;
      }

      // Clear follow-up state from any previous demo — stale message,
      // preview name, share-url chip would all be wrong for the new trailer.
      setFollowUpMessage('');
      setFollowUpShareUrl('');
      setFollowUpPreviewName('');
      setShowFollowUp(false);
      setLastSendResult(null);
      setAttendees([]);
      setNewAttendee({ firstName: '', email: '' });
      // Load this demo's persisted attendee list so Send is ready without
      // retyping emails.
      loadAttendees(d.id);

      // Populate the state the completed-phase view reads from. trailerPath
      // is a filesystem path from the backend, but the <video> element uses
      // getVideoUrl() which is jobId-scoped so setting trailerPath is just
      // a truthy flag here ("we have a trailer").
      setJobId(d.id);
      setMoments(d.moments || []);
      setDemoNarrationTexts(Array.isArray(d.narrationTexts) ? d.narrationTexts : null);
      setAllMoments(d.allMoments || null);
      setTrailerPath(d.trailerPath || 'hosted');
      setVideoLoadError(false);
      setCurrentDemoSourceAvailable(!!d.sourceAvailable);
      setCurrentDemoDealBrief(d.dealBrief || null);
      setCurrentDemoTranscript(d.transcript || null);
      setCurrentDemoParticipantTalkTime(d.participantTalkTime || null);
      setCurrentDemoSpeakerNameOverrides(d.speakerNameOverrides || null);
      setCurrentDemoCalendarAttendees(d.calendarAttendees || null);
      setCurrentDemoExtractedNames(d.extractedNames || null);
      // Reset + fetch heat-map for this demo. Gated on d.id so we
      // never aim at a stale jobId. Failures are non-fatal — the
      // heat-strip simply doesn't render.
      setCurrentDemoHeatmap(null);
      setHeatmapLoading(true);
      authFetch(`${API_BASE}/api/demos/${d.id}/engagement-heatmap`)
        .then((r) => (r.ok ? r.json() : null))
        .then((hm) => setCurrentDemoHeatmap(hm || null))
        .catch(() => { /* noop — heat-map is best-effort */ })
        .finally(() => setHeatmapLoading(false));
      setCurrentDemoFilename(d.filename || '');
      if (d.brandConfig) setBrandConfig((prev) => ({ ...prev, ...d.brandConfig }));
      // Preserve prospectName if the demo has it (older demos may not).
      if (d.prospectName) setProspectName(d.prospectName);

      setPhase('completed');
      // Persist the current demo id in the URL hash so a page refresh
      // keeps the seller on the same recording instead of dumping them
      // back to the dashboard. Hash (not query) because we don't need
      // the server to see it — it's a pure client-side state marker.
      // The bootstrap useEffect reads this on mount and re-invokes
      // loadCompletedDemo if present.
      try {
        const hash = `#demo=${encodeURIComponent(d.id)}`;
        if (typeof window !== 'undefined' && window.location.hash !== hash) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search + hash);
        }
      } catch (_) { /* history API might be blocked in sandboxed contexts */ }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('[dashboard] loadCompletedDemo failed:', err);
      showToast(`Couldn't open demo: ${err.message}`);
    }
  };

  // Restore an in-progress (analyzed / generating / processing) demo
  // from a #job=<id> URL hash. This survives page refreshes that
  // happen between upload and clip-selection — without it, refreshing
  // dumped the seller back to the dashboard with no way to find their
  // pending demo. Companion to loadCompletedDemo (which handles the
  // post-compose case via #demo=<id>).
  const loadInProgressDemo = async (demoId) => {
    if (!demoId) return;
    if (!authToken) {
      showToast('You need to be signed in.');
      return;
    }
    console.log('[resume] loading in-progress demo', demoId);
    try {
      const res = await authFetch(`${API_BASE}/api/demos/${demoId}`);
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `Failed to load demo (status ${res.status})`);
      }
      const body = await res.json();
      const d = body.demo || body;
      console.log('[resume] demo status =', d.status, '| moments =', (d.moments || []).length);

      // Completed → delegate to the dedicated restore path so all the
      // completed-view state (heat-map, attendees, deal brief, etc.)
      // gets populated identically to the standard click-from-library.
      if (d.status === 'completed') {
        return loadCompletedDemo(demoId);
      }

      if (d.status === 'failed') {
        showToast(`That demo failed: ${d.error || 'unknown error'}`);
        return;
      }

      // Restore enough state for the select (analyzed) or generating views.
      setJobId(d.id);
      setMoments(d.moments || []);
      setDemoNarrationTexts(Array.isArray(d.narrationTexts) ? d.narrationTexts : null);
      setAllMoments(d.allMoments || null);
      setSelectedMoments((d.moments || []).map((_, i) => i));
      setCurrentDemoFilename(d.filename || '');
      if (d.brandConfig) setBrandConfig((prev) => ({ ...prev, ...d.brandConfig }));
      if (d.prospectName) setProspectName(d.prospectName);
      setVideoLoadError(false);
      setError(null);

      if (d.status === 'analyzed') {
        // Drop straight into clip-selection — moments are already detected.
        setLoading(false);
        setPhase('select');
      } else {
        // Still processing somewhere in the analyze or compose pipeline.
        // Resume polling so the existing terminal-state handlers fire.
        setStatus(d.status || 'processing');
        setProgress(d.progress || 0);
        setLoading(true);
        setPhase(d.status === 'generating' ? 'generating' : 'analyzing');
        if (d.status === 'generating') {
          pollForCompletion(d.id);
        } else {
          pollForMoments(d.id);
        }
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('[dashboard] loadInProgressDemo failed:', err);
      showToast(`Couldn't resume demo: ${err.message}`);
    }
  };

  // User-facing click handler: resolves the share URL, copies it to the
  // clipboard, and fires a toast. Shows a distinct error toast if any
  // step fails (backend unreachable, token expired, clipboard API blocked).
  const copyShareLinkForJob = async (targetJobId) => {
    try {
      const url = await getOrCreateShareLink(targetJobId);
      try {
        await navigator.clipboard.writeText(url);
        showToast('Share link copied to clipboard');
      } catch (_) {
        // Clipboard API can fail in insecure contexts or when blurred —
        // fall back to showing the URL in a toast action the user can
        // manually select. Acceptable degraded UX vs a silent failure.
        showToast(`Link: ${url}`, {
          label: 'Copy',
          onClick: () => navigator.clipboard.writeText(url).catch(() => {}),
        });
      }
    } catch (err) {
      console.error('[share] copy failed:', err);
      showToast(`Couldn't create share link: ${err.message}`);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // NOTIFICATIONS — toast + browser Notification API
  // ═══════════════════════════════════════════════════════════════

  // Ask for browser notification permission. Safe to call multiple times:
  // if the user has already granted or denied it, this resolves instantly.
  const ensureNotificationPermission = async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    try {
      const result = await Notification.requestPermission();
      return result;
    } catch (_) {
      return 'denied';
    }
  };

  // Show an in-app toast. Auto-dismisses after 10s unless replaced first.
  const showToast = (message, action = null) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast({ message, action });
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 10000);
  };

  const dismissToast = () => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast(null);
  };

  // Stale-bundle guard (2026-09-23): a restored or long-open tab boots
  // the old hashed bundle and keeps it until a hard refresh, so shipped
  // features looked missing. At boot and on login nothing is at risk,
  // so reload silently (once per build); mid-session only offer it.
  const buildCheckRef = useRef({ lastAt: 0 });
  const checkBuild = async ({ autoReload, force = false } = {}) => {
    if (process.env.NODE_ENV !== 'production') return;
    const now = Date.now();
    if (!force && now - buildCheckRef.current.lastAt < 60 * 1000) return;
    buildCheckRef.current.lastAt = now;
    let result;
    try { result = await checkForNewerBuild(); } catch (_) { return; }
    if (!result.stale) return;
    if (autoReload && reloadOnceFor(result.liveKey)) return;
    showToast('A newer version of TrailerCast is available.', {
      label: 'Reload now',
      onClick: () => window.location.reload(),
    });
  };
  useEffect(() => {
    checkBuild({ autoReload: true, force: true });
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkBuild({ autoReload: false });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (authUser && authUser.id) checkBuild({ autoReload: true, force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser && authUser.id]);

  // Fire both a browser notification (when tab is hidden) and an in-app
  // toast when clips finish analyzing and are ready for the user to review.
  const notifyClipsReady = (clipCount) => {
    const msg = `${clipCount} clip${clipCount !== 1 ? 's' : ''} detected — ready to review.`;

    // Browser notification — only if the user isn't already looking at the tab.
    if (
      typeof window !== 'undefined' &&
      'Notification' in window &&
      Notification.permission === 'granted' &&
      document.hidden
    ) {
      try {
        // Close any prior notification so we don't stack them up.
        if (browserNotificationRef.current) {
          try { browserNotificationRef.current.close(); } catch (_) {}
        }
        const n = new Notification('Your TrailerCast clips are ready ', {
          body: msg,
          tag: 'trailercast-clips-ready',
          requireInteraction: false,
        });
        n.onclick = () => {
          window.focus();
          n.close();
        };
        browserNotificationRef.current = n;
      } catch (err) {
        console.warn('Browser notification failed:', err);
      }
    }

    // Always show the in-app toast too.
    showToast(msg);
  };

  // Clear any pending toast timer on unmount to avoid leaks.
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      if (browserNotificationRef.current) {
        try { browserNotificationRef.current.close(); } catch (_) {}
      }
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // CLIP PREVIEW (scrubbing the ORIGINAL upload to preview a moment)
  // ═══════════════════════════════════════════════════════════════
  const getSourceUrl = () => {
    if (!jobId) return null;
    return `${API_BASE}/api/source/${jobId}`;
  };

  // Whether the uploaded source is a video. Prefer the in-memory File, fall
  // back to scanning the job's filename if we have one (e.g. after a refresh).
  const sourceIsVideo = () => {
    if (selectedFile) return isVideoFile(selectedFile);
    return true; // default: <video> can play audio too
  };

  const playClip = (index) => {
    const moment = moments[index];
    if (!moment) return;
    const player = previewPlayerRef.current;
    if (!player) return;

    const start = Number(moment.startSeconds) || 0;
    const end = Number(moment.endSeconds) || start + 5;

    previewStopAtRef.current = end;
    setPreviewingMomentIndex(index);

    // Seek, then play. If metadata isn't loaded yet, wait for it.
    try {
      if (player.readyState >= 1) {
        player.currentTime = start;
        player.play().catch(() => {});
      } else {
        const onReady = () => {
          player.currentTime = start;
          player.play().catch(() => {});
          player.removeEventListener('loadedmetadata', onReady);
        };
        player.addEventListener('loadedmetadata', onReady);
      }
    } catch (err) {
      console.warn('Clip preview seek failed:', err);
    }
  };

  const stopPreview = () => {
    const player = previewPlayerRef.current;
    if (player) {
      try { player.pause(); } catch (_) {}
    }
    previewStopAtRef.current = null;
    setPreviewingMomentIndex(null);
  };

  // Handler attached to the shared preview player — stops playback once we
  // pass the end of the currently-previewing clip.
  const handlePreviewTimeUpdate = () => {
    const player = previewPlayerRef.current;
    const stopAt = previewStopAtRef.current;
    if (!player || stopAt == null) return;
    if (player.currentTime >= stopAt) {
      try { player.pause(); } catch (_) {}
      previewStopAtRef.current = null;
      setPreviewingMomentIndex(null);
    }
  };

  // Clear the #demo=... hash whenever navigation leaves the
  // completed-view. Called by resetApp and by the top-level "back to
  // dashboard" buttons in the reports / library / conversations views
  // so refresh state stays consistent.
  const clearDemoHash = () => {
    try {
      if (typeof window !== 'undefined' && window.location.hash.startsWith('#demo=')) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    } catch (_) { /* noop */ }
  };

  const resetApp = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setSelectedFile(null);
    setJobId(null);
    setStatus(null);
    setProgress(0);
    setLoading(false);
    setError(null);
    setDragActive(false);
    setTrailerPath(null);
    setMoments([]);
    setAllMoments(null);
    setSelectedMoments([]);
    setPreviewingMomentIndex(null);
    previewStopAtRef.current = null;
    setProspectName('');
    setPhase('dashboard');
    // Clear the #demo=... hash so a refresh on the dashboard stays on
    // the dashboard (not bounces back to the last viewed recording).
    try {
      if (typeof window !== 'undefined' && window.location.hash.startsWith('#demo=')) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    } catch (_) { /* noop */ }
    setShowFollowUp(false);
    setFollowUpMessage('');
    setLogoFile(null);
  };

  // Render helper — Security & Permissions content. Used by the
  // `security` phase renderer below.
  const renderSecurityContent = () => {
    const isGlobalAdmin = !!(authUser && authUser.company && authUser.company.isGlobalAdmin);
    const globalAdminMember = teamMembers.find((m) => m.role === 'global_admin') || null;
    const transferableMembers = teamMembers.filter((m) => m.role !== 'global_admin' && !m.isSelf);
    return (
      <>
                        <div className="settings-section">
                          <div className="settings-section-title">Security &amp; Permissions</div>
                          <p className="settings-section-sub">
                            {isCompanyAdmin
                              ? `Manage who's on ${displayCompanyName(authUser?.company?.name, 'your team')}, invite new teammates, and adjust roles. Only admins can change permissions.`
                              : `These are the people on ${displayCompanyName(authUser?.company?.name, 'your team')} and their roles. Only admins can invite or change permissions.`}
                          </p>

                          {/* Org security settings (2026-08-11): the
                              require-2FA mandate previously existed only
                              as an API endpoint, and the enrollment flow
                              hides in the profile modal — this card gives
                              both a visible home on the page an admin
                              actually opens for security matters. The
                              deep-link hands off to the profile modal's
                              Password tab, where TwoFactorSetup lives. */}
                          <SecuritySettingsCard
                            authToken={authToken}
                            isAdmin={isCompanyAdmin}
                            showToast={showToast}
                            onOpenTwoFactorSetup={() => openProfileSettings('password')}
                          />

                          {/* Global Admin block — surfaces who currently
                              holds the highest privilege role + (for the
                              GA themselves) the Transfer button. Phase-3
                              of the granular-permissions rollout
                              (2026-05-07). */}
                          {globalAdminMember && (
                            <div style={{
                              display: 'flex', alignItems: 'flex-start',
                              justifyContent: 'space-between', gap: 16,
                              flexWrap: 'wrap',
                              padding: 16, marginTop: 16, marginBottom: 8,
                              borderRadius: 12,
                              background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.06), rgba(79, 70, 229, 0.06))',
                              border: '1px solid rgba(124, 58, 237, 0.25)',
                            }}>
                              <div style={{ minWidth: 240 }}>
                                <div style={{
                                  fontSize: 12, fontWeight: 700,
                                  textTransform: 'uppercase', letterSpacing: '0.06em',
                                  color: '#6d28d9', marginBottom: 4,
                                }}>
                                  Global Admin
                                </div>
                                <div style={{ fontSize: 16, fontWeight: 600 }}>
                                  {globalAdminMember.fullName || globalAdminMember.email}
                                  {globalAdminMember.isSelf && (
                                    <span style={{ marginLeft: 6, opacity: 0.7, fontWeight: 400 }}>(you)</span>
                                  )}
                                </div>
                                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                                  Always has every permission. Cannot be edited by anyone else. To change who is Global Admin, transfer the role.
                                </div>
                              </div>
                              {isGlobalAdmin && (
                                <button
                                  type="button"
                                  onClick={() => setTransferGlobalAdminOpen(true)}
                                  disabled={transferableMembers.length === 0}
                                  title={transferableMembers.length === 0
                                    ? 'No teammates yet — invite someone first, then you can transfer Global Admin to them.'
                                    : 'Pick a teammate to take over as Global Admin.'}
                                  style={{
                                    padding: '8px 16px', borderRadius: 8,
                                    border: '1px solid rgba(124, 58, 237, 0.4)',
                                    background: 'var(--room-card, #ffffff)', color: '#6d28d9',
                                    cursor: transferableMembers.length === 0 ? 'not-allowed' : 'pointer',
                                    opacity: transferableMembers.length === 0 ? 0.5 : 1,
                                    fontSize: 13, fontWeight: 600,
                                  }}
                                >
                                  Transfer Global Admin
                                </button>
                              )}
                            </div>
                          )}

                          {/* Role legend — always visible so everyone knows
                              what each role means. Phase-3 added Global
                              Admin to the existing Admin/Member legend. */}
                          <div className="role-legend">
                            <div className="role-legend-item">
                              <span className="role-badge role-global-admin">Global Admin</span>
                              <span>One per company. Every permission, immutable. Only the current Global Admin can transfer this role.</span>
                            </div>
                            <div className="role-legend-item">
                              <span className="role-badge role-admin">Admin</span>
                              <span>Default everything-on. Global Admin can revoke or lock individual capabilities.</span>
                            </div>
                            <div className="role-legend-item">
                              <span className="role-badge role-member">Member</span>
                              <span>Curated default-on set. Admins can grant additional permissions per user.</span>
                            </div>
                          </div>

                          {/* Team members list */}
                          <div className="team-invite-list-header" style={{ marginTop: '1.5rem' }}>
                            <h4>Team members</h4>
                            <span>{teamMembers.length === 0 ? 'Loading…' : `${teamMembers.length} total`}</span>
                          </div>
                          <ul className="team-invite-list">
                            {teamMembers.map((m) => {
                              // Granular permissions Phase-1 (2026-05-07) introduced
                              // 'global_admin' as a distinct role. Render it as
                              // "Global Admin" with its own badge styling. Existing
                              // 'admin' rows render unchanged. canEditThis stays
                              // gated to non-Global-Admin targets — only Global
                              // Admin can change other Global Admins, via Transfer.
                              let badge;
                              let badgeClass;
                              // Granular-permissions Phase-3 (2026-05-07):
                              // 'global_admin' takes priority over the legacy
                              // 'isOwner' flag — Global Admin is the canonical
                              // role going forward; ownership at the company
                              // level always co-resides with Global Admin (the
                              // backfill picks the earliest user, who is also
                              // the owner). Showing "Owner" alongside Admin
                              // hid the new role; this surfaces it instead.
                              if (m.role === 'global_admin') {
                                badge = 'Global Admin'; badgeClass = 'role-global-admin';
                              } else if (m.isOwner) {
                                badge = 'Owner'; badgeClass = 'role-owner';
                              } else if (m.role === 'admin') {
                                badge = 'Admin'; badgeClass = 'role-admin';
                              } else {
                                badge = 'Member'; badgeClass = 'role-member';
                              }
                              // Cannot demote / change role on the Global Admin
                              // — that requires Transfer. Owner-without-GA
                              // (shouldn't happen post-migration, but legacy
                              // safety) is also untouchable via this row.
                              const canEditThis = isCompanyAdmin && !m.isOwner && !m.isSelf
                                && m.role !== 'global_admin';
                              // Permission-editor button: anyone can open
                              // their own panel (self-edit; backend filters
                              // locked rows). Admins can open anyone's
                              // (except Global Admin's, which is immutable).
                              const canOpenPermissions = m.isSelf
                                || (isCompanyAdmin && m.role !== 'global_admin');
                              return (
                                <li
                                  key={m.id}
                                  className="team-invite-row"
                                  // Selector hook for the top-bar search
                                  // scroll-and-flash effect. See the
                                  // highlightedMemberId useEffect above.
                                  data-team-member-id={m.id}
                                >
                                  <div className="team-invite-info">
                                    <span className="team-invite-email">
                                      {m.fullName || m.email}
                                      {m.isSelf && <span className="team-invite-meta" style={{ marginLeft: '0.4rem' }}>(you)</span>}
                                    </span>
                                    <span className="team-invite-meta">{m.email}</span>
                                  </div>
                                  <span className={`role-badge ${badgeClass}`}>{badge}</span>
                                  {canEditThis && (
                                    m.role === 'admin' ? (
                                      <button
                                        type="button"
                                        className="team-invite-revoke"
                                        onClick={() => changeMemberRole(m.id, 'member')}
                                        disabled={changingRoleId === m.id}
                                      >
                                        {changingRoleId === m.id ? '…' : 'Demote'}
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="team-invite-revoke"
                                        onClick={() => changeMemberRole(m.id, 'admin')}
                                        disabled={changingRoleId === m.id}
                                      >
                                        {changingRoleId === m.id ? '…' : 'Promote'}
                                      </button>
                                    )
                                  )}
                                  {/* Admin-only password reset. Hidden for
                                      the owner (must self-serve) and for
                                      yourself (use change-password in the
                                      Account tab). */}
                                  {isCompanyAdmin && !m.isOwner && !m.isSelf && (
                                    <button
                                      type="button"
                                      className="team-invite-revoke team-invite-reset"
                                      onClick={() => sendAdminPasswordReset(m)}
                                      disabled={resetingMemberId === m.id}
                                      title={`Email ${m.email} a password reset link`}
                                    >
                                      {resetingMemberId === m.id ? 'Sending…' : 'Send reset link'}
                                    </button>
                                  )}
                                  {/* Manage permissions — opens the granular
                                      permission editor modal. Visible per-
                                      row for any member the caller can act
                                      on (their own row for self-edit, or
                                      anyone-but-Global-Admin for admins). */}
                                  {canOpenPermissions && (
                                    <button
                                      type="button"
                                      className="team-invite-revoke"
                                      onClick={() => setEditingPermissionsUserId(m.id)}
                                      title={m.isSelf ? 'View your effective permissions' : `Manage permissions for ${m.email}`}
                                      style={{ borderColor: 'rgba(124, 58, 237, 0.4)', color: '#6d28d9' }}
                                    >
                                      {m.isSelf ? 'My permissions' : 'Permissions'}
                                    </button>
                                  )}
                                  {/* Delete teammate — admin-only, owner +
                                      self protected. Soft-delete: revokes
                                      links + scrubs PII immediately, hard-
                                      delete cron lands after 30 days. */}
                                  {isCompanyAdmin && !m.isOwner && !m.isSelf && (
                                    <button
                                      type="button"
                                      className="team-invite-revoke"
                                      onClick={() => deleteTeamMember(m)}
                                      title={`Delete ${m.email}'s account`}
                                      style={{ borderColor: '#fecaca', color: '#b91c1c' }}
                                    >
                                      Delete
                                    </button>
                                  )}
                                </li>
                              );
                            })}
                          </ul>

                          {/* Admin-only: invite form + pending invites */}
                          {isCompanyAdmin && (
                            <>
                              <div className="team-invite-list-header" style={{ marginTop: '2rem' }}>
                                <h4>Invite a teammate</h4>
                              </div>
                              <form className="team-invite-form" onSubmit={sendTeamInvite}>
                                <input
                                  type="email"
                                  className="team-invite-input"
                                  placeholder="teammate@company.com"
                                  value={inviteForm.email}
                                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                                  required
                                />
                                <button
                                  type="submit"
                                  className="team-invite-send"
                                  disabled={sendingInvite}
                                >
                                  {sendingInvite ? 'Sending…' : 'Send invite'}
                                </button>
                              </form>

                              <div className="team-invite-list-header">
                                <h4>Pending invites</h4>
                                <span>{teamInvites.length === 0 ? 'None yet' : `${teamInvites.length} pending`}</span>
                              </div>

                              {teamInvites.length === 0 ? (
                                <p className="team-invite-empty">
                                  No pending invites. Sent invites appear here until they're accepted or revoked.
                                </p>
                              ) : (
                                <ul className="team-invite-list">
                                  {teamInvites.map((inv) => (
                                    <li key={inv.id} className="team-invite-row">
                                      <div className="team-invite-info">
                                        <span className="team-invite-email">{inv.email}</span>
                                        <span className="team-invite-meta">
                                          {inv.isExpired
                                            ? <span className="team-invite-expired">Expired</span>
                                            : `Expires ${new Date(inv.expiresAt).toLocaleDateString()}`}
                                          {inv.role === 'admin' && ' · admin'}
                                        </span>
                                      </div>
                                      <button
                                        type="button"
                                        className="team-invite-revoke"
                                        onClick={() => revokeTeamInvite(inv.id)}
                                        title="Revoke this invite"
                                        aria-label={`Revoke invite for ${inv.email}`}
                                      >
                                        Revoke
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </>
                          )}
                        </div>

        {/* Granular permission editor — opens when a row's
            "Permissions" / "My permissions" button fires. Loads
            /api/permissions/users/:id and posts diffs back via PATCH.
            Self-edit auto-filters locked rows on the backend. */}
        {editingPermissionsUserId && (
          <PermissionEditorModal
            authToken={authToken}
            userId={editingPermissionsUserId}
            isCurrentUserGlobalAdmin={isGlobalAdmin}
            onClose={() => setEditingPermissionsUserId(null)}
            onSaved={() => {
              setEditingPermissionsUserId(null);
              loadTeamMembers();
            }}
            showToast={showToast}
          />
        )}

        {/* Transfer Global Admin — only the current GA opens this.
            Atomic transfer on the backend; current GA drops to
            'admin' and the picked teammate becomes the new GA. */}
        {transferGlobalAdminOpen && (
          <TransferGlobalAdminModal
            authToken={authToken}
            candidates={transferableMembers}
            onClose={() => setTransferGlobalAdminOpen(false)}
            onSuccess={() => {
              setTransferGlobalAdminOpen(false);
              loadTeamMembers();
              if (showToast) showToast('Global Admin transferred. You are now an Admin.');
              // Refetch /me so the UI immediately reflects the role
              // demotion (isGlobalAdmin flips false → Transfer button
              // disappears for the now-just-Admin caller).
              if (typeof refreshAuthUser === 'function') refreshAuthUser();
            }}
            showToast={showToast}
          />
        )}
      </>
    );
  };

  // Render helper — Integrations content. Used by the `integrations`
  // phase renderer below.
  const renderIntegrationsContent = () => {
                        const googleConn = oauthStatus?.connections?.find(c => c.provider === 'google') || null;
                        const microsoftConn = oauthStatus?.connections?.find(c => c.provider === 'microsoft') || null;
                        const googleAvailable = oauthStatus?.providers?.google?.enabled !== false;
                        const microsoftAvailable = oauthStatus?.providers?.microsoft?.enabled !== false;
                        // displayCompanyName filters out the __pending__ placeholder,
                        // so a half-onboarded user doesn't see
                        // "__pending__email@gmail.com Notetaker" as their bot name.
                        const sanitizedCompany = displayCompanyName(authUser?.company?.name, '');
                        const defaultBotName = sanitizedCompany ? `${sanitizedCompany} Notetaker` : 'TrailerCast Notetaker';
                        const isCompanyAdmin = !!(authUser?.company?.isAdmin);
                        const currentJurisdiction = authUser?.company?.consentJurisdiction || null;
                        const currentJurisdictionLabel = currentJurisdiction
                          ? (CONSENT_JURISDICTION_LABEL[currentJurisdiction] || currentJurisdiction)
                          : null;
                        const userAcknowledgedAt = authUser?.consentAcknowledgedAt || null;
                        const userAcknowledgedJurisdiction = authUser?.consentAcknowledgedJurisdiction || null;
                        const hasFreshAck = !!userAcknowledgedAt
                          && userAcknowledgedJurisdiction === currentJurisdiction;
                        return (
                          <div className="profile-integrations">

                            <h3 className="settings-section-title">Connect your calendar</h3>
                            <p className="profile-muted">
                              TrailerCast will automatically record your sales calls, turn them
                              into trailers, and draft a follow-up for every attendee.
                            </p>

                            {/* Eligibility banner (2026-08-27). Names the exact
                                server-side gate keeping the poller away — an account
                                that fails one otherwise looks connected while nothing
                                schedules. The green state doubles as the heartbeat. */}
                            {recordingEligibility && recordingEligibility.gates.calendarConnected && (
                              recordingEligibility.eligible ? (
                                <div className="eligibility-banner eligibility-banner--ok">
                                  Auto-recording is active
                                  {recordingEligibility.lastPolledAt
                                    ? ` — calendar last checked ${Math.max(1, Math.round((Date.now() - new Date(recordingEligibility.lastPolledAt).getTime()) / 60000))}m ago.`
                                    : ' — first calendar check runs within 10 minutes.'}
                                </div>
                              ) : (
                                <div className="eligibility-banner eligibility-banner--warn">
                                  <strong>Calendar connected, but auto-recording is paused: </strong>
                                  {!recordingEligibility.gates.tokenHealthy
                                    ? 'the calendar connection needs to be reconnected.'
                                    : !recordingEligibility.gates.autoRecordEnabled
                                      ? 'auto-record is switched off below.'
                                      : !recordingEligibility.gates.jurisdictionSet
                                        ? 'an admin needs to pick a recording-consent jurisdiction below.'
                                        : !recordingEligibility.gates.consentAcknowledged
                                          ? 'the recording-consent acknowledgment below needs to be completed.'
                                          : 'a payment method is needed on the account (Settings → Billing).'}
                                </div>
                              )
                            )}

                            {integrationsLoading && !oauthStatus ? (
                              <p className="profile-muted">Loading…</p>
                            ) : (
                              <div className="integrations-provider-list">
                                <div className="integration-card">
                                  <div className="integration-card-head">
                                    <div>
                                      <strong>Google Calendar</strong>
                                      {googleConn && (
                                        <div className="integration-card-sub">
                                          Connected as {googleConn.accountEmail || 'your Google account'}
                                        </div>
                                      )}
                                      {!googleConn && (
                                        <div className="integration-card-sub">
                                          Bot joins Google Meet + any Zoom/Teams URLs in your events.
                                        </div>
                                      )}
                                    </div>
                                    {googleConn ? (
                                      <button
                                        type="button"
                                        className="integration-disconnect-btn"
                                        onClick={() => disconnectProvider('google')}
                                      >
                                        Disconnect
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="integration-connect-btn"
                                        onClick={() => connectProvider('google')}
                                        disabled={!googleAvailable}
                                      >
                                        {googleAvailable ? 'Connect' : 'Not configured'}
                                      </button>
                                    )}
                                  </div>
                                </div>

                                <div className="integration-card">
                                  <div className="integration-card-head">
                                    <div>
                                      <strong>Microsoft 365</strong>
                                      {microsoftConn && (
                                        <div className="integration-card-sub">
                                          Connected as {microsoftConn.accountEmail || 'your Microsoft account'}
                                        </div>
                                      )}
                                      {!microsoftConn && (
                                        <div className="integration-card-sub">
                                          Bot joins Teams + any Zoom/Meet URLs in your events.
                                        </div>
                                      )}
                                    </div>
                                    {microsoftConn ? (
                                      <button
                                        type="button"
                                        className="integration-disconnect-btn"
                                        onClick={() => disconnectProvider('microsoft')}
                                      >
                                        Disconnect
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="integration-connect-btn"
                                        onClick={() => connectProvider('microsoft')}
                                        disabled={!microsoftAvailable}
                                      >
                                        {microsoftAvailable ? 'Connect' : 'Not configured'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* ─── Auto-schedule toggle ─────────────────
                                Opt-in per seller. When on, the backend
                                polls this user's connected calendars
                                every 10 min and schedules Recall bots
                                for upcoming events matching the filter
                                ("demo" in title/body OR mentions
                                "trailercast"). Gated on consent
                                acknowledgment + company jurisdiction —
                                the toggle disables itself when either
                                is missing and explains why. */}
                            {oauthStatus && oauthStatus.connections && oauthStatus.connections.length > 0 && (() => {
                              const autoOn = !!oauthStatus.autoScheduleEnabled;
                              const jurisdictionSet = !!(authUser?.company?.consentJurisdiction);
                              const hasFreshAck = !!authUser?.consentAcknowledgedAt
                                && authUser?.consentAcknowledgedJurisdiction === authUser?.company?.consentJurisdiction;
                              const canToggle = jurisdictionSet && hasFreshAck;
                              const mostRecentPoll = oauthStatus.connections.reduce((acc, c) => {
                                if (!c.lastPolledAt) return acc;
                                const t = new Date(c.lastPolledAt).getTime();
                                return t > acc ? t : acc;
                              }, 0);
                              const lastPolledLabel = mostRecentPoll
                                ? formatRelative(new Date(mostRecentPoll))
                                : null;
                              return (
                                <div className="auto-schedule-card">
                                  <div className="auto-schedule-body">
                                    <div>
                                      <h4>Auto-schedule bots on matching meetings</h4>
                                      <p>
                                        When on, TrailerCast checks your connected calendar
                                        every ~10 minutes and dispatches a bot for upcoming
                                        events with <strong>&quot;demo&quot;</strong> in the
                                        title/body, or any event that mentions{' '}
                                        <strong>TrailerCast</strong>. Meetings under 10 minutes
                                        are skipped. You can still manually schedule bots below.
                                      </p>
                                      {autoOn && lastPolledLabel && (
                                        <p className="auto-schedule-heartbeat">
                                          Last checked {lastPolledLabel}.
                                        </p>
                                      )}
                                      {!canToggle && (
                                        <p className="auto-schedule-blocker">
                                          {!jurisdictionSet ? (
                                            <>
                                              {authUser?.company?.isAdmin
                                                ? 'You need to pick a consent jurisdiction in the '
                                                : 'Your admin needs to pick a consent jurisdiction in the '}
                                              <a
                                                href="#recording-consent"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  const el = document.getElementById('recording-consent');
                                                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                }}
                                                style={{ color: '#b45309', textDecoration: 'underline', fontWeight: 600 }}
                                              >
                                                Recording consent
                                              </a>
                                              {' '}section at the top of this page before auto-scheduling can turn on.
                                              {!authUser?.company?.isAdmin && ' (Only an admin can change it.)'}
                                            </>
                                          ) : (
                                            <>
                                              Acknowledge the recording-consent rules in the{' '}
                                              <a
                                                href="#recording-consent"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  const el = document.getElementById('recording-consent');
                                                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                }}
                                                style={{ color: '#b45309', textDecoration: 'underline', fontWeight: 600 }}
                                              >
                                                Recording consent
                                              </a>
                                              {' '}section at the top of this page before auto-scheduling can turn on.
                                            </>
                                          )}
                                        </p>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      className={`auto-schedule-toggle ${autoOn ? 'is-on' : ''}`}
                                      onClick={() => toggleAutoSchedule(!autoOn)}
                                      disabled={!canToggle && !autoOn}
                                      aria-label={autoOn ? 'Turn auto-schedule off' : 'Turn auto-schedule on'}
                                      aria-pressed={autoOn}
                                    >
                                      <span className="auto-schedule-toggle-dot" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })()}

                            <div className="integrations-divider" />

                            <h3 className="settings-section-title">Schedule a bot on a meeting</h3>
                            <p className="profile-muted">
                              Paste a Zoom, Google Meet, or Microsoft Teams URL. The bot joins
                              the call, records it, and processes it based on the call type you pick.
                            </p>
                            <form className="integrations-schedule-form" onSubmit={scheduleManualBot}>
                              <div className="auth-field">
                                <label>Meeting URL</label>
                                <input
                                  type="url"
                                  className="auth-input"
                                  value={scheduleForm.meetingUrl}
                                  onChange={(e) => setScheduleForm({ ...scheduleForm, meetingUrl: e.target.value })}
                                  placeholder="https://zoom.us/j/123456789"
                                  required
                                />
                              </div>
                              <div className="auth-field">
                                <label>Meeting title <span className="profile-muted">(optional)</span></label>
                                <input
                                  type="text"
                                  className="auth-input"
                                  value={scheduleForm.meetingTitle}
                                  onChange={(e) => setScheduleForm({ ...scheduleForm, meetingTitle: e.target.value })}
                                  placeholder="Acme Corp — Demo"
                                  maxLength={120}
                                />
                              </div>
                              <div className="auth-field">
                                <label>Join at <span className="profile-muted">(optional)</span></label>
                                <input
                                  type="datetime-local"
                                  className="auth-input"
                                  value={scheduleForm.joinAt}
                                  onChange={(e) => setScheduleForm({ ...scheduleForm, joinAt: e.target.value })}
                                />
                                <p className="profile-muted" style={{ margin: '6px 0 0', fontSize: 12 }}>
                                  Leave empty and the bot joins the moment you click Schedule —
                                  right for a meeting already in progress. For a meeting later,
                                  set the start time so the bot arrives with everyone else.
                                </p>
                              </div>
                              {/* Call type picker (2026-08-13): decides the pipeline
                                  (demo → trailer; qualifying → structured summary;
                                  meeting → generic summary) AND the recorded layout
                                  (demo records speaker view for clean trailer cuts;
                                  the others record gallery view). Default matches
                                  the old always-qualifying behavior. */}
                              <div className="auth-field">
                                <label>Call type</label>
                                <select
                                  className="auth-input"
                                  value={scheduleForm.recordingKind}
                                  onChange={(e) => setScheduleForm({ ...scheduleForm, recordingKind: e.target.value })}
                                >
                                  <option value="qualifying_call">Qualifying Call — discovery/intro, AI summary with pain points &amp; next steps</option>
                                  <option value="demo">Demo — product walkthrough, builds a trailer</option>
                                  <option value="meeting">Meeting — internal or generic, key takeaways &amp; action items</option>
                                </select>
                                <p className="profile-muted" style={{ margin: '6px 0 0', fontSize: 12 }}>
                                  You can still change this from Calls → Upcoming before the bot joins,
                                  or re-tag the recording afterwards.
                                </p>
                              </div>
                              {scheduleError && (
                                <div className="error-message"><span></span> {scheduleError}</div>
                              )}
                              <button
                                type="submit"
                                className="generate-btn ready"
                                disabled={scheduleSaving}
                              >
                                <span className="btn-text">{scheduleSaving ? 'Scheduling…' : 'Schedule bot'}</span>
                              </button>
                            </form>

                            {userBots.length > 0 && (
                              <>
                                <div className="integrations-divider" />
                                <h3 className="settings-section-title">Recent bots</h3>
                                <div className="integrations-bot-list">
                                  {userBots.slice(0, 10).map((bot) => (
                                    <div key={bot.id} className="integration-bot-row">
                                      <div className="integration-bot-row-main">
                                        <div className="integration-bot-row-title">
                                          {bot.meeting_title || bot.meeting_url}
                                        </div>
                                        <div className="integration-bot-row-meta">
                                          {formatDemoDate(bot.created_at)}
                                        </div>
                                      </div>
                                      <span className={`integration-bot-status status-${bot.status}`}>
                                        {bot.status}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}

                            {/* ─── CRM section ────────────────────────
                                Auto-pulls deal status into the demo
                                cards + lights up the existing real-data
                                swap-in points (DealStatusPill, By User
                                outcomes). Admin-only. */}
                            <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--room-border, #e2e8f0)' }}>
                              <h3 className="settings-section-title">CRM</h3>
                              <p className="profile-muted">
                                Auto-link prospects to deals + sync deal status into your demo cards. Adds the missing piece between "we sent a trailer" and "what happened in the pipeline."
                              </p>
                              <CrmIntegrationTiles
                                authToken={authToken}
                                isAdmin={isCompanyAdmin}
                                onToast={showToast}
                              />
                            </div>

                            {/* ─── eSignature section ──────────────────
                                Tracks contract engagement on the Buyer
                                Space Documents tab + auto-fires the
                                Handoff Brief when an envelope completes.
                                Spec: backend/docs/specs/ESIGN_TRACKING_SPEC.md */}
                            <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--room-border, #e2e8f0)' }}>
                              <h3 className="settings-section-title">eSignature tracking</h3>
                              <p className="profile-muted">
                                Watch contracts signed externally (DocuSign V1; PandaDoc / HelloSign / Adobe Sign coming). Surfaces engagement on the Buyer Space and auto-fires the Handoff Brief when an envelope completes.
                              </p>
                              <DocuSignSettings
                                authToken={authToken}
                                isAdmin={isCompanyAdmin}
                                onToast={showToast}
                              />
                            </div>

                            {/* ─── API access (flag integration_api) ──
                                API keys + webhook endpoints for an
                                external CRM / warehouse / workflow
                                tool. Management routes only — data
                                routes take a key, never a session.
                                Guide: docs.trailercast.io → API
                                reference. */}
                            {authUser?.company?.featureFlags?.integration_api && (
                              <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--room-border, #e2e8f0)' }}>
                                <h3 className="settings-section-title">API access</h3>
                                <p className="profile-muted">
                                  Pull call summaries, prospect engagement and Decision Room metrics into any system with a read-only API key, and have events pushed to it with signed webhooks. Keys and secrets are shown once.
                                </p>
                                <ApiAccessSettings
                                  authToken={authToken}
                                  isAdmin={isCompanyAdmin}
                                  onToast={showToast}
                                />
                              </div>
                            )}
                          </div>
                        );
  };

  // Render helper — Products catalogue (Settings → Products, flag
  // proposals). The price list behind proposal quote tables; "Import
  // from CRM" appears when crm_depth is on as well.
  const renderProductsContent = () => (
    <ProductsSettings
      authToken={authToken}
      isAdmin={!!authUser?.company?.isAdmin}
      onToast={showToast}
      crmImportEnabled={!!authUser?.company?.featureFlags?.crm_depth}
    />
  );

  // Render helper — AI Personalization (5-question onboarding form,
  // admin-editable / member read-only). Used by the Settings phase.
  const renderPersonalizationContent = () => (
                        <div className="settings-section">
                          <div className="settings-section-title">AI Personalization</div>
                          <p className="settings-section-desc">
                            These 5 answers are injected into TrailerCast's AI prompts so
                            moment detection, narration, and follow-up emails reflect your
                            product + ICP. Edit anytime — changes apply to every new demo
                            (existing trailers keep the context they were built with).
                          </p>

                          {authUser.company && !authUser.company.isAdmin && (
                            <div className="settings-section-sub" style={{ marginBottom: 12 }}>
                              Only company admins or the owner can edit personalization. You can view the current answers below.
                            </div>
                          )}

                          {/* key ensures the form remounts with fresh state
                              when the user's saved answers change (another
                              admin edits, or a /me refetch lands). */}
                          <OnboardingForm
                            key={(authUser && authUser.company && authUser.company.onboardedAt) || 'unseeded'}
                            initialAnswers={authUser && authUser.company && authUser.company.onboardingAnswers}
                            isReadOnly={!(authUser.company && authUser.company.isAdmin)}
                            // Settings-tab edit view: never show the team-size
                            // tier router — that question is for first-signup
                            // routing only. Once onboarded, the user can change
                            // tiers via the pricing modal directly.
                            isFirstAdmin={false}
                            onSave={saveOnboardingAnswers}
                          />
                        </div>

  );

  // Render helper — Brand. Rebuilt 2026-09-04 as
  // components/BrandSettings.jsx: identity card, engine-rendered slide
  // theme picker (flag slide_themes), and the custom-background uploader
  // folded into an advanced disclosure. The uploader + zone editor keep
  // their state here and are passed in as a rendered child.
  const renderBrandContent = () => {
    const brandFlags = (authUser && authUser.company && authUser.company.featureFlags) || {};
    return (
      <BrandSettings
        apiBase={API_BASE}
        authFetch={authFetch}
        brandForm={brandForm}
        savedBrand={(authUser && authUser.company && authUser.company.brand) || {}}
        onChange={(patch) => setBrandForm((prev) => ({ ...prev, ...patch }))}
        onSave={saveBrand}
        saving={brandSaving}
        error={brandError}
        onLogoSelect={handleBrandLogoSelect}
        onRemoveLogo={removeBrandLogo}
        companyLabel={displayCompanyName(authUser.companyName, 'your team')}
        themesEnabled={!!brandFlags.slide_themes}
        brandVersion={brandSavedVersion}
        customBackgroundCount={Object.keys(customSlideTemplates || {}).length}
        customBackgrounds={renderCustomSlideBackgroundsSection()}
      />
    );
  };

  // Render helper — Recording Consent (jurisdiction picker for admins,
  // readonly + seller ack for everyone). Used by the Settings phase.
  // Relies on closures in render body (jurisdictionForm, currentJurisdiction,
  // hasFreshAck, etc.) so it is defined inside the component body.
  // Deliverability tab (2026-08-24). Lives in Settings rather than
  // Security because the question it answers — "why didn't my email
  // arrive?" — is a sending question, not an access one.
  const renderDeliverabilityContent = () => (
    <SuppressionListCard apiBase={API_BASE} authToken={authToken} />
  );

  const renderConsentContent = () => {
    const isCompanyAdmin = !!(authUser?.company?.isAdmin);
    const currentJurisdiction = authUser?.company?.consentJurisdiction || null;
    const currentJurisdictionLabel = currentJurisdiction
      ? (CONSENT_JURISDICTION_LABEL[currentJurisdiction] || currentJurisdiction)
      : null;
    const userAcknowledgedAt = authUser?.consentAcknowledgedAt || null;
    const userAcknowledgedJurisdiction = authUser?.consentAcknowledgedJurisdiction || null;
    const hasFreshAck = !!userAcknowledgedAt
      && userAcknowledgedJurisdiction === currentJurisdiction;
    return (
      <div className="profile-integrations">
                            {/* ─── Recording consent ─────────────────
                                Must come BEFORE the calendar cards
                                because connecting a calendar and not
                                having jurisdiction set means the
                                seller can't actually schedule any
                                bots. Show jurisdiction selector for
                                admins; show current value + ack
                                button for everyone. */}
                            {/* id anchor so the blocker text below can
                                deep-link straight to this section instead
                                of saying a vague "above". */}
                            <h3 id="recording-consent" className="settings-section-title">Recording consent</h3>
                            <p className="profile-muted">
                              Recording laws vary by jurisdiction. Your admin picks the regime, and
                              TrailerCast posts a matching in-meeting announcement when the bot joins
                              so every participant knows a recording is happening.
                            </p>

                            {isCompanyAdmin ? (
                              <div className="consent-jurisdiction-form">
                                <label className="auth-field">
                                  <span>Your jurisdiction</span>
                                  <select
                                    value={jurisdictionForm.jurisdiction}
                                    onChange={(e) => setJurisdictionForm({ ...jurisdictionForm, jurisdiction: e.target.value })}
                                  >
                                    <option value="">Select one…</option>
                                    {CONSENT_JURISDICTIONS.map((j) => (
                                      <option key={j.id} value={j.id}>{j.label}</option>
                                    ))}
                                  </select>
                                  {jurisdictionForm.jurisdiction && (
                                    <span className="auth-field-hint">
                                      {CONSENT_JURISDICTIONS.find((j) => j.id === jurisdictionForm.jurisdiction)?.desc}
                                    </span>
                                  )}
                                </label>

                                <label className="auth-field">
                                  <span>Privacy policy URL (optional)</span>
                                  <input
                                    type="url"
                                    placeholder="https://yourcompany.com/privacy"
                                    value={jurisdictionForm.privacyPolicyUrl}
                                    onChange={(e) => setJurisdictionForm({ ...jurisdictionForm, privacyPolicyUrl: e.target.value })}
                                  />
                                  <span className="auth-field-hint">
                                    Shared in the bot's join announcement. Defaults to trailercast.io/privacy when blank.
                                  </span>
                                </label>

                                {jurisdictionError && (
                                  <div className="error-message">{jurisdictionError}</div>
                                )}

                                <button
                                  type="button"
                                  className="generate-btn ready"
                                  onClick={saveCompanyConsent}
                                  disabled={jurisdictionSaving}
                                >
                                  <span className="btn-text">{jurisdictionSaving ? 'Saving…' : 'Save jurisdiction'}</span>
                                </button>
                              </div>
                            ) : (
                              <div className="consent-jurisdiction-readonly">
                                <strong>
                                  {currentJurisdictionLabel || 'Not set — ask your admin'}
                                </strong>
                                {authUser?.company?.privacyPolicyUrl && (
                                  <div className="consent-jurisdiction-sub">
                                    Privacy: {authUser.company.privacyPolicyUrl}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Seller-level acknowledgment status. Admins
                                also have to acknowledge for themselves. */}
                            {currentJurisdiction && (
                              <div className={`consent-ack-status ${hasFreshAck ? 'is-acked' : 'is-pending'}`}>
                                {hasFreshAck ? (
                                  <>
                                    <span className="consent-ack-dot" aria-hidden="true" />
                                    You've acknowledged the recording rules for {currentJurisdictionLabel}.
                                  </>
                                ) : (
                                  <>
                                    <span className="consent-ack-dot" aria-hidden="true" />
                                    You haven't acknowledged the recording rules for {currentJurisdictionLabel} yet.
                                    <button
                                      type="button"
                                      className="consent-ack-cta"
                                      onClick={() => { setConsentModalOpen(true); setConsentError(null); }}
                                    >
                                      Acknowledge now
                                    </button>
                                  </>
                                )}
                              </div>
                            )}      </div>
    );
  };

  // Render helper — Bot Display Name (what the meeting bot shows as
  // its attendee name). Used by the Settings phase. Relies on
  // authUser.company.name + botNameInput state closures.
  const renderBotNameContent = () => {
    // displayCompanyName filters __pending__ so the bot's display
    // name doesn't accidentally read like a placeholder email.
    const sanitizedCompany = displayCompanyName(authUser?.company?.name, '');
    const defaultBotName = sanitizedCompany ? `${sanitizedCompany} Notetaker` : 'TrailerCast Notetaker';
    return (
      <div className="profile-integrations">
                            <h3 className="settings-section-title">Bot display name</h3>
                            <p className="profile-muted">
                              Shown in the meeting attendee list when the bot joins your calls.
                              Keep it short — Zoom truncates long names.
                            </p>
                            <div className="integrations-inline-form">
                              <input
                                type="text"
                                className="auth-input"
                                value={botNameInput}
                                onChange={(e) => setBotNameInput(e.target.value)}
                                placeholder={defaultBotName}
                                maxLength={64}
                              />
                              <button
                                type="button"
                                className="integration-save-btn"
                                onClick={saveBotDisplayName}
                                disabled={botNameSaving}
                              >
                                {botNameSaving ? 'Saving…' : 'Save'}
                              </button>
                            </div>
                            <p className="profile-muted integration-hint">
                              Leaving this blank falls back to <em>{defaultBotName}</em>.
                            </p>

      </div>
    );
  };

  // ─── Billing tab — helpers + render ───────────────────────────────
  // Stripe Customer Portal only. The backend issues a short-lived URL
  // we redirect to; Stripe sends the customer back to the app when
  // they're done. Checkout is no longer started from the app.
  const openBillingPortal = async (busyKey = 'portal') => {
    setPricingError(null);
    setPricingBusy(busyKey);
    try {
      const res = await authFetch(`${API_BASE}/api/billing/portal-session`, {
        method: 'POST',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Could not open portal.');
      }
      const data = await res.json();
      if (!data.url) throw new Error('Portal URL missing.');
      window.location.href = data.url;
    } catch (err) {
      setPricingError(err.message || 'Could not open portal.');
      setPricingBusy(null);
    }
  };

  // Display labels keyed off the canonical tier code. Every paid tier
  // code renders as TrailerCast; 'free' is the trial / unsubscribed
  // state. No prices live in the app.
  const PLAN_LABELS = {
    starter:    'TrailerCast',
    pro:        'TrailerCast',
    enterprise: 'TrailerCast',
    free:       'Free trial',
  };

  const planContactHref = () => {
    const name = authUser?.company?.name;
    const subject = name ? `TrailerCast plan for ${name}` : 'TrailerCast plan';
    return `mailto:${PLAN_CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;
  };

  const renderBillingContent = () => {
    const company = authUser?.company || {};
    const currentTier = company.tier || 'free';
    const billingStatus = company.billingStatus;
    const periodEnd = company.currentPeriodEnd ? new Date(company.currentPeriodEnd) : null;
    const hasSubscription = !!company.hasSubscription;
    // A Stripe customer exists (from any prior checkout) even if the
    // subscription_id never linked back. The portal only needs the
    // customer, so this — not hasSubscription — is what should gate the
    // Manage-billing/cancel button. Decoupling the two is what stops a
    // missed webhook from locking a charged customer out of cancelling.
    const hasStripeCustomer = !!company.hasStripeCustomer;
    const trialActive = !!company.trialActive;
    const trialEndsAt = company.trialEndsAt ? new Date(company.trialEndsAt) : null;
    const planLabel = PLAN_LABELS[currentTier] || PLAN_LABELS.free;

    // Status banner copy — only shows on terminal/abnormal states.
    // 'past_due' is the most common one to surface; canceled is also
    // worth flagging so the seller knows they're losing access on
    // current_period_end.
    let statusBanner = null;
    if (billingStatus === 'past_due') {
      statusBanner = {
        tone: 'warn',
        text: 'Your last payment failed. Update your card in the customer portal before access is suspended.',
      };
    } else if (billingStatus === 'canceled' || billingStatus === 'unpaid') {
      statusBanner = {
        tone: 'bad',
        text: `Subscription is ${billingStatus.replace('_', ' ')}. Get in touch to keep paid features after the current period ends.`,
      };
    }

    return (
      <div className="profile-integrations">
        <h3 className="settings-section-title">Billing</h3>

        {statusBanner && (
          <div className={`billing-status-banner billing-status-banner--${statusBanner.tone}`}>
            {statusBanner.text}
          </div>
        )}

        {pricingError && (
          <div className="billing-status-banner billing-status-banner--bad">
            {pricingError}
          </div>
        )}

        {/* Current plan summary card. No recurring price is shown —
            plans are quoted by the team, not in the app. */}
        <div className="billing-current-plan">
          <div className="billing-current-plan-row">
            <span className="billing-current-plan-label">Current plan</span>
            <span className="billing-current-plan-value">
              <strong>{planLabel}</strong>
              {!hasSubscription && currentTier !== 'free' && (
                <span className="billing-current-plan-price billing-current-plan-price--muted">
                  {trialActive ? 'Free trial' : 'Comped'}
                </span>
              )}
            </span>
          </div>
          {/* Seat count for the paid subscription. Sourced from
              companies.stripe_seat_quantity which the webhook mirrors
              from subscription.items.data[0].quantity. */}
          {hasSubscription && company.stripeSeatQuantity && (
            <div className="billing-current-plan-row">
              <span className="billing-current-plan-label">Seats</span>
              <span className="billing-current-plan-value">
                {company.stripeSeatQuantity}
              </span>
            </div>
          )}
          {trialActive && trialEndsAt && (
            <div className="billing-current-plan-row">
              <span className="billing-current-plan-label">Trial ends</span>
              <span className="billing-current-plan-value">
                {trialEndsAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          )}
          {periodEnd && hasSubscription && (
            <div className="billing-current-plan-row">
              <span className="billing-current-plan-label">Next charge</span>
              <span className="billing-current-plan-value">
                {periodEnd.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          )}
          {billingStatus && (
            <div className="billing-current-plan-row">
              <span className="billing-current-plan-label">Status</span>
              <span className="billing-current-plan-value">
                <span className={`billing-status-pill billing-status-pill--${billingStatus}`}>
                  {billingStatus.replace('_', ' ')}
                </span>
              </span>
            </div>
          )}
        </div>

        {/* Contact-us space. Replaces the old "Choose a plan" /
            "Manage plan" button + in-app pricing modal (2026-09-17).
            Plans, seat changes, and upgrades all go through the team. */}
        <div className="billing-contact">
          <h4 className="billing-contact-title">
            {hasSubscription ? 'Want to change your plan?' : 'Ready to pick a plan?'}
          </h4>
          <p className="profile-muted integration-hint">
            {trialActive
              ? 'You have access to every feature during your free trial. When you\'re ready, reach out and we\'ll set your team up on the right plan — nothing is charged until then.'
              : hasSubscription
                ? 'Plans are tailored to your team. Reach out to add or remove seats, switch billing cadence, or talk through options.'
                : 'Plans are tailored to your team. Reach out and we\'ll get you set up on the right one.'}
          </p>
          <a className="integration-save-btn billing-contact-btn" href={planContactHref()}>
            Contact us
          </a>
        </div>

        {/* Stripe Customer Portal — card, invoices, seat count, cancel.
            Gated on hasStripeCustomer (not hasSubscription) so a charged
            customer whose webhook never linked the sub can ALWAYS reach
            update-card / invoices / cancel. */}
        {hasStripeCustomer && (
          <div className="billing-actions">
            <button
              type="button"
              className="integration-save-btn"
              onClick={openBillingPortal}
              disabled={pricingBusy === 'portal'}
            >
              {pricingBusy === 'portal' ? 'Opening…' : 'Manage billing'}
            </button>
            <p className="profile-muted integration-hint">
              Opens the Stripe customer portal where you can update your card,
              view invoices, or <strong>cancel</strong>.
            </p>
          </div>
        )}
      </div>
    );
  };

  // ─── Notifications tab — helpers + render ─────────────────────────
  // Why a dedicated loader: the Notifications tab is the only Settings
  // sub-tab that fans out across 3 GETs (meta + prefs + integrations).
  // We fetch lazily on first open so the Settings phase itself stays
  // fast, and we cache in component state until the tab remounts.
  const loadNotificationSettings = async () => {
    setNotifLoadError(null);
    try {
      const [metaRes, prefsRes, intsRes] = await Promise.all([
        authFetch(`${API_BASE}/api/notification-preferences/meta`),
        authFetch(`${API_BASE}/api/notification-preferences`),
        authFetch(`${API_BASE}/api/chat-integrations`),
      ]);
      if (!metaRes.ok || !prefsRes.ok || !intsRes.ok) throw new Error('load failed');
      const meta = await metaRes.json();
      const prefs = await prefsRes.json();
      const ints = await intsRes.json();
      setNotifMeta(meta);
      setNotifPrefs(prefs.preferences || {});
      setNotifIntegrations(ints.integrations || []);
    } catch (err) {
      setNotifLoadError('Couldn\'t load notification settings. Try again.');
    }
  };

  // Per-event toggle — optimistic update + PUT. We send the whole
  // preferences object on each change (API is merged-with-defaults
  // server-side) because batching would hide "save failed" feedback
  // until the user remembers to click Save. Immediate-save keeps the
  // UX model honest: if the checkbox sticks, it's saved.
  const toggleNotifPref = async (eventKey, channel) => {
    if (!notifPrefs) return;
    const prev = notifPrefs;
    const current = (prev[eventKey] && prev[eventKey][channel]) || false;
    const next = {
      ...prev,
      [eventKey]: { ...(prev[eventKey] || {}), [channel]: !current },
    };
    setNotifPrefs(next);
    try {
      const res = await authFetch(`${API_BASE}/api/notification-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: next }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setNotifPrefs(data.preferences || next);
    } catch (err) {
      setNotifPrefs(prev);
      showToast('Couldn\'t save — try again.');
    }
  };

  // Connect / reconnect a webhook. Same endpoint handles both first-
  // time save and URL rotation; backend returns 400 on bad URL shape,
  // which we surface inline rather than via toast so the error is
  // anchored to the input field.
  const saveChatIntegration = async (kind) => {
    if (!notifFormUrl.trim()) return;
    setNotifBusy(true);
    setNotifChannelMsg((m) => ({ ...m, [kind]: null }));
    try {
      const res = await authFetch(`${API_BASE}/api/chat-integrations/${kind}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ webhookUrl: notifFormUrl.trim(), enabled: true }),
      });
      const data = await res.json();
      if (!res.ok) {
        setNotifChannelMsg((m) => ({ ...m, [kind]: { kind: 'err', text: data.error || 'Save failed.' } }));
        return;
      }
      // Auto-fire a test ping after save so the user gets immediate
      // confirmation the URL works, rather than waiting for a real
      // event to land. "Save & Test" is one click from their view.
      const testRes = await authFetch(`${API_BASE}/api/chat-integrations/${kind}/test`, { method: 'POST' });
      const testData = await testRes.json();
      setNotifIntegrations((list) => {
        const others = (list || []).filter((i) => i.kind !== kind);
        return [...others, testData.integration || data.integration];
      });
      if (testData.ok) {
        setNotifChannelMsg((m) => ({ ...m, [kind]: { kind: 'ok', text: 'Connected + test message sent.' } }));
      } else {
        setNotifChannelMsg((m) => ({ ...m, [kind]: { kind: 'err', text: `Saved but test failed: ${testData.error || 'unknown error'}` } }));
      }
      setNotifFormOpen(null);
      setNotifFormUrl('');
    } finally {
      setNotifBusy(false);
    }
  };

  const testChatIntegration = async (kind) => {
    setNotifBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/api/chat-integrations/${kind}/test`, { method: 'POST' });
      const data = await res.json();
      setNotifIntegrations((list) => {
        const others = (list || []).filter((i) => i.kind !== kind);
        return data.integration ? [...others, data.integration] : list;
      });
      setNotifChannelMsg((m) => ({
        ...m,
        [kind]: data.ok
          ? { kind: 'ok', text: 'Test message sent.' }
          : { kind: 'err', text: `Test failed: ${data.error || 'unknown error'}` },
      }));
    } finally {
      setNotifBusy(false);
    }
  };

  const disconnectChatIntegration = async (kind) => {
    if (!window.confirm(`Disconnect ${kind === 'slack' ? 'Slack' : 'Microsoft Teams'}? Notifications for that channel will stop immediately.`)) return;
    setNotifBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/api/chat-integrations/${kind}`, { method: 'DELETE' });
      if (res.ok) {
        setNotifIntegrations((list) => (list || []).filter((i) => i.kind !== kind));
        setNotifChannelMsg((m) => ({ ...m, [kind]: null }));
      }
    } finally {
      setNotifBusy(false);
    }
  };

  // Humanized "fired 5 min ago" — avoids pulling in a date lib for one
  // label. Resolution tops out at days because older values are rare
  // (integrations get re-tested if the user's troubleshooting).
  const relTime = (iso) => {
    if (!iso) return null;
    const diffMs = Date.now() - new Date(iso).getTime();
    if (diffMs < 0 || Number.isNaN(diffMs)) return null;
    const m = Math.round(diffMs / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
  };

  // Render helper — Notifications. Connected channels (email / Slack /
  // Teams / in-app) on top, then the per-event channel matrix. Used by
  // the Settings phase.
  const renderNotificationsContent = () => {
    if (notifLoadError) {
      return (
        <div className="settings-section">
          <div className="settings-section-title">Notifications</div>
          <div className="inline-error-banner" style={{ padding: 12, borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#ef4444', marginTop: 12 }}>
            {notifLoadError}
            <button type="button" className="integration-save-btn" onClick={loadNotificationSettings} style={{ marginLeft: 12 }}>Retry</button>
          </div>
        </div>
      );
    }
    if (!notifMeta || !notifPrefs || !notifIntegrations) {
      return (
        <div className="settings-section">
          <div className="settings-section-title">Notifications</div>
          <p className="profile-muted">Loading…</p>
        </div>
      );
    }

    const byKind = { slack: null, teams: null };
    (notifIntegrations || []).forEach((i) => { byKind[i.kind] = i; });

    // Helper to render a single chat-integration row — same shape for
    // Slack and Teams, just with channel-specific copy. Extracted to
    // avoid duplicating the connect/connected branches twice.
    const renderChatRow = (kind) => {
      const integ = byKind[kind];
      const label = kind === 'slack' ? 'Slack' : 'Microsoft Teams';
      const msg = notifChannelMsg[kind];
      const helpOpen = notifHelpOpen === kind;
      const formOpen = notifFormOpen === kind;
      const helpSteps = kind === 'slack'
        ? ['Go to api.slack.com/messaging/webhooks', "Click 'Create an app'", "Pick 'From scratch' + your workspace", "Enable 'Incoming Webhooks', add new webhook", 'Choose the channel', 'Copy the URL here']
        : ['In Teams, go to the channel where alerts should post', "Click the ⋮ (more) → 'Workflows'", "Search for 'Post to a channel when a webhook request is received'", 'Create it, copy the URL here'];
      return (
        <div className="notif-channel-row" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', gap: 16, textAlign: 'left' }}>
          <div style={{ flex: 1, textAlign: 'left' }}>
            <div style={{ fontWeight: 600 }}>{label}</div>
            {integ ? (
              <div className="profile-muted" style={{ fontSize: 13, marginTop: 4 }}>
                <span style={{ color: '#22c55e' }}>✓ Connected</span> · {integ.webhookUrlMasked || '…'}{' '}
                {integ.lastFiredAt && <>· last fired {relTime(integ.lastFiredAt)}</>}
                {integ.lastError && (
                  <span style={{ color: '#ef4444', display: 'block', marginTop: 2 }}>
                    ⚠ {integ.lastError}{integ.lastErrorAt && ` (${relTime(integ.lastErrorAt)})`}
                  </span>
                )}
              </div>
            ) : (
              <div className="profile-muted" style={{ fontSize: 13, marginTop: 4 }}>Not connected</div>
            )}
            {formOpen && (
              <div style={{ marginTop: 10 }}>
                {/* Collapsible help — defaults collapsed because the URL
                    is the only required input; help is a safety net for
                    the non-admin who's setting this up first time. */}
                <button
                  type="button"
                  className="profile-muted"
                  style={{ background: 'none', border: 'none', color: '#8b9eb0', cursor: 'pointer', padding: 0, fontSize: 13 }}
                  onClick={() => setNotifHelpOpen(helpOpen ? null : kind)}
                >
                  {helpOpen ? '▼' : '▶'} How do I get the webhook URL?
                </button>
                {helpOpen && (
                  <ol style={{ fontSize: 13, color: '#8b9eb0', paddingLeft: 20, marginTop: 6 }}>
                    {helpSteps.map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                )}
                <input
                  type="text"
                  className="auth-input"
                  placeholder="Paste the webhook URL"
                  value={notifFormUrl}
                  onChange={(e) => setNotifFormUrl(e.target.value)}
                  style={{ width: '100%', marginTop: 8 }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    type="button"
                    className="integration-save-btn"
                    onClick={() => saveChatIntegration(kind)}
                    disabled={notifBusy || !notifFormUrl.trim()}
                  >
                    {notifBusy ? 'Saving…' : 'Save & Test'}
                  </button>
                  <button
                    type="button"
                    className="profile-picture-remove-btn"
                    onClick={() => { setNotifFormOpen(null); setNotifFormUrl(''); setNotifChannelMsg((m) => ({ ...m, [kind]: null })); }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {msg && (
              <div style={{ fontSize: 13, marginTop: 8, color: msg.kind === 'ok' ? '#22c55e' : '#ef4444' }}>
                {msg.text}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {integ ? (
              <>
                <button type="button" className="integration-save-btn" onClick={() => testChatIntegration(kind)} disabled={notifBusy}>Test</button>
                <button type="button" className="profile-picture-remove-btn" onClick={() => disconnectChatIntegration(kind)} disabled={notifBusy}>Disconnect</button>
              </>
            ) : !formOpen && (
              <button
                type="button"
                className="integration-save-btn"
                onClick={() => { setNotifFormOpen(kind); setNotifFormUrl(''); setNotifChannelMsg((m) => ({ ...m, [kind]: null })); }}
              >
                Connect {label}
              </button>
            )}
          </div>
        </div>
      );
    };

    const channels = notifMeta.channels || ['email', 'slack', 'teams', 'in_app'];
    const events = notifMeta.events || [];
    // Connected state per channel — email + in_app are always on; Slack
    // and Teams track their integration row's enabled flag. Muted+
    // disabled checkboxes convey "you can't toggle this until the
    // channel is connected" without hiding the column (which would
    // make the matrix feel incomplete on first visit).
    const channelConnected = {
      email: true,
      in_app: true,
      slack: !!(byKind.slack && byKind.slack.enabled),
      teams: !!(byKind.teams && byKind.teams.enabled),
    };
    const channelLabel = { email: 'Email', slack: 'Slack', teams: 'Teams', in_app: 'Bell' };

    return (
      <div className="settings-section">
        <div className="settings-section-title">Notifications</div>
        <p className="settings-section-desc">
          Pick which channels get pinged for each event. Email + the in-app bell are always on; connect Slack or Teams below to enable those columns.
        </p>

        {/* Section A — connected channels. Email + in-app render as
            "always connected" rows so the user sees the full picture
            without a hidden assumption that they're on by default. */}
        <div style={{ marginTop: 16 }}>
          <div className="notif-channel-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div>
              <div style={{ fontWeight: 600 }}>Email</div>
              <div className="profile-muted" style={{ fontSize: 13, marginTop: 4 }}>{authUser?.email || ''}</div>
            </div>
            <div className="profile-muted" style={{ fontSize: 13 }}>Always connected</div>
          </div>
          {renderChatRow('slack')}
          {renderChatRow('teams')}
          <div className="notif-channel-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
            <div>
              <div style={{ fontWeight: 600 }}>In-app bell</div>
              <div className="profile-muted" style={{ fontSize: 13, marginTop: 4 }}>Shown in the top-bar bell.</div>
            </div>
            <div className="profile-muted" style={{ fontSize: 13 }}>Always connected</div>
          </div>
        </div>

        {/* Section B — event × channel matrix. Horizontal-scroll wrapper
            so narrow viewports don't break the grid. */}
        <div style={{ marginTop: 28 }}>
          <div className="settings-section-title" style={{ fontSize: 16 }}>What triggers a notification</div>
          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <table className="notif-matrix" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 13, color: '#8b9eb0', fontWeight: 600 }}>Event</th>
                  {channels.map((c) => (
                    <th key={c} style={{ padding: '8px 12px', fontSize: 13, color: '#8b9eb0', fontWeight: 600, textAlign: 'center', width: 80 }}>
                      {channelLabel[c] || c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => {
                  const row = notifPrefs[ev.key] || {};
                  return (
                    <tr key={ev.key} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                        <div style={{ fontWeight: 500 }}>{ev.label}</div>
                        {ev.description && (
                          <div className="profile-muted" style={{ fontSize: 12, fontStyle: 'italic', marginTop: 2 }}>{ev.description}</div>
                        )}
                      </td>
                      {channels.map((c) => {
                        const checked = !!row[c];
                        const disabled = !channelConnected[c];
                        return (
                          <td key={c} style={{ padding: '10px 12px', textAlign: 'center', verticalAlign: 'top' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={disabled}
                              onChange={() => toggleNotifPref(ev.key, c)}
                              title={disabled ? `Connect ${channelLabel[c]} to enable.` : undefined}
                              style={disabled ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  // Optimistic has-left-nav: apply the wide-container class the
  // moment a token exists in localStorage, not after /me resolves.
  // Previously the container was unstyled during bootstrap → page
  // painted narrow → /me resolved → class added → page snapped
  // wider. Reading the token synchronously on every render is cheap
  // (localStorage is an in-process read) and dodges the one-frame
  // flicker. Login / signup screens still render with no left nav —
  // the check keys off phase too.
  const hasStoredToken = !!authUser
    || (typeof window !== 'undefined'
        && !!window.localStorage
        && !!window.localStorage.getItem('trailercst_token'));
  const appShellClass = `app ${hasStoredToken && phase !== 'login' && phase !== 'signup' ? 'has-left-nav' : ''} ${navCollapsed ? 'nav-collapsed' : ''}`;

  // ── MFA enrollment gate (2026-08-11) ──────────────────────────────
  // When the company mandates 2FA, non-enrolled members get a
  // RESTRICTED session (JWT claim mfaEnroll: true) that can only reach
  // the enrollment endpoints — every other call 403s. Rendering the
  // normal shell on such a session produces a dashboard of empty
  // panels and errors with no explanation, so the gate replaces the
  // shell entirely with the one thing the session can do: enrol.
  // Early-return ABOVE the shell so no data loads even start.
  if (authToken && !bootstrapping && isMfaRestricted(authToken)) {
    return <MfaEnrollmentGate authToken={authToken} onSignOut={handleLogout} />;
  }

  return (
    <HelpProvider>
      {/* Tiny inner component that pushes the current phase's docs
          slug into the HelpContext so the Help menu's "Help on this
          page" link adapts. Per-detail surfaces (call detail, demo
          detail) can override with more specific slugs via their
          own useRegisterHelpSlug() hook calls. */}
      <PhaseSlugRegistrar phase={phase} />
    <div className={appShellClass}>
      {/* Background intentionally left blank — body's solid color
          (App.css .bg-dark) is the only layer. The old animated
          orbs + drifting dust particles were pulled 2026-04-20 to
          simplify the dashboard aesthetic + save the compositor
          work of animating 20+ absolutely-positioned elements
          every frame. */}

      {/* Primary navigation — persistent sidebar on desktop, hamburger
          drawer on mobile. Rendered whenever a token exists in
          localStorage (optimistic), even before /me resolves, so the
          sidebar is present on first paint and doesn't pop in after
          bootstrap completes. Hidden on the auth screens since no
          token exists there. */}
      {hasStoredToken && !authUser && bootstrapping ? (
        <LeftNav
          phase={phase}
          authUser={{ company: {} }}
          onNavigate={() => { /* no-op until /me resolves */ }}
          collapsed={navCollapsed}
          onToggleCollapse={toggleNavCollapsed}
        />
      ) : null}
      {authUser && !bootstrapping && phase !== 'login' && (
        <LeftNav
          phase={phase}
          authUser={authUser}
          upcomingMeetings={upcomingMeetings}
          upcomingMeta={upcomingCalendarMeta}
          collapsed={navCollapsed}
          onToggleCollapse={toggleNavCollapsed}
          onNavigate={(item) => {
            if (item.kind === 'phase') {
              setPhase(item.target);
              // Phase-specific lazy-loads mirror what the equivalent tab
              // click used to trigger when these surfaces lived inside
              // the profile modal.
              if (item.target === 'conversations') loadConversations();
              // 'reports' needs no imperative load — the phase effect
              // fetches the CURRENT tab (this call hardcoded top-demos
              // and produced a duplicate request).
              // 'library' needs no imperative load — the Trailers
              // screen's effect fetches with the active tab and filters.
              // Calling here would fire first, unfiltered, and race it.
              if (item.target === 'security') {
                loadTeamMembers();
                if (authUser?.company?.isAdmin) loadTeamInvites();
              }
              if (item.target === 'integrations') {
                loadIntegrations();
              }
              if (item.target === 'settings') {
                // Seed the two form inputs the Settings phase's Bot +
                // Consent sub-tabs need on first render, so the admin
                // doesn't see stale blanks.
                setBotNameInput(authUser?.company?.botDisplayName || '');
                setJurisdictionForm({
                  jurisdiction: authUser?.company?.consentJurisdiction || '',
                  privacyPolicyUrl: authUser?.company?.privacyPolicyUrl || '',
                });
                setJurisdictionError(null);
                // Default to Brand when entering the phase fresh.
                setSettingsPhaseTab('brand');
              }
            }
          }}
        />
      )}

      <div className="container">
        <TopBar
          authUser={authUser}
          authToken={authToken}
          onLogoClick={() => { if (authUser) setPhase('dashboard'); }}
          onResultClick={(result) => {
            // Hash-routed SPA — window.location.assign with a #hash
            // doesn't trigger phase switches, so dispatch into the
            // existing in-app navigation handlers per result type.
            // Demos → loadCompletedDemo (phase='completed' + hash sync).
            // Calls → setPhase('calls') + #/calls/<id> (CallsPhase reads
            //         the hash on mount via parseDetailHash).
            // Spaces → setPhase('buyer-spaces') + #/buyer-spaces/<id>.
            // Team → setPhase('security') + set highlightedMemberId so
            //        the team roster scrolls to + flashes the row.
            if (result.type === 'demo') {
              loadCompletedDemo(result.id);
            } else if (result.type === 'call') {
              const hash = `#/calls/${encodeURIComponent(result.id)}`;
              if (typeof window !== 'undefined' && window.location.hash !== hash) {
                window.history.replaceState(null, '', window.location.pathname + window.location.search + hash);
              }
              setPhase('calls');
            } else if (result.type === 'space') {
              const hash = `#/buyer-spaces/${encodeURIComponent(result.id)}`;
              if (typeof window !== 'undefined' && window.location.hash !== hash) {
                window.history.replaceState(null, '', window.location.pathname + window.location.search + hash);
              }
              setPhase('buyer-spaces');
            } else if (result.type === 'team') {
              // Set the highlight target BEFORE switching phase so the
              // Security phase's useEffect sees it on mount/first render.
              setHighlightedMemberId(result.id);
              setPhase('security');
            }
          }}
          rightSlot={authUser && (
            <>
              {/* Inbox (2026-08-12): Conversations moved OUT of the
                  left nav into the top ribbon — prospect replies are an
                  inbox, not a phase you "navigate" to, and the ribbon
                  is where always-on surfaces (bell, help, identity)
                  live. Same bell styling; unread count = threads with
                  prospect messages newer than the seller's last read.
                  The conversations PHASE is unchanged — this button is
                  its new (only) entry point; deep links still work. */}
              <button
                type="button"
                className={`notifications-bell ${conversationsUnreadCount > 0 ? 'has-unread' : ''}`}
                style={phase === 'conversations' ? { color: 'var(--room-accent, #6366f1)' } : undefined}
                onClick={() => {
                  setPhase('conversations');
                  loadConversations();
                }}
                aria-label={`Inbox${conversationsUnreadCount > 0 ? ` (${conversationsUnreadCount} unread)` : ''}`}
                title="Inbox — prospect conversations"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                {conversationsUnreadCount > 0 && (
                  <span className="notifications-badge" aria-hidden="true">
                    {conversationsUnreadCount > 9 ? '9+' : conversationsUnreadCount}
                  </span>
                )}
              </button>

              <div className="notifications-panel-wrap">
                <button
                  type="button"
                  className={`notifications-bell ${unreadNotifications > 0 ? 'has-unread' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setNotificationsOpen((v) => !v);
                    if (!notificationsOpen) loadNotifications();
                  }}
                  aria-label={`Notifications${unreadNotifications > 0 ? ` (${unreadNotifications} unread)` : ''}`}
                  title="Notifications"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  {unreadNotifications > 0 && (
                    <span className="notifications-badge" aria-hidden="true">
                      {unreadNotifications > 9 ? '9+' : unreadNotifications}
                    </span>
                  )}
                </button>

                {notificationsOpen && (
                  <div className="notifications-panel" role="dialog" aria-label="Notifications">
                    <div className="notifications-panel-head">
                      <strong>Notifications</strong>
                      {unreadNotifications > 0 && (
                        <button
                          type="button"
                          className="notifications-mark-all"
                          onClick={markAllNotificationsRead}
                        >
                          Mark all read
                        </button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <div className="notifications-empty">
                        You're all caught up.
                      </div>
                    ) : (
                      <ul className="notifications-list">
                        {notifications.map((n) => (
                          <li
                            key={n.id}
                            className={`notifications-item ${n.readAt ? 'is-read' : 'is-unread'}`}
                            onClick={() => openNotificationLink(n)}
                            onKeyDown={(e) => { if (e.key === 'Enter') openNotificationLink(n); }}
                            role="button"
                            tabIndex={0}
                          >
                            <div className="notifications-item-title">{n.title}</div>
                            {n.body && (
                              <div className="notifications-item-body">{n.body}</div>
                            )}
                            <div className="notifications-item-meta">
                              {formatDemoDate(n.createdAt)}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* Help menu — sits between notifications + profile.
                  Reads the current page's docs slug from HelpContext
                  (registered by PhaseSlugRegistrar above) and deep-
                  links the "Help on this page" item accordingly.
                  "Ask Winter" item dispatches the global event the
                  AskWinter component listens for, popping its
                  chat panel without prop-drilling. */}
              <HelpMenu
                onOpenWinter={() => {
                  if (typeof window !== 'undefined') {
                    window.dispatchEvent(new Event('trailercast:open-winter'));
                  }
                }}
              />

              {/* Profile menu (2026-08-12 audit): the avatar now opens a
                  short dropdown — identity, personal-settings links,
                  role-gated workspace links, theme, sign out — instead
                  of slamming the full modal. Menu items route into the
                  modal via openProfileSettings (same hydration as the
                  old direct-open path) or into the admin phases. */}
              <ProfileMenu
                authUser={{ ...authUser, companyName: displayCompanyName(authUser.companyName, 'Set up company') }}
                isPlaceholderCompany={isPlaceholderCompanyName(authUser.companyName)}
                theme={theme}
                onSetTheme={setTheme}
                onOpenSettingsTab={openProfileSettings}
                onNavigateAdmin={(dest) => {
                  if (dest === 'security') { setPhase('security'); }
                  else if (dest === 'billing') { setSettingsPhaseTab('billing'); setPhase('settings'); }
                  else { setSettingsPhaseTab('brand'); setPhase('settings'); }
                }}
                onLogout={handleLogout}
              />

              <div className="profile-container">
              {profileOpen && (
                <>
                  <div className="settings-backdrop" onClick={() => setProfileOpen(false)} />
                  <div className="settings-modal" role="dialog" aria-label="Settings">
                    <div className="settings-modal-header">
                      <div className="settings-modal-header-identity">
                        {authUser.profilePictureUrl ? (
                          <img
                            src={authUser.profilePictureUrl}
                            alt=""
                            className="settings-avatar-img"
                          />
                        ) : (
                          <div className="profile-avatar profile-avatar-lg">
                            {(authUser.firstName || authUser.username || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="settings-header-info">
                          <h3>
                            {authUser.firstName
                              ? `${authUser.firstName}${authUser.lastName ? ' ' + authUser.lastName : ''}`
                              : (authUser.fullName || authUser.username)}
                          </h3>
                          {isPlaceholderCompanyName(authUser.companyName) ? (
                            <button
                              type="button"
                              onClick={() => {
                                // Inside the settings modal already —
                                // jump straight to the Profile tab
                                // (single tab in the 'profile' group)
                                // so they can finish in one click.
                                setProfileTab('profile');
                              }}
                              title="Open Profile to finish setting up your company"
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                fontStyle: 'italic',
                                color: '#b45309', /* amber-700 */
                                fontFamily: 'inherit',
                                fontSize: 'inherit',
                                textDecoration: 'underline',
                                textUnderlineOffset: '2px',
                                textDecorationStyle: 'dotted',
                              }}
                            >
                              Company not set yet — finish setup
                            </button>
                          ) : (
                            <span>{authUser.companyName}</span>
                          )}
                        </div>
                      </div>
                      <button
                        className="settings-close-btn"
                        onClick={() => setProfileOpen(false)}
                        aria-label="Close settings"
                      >
                        ×
                      </button>
                    </div>

                    <div className="settings-modal-body">
                      {/* Left: vertical sidebar nav.
                          Post-2026-04-21 profile modal is personal only — it
                          holds Account, Profile, and Preferences. Admin
                          surfaces (Security & Permissions, Integrations) and
                          company-level config (Brand, AI Personalization) live
                          in their own phases now, reached from the left nav.

                          Sub-items render indented under their parent:
                            Account → History, Password
                            Preferences → AI Narration, Appearance

                          The parent itself is also a valid destination — Account
                          maps to the existing 'overview' tab, Preferences
                          defaults to 'narration'. */}
                      <nav className="settings-sidebar">
                        {(() => {
                          // 2026-08-12 profile-menu audit: Overview (stat
                          // grid — a dashboard fragment) and History
                          // (content — the Trailers phase's job) left the
                          // modal; the dropdown's identity header replaced
                          // Overview's "who am I" role. "Password" renamed
                          // to say what it actually holds — password AND
                          // two-factor (the old label is why 2FA was
                          // undiscoverable during the SOC 2 rollout).
                          const GROUPS = [
                            {
                              id: 'profile',
                              label: 'Profile',
                              defaultTab: 'profile',
                              tabs: ['profile'],
                              children: [],
                            },
                            {
                              id: 'security',
                              label: 'Password & security',
                              defaultTab: 'password',
                              tabs: ['password'],
                              children: [],
                            },
                            {
                              id: 'preferences',
                              label: 'Preferences',
                              defaultTab: 'narration',
                              tabs: ['narration', 'appearance'],
                              children: [
                                { id: 'narration',  label: 'AI Narration' },
                                { id: 'appearance', label: 'Appearance' },
                              ],
                            },
                            // Notifications — per-user surface for channels
                            // (email / Slack / Teams / in-app bell) plus the
                            // event × channel matrix. Lives in the user's
                            // profile modal (not the company Settings phase)
                            // because every teammate controls their own
                            // notifications; an admin shouldn't be the only
                            // person who can mute their own Slack alerts.
                            {
                              id: 'notifications',
                              label: 'Notifications',
                              defaultTab: 'notifications',
                              tabs: ['notifications'],
                              children: [],
                            },
                          ];
                          const handleTabSwitch = (tabId) => {
                            setProfileTab(tabId);
                            if (tabId === 'narration') loadAvailableVoices();
                            // Lazy-load the chat integrations + prefs matrix
                            // + meta the moment the user opens the tab; re-
                            // calling is cheap (3 small GETs) and keeps the
                            // UI fresh after a Slack/Teams disconnect-and-
                            // reconnect cycle.
                            if (tabId === 'notifications') loadNotificationSettings();
                          };
                          return GROUPS.map((group) => {
                            const groupActive = group.tabs.includes(profileTab);
                            const parentActive = profileTab === group.defaultTab;
                            return (
                              <div key={group.id} className={`settings-nav-group ${groupActive ? 'is-open' : ''}`}>
                                <button
                                  type="button"
                                  className={`settings-nav-item settings-nav-parent ${parentActive ? 'active' : ''}`}
                                  onClick={() => handleTabSwitch(group.defaultTab)}
                                >
                                  {group.label}
                                </button>
                                {groupActive && group.children.length > 0 && (
                                  <div className="settings-nav-children">
                                    {group.children.map((child) => (
                                      <button
                                        key={child.id}
                                        type="button"
                                        className={`settings-nav-item settings-nav-child ${profileTab === child.id ? 'active' : ''}`}
                                        onClick={() => handleTabSwitch(child.id)}
                                      >
                                        {child.label}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          });
                        })()}
                      </nav>

                      {/* Right: active section */}
                      <div className="settings-content">
                      {profileTab === 'profile' && (
                        <form className="settings-section" onSubmit={saveProfile}>
                          <div className="settings-section-title">Profile</div>

                          <div className="profile-picture-row">
                            <div className="profile-picture-preview">
                              {profileForm.profilePictureUrl ? (
                                <img src={profileForm.profilePictureUrl} alt="" />
                              ) : (
                                <div className="profile-picture-placeholder">
                                  {(profileForm.firstName || authUser.username || '?').charAt(0).toUpperCase()}
                                </div>
                              )}
                            </div>
                            <div className="profile-picture-actions">
                              <label className="profile-picture-upload-btn">
                                {profileForm.profilePictureUrl ? 'Change photo' : 'Upload photo'}
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleProfilePictureSelect(e.target.files?.[0])}
                                  style={{ display: 'none' }}
                                />
                              </label>
                              {profileForm.profilePictureUrl && (
                                <button
                                  type="button"
                                  className="profile-picture-remove-btn"
                                  onClick={removeProfilePicture}
                                >
                                  Remove
                                </button>
                              )}
                              <span className="profile-picture-hint">
                                PNG or JPG — resized to 256×256 automatically.
                              </span>
                            </div>
                          </div>

                          <div className="settings-row-2col">
                            <div className="auth-field">
                              <label>First name</label>
                              <input
                                type="text"
                                value={profileForm.firstName}
                                onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                                // Prefix placeholders with "e.g." so they
                                // read as hints, not pre-filled values.
                                // Several beta testers thought the
                                // placeholder text was their saved value
                                // and clicked Save without typing.
                                placeholder="e.g. Jane"
                                autoComplete="given-name"
                              />
                            </div>
                            <div className="auth-field">
                              <label>Last name</label>
                              <input
                                type="text"
                                value={profileForm.lastName}
                                onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                                placeholder="e.g. Smith"
                                autoComplete="family-name"
                              />
                            </div>
                          </div>

                          {/* Email — read-only display (2026-08-12 audit:
                              the signed-in email previously appeared
                              NOWHERE in the product). Change-email is a
                              real feature (re-verification, invite
                              identity) — not faked with an editable box. */}
                          <div className="auth-field">
                            <label>Email</label>
                            <input type="email" value={authUser.email || ''} disabled readOnly />
                            <span className="auth-field-hint">
                              This is your sign-in email. Need to change it? Contact support — it's tied to your team invites and sign-in.
                            </span>
                          </div>

                          {/* Job title flows into the "Prepared by" byline
                              on every share page and the signature on
                              every follow-up email. Keep it externally-
                              facing (what you put on LinkedIn), not the
                              internal functional bucket. */}
                          <div className="auth-field">
                            <label>Job title</label>
                            <input
                              type="text"
                              value={profileForm.jobTitle}
                              onChange={(e) => setProfileForm({ ...profileForm, jobTitle: e.target.value })}
                              placeholder="e.g. Senior Account Executive"
                              autoComplete="organization-title"
                              maxLength={150}
                            />
                            <span className="auth-field-hint">
                              {profileForm.jobTitle
                                ? `Currently: "${profileForm.jobTitle}". Appears on share page byline + follow-up signatures.`
                                : 'Appears on the share page byline and follow-up email signature.'}
                            </span>
                          </div>

                          <div className="auth-field">
                            <label>Email</label>
                            <input
                              type="email"
                              value={authUser.email || ''}
                              disabled
                              readOnly
                            />
                            <span className="auth-field-hint">
                              Email changes aren't self-serve yet — ping support if you need to move accounts.
                            </span>
                          </div>

                          {profileError && (
                            <div className="error-message"><span></span> {profileError}</div>
                          )}

                          <button
                            type="submit"
                            className={`generate-btn ready ${profileSaved ? 'is-saved' : ''}`}
                            disabled={profileSaving}
                          >
                            <span className="btn-text">
                              {profileSaving ? 'Saving…' : profileSaved ? 'Saved ✓' : 'Save profile'}
                            </span>
                          </button>
                        </form>
                      )}

                      {/* Narration tab — per-user voice + language.
                          Shares selectedVoiceId / selectedLanguage state
                          with the upload-phase modal, so whichever
                          surface the seller edits from, both reflect
                          the same preference. PATCH /profile persists
                          to users.preferred_voice_id and
                          preferred_language (not company) — two sellers
                          on the same company can have different voices. */}
                      {profileTab === 'narration' && (
                        <div className="settings-section">
                          <div className="settings-section-title">Narration voice</div>
                          <p className="settings-section-desc">
                            This is the voice that narrates <strong>your</strong> highlight
                            trailers. Each seller on your team picks their own — a personal
                            preference, not a company setting.
                          </p>

                          {voicesLoading && availableVoices.length === 0 ? (
                            <p className="profile-muted">Loading voices…</p>
                          ) : voicesError && availableVoices.length === 0 ? (
                            <p className="loader-error">
                              {voicesError}
                              <button type="button" className="loader-retry" onClick={() => loadAvailableVoices()}>Retry</button>
                            </p>
                          ) : (
                            <>
                              {/* Language picker — only relevant for Pro+
                                  where translation is unlocked. Placed
                                  above the voice grid so the seller
                                  picks language first. */}
                              {availableLanguages.length > 1 && (
                                <div className="auth-field">
                                  <label>Narration language</label>
                                  <select
                                    value={selectedLanguage}
                                    onChange={(e) => setSelectedLanguage(e.target.value)}
                                  >
                                    {availableLanguages.map((l) => (
                                      <option key={l.code} value={l.code}>
                                        {l.name}{l.nativeName && l.nativeName !== l.name ? ` — ${l.nativeName}` : ''}
                                      </option>
                                    ))}
                                  </select>
                                  <span className="auth-field-hint">
                                    {selectedLanguage === 'en'
                                      ? 'Default — narration is written in English.'
                                      : 'Narration will be translated before voice synthesis.'}
                                  </span>
                                </div>
                              )}

                              {/* Voice catalogue — every tier gets the full
                                  grid. Legacy 'free' accounts also see it;
                                  the backend quota check gates what they can
                                  actually generate, but the selection itself
                                  is always available. */}
                              <div className="voice-grid">
                                  {availableVoices.map((voice) => {
                                    const isActive = selectedVoiceId === voice.id;
                                    const genderIcon = voice.gender === 'female' ? '♀' : voice.gender === 'male' ? '♂' : '•';
                                    return (
                                      <button
                                        type="button"
                                        key={voice.id}
                                        className={`voice-card ${isActive ? 'active' : ''} ${voice.isCloned ? 'is-cloned' : ''}`}
                                        onClick={() => setSelectedVoiceId(voice.id)}
                                      >
                                        <div className="voice-card-header">
                                          <span className="voice-card-name">
                                            {voice.name}
                                            {voice.isCloned && (
                                              <span className="voice-card-your-badge">Your voice</span>
                                            )}
                                          </span>
                                          <span className={`voice-card-gender gender-${voice.gender || 'other'}`}>
                                            {genderIcon}
                                          </span>
                                        </div>
                                        {voice.description && (
                                          <p className="voice-card-desc">{voice.description}</p>
                                        )}
                                        {isActive && (
                                          <span className="voice-card-check" aria-hidden="true">✓</span>
                                        )}
                                      </button>
                                    );
                                  })}
                              </div>

                              {/* Voice cloning — available on every tier.
                                  The actual clone flow (audio upload +
                                  preview) still lives in the upload-phase
                                  narration modal because the file-picker
                                  UI doesn't fit cleanly here. This is just
                                  the breadcrumb. */}
                              <p className="auth-field-hint" style={{ marginTop: 14 }}>
                                {authUser && authUser.clonedVoiceId
                                  ? <>Your cloned voice <strong>{authUser.clonedVoiceName || ''}</strong> is active. To replace or delete it, use the narration picker from the upload screen.</>
                                  : <>Want to clone your own voice? Open the narration picker from the upload screen to upload a 30–60 second audio sample.</>
                                }
                              </p>

                              <button
                                type="button"
                                className="generate-btn ready"
                                style={{ marginTop: 16 }}
                                onClick={async () => {
                                  await saveNarrationPrefs({
                                    voiceId: selectedVoiceId,
                                    language: selectedLanguage,
                                  });
                                  showToast('Narration preferences saved.');
                                }}
                              >
                                <span className="btn-text">Save narration</span>
                              </button>
                            </>
                          )}
                        </div>
                      )}

                      {profileTab === 'appearance' && (
                        <div className="profile-appearance">
                          <p className="profile-muted">
                            Choose how TrailerCast looks. We'll remember your choice on this device.
                          </p>
                          <div className="theme-options">
                            <button
                              type="button"
                              className={`theme-option ${theme === 'light' ? 'active' : ''}`}
                              onClick={() => setTheme('light')}
                            >
                              <div className="theme-swatch theme-swatch-light">
                                <div className="theme-swatch-bar" />
                                <div className="theme-swatch-bar short" />
                              </div>
                              <div className="theme-option-label">
                                <strong>Light</strong>
                                <span>Pure white with bold indigo accents</span>
                              </div>
                              {theme === 'light' && <span className="theme-option-check">✓</span>}
                            </button>
                            <button
                              type="button"
                              className={`theme-option ${theme === 'dark' ? 'active' : ''}`}
                              onClick={() => setTheme('dark')}
                            >
                              <div className="theme-swatch theme-swatch-dark">
                                <div className="theme-swatch-bar" />
                                <div className="theme-swatch-bar short" />
                              </div>
                              <div className="theme-option-label">
                                <strong>Dark</strong>
                                <span>Deep charcoal with the original glow</span>
                              </div>
                              {theme === 'dark' && <span className="theme-option-check">✓</span>}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Sign-in methods (2026-08-12 connected-accounts):
                          which ways this account can authenticate. Google
                          state comes from users.google_signin_at — stamped
                          on each Google sign-in, so an existing Google
                          user shows "not connected" until their next
                          Google login (honest limitation of backfill-free
                          tracking). */}
                      {profileTab === 'password' && (
                        <div className="settings-section" style={{ marginBottom: 20 }}>
                          <div className="settings-section-title" style={{ fontSize: 14 }}>Sign-in methods</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--room-border, rgba(120,120,150,0.25))' }}>
                              <div>
                                <div style={{ fontSize: 13.5, fontWeight: 600 }}>Google</div>
                                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                  {authUser.googleSigninAt
                                    ? `Connected · last used ${formatDemoDate(authUser.googleSigninAt)}`
                                    : `Not connected — use "Continue with Google" on the sign-in page with ${authUser.email} to link it.`}
                                </div>
                              </div>
                              {authUser.googleSigninAt && (
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', background: 'rgba(22,163,74,0.1)', borderRadius: 8, padding: '3px 8px' }}>Connected</span>
                              )}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--room-border, rgba(120,120,150,0.25))' }}>
                              <div>
                                <div style={{ fontSize: 13.5, fontWeight: 600 }}>Email &amp; password</div>
                                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                  {authUser.googleSigninAt
                                    ? 'If you signed up with Google and never set a password, use "Forgot password?" on the sign-in page to create one.'
                                    : 'Change your password below.'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {profileTab === 'password' && (
                        <form className="profile-password-form" onSubmit={handleChangePassword}>
                          <div className="auth-field">
                            <label>Current password</label>
                            <input
                              type="password"
                              autoComplete="current-password"
                              value={passwordForm.currentPassword}
                              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                              required
                            />
                          </div>
                          <div className="auth-field">
                            <label>New password</label>
                            <input
                              type="password"
                              autoComplete="new-password"
                              value={passwordForm.newPassword}
                              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                              required
                              minLength={10}
                            />
                            <PasswordStrengthHint value={passwordForm.newPassword} />
                          </div>
                          <div className="auth-field">
                            <label>Confirm new password</label>
                            <input
                              type="password"
                              autoComplete="new-password"
                              value={passwordForm.confirmPassword}
                              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                              required
                              minLength={10}
                            />
                          </div>
                          {passwordError && (
                            <div className="error-message"><span></span> {passwordError}</div>
                          )}
                          {passwordSuccess && (
                            <div className="success-message"><span></span> Password updated</div>
                          )}
                          <button type="submit" className="generate-btn ready" disabled={passwordLoading}>
                            <span className="btn-text">
                              {passwordLoading ? 'Updating…' : 'Update password'}
                            </span>
                          </button>
                        </form>
                      )}

                      {/* Two-factor authentication — sits with the
                          password form because they're the same logical
                          family ("account security"). Self-contained:
                          fetches its own status + handles the enable /
                          disable / regenerate flows internally. */}
                      {profileTab === 'password' && authToken && (
                        <div style={{ marginTop: 16 }}>
                          <TwoFactorSetup authToken={authToken} />
                        </div>
                      )}

                      {/* Sign out everywhere (2026-08-12) — kills every
                          session on every device, including this one.
                          Backed by POST /api/auth/logout-all
                          (users.sessions_revoked_at vs JWT iat, enforced
                          centrally in requireAuth). Other devices die
                          within ≤60s; this one immediately via the
                          normal logout path. */}
                      {profileTab === 'password' && authToken && (
                        <div className="settings-section" style={{ marginTop: 16 }}>
                          <div className="settings-section-title" style={{ fontSize: 14 }}>Sessions</div>
                          <p className="profile-muted" style={{ marginTop: 4 }}>
                            Signed in on a shared or lost device? This signs you out of
                            TrailerCast on <strong>every</strong> device, including this one —
                            you'll sign back in with your password and 2FA code.
                          </p>
                          <button
                            type="button"
                            className="logout-btn"
                            disabled={logoutAllBusy}
                            onClick={async () => {
                              // eslint-disable-next-line no-alert
                              if (!window.confirm('Sign out of TrailerCast on every device, including this one?')) return;
                              setLogoutAllBusy(true);
                              try {
                                const r = await fetch(`${API_BASE}/api/auth/logout-all`, {
                                  method: 'POST',
                                  headers: { Authorization: `Bearer ${authToken}` },
                                });
                                if (!r.ok) throw new Error(`status ${r.status}`);
                                // Server has revoked everything — now drop
                                // this device's token through the normal
                                // logout path (clears state + deep links).
                                handleLogout();
                              } catch (err) {
                                console.error('[logout-all] failed:', err);
                                setLogoutAllBusy(false);
                                // eslint-disable-next-line no-alert
                                window.alert("Couldn't sign out everywhere. Check your connection and try again.");
                              }
                            }}
                          >
                            {logoutAllBusy ? 'Signing out…' : 'Sign out everywhere'}
                          </button>
                        </div>
                      )}

                      {/* Notifications tab body — reuses the same render
                          helper that powers the settings experience so
                          channel connection + event-matrix behavior stays
                          in one place. The fetch is kicked off by
                          handleTabSwitch the moment the nav button is
                          clicked; render function handles loading + error
                          states internally. */}
                      {profileTab === 'notifications' && renderNotificationsContent()}
                      </div> {/* /.settings-content */}
                    </div> {/* /.settings-modal-body */}

                    <div className="settings-modal-footer">
                      <button className="logout-btn" onClick={handleLogout}>
                        Log out
                      </button>
                    </div>
                  </div>
                </>
              )}
              </div>
            </>
          )}
        />

        <main className="main-card">
          {/* BOOTSTRAPPING — waiting for /auth/me to return.
              Two modes:
              (a) No stored token → full-width loading card so the
                  unauthenticated visitor doesn't see a blank screen
                  before the auth form renders.
              (b) Stored token → skip the loading card entirely. Any
                  visible loading UI during this window flashes from
                  "narrow centered" to "wide dashboard" when /me
                  resolves ~100-500ms later. Rendering nothing here
                  keeps the layout stable — the dashboard phase fills
                  in at full width on its first paint. */}
          {bootstrapping && !hasStoredToken && (
            <div className="processing-view">
              <div className="processing-animation">
                <div className="wave-container">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="wave-bar" style={{ animationDelay: `${i * 0.1}s` }} />
                  ))}
                </div>
              </div>
              <h2>Loading…</h2>
            </div>
          )}

          {/* AUTH PHASE — routed view */}
          {!bootstrapping && phase === 'login' && authView === 'login' && (
            <Login
              form={loginForm}
              setForm={setLoginForm}
              onSubmit={handleLogin}
              onSwitchToSignup={goToSignup}
              onForgotPassword={goToForgot}
              onGoogleSignIn={handleGoogleSignIn}
              error={authError}
              loading={authLoading}
            />
          )}

          {!bootstrapping && phase === 'login' && authView === 'signup1' && (
            <SignupStep1
              form={signupForm1}
              setForm={setSignupForm1}
              onSubmit={handleSignupStep1}
              onSwitchToLogin={goToLogin}
              onGoogleSignIn={handleGoogleSignIn}
              error={authError}
              loading={authLoading}
              invite={pendingInvite}
            />
          )}

          {!bootstrapping && phase === 'login' && authView === 'signup2' && (
            <SignupStep2
              form={signupForm2}
              setForm={setSignupForm2}
              onSubmit={handleSignupStep2}
              invitedCompanyName={pendingInvite ? pendingInvite.companyName : null}
              onSkip={handleSignupSkip}
              error={authError}
              loading={authLoading}
            />
          )}

          {!bootstrapping && phase === 'login' && authView === 'forgot' && (
            <ForgotPassword
              form={forgotForm}
              setForm={setForgotForm}
              onSubmit={handleForgotPassword}
              onBackToLogin={goToLogin}
              error={authError}
              loading={authLoading}
              submitted={forgotSubmitted}
            />
          )}

          {!bootstrapping && authView === 'reset' && (
            <ResetPassword
              form={resetForm}
              setForm={setResetForm}
              onSubmit={handleResetPassword}
              onBackToLogin={goToLogin}
              error={authError}
              loading={authLoading}
              success={resetSuccess}
            />
          )}

          {!bootstrapping && phase === 'login' && authView === 'twoFactor' && twoFactorChallenge && (
            <TwoFactorChallenge
              email={twoFactorChallenge.email}
              onVerify={handleTwoFactorVerify}
              onCancel={cancelTwoFactor}
              error={authError}
              loading={authLoading}
            />
          )}

          {/* DASHBOARD PHASE — landing page after login */}
          {!bootstrapping && phase === 'dashboard' && authUser && (() => {
            const tier = (authUser && authUser.tier) || 'free';
            // During the 14-day trial the effective cap is the trialDemoCap
            // (10 calls, any combination of demo + qualifying + meeting kinds)
            // not the tier's monthly limit. Mirrors the logic in
            // backend/utils/quota.js getEffectiveLimit. trialActive comes
            // from the DTO as a server-computed boolean.
            const companyMeta = (authUser && authUser.company) || {};
            const trialActive = !!companyMeta.trialActive;
            const trialEndsAt = companyMeta.trialEndsAt ? new Date(companyMeta.trialEndsAt) : null;
            const trialDaysLeft = trialEndsAt
              ? Math.max(0, Math.ceil((trialEndsAt - new Date()) / (1000 * 60 * 60 * 24)))
              : null;
            const tierMonthlyLimit = TIER_LIMITS[tier];
            const tierLimit = trialActive
              ? (companyMeta.trialDemoCap || 10)
              : tierMonthlyLimit;
            const tierLabel = trialActive
              ? 'Trial'
              : (TIER_DISPLAY[tier] || tier);
            const usedThisMonth = demoStats.completed || 0;
            const firstName = (authUser.fullName || '').split(' ')[0] || authUser.username || 'there';

            // Team library is a capacity feature — requires multiple
            // users, which Starter (single-seat) doesn't have. We HIDE
            // the Team tab for ineligible tiers rather than rendering
            // it locked; least-privilege UI means a Starter user
            // doesn't see the upsell hint at all.
            const libraryTier = (authUser && authUser.tier) || 'free';
            const canSeeTeamLibrary = ['pro', 'enterprise', 'beta'].includes(libraryTier);

            // Client-side filter for the sidebar — backend already scoped
            // the rows to personal or team, we just narrow them further by
            // search term. Matches filename OR prospectName, case-insensitive.
            const libraryFiltered = (() => {
              const q = (libraryQuery || '').trim().toLowerCase();
              if (!q) return libraryDemos;
              return libraryDemos.filter((d) => {
                const fn = (d.filename || '').toLowerCase();
                const pn = (d.prospectName || '').toLowerCase();
                return fn.includes(q) || pn.includes(q);
              });
            })();

            return (
              <div className="dashboard-layout dashboard-layout--single-column">
                {/* Library sidebar removed 2026-04-21 — Trailers
                    is now a top-level item in the left nav, Reports +
                    Conversations too. The dashboard is now a single
                    content column focused on recent activity + setup. */}
                {/* (dead {false && <aside>} block deleted 2026-07-22 — 183 lines) */}

              <div className="dashboard-view">
                {/* Email-verification banner — shows until the user clicks
                    the link in their verification email. Critical actions
                    (upload, send follow-up) are backend-gated behind
                    email_verified, so this isn't decorative: the user can't
                    progress until they verify. Legacy users were backfilled
                    as verified in the migration so this block is only seen
                    by brand-new accounts. */}
                {/* Stale-state fix (2026-08-27, found by an automated QA
                    pass): the verification link opens in a NEW tab, so this
                    tab's authUser snapshot never learns the click happened —
                    the banner outlived verification indefinitely. Refresh the
                    snapshot whenever this tab regains focus while unverified,
                    which is exactly the moment someone returns from their
                    inbox. */}
                {authUser && authUser.emailVerified === false && (
                  <div className="verify-banner">
                    <span className="verify-banner-icon"></span>
                    <div className="verify-banner-body">
                      <strong>Verify your email to start generating trailers.</strong>
                      <p>
                        We sent a verification link to <span className="verify-banner-email">{authUser.email}</span>.
                        Upload and send follow-ups will unlock once you click it.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="verify-banner-btn"
                      onClick={resendVerificationEmail}
                      disabled={resendingVerification}
                    >
                      {resendingVerification ? 'Sending…' : 'Resend email'}
                    </button>
                    <button
                      type="button"
                      className="verify-banner-btn"
                      onClick={() => refreshAuthUser()}
                      title="Already clicked the link? Refresh your status."
                    >
                      I've verified
                    </button>
                  </div>
                )}

                {/* Signal strip (2026-08-26). Replaced the greeting hero:
                    "Welcome back, you've created 37 trailers" answered
                    none of what-happened / so-what / now-what, and the
                    plan badge was billing information in the product's
                    most valuable pixels (it moved to the view-switcher
                    row; trial state stays visible there).

                    First-run keeps a welcome instead — a strip of zeros
                    for a brand-new user reads as broken, and the one
                    thing that user needs is the path to a first trailer. */}
                {demoStats.completed === 0 ? (
                  <div className="dashboard-welcome">
                    <div>
                      <h2>Welcome, <span className="dashboard-welcome-name">{firstName}</span></h2>
                      <p className="dashboard-welcome-sub">
                        {displayCompanyName(authUser.companyName, null)
                          ? `Ready to create your first trailer for ${displayCompanyName(authUser.companyName, null)}?`
                          : 'Ready to create your first trailer?'}
                      </p>
                    </div>
                  </div>
                ) : (() => {
                  const sig = dashboardInsights?.signals;
                  if (!sig) {
                    // Loading (or an older cached payload without
                    // signals): ghost tiles hold the layout so the strip
                    // doesn't pop in and shove the page down.
                    return dashboardInsightsLoading ? (
                      <div className="dashboard-signals" aria-hidden="true">
                        {[0, 1, 2, 3].map((i) => <div key={i} className="dashboard-signal dashboard-signal--ghost" />)}
                      </div>
                    ) : null;
                  }
                  const openConversations = () => { setPhase('conversations'); loadConversations(); };
                  // Same navigation the inbox rows use: hash first so
                  // BuyerSpaceDetail (which owns hash-routing) mounts
                  // straight onto the section, then the phase switch.
                  const goToRoomSection = (roomId, section) => {
                    const targetHash = `#/buyer-spaces/${roomId}/${section}`;
                    if (typeof window !== 'undefined') {
                      window.history.pushState(null, '', window.location.pathname + window.location.search + targetHash);
                    }
                    setPhase('buyer-spaces');
                  };
                  // Reports drills honor the dashboard scope: a company
                  // number must never drill into personal-scope data —
                  // the destination would not match the tile (found in
                  // the 2026-08-29 click-path audit).
                  const reportsScopeForDrill = (isCompanyAdmin && dashboardUserFilter === 'all') ? 'team' : 'personal';
                  const waiting = sig.waitingOnYou?.count ?? 0;
                  const buyers = sig.buyersActive;
                  const buyersDelta = buyers ? deltaChip(buyers.count, buyers.count - buyers.delta) : null;
                  const hot = sig.hottest;
                  const cold = sig.goingCold;
                  return (
                    <div className="dashboard-signals">
                      <button
                        type="button"
                        className={`dashboard-signal${waiting > 0 ? ' dashboard-signal--urgent' : ''}`}
                        onClick={openConversations}
                        title="Open Conversations inbox"
                      >
                        <span className="dashboard-signal-k">Waiting on you</span>
                        <span className="dashboard-signal-v">{waiting}</span>
                        <span className="dashboard-signal-s">
                          {waiting > 0
                            ? `prospect ${waiting === 1 ? 'thread' : 'threads'} awaiting your reply`
                            : 'nothing awaiting your reply'}
                        </span>
                      </button>
                      {buyers && (
                        <button
                          type="button"
                          className="dashboard-signal"
                          onClick={() => { setReportsScope(reportsScopeForDrill); setPhase('reports'); loadReportsTab('top-demos', reportsScopeForDrill); }}
                          title="Open Reports"
                        >
                          <span className="dashboard-signal-k">Buyers active this week</span>
                          <span className="dashboard-signal-v">
                            {buyers.count}
                            {buyersDelta && buyersDelta.dir !== 'flat' && (
                              <span className={`dashboard-signal-chip dashboard-signal-chip--${buyersDelta.dir}`}>{buyersDelta.label}</span>
                            )}
                          </span>
                          <span className="dashboard-signal-s">vs. the week before</span>
                        </button>
                      )}
                      {hot && (
                        <button
                          type="button"
                          className="dashboard-signal dashboard-signal--hot"
                          onClick={() => (hot.roomId ? goToRoomSection(hot.roomId, 'conversations') : openConversations())}
                          title={hot.roomId ? "Open this deal's conversation" : "Open Conversations inbox"}
                        >
                          <span className="dashboard-signal-k">Hottest right now</span>
                          <span className="dashboard-signal-v dashboard-signal-v--text">
                            {hot.who}{hot.deal ? ` — ${hot.deal}` : ''} watched {hot.watchPct}%
                          </span>
                          <span className="dashboard-signal-s">
                            {hot.when}
                            {/* A lost deal only lands here when the buyer
                                came back AFTER the close (backend rule,
                                utils/dealState.js). Say so, or it reads as
                                the "still hot after I closed it" bug. */}
                            {hot.dealStatus === 'lost'
                              ? ` · marked lost ${hot.closedAgo || 'recently'}, still watching`
                              : (hot.replied ? '' : ' · no reply sent yet')}
                          </span>
                        </button>
                      )}
                      {cold && cold.count > 0 && cold.top && (
                        <button
                          type="button"
                          className="dashboard-signal dashboard-signal--cold"
                          onClick={() => (cold.top.roomId ? goToRoomSection(cold.top.roomId, 'engagement') : setPhase('buyer-spaces'))}
                          title={cold.top.roomId ? `Open ${cold.top.name} — engagement` : "Open Decision Rooms"}
                        >
                          <span className="dashboard-signal-k">Going cold</span>
                          <span className="dashboard-signal-v">
                            {cold.count}
                            <span className="dashboard-signal-chip dashboard-signal-chip--warn">{cold.top.daysSilent}d silent</span>
                          </span>
                          <span className="dashboard-signal-s">{cold.top.name} — no buyer visit lately</span>
                        </button>
                      )}
                    </div>
                  );
                })()}

                {/* Dashboard view switcher — pills + admin user filter.
                    Mirrors the Reports tab pattern but flips the whole
                    dashboard instead of an inner widget. Pills persist
                    to the URL hash so refresh stays on the chosen view. */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  flexWrap: 'wrap', marginBottom: 16,
                }}>
                  {/* Plan badge, relocated from the hero (2026-08-26):
                      billing info doesn't belong in the page's most
                      valuable pixels, but trial state + quota must stay
                      visible — this row is its new home. order:99 +
                      marginLeft:auto seat it at the right edge without
                      moving it after the pills in the DOM. */}
                  <button
                    type="button"
                    className={`dashboard-tier-badge dashboard-tier-badge--btn tier-${trialActive ? 'trial' : tier}`}
                    style={{ order: 99, marginLeft: 'auto' }}
                    onClick={openPlanContact}
                    title="Talk to us about your plan"
                  >
                    {trialActive
                      ? `Trial · ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left${tierLimit != null ? ` · ${usedThisMonth}/${tierLimit} demos` : ''}`
                      : `${tierLabel} plan`}
                  </button>
                  <div style={{
                    display: 'inline-flex', padding: 4, gap: 4,
                    background: 'var(--room-border-soft, #f1f5f9)', borderRadius: 12,
                  }}>
                    {[
                      { id: 'overview', label: 'Overview' },
                      { id: 'deal-cycle', label: 'Deal cycle' },
                      // Impact = the renewal artifact (2026-08-05 ROI pack):
                      // reach, watch time, time saved, outcomes, threading.
                      { id: 'impact', label: 'Impact' },
                      // Nav merge (2026-08-06): Reports moved from a
                      // top-level nav slot into a tab here — one analytics
                      // door. Legacy phase deep-links still render.
                      { id: 'reports', label: 'Reports' },
                      // QBR briefs' Dashboards residency lasted one day:
                      // a generated document in a strip of live-analytics
                      // views read as a category error. Its home is the
                      // Coaching tab now — this pill renders only while
                      // that flag is off, so the brief always has exactly
                      // one visible home (the dashboardView==='qbr' branch
                      // below stays for legacy #dashboard=qbr hashes).
                      ...(authUser?.company?.featureFlags?.coaching_tab
                        ? [] : [{ id: 'qbr', label: 'QBR briefs' }]),
                      ...(isCompanyAdmin ? [{ id: 'team-performance', label: 'Team performance' }] : []),
                    ].map((v) => {
                      const active = dashboardView === v.id;
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => {
                            setDashboardView(v.id);
                            if (typeof window !== 'undefined') {
                              const target = `#dashboard=${v.id}`;
                              if (window.location.hash !== target) {
                                window.history.replaceState(null, '',
                                  window.location.pathname + window.location.search + target);
                              }
                            }
                          }}
                          style={{
                            padding: '8px 16px', borderRadius: 8,
                            border: 'none', cursor: 'pointer',
                            background: active ? 'var(--room-card, #fff)' : 'transparent',
                            color: active ? 'var(--room-text-strong, #0f172a)' : 'var(--room-text-muted, #64748b)',
                            fontWeight: active ? 600 : 500, fontSize: 13,
                            fontFamily: 'inherit',
                            boxShadow: active ? '0 1px 3px rgba(15, 23, 42, 0.08)' : 'none',
                            transition: 'background 0.15s ease, color 0.15s ease',
                          }}
                        >
                          {v.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Period selector (Tier-2, 2026-07-22) — only shown on
                      the two period-aware child views; Overview's funnel
                      is quarter-anchored server-side. */}
                  {dashboardView !== 'overview' && (
                    <div style={{ display: 'inline-flex', gap: 4, padding: 3, borderRadius: 10, background: 'var(--room-border-soft, #f1f5f9)', border: '1px solid var(--room-border, #e2e8f0)' }}>
                      {[['7d', '7d'], ['30d', '30d'], ['90d', '90d'], ['quarter', 'Qtr']].map(([val, label]) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setDashboardPeriod(val)}
                          aria-pressed={dashboardPeriod === val}
                          style={{
                            padding: '5px 10px', borderRadius: 7, border: 'none',
                            background: dashboardPeriod === val ? 'var(--room-card, #fff)' : 'transparent',
                            color: dashboardPeriod === val ? 'var(--room-text-strong, #0f172a)' : 'var(--room-text-muted, #64748b)',
                            fontWeight: 600, fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
                            boxShadow: dashboardPeriod === val ? '0 1px 3px rgba(15,23,42,0.1)' : 'none',
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* User filter — admin only, hidden on team-performance
                      (that view is intentionally cross-team). Renders
                      immediately with just "Whole company" so it's
                      visible on first paint; rep options populate when
                      the teamMembers roster fetch lands (~200-800ms
                      later). Avoids the previous "filter pops in late"
                      issue where the entire dropdown was gated on the
                      roster being non-empty. */}
                  {isCompanyAdmin && dashboardView !== 'team-performance' && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)' }}>Showing:</span>
                      <select
                        value={dashboardUserFilter || ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDashboardUserFilter(v ? Number(v) : null);
                        }}
                        disabled={teamMembers.length === 0}
                        style={{
                          padding: '7px 10px', borderRadius: 8,
                          border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)',
                          fontSize: 13, fontFamily: 'inherit',
                          color: 'var(--room-text-body, #334155)',
                          cursor: teamMembers.length === 0 ? 'wait' : 'pointer',
                          opacity: teamMembers.length === 0 ? 0.7 : 1,
                        }}
                      >
                        <option value="">Whole company</option>
                        {teamMembers.map((m) => (
                          <option key={m.id} value={m.id}>
                            {[m.firstName, m.lastName].filter(Boolean).join(' ') || m.email}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* QBR BRIEFS branch — demoted from top-level nav
                    (2026-08-06): a quarterly ritual doesn't need a daily
                    slot. Same component the old phase rendered. */}
                {dashboardView === 'qbr' && (
                  <QbrBriefsPhase
                    authToken={authToken}
                    currentUserId={authUser?.id || null}
                    isCompanyAdmin={!!(authUser?.company?.isAdmin)}
                  />
                )}

                {/* IMPACT report branch — the ROI rollup (2026-08-05). */}
                {dashboardView === 'impact' && (
                  <ImpactReport
                    authToken={authToken}
                    authFetch={authFetch}
                    isCompanyAdmin={isCompanyAdmin}
                  />
                )}

                {/* DEAL CYCLE dashboard branch */}
                {dashboardView === 'deal-cycle' && (
                  <DealCycleDashboard
                    authToken={authToken}
                    authFetch={authFetch}
                    period={dashboardPeriod}
                    userId={dashboardUserFilter}
                    onOpenSpace={(spaceId) => {
                      setPhase('buyer-spaces');
                      if (typeof window !== 'undefined') {
                        window.location.hash = `#/buyer-spaces/${spaceId}`;
                      }
                    }}
                    // Funnel rows + the open-pipeline tile drill into
                    // their canonical phase. Library is the closest
                    // match for "all demos"; buyer-spaces is the home
                    // for Decision Rooms (open + closed).
                    onNavigate={(target) => {
                      if (target === 'qualifying-calls' || target === 'calls') setPhase('calls');
                      else if (target === 'library') setPhase('library');
                      else if (target === 'buyer-spaces') {
                        setPhase('buyer-spaces');
                        if (typeof window !== 'undefined') {
                          window.location.hash = '#/buyer-spaces';
                        }
                      }
                    }}
                  />
                )}

                {/* TEAM PERFORMANCE dashboard branch (admin-only) */}
                {dashboardView === 'team-performance' && (
                  <TeamPerformanceDashboard
                    authToken={authToken}
                    authFetch={authFetch}
                    period={dashboardPeriod}
                    currentUserId={authUser?.id}
                    // "Waiting on you" self-row → the actual next step
                    // (your inbox), not a rep filter (design audit fold).
                    onOpenInbox={() => { setPhase('conversations'); loadConversations(); }}
                    // Click a rep row → flip to Deal Cycle filtered to
                    // that rep. The user-filter dropdown also reflects
                    // the new selection. Click a top-trailer → load
                    // the demo via the existing pipeline.
                    onSelectRep={(userId) => {
                      setDashboardUserFilter(userId);
                      setDashboardView('deal-cycle');
                      if (typeof window !== 'undefined') {
                        window.history.replaceState(null, '',
                          window.location.pathname + window.location.search + '#dashboard=deal-cycle');
                      }
                    }}
                    onOpenDemo={loadCompletedDemo}
                  />
                )}

                {/* OVERVIEW (existing dashboard content) — wrapped in a
                    fragment so the switcher can flip between views. The
                    trial banner + every original dashboard section
                    render only on this branch. */}
                {dashboardView === 'overview' && (<>

                {/* Post-trial paywall banner. Renders when an admin's
                    free trial has ended AND they don't yet have a paid
                    subscription. Hidden during the trial itself
                    (changed 2026-05-07 — trial requires no card; we
                    only nag once trial expires) and for invited
                    members + already-subscribed accounts.

                    Comped tiers are ALSO exempt — beta (founder-assigned
                    early adopters) and enterprise (contractually billed,
                    no Stripe self-serve) never require a self-serve card.
                    This mirrors backend/utils/quota.js requiresCardCollection,
                    which exempts both tiers server-side. The 2026-05-15
                    single-tier migration dropped the enterprise check HERE
                    but left it exempt in the backend, and beta was never
                    checked here at all — so comped admins saw a false
                    "trial ended" paywall while the backend happily let them
                    keep uploading. Re-added 2026-05-21 to realign UI<>backend. */}
                {(() => {
                  const isAdmin = !!authUser?.company?.isAdmin;
                  const hasSub = !!authUser?.company?.hasSubscription;
                  if (!isAdmin || hasSub) return null;
                  if (tier === 'beta' || tier === 'enterprise') return null; // comped — see note above
                  if (trialActive) return null; // trial in progress; nothing to nag about
                  return (
                    <div className="card-required-banner">
                      <div className="card-required-banner-body">
                        {/* trialEndsAt null = the trial never STARTED
                            (signup checkout was skipped) — telling a
                            brand-new account its trial "has ended" reads
                            as a billing bug, and an automated QA pass
                            reported it as exactly that (2026-08-27). */}
                        <strong>{companyMeta.trialEndsAt
                          ? 'Your free trial has ended — get in touch to keep going.'
                          : 'Get in touch to get started.'}</strong>
                        <span>
                          Reach out and we&apos;ll get your team set up on a plan so you can keep uploading demos and using bots. Existing recordings stay accessible.
                        </span>
                      </div>
                      <button
                        type="button"
                        className="card-required-banner-cta"
                        onClick={openPlanContact}
                      >
                        Contact us →
                      </button>
                    </div>
                  );
                })()}

                {/* Trial banner. Once a user is signed up they DO see
                    their trial window — public copy never showed it,
                    but in-app surface is fine. Auto-hides once the
                    trial flips over to the paid TrailerCast tier
                    (Stripe webhook updates company.tier + clears the
                    trial window). */}
                {trialActive && (
                  <div className="trial-banner">
                    <div className="trial-banner-body">
                      <strong>
                        You're on the free trial. {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} left.
                      </strong>
                      <span>
                        {usedThisMonth} of {tierLimit} trial demos used. Every product feature is unlocked, recordings are unlimited. When the trial ends, get in touch from Settings → Billing to keep going.
                      </span>
                    </div>
                  </div>
                )}

                {/* Setup checklist — 4-step self-service onboarding card.
                    Auto-hides once every item is done. Replaces the
                    older single-item "onboarding reminder" banner
                    because self-service learning is a multi-step
                    path, not one task. Admin-only: items like
                    brand / personalize are company-wide, members
                    can't edit them from this surface. */}
                {(() => {
                  const isAdmin = authUser.company && authUser.company.isAdmin;
                  const brand = (authUser.company && authUser.company.brand) || {};
                  const checklistItems = [
                    {
                      id: 'personalize',
                      label: 'Personalize your AI editor',
                      desc: 'Answer 5 questions about what you sell so every trailer + follow-up is tuned to your product and ICP.',
                      done: !!(authUser.company && authUser.company.onboarded),
                      actionLabel: 'Set up',
                      action: () => { setOnboardingModalOpen(true); setOnboardingDismissed(false); },
                      adminOnly: true,
                    },
                    {
                      id: 'profile',
                      label: 'Complete your profile',
                      desc: 'Your name, job title, and photo appear on every share page and follow-up email.',
                      done: !!(authUser.firstName && authUser.lastName && authUser.jobTitle && authUser.profilePictureUrl),
                      actionLabel: 'Set up',
                      action: () => { setProfileTab('profile'); setProfileOpen(true); },
                      adminOnly: false,
                    },
                    {
                      id: 'brand',
                      label: 'Add your brand',
                      desc: 'Logo, company name, and colors flow onto every trailer so prospects see your brand, not ours.',
                      done: !!(brand.companyName && brand.logoUrl),
                      actionLabel: 'Set up',
                      // Brand lives in the Settings phase (Brand sub-tab)
                      // post-UI-restructure — the modal no longer has a
                      // brand tab. Navigate to the phase + land on the
                      // Brand pill directly.
                      action: () => {
                        setSettingsPhaseTab('brand');
                        setPhase('settings');
                      },
                      adminOnly: true,
                    },
                    {
                      id: 'calendar',
                      label: 'Connect your calendar',
                      desc: 'Auto-schedule a branded bot to every Zoom, Meet, or Teams demo on your schedule.',
                      done: !!(oauthStatus && Array.isArray(oauthStatus.connections) && oauthStatus.connections.length > 0),
                      actionLabel: 'Connect',
                      // Integrations is a phase now, not a modal tab.
                      action: () => {
                        setPhase('integrations');
                        loadIntegrations();
                      },
                      adminOnly: false,
                    },
                    {
                      id: 'crm',
                      label: 'Connect your CRM',
                      desc: 'Auto-link prospects to deals + sync deal status onto every demo card. HubSpot + Salesforce supported.',
                      // crmStatus shape: { providers: { hubspot: { connected, ... }, salesforce: { ... } } }
                      // Done when at least one provider is connected.
                      done: !!(crmStatus && crmStatus.providers
                        && Object.values(crmStatus.providers).some((p) => p && p.connected)),
                      actionLabel: 'Connect',
                      action: () => {
                        setPhase('integrations');
                        loadIntegrations();
                      },
                      adminOnly: true,
                    },
                  ];
                  // Filter items that this user isn't the right role for.
                  // Members see profile + calendar only; admins see all 4.
                  const visible = checklistItems.filter((it) => isAdmin || !it.adminOnly);
                  const doneCount = visible.filter((it) => it.done).length;
                  const totalCount = visible.length;
                  // Don't render anything once everything in-scope is done.
                  if (doneCount === totalCount) return null;
                  // Persistent user dismissal — see setupChecklistHidden state.
                  if (setupChecklistHidden) return null;
                  const pct = Math.round((doneCount / totalCount) * 100);
                  return (
                    <div className="setup-checklist">
                      <div className="setup-checklist-header">
                        <div>
                          <h3>Finish setting up TrailerCast</h3>
                          <p>
                            These steps turn TrailerCast from a generic AI editor
                            into one tuned for your team. {doneCount} of {totalCount} complete.
                          </p>
                        </div>
                        <div className="setup-checklist-progress-wrap" aria-hidden="true">
                          <div className="setup-checklist-progress">
                            <div className="setup-checklist-progress-fill" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="setup-checklist-progress-label">{doneCount}/{totalCount}</span>
                        </div>
                        <button
                          type="button"
                          className="setup-checklist-hide"
                          onClick={dismissSetupChecklist}
                          aria-label="Hide setup checklist"
                          title="Hide setup checklist"
                        >
                          Hide
                        </button>
                      </div>
                      <ul className="setup-checklist-items">
                        {visible.map((it) => (
                          <li
                            key={it.id}
                            className={`setup-checklist-item ${it.done ? 'is-done' : ''}`}
                          >
                            <span className={`setup-checklist-check ${it.done ? 'is-done' : ''}`} aria-hidden="true">
                              {it.done ? (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              ) : null}
                            </span>
                            <div className="setup-checklist-item-body">
                              <strong>{it.label}</strong>
                              <span>{it.desc}</span>
                            </div>
                            {!it.done && (
                              <button
                                type="button"
                                className="setup-checklist-item-cta"
                                onClick={it.action}
                              >
                                {it.actionLabel}
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}

                {showProfileCompletion && authUser && !authUser.profileCompleted && (
                  <div className="profile-banner">
                    <span className="profile-banner-icon"></span>
                    <div className="profile-banner-text">
                      <strong>Complete your profile</strong>
                      <span>Tell us a bit about you and your team to personalize TrailerCast.</span>
                    </div>
                    <button
                      className="profile-banner-cta"
                      onClick={() => {
                        setSignupForm2({
                          fullName: authUser.fullName || '',
                          companyName: authUser.companyName && !authUser.companyName.startsWith('__pending__') ? authUser.companyName : '',
                          role: authUser.role || '',
                          companySize: authUser.companySize || '',
                          useCase: authUser.useCase || '',
                          joinCode: '',
                        });
                        setAuthView('signup2');
                        setPhase('login');
                      }}
                    >
                      Complete profile
                    </button>
                    <button
                      className="profile-banner-dismiss"
                      onClick={() => {
                        setShowProfileCompletion(false);
                        // One-time toast pointing the user at where the
                        // banner CTA actually lives now that it's gone.
                        // Keyed per-user-id so a different person on the
                        // same browser still gets the reminder once.
                        // Using localStorage rather than the audit log
                        // because this is a UI affordance, not a
                        // security-relevant event.
                        try {
                          const flagKey = `profile_reminder_seen_${authUser?.id || 'anon'}`;
                          const seen = window.localStorage && window.localStorage.getItem(flagKey);
                          if (!seen) {
                            window.localStorage.setItem(flagKey, '1');
                            showToast('Finish profile setup any time from your avatar menu → Profile.');
                          }
                        } catch (_) { /* private-mode localStorage may throw */ }
                      }}
                      aria-label="Dismiss"
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Stats row REMOVED (2026-07-26 design audit): the first
                    full-width band was account chrome — usage count,
                    all-time total (vanity), and a plan card duplicating
                    the tier badge two inches up. Deal momentum (the
                    insights row below) now leads. Trial usage lives in
                    the tier badge + trial banner; plan management moved
                    into the (now clickable) tier badge. */}

                {/* Insights row — pipeline funnel on the left, a
                    "Needs attention" action queue on the right. Replaces
                    the older CTA-pill + pulse-tile strip which was more
                    "sales rep action bar" than dashboard (the "New
                    trailer" entry point still lives on the left
                    nav + the Library header). Funnel is the passive
                    "how am I doing" view; action queue is the "what
                    should I click next" view. Both run on the real
                    /api/dashboard/insights aggregate. */}
                {dashboardInsightsError && !dashboardInsightsLoading && (
                  <p className="loader-error" style={{ marginBottom: 16 }}>
                    {dashboardInsightsError}
                    <button type="button" className="loader-retry" onClick={() => loadDashboardInsights()}>Retry</button>
                  </p>
                )}
                <div className="dashboard-insights-row">
                  {/* ─── Pipeline funnel ────────────────────────────
                      Horizontal stacked bars. Bar width is proportional
                      to the first stage so drop-off is visually
                      obvious; conversion % on the right is stage-to-
                      previous-stage, which matches how sales orgs read
                      a funnel. Each stage is a button that drills into
                      the relevant surface with filters pre-applied. */}
                  {(() => {
                    // Funnel data sourced from GET /api/dashboard/insights
                    // (loaded by the dashboard-mount useEffect above).
                    // While the request is in flight or if it fails, we
                    // render zeros + an empty-state hint inside the funnel
                    // body — the JSX shape is identical so the layout
                    // stays stable through the load. Once a seller has
                    // real data, the funnel reflects their actual quarter.
                    const funnel = dashboardInsights?.funnel || {};
                    const stage1 = funnel.shares || 0;   // shares sent
                    const stage2 = funnel.opened || 0;   // opened
                    const stage3 = funnel.watched || 0;  // watched ≥50%
                    const stage4 = funnel.replied || 0;  // replied
                    const stage5 = funnel.booked || 0;   // cta_clicks (honest label since 2026-07-26)

                    const now = new Date();
                    const qStartMonth = Math.floor(now.getMonth() / 3) * 3;
                    const qStart = new Date(now.getFullYear(), qStartMonth, 1);
                    const qEnd = new Date(now.getFullYear(), qStartMonth + 3, 0);
                    const fmt = (d) => d.toISOString().slice(0, 10);

                    const drillToLibraryQuarter = () => {
                      setFilterDateFrom(fmt(qStart));
                      setFilterDateTo(fmt(qEnd));
                      // Carry the dashboard admin filter — drilling from
                      // a rep-scoped number into an all-reps list made the
                      // counts disagree (2026-08-29 audit).
                      setFilterCreatorId(dashboardUserFilter !== 'all' ? dashboardUserFilter : 'all');
                      setLibraryQuery('');
                      setPhase('library');
                      // No imperative load: the Trailers effect fires on
                      // the phase + filter change and sends the dates.
                    };
                    // Recomputed here — the strip and the funnel live in
                    // separate IIFEs, and both must honor the dashboard
                    // scope for the same reason.
                    const reportsScopeForDrill = (isCompanyAdmin && dashboardUserFilter === 'all') ? 'team' : 'personal';
                    const drillToReports = (tab) => {
                      setReportsScope(reportsScopeForDrill);
                      setPhase('reports');
                      loadReportsTab(tab, reportsScopeForDrill);
                    };
                    const drillToConversations = () => {
                      setPhase('conversations');
                      loadConversations();
                    };

                    // Stage-to-previous conversion %, shown as a small
                    // badge on rows 2..N. Stage 1 has nothing to
                    // convert from so its badge slot stays empty.
                    const convPct = (curr, prev) => (prev > 0 ? Math.round((curr / prev) * 100) : 0);

                    const stages = [
                      {
                        key: 'shares',
                        label: 'Sent',
                        value: stage1,
                        prev: null,
                        onClick: drillToLibraryQuarter,
                        hint: 'Open Trailers for this quarter',
                      },
                      {
                        key: 'opened',
                        label: 'Opened',
                        value: stage2,
                        prev: stage1,
                        onClick: () => drillToReports('top-demos'),
                        hint: 'Open Reports — top demos',
                      },
                      {
                        key: 'watched',
                        label: 'Engaged',
                        value: stage3,
                        prev: stage2,
                        onClick: () => drillToReports('top-demos'),
                        hint: 'Watched at least half — open Reports',
                      },
                      {
                        key: 'replied',
                        label: 'Responded',
                        value: stage4,
                        prev: stage3,
                        onClick: drillToConversations,
                        hint: 'Open Conversations inbox',
                      },
                      // CTA clicks left the funnel (2026-08-26): a click
                      // does not require having responded, so it was never
                      // a stage — a strict sequence it doesn't obey made
                      // the stage-to-stage rates fiction. It stays visible
                      // in the card's footnote below.
                    ];

                    const maxVal = Math.max(1, stage1);
                    // Empty state — no shares this quarter. Render a
                    // helpful "share your first trailer" hint instead of
                    // five rows of "0" which reads as broken. Loading
                    // case (insights null + currently loading) shares
                    // the same hidden body so the panel doesn't flicker
                    // numbers in then out.
                    const isEmpty = stage1 === 0;

                    return (
                      <div className="dashboard-funnel">
                        <div className="dashboard-widget-header">
                          <h3>Engagement funnel</h3>
                          <span className="dashboard-widget-sub">
                            {dashboardInsightsLoading && !dashboardInsights
                              ? 'Loading…'
                              : 'Share links · this quarter'}
                          </span>
                        </div>
                        {isEmpty ? (
                          <div className="dashboard-funnel-empty">
                            <p>
                              No shares yet this quarter. Once you start sharing trailers with
                              prospects, you'll see your full pipeline funnel here —
                              from shares sent through meetings booked.
                            </p>
                            <button
                              type="button"
                              className="dashboard-funnel-empty-cta"
                              onClick={drillToLibraryQuarter}
                            >
                              Open Trailers →
                            </button>
                          </div>
                        ) : (
                        <div className="dashboard-funnel-body">
                          {stages.map((s) => {
                            const widthPct = Math.max(2, Math.round((s.value / maxVal) * 100));
                            // Percentages are earned by volume: below
                            // RATE_MIN upstream, "1 of 1 — 100%" and
                            // "0 of 1 — 0%" are division-by-one noise
                            // that reads as failure when the truth is
                            // just "early". Honest counts only.
                            const conv = s.prev !== null && rateVisible(s.prev) ? convPct(s.value, s.prev) : null;
                            const weak = conv !== null && conv < 50;
                            const delta = s.key === 'shares' && dashboardInsights?.prevFunnel
                              ? deltaChip(s.value, dashboardInsights.prevFunnel.shares)
                              : null;
                            return (
                              <button
                                key={s.key}
                                type="button"
                                className="dashboard-funnel-row"
                                onClick={s.onClick}
                                title={s.hint}
                              >
                                <span className="dashboard-funnel-label">{s.label}</span>
                                <span className="dashboard-funnel-bar-wrap">
                                  <span
                                    className={`dashboard-funnel-bar dashboard-funnel-bar--${s.key}`}
                                    style={{ width: `${widthPct}%` }}
                                  />
                                </span>
                                <span className="dashboard-funnel-value">{s.value.toLocaleString()}</span>
                                {conv !== null && (
                                  <span className={`dashboard-funnel-conv${weak ? ' dashboard-funnel-conv--weak' : ''}`}>
                                    {conv}%
                                  </span>
                                )}
                                {conv === null && delta && (
                                  <span className="dashboard-funnel-conv dashboard-funnel-delta" title="vs. last quarter">
                                    {delta.label}
                                  </span>
                                )}
                                {conv === null && !delta && <span className="dashboard-funnel-conv dashboard-funnel-conv--placeholder" />}
                              </button>
                            );
                          })}
                          <div className="dashboard-funnel-note">
                            <span>
                              {stages.some((st) => st.prev !== null && !rateVisible(st.prev))
                                ? 'Rates appear once a stage passes 20 — small numbers stay honest counts.'
                                : ''}
                            </span>
                            <b>CTA clicks: {stage5.toLocaleString()}</b>
                          </div>
                        </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* ─── Needs attention queue ──────────────────────
                      Short list (max 5) of the most click-worthy
                      follow-ups waiting for the seller. Items come from
                      /api/dashboard/insights with the unread-threads
                      row layered on from realtime in-memory state. */}
                  {(() => {
                    // Items come from /api/dashboard/insights
                    // (loadDashboardInsights). The unread-threads
                    // count is layered on from in-memory state since
                    // conversationsUnreadCount already reflects
                    // realtime polling.
                    const drillHandlers = {
                      conversations: () => { setPhase('conversations'); loadConversations(); },
                      library: () => {
                        setFilterCreatorId('all');
                        setLibraryQuery('');
                        setPhase('library');
                      },
                    };

                    const apiItems = (dashboardInsights?.needsAttention || []).map((it) => ({
                      key: it.key,
                      tone: it.tone,
                      title: it.title,
                      sub: it.sub,
                      // Room-attached threads drill into the deal, not
                      // the generic inbox — same rule as the inbox rows.
                      onClick: it.roomId
                        ? () => {
                          // A row can name the room section it is about
                          // (the "came back after you marked it lost" row
                          // lands on engagement, not the thread).
                          const targetHash = `#/buyer-spaces/${it.roomId}/${it.roomSection || 'conversations'}`;
                          if (typeof window !== 'undefined') {
                            window.history.pushState(null, '', window.location.pathname + window.location.search + targetHash);
                          }
                          setPhase('buyer-spaces');
                        }
                        : (drillHandlers[it.drillTo] || drillHandlers.conversations),
                      hint: it.roomId
                        ? (it.roomSection === 'engagement' ? "Open this deal's engagement" : "Open this deal's conversation")
                        : (it.drillTo === 'library' ? 'Open Trailers' : 'Open Conversations inbox'),
                      action: it.action || null,
                      hygiene: !!it.hygiene,
                    }));

                    const unread = conversationsUnreadCount || 0;
                    const items = [];
                    if (unread > 0) {
                      items.push({
                        key: 'unread',
                        tone: 'info',
                        title: `${unread} ${unread === 1 ? 'thread awaits' : 'threads await'} your reply`,
                        sub: 'Open the inbox to respond',
                        onClick: drillHandlers.conversations,
                        hint: 'Open Conversations inbox',
                        action: 'Reply',
                      });
                    }
                    items.push(...apiItems);

                    // Hygiene rows (backlog counts) render as a quiet
                    // footer, never queue slots — "28 unshared" next to
                    // "a live buyer is waiting on you" dilutes the
                    // queue's authority.
                    const hygieneRows = items.filter((i) => i.hygiene);
                    const shown = items.filter((i) => !i.hygiene).slice(0, 5);

                    return (
                      <div className="dashboard-queue">
                        <div className="dashboard-widget-header">
                          <h3>Needs attention</h3>
                          <span className="dashboard-widget-sub">
                            {shown.length === 0 ? 'All caught up' : `${shown.length} ${shown.length === 1 ? 'item' : 'items'}`}
                          </span>
                        </div>
                        {shown.length === 0 ? (
                          <div className="dashboard-queue-empty">
                            <p>You're all caught up. No follow-ups waiting.</p>
                          </div>
                        ) : (
                          <ul className="dashboard-queue-list">
                            {shown.map((it) => (
                              <li key={it.key}>
                                <button
                                  type="button"
                                  className={`dashboard-queue-row dashboard-queue-row--${it.tone}`}
                                  onClick={it.onClick}
                                  title={it.hint}
                                >
                                  <span className="dashboard-queue-text">
                                    <span className="dashboard-queue-title">{it.title}</span>
                                    <span className="dashboard-queue-sub">{it.sub}</span>
                                  </span>
                                  {it.action && (
                                    <span className="dashboard-queue-action">{it.action}</span>
                                  )}
                                  <span className="dashboard-queue-chevron" aria-hidden="true">›</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        {hygieneRows.map((h) => (
                          <button
                            key={h.key}
                            type="button"
                            className="dashboard-queue-hygiene"
                            onClick={h.onClick}
                            title={h.hint}
                          >
                            <span>{h.title}</span>
                            <span className="dashboard-queue-hygiene-cta">Review →</span>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Engagement band (2026-07-26 design audit): Buyer
                    activity + Top performing side by side, directly
                    under the insights row — the two live-engagement
                    surfaces read as one band and sit above the fold.
                    Collapses to one column under ~760px. */}
                <div className="dashboard-two-col">
                {/* Buyer activity (Tier-2, 2026-07-22) — cross-surface
                    feed from engagement_events: trailer views, chapter
                    jumps, full-recording opens, Decision Room visits,
                    doc downloads, forwards. The "what's my buying
                    committee doing" stream, on the page sellers open
                    first. */}
                <div className="dashboard-section">
                  <div className="dashboard-section-header">
                    <h3>Buyer activity</h3>
                  </div>
                  {dashboardActivityError ? (
                    <p className="loader-error">
                      {dashboardActivityError}
                      <button type="button" className="loader-retry" onClick={() => loadDashboardActivity()}>Retry</button>
                    </p>
                  ) : dashboardActivity.length === 0 ? (
                    <p className="dashboard-empty">
                      No buyer activity yet. When prospects open trailers, visit
                      Decision Rooms, or download documents, it shows up here.
                    </p>
                  ) : (() => {
                    const ACTIVITY_LABELS = {
                      trailer_view: 'opened a trailer',
                      trailer_complete: 'finished a trailer',
                      cta_click: 'clicked the CTA',
                      chapter_click: 'jumped to a chapter',
                      source_open: 'opened the full recording',
                      space_visit: 'visited the Decision Room',
                      doc_download: 'downloaded a document',
                      doc_upload: 'uploaded a document',
                      map_checkoff: 'checked off an action item',
                      map_comment: 'commented on the action plan',
                      message: 'sent a message',
                      forward: 'forwarded to a teammate',
                    };
                    // High-intent events get visual weight — a leader
                    // scanning for buying signals should catch these
                    // without reading. Routine opens stay muted.
                    const HIGH_INTENT = new Set([
                      'trailer_complete', 'cta_click', 'doc_download',
                      'map_checkoff', 'message', 'forward',
                    ]);
                    // Collapse consecutive events by the same person on
                    // the same demo/room ("Sarah opened, jumped, jumped,
                    // finished" → one row, +3 more). The feed arrives
                    // newest-first, so the group's lead event is its most
                    // recent — we surface the highest-intent event in the
                    // group as the headline instead when one exists.
                    const groups = [];
                    for (const ev of dashboardActivity) {
                      const gkey = `${ev.actorName || ev.actorEmail || '?'}|${ev.demoId || ev.buyerSpaceId || '?'}`;
                      const last = groups[groups.length - 1];
                      if (last && last.gkey === gkey) {
                        last.count += 1;
                        if (!HIGH_INTENT.has(last.lead.eventType) && HIGH_INTENT.has(ev.eventType)) {
                          last.lead = { ...ev, occurredAt: last.lead.occurredAt };
                        }
                      } else {
                        groups.push({ gkey, lead: ev, count: 1 });
                      }
                    }
                    const shown = groups.slice(0, 10);
                    const relTime = (when) => {
                      if (!when) return '';
                      const diffMs = Date.now() - new Date(when).getTime();
                      if (diffMs < 60000) return 'just now';
                      const mins = Math.round(diffMs / 60000);
                      if (mins < 60) return `${mins}m ago`;
                      const hrs = Math.round(mins / 60);
                      if (hrs < 24) return `${hrs}h ago`;
                      const days = Math.round(hrs / 24);
                      if (days < 30) return `${days}d ago`;
                      return new Date(when).toLocaleDateString();
                    };
                    return (
                      <ul className="buyer-activity-list">
                        {shown.map((g, i) => {
                          const ev = g.lead;
                          const hot = HIGH_INTENT.has(ev.eventType);
                          const actor = ev.actorName || ev.actorEmail || 'Someone';
                          const action = ACTIVITY_LABELS[ev.eventType] || ev.eventType;
                          const chapter = ev.metadata && ev.metadata.chapterTitle ? ` — “${ev.metadata.chapterTitle}”` : '';
                          // Every row points at its next step: the Decision
                          // Room when the event is deal-attached, else the
                          // demo itself. IDs come straight off the payload.
                          const open = ev.buyerSpaceId
                            ? () => {
                                setPhase('buyer-spaces');
                                if (typeof window !== 'undefined') {
                                  window.location.hash = `#/buyer-spaces/${ev.buyerSpaceId}`;
                                }
                              }
                            : ev.demoId
                              ? () => loadCompletedDemo(ev.demoId)
                              : null;
                          return (
                            <li key={i}>
                              <button
                                type="button"
                                className={`buyer-activity-row${hot ? ' buyer-activity-row--hot' : ''}`}
                                onClick={open || undefined}
                                disabled={!open}
                                title={open ? (ev.buyerSpaceId ? 'Open the Decision Room' : 'Open this trailer') : undefined}
                              >
                                <span className="buyer-activity-text">
                                  <strong>{actor}</strong>{' '}
                                  <span className={hot ? 'buyer-activity-verb--hot' : undefined}>{action}</span>
                                  {chapter}
                                  {ev.demoTitle ? <span className="buyer-activity-demo"> · {ev.demoTitle}</span> : null}
                                  {g.count > 1 && (
                                    <span className="buyer-activity-more"> +{g.count - 1} more</span>
                                  )}
                                </span>
                                <span className="buyer-activity-time">{relTime(ev.occurredAt)}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    );
                  })()}
                </div>

                {/* Top-performing — real data now. Pulls top 3 from
                    reportsTopDemos (populated by loadReportsTab on
                    dashboard mount). Empty state until the first view
                    comes in. Click anything to deep-link into the demo;
                    "See all reports" jumps to the full Reports page. */}
                <div className="dashboard-section">
                  <div className="dashboard-section-header">
                    <h3>Top performing</h3>
                    <button
                      type="button"
                      className="dashboard-see-all"
                      onClick={() => setPhase('reports')}
                    >
                      See all reports →
                    </button>
                  </div>
                  {reportsTopDemos && reportsTopDemos.length > 0 ? (
                    <ul className="top-performing-list">
                      {reportsTopDemos.slice(0, 3).map((d, i) => (
                        <li
                          key={d.id}
                          className="top-performing-row"
                          onClick={() => loadCompletedDemo(d.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              loadCompletedDemo(d.id);
                            }
                          }}
                        >
                          <span className="top-performing-rank">#{i + 1}</span>
                          {/* Compact thumbnail — top performers have
                              views so they're always completed trailers;
                              no placeholder branch needed. */}
                          <div className="top-performing-thumb" aria-hidden="true">
                            <ReelThumb demoId={d.id} hasSprite={d.hasThumbnailSprite} className="top-performing-thumb-video" />
                          </div>
                          <div className="top-performing-body">
                            <div className="top-performing-title">{d.title}</div>
                            <div className="top-performing-meta">
                              {d.views} view{d.views === 1 ? '' : 's'}
                              {d.uniqueViewers > 0 && ` · ${d.uniqueViewers} unique`}
                              {d.avgCompletionPct > 0 && ` · ${d.avgCompletionPct}% avg watch`}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="dashboard-placeholder">
                      <p>
                        No views yet. Once recipients open your shared trailers, the top performers
                        show up here. Send a trailer from a completed demo to get started.
                      </p>
                    </div>
                  )}
                </div>
                </div>{/* /dashboard-two-col */}

                {/* Recent demos */}
                <div className="dashboard-section">
                  <div className="dashboard-section-header">
                    <h3>Recent demos</h3>
                    {demoHistory.length > 5 && (
                      <button className="dashboard-view-all" onClick={() => setPhase('library')}>
                        View all
                      </button>
                    )}
                  </div>
                  {historyLoading ? (
                    <p className="dashboard-empty">Loading…</p>
                  ) : historyError && demoHistory.length === 0 ? (
                    <p className="dashboard-empty loader-error">
                      {historyError}
                      <button type="button" className="loader-retry" onClick={() => loadDemoHistory()}>Retry</button>
                    </p>
                  ) : demoHistory.length === 0 ? (
                    <p className="dashboard-empty">
                      No demos yet. Click <strong>Create a new trailer</strong> above to start.
                    </p>
                  ) : (
                    <div className="dashboard-recent-grid">
                      {demoHistory.slice(0, 5).map((d) => {
                        const isCompleted = d.status === 'completed';
                        // Completed AND analyzed are clickable — analyzed
                        // routes through loadCompletedDemo → the in-progress
                        // restorer → the clip selector, so "clips ready for
                        // review" tiles resume selection instead of dead-ending.
                        const canOpen = isCompleted || d.status === 'analyzed';
                        return (
                          <div
                            key={d.id}
                            className={`recent-demo-tile ${canOpen ? 'clickable' : ''}`}
                            onClick={canOpen ? () => loadCompletedDemo(d.id) : undefined}
                            role={canOpen ? 'button' : undefined}
                            tabIndex={canOpen ? 0 : undefined}
                            onKeyDown={canOpen ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                loadCompletedDemo(d.id);
                              }
                            } : undefined}
                            title={isCompleted ? 'Open this trailer to share or generate a follow-up' : undefined}
                          >
                            {/* Thumbnail at the top — same ReelThumb
                                as the library page, plus an overlay
                                showing status + date so the tile body
                                below is just the title + meta + kebab. */}
                            <div className="recent-demo-tile-thumb" aria-hidden="true">
                              {isCompleted ? (
                                <ReelThumb demoId={d.id} hasSprite={d.hasThumbnailSprite} className="recent-demo-tile-thumb-video" />
                              ) : (
                                <div className={`recent-demo-tile-thumb-placeholder status-${d.status}`}>
                                  {d.status === 'processing' ? (
                                    <ProcessingWave progress={d.progress} size="md" />
                                  ) : d.status === 'failed' ? '⚠' : '🎬'}
                                </div>
                              )}
                              <div className="recent-demo-tile-thumb-overlay">
                                <span className={`recent-demo-tile-status status-${d.status}`}>
                                  {d.status === 'completed' ? '✓ Done' : d.status}
                                </span>
                                <span className="recent-demo-tile-date">{formatDemoDate(d.createdAt)}</span>
                              </div>
                            </div>
                            <div className="recent-demo-tile-body">
                              <div className="recent-demo-tile-name" title={d.filename || 'Untitled'}>
                                {d.filename || 'Untitled'}
                              </div>
                              <div className="recent-demo-tile-meta">
                                {d.moments && d.moments.length > 0 ? `${d.moments.length} clips` : '—'}
                              </div>
                              {isCompleted && (
                                <DealStatusPill demo={d} compact />
                              )}
                              {renderDemoActions(d, 'default')}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* /OVERVIEW branch — close of dashboardView fragment */}
                </>)}
              </div>
              </div>
            );
          })()}

          {/* LIBRARY PHASE — full-page "See all" view with grid + filters.
              Accessed from the dashboard sidebar's "See all →" button.
              Shares the same libraryDemos state as the sidebar so the two
              surfaces always match. */}
          {!bootstrapping && phase === 'library' && authUser && (() => {
            const libraryTier = (authUser && authUser.tier) || 'free';
            // Shared style for the "+ New trailer" dropdown menu items.
            // Defined inside the IIFE so it's only created when the
            // Trailers phase is rendered, not on every Settings/Reports
            // page render.
            const menuItemStyle = {
              display: 'block', width: '100%',
              padding: '12px 14px', borderRadius: 8,
              border: 'none', background: 'transparent', textAlign: 'left',
              cursor: 'pointer', fontFamily: 'inherit',
              transition: 'background 0.14s ease',
            };
            // Team scope is a capacity feature (multi-user) — hidden
            // from single-seat tiers rather than rendered locked.
            const canSeeTeamLibrary = ['pro', 'enterprise', 'beta'].includes(libraryTier);

            // ── Deal / Reusable ──────────────────────────────────────
            // Every filter below is a query param now (see
            // loadLibraryDemos). Nothing on this screen filters the
            // loaded array any more, which is what makes the count
            // honest and row 51 reachable.
            const activeTab = TRAILER_TABS.find((t) => t.value === libraryContentScope)
              || TRAILER_TABS[0];
            const onReusableTab = libraryContentScope === 'reusable';
            const rows = libraryDemos;
            const hasMore = rows.length < libraryTotal;
            const isFirstLoad = libraryLoading && rows.length === 0;
            const hasActiveFilters = !!(
              (libraryQuery || '').trim()
              || libraryStatusFilter !== 'all'
              || filterDateFrom
              || filterDateTo
              || (libraryScope === 'team' && filterCreatorId !== 'all')
            );

            // Open the standalone recording tab.
            //
            // The old handler wrapped window.open in try/catch and
            // swallowed the rejection, so a blocked pop-up produced
            // literally nothing on screen. Two things were wrong with
            // that: a blocked pop-up does not throw (it returns null),
            // and there was no fallback offered either way. Note the
            // dropped 'noopener' feature — with it, window.open returns
            // null on success too, which makes blocked and succeeded
            // indistinguishable. The tab is same-origin, so we get the
            // same protection by nulling `opener` ourselves.
            const openRecorderTab = () => {
              setNewTrailerMenuOpen(false);
              const url = `${window.location.pathname}#/record-trailer`;
              let win = null;
              try {
                win = window.open(url, '_blank');
              } catch (err) {
                console.error('[record] window.open threw:', err);
              }
              if (win && !win.closed) {
                try { win.opener = null; } catch (_) { /* older browsers */ }
                return;
              }
              showToast(
                'Your browser blocked the recording tab. Allow pop-ups for TrailerCast, or open the recorder right here.',
                {
                  label: 'Open recorder here',
                  onClick: () => {
                    // IS_RECORDING_TAB is read once at module load, so a
                    // hash change alone would not switch surfaces.
                    window.location.hash = '#/record-trailer';
                    window.location.reload();
                  },
                }
              );
            };

            // ── Card + row renderers ─────────────────────────────────
            const demoTitle = (d) => d.prospectName || d.filename || 'Untitled';
            const demoCreatorName = (d) => (d.creator
              ? `${d.creator.firstName || ''} ${d.creator.lastName || ''}`.trim() || d.creator.email
              : null);

            const renderCard = (d) => {
              const isDone = d.status === 'completed';
              // Analyzed cards open the clip selector (via
              // loadCompletedDemo's in-progress routing) — see the
              // library-row comment above for the dead-tile history.
              const canOpen = isDone || d.status === 'analyzed';
              const creatorName = demoCreatorName(d);
              const durationLabel = formatTrailerDuration(trailerDurationSeconds(d));
              return (
                <div
                  key={d.id}
                  className={`library-card status-${d.status} ${canOpen ? 'clickable' : ''}`}
                  onClick={() => canOpen && loadCompletedDemo(d.id)}
                  role={canOpen ? 'button' : undefined}
                  tabIndex={canOpen ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (canOpen && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      loadCompletedDemo(d.id);
                    }
                  }}
                >
                  {/* Thumbnail — ReelThumb handles the resting poster
                      frame + hover-to-scrub. Status pill, date and
                      duration float as overlays on top so the thumbnail
                      owns the whole 16:9 block. */}
                  <div className="library-card-thumb" aria-hidden="true">
                    {isDone ? (
                      <ReelThumb
                        demoId={d.id}
                        hasSprite={d.hasThumbnailSprite}
                        posterFrame={d.posterFrameIndex}
                        className="library-card-thumb-video"
                      />
                    ) : (
                      <div className={`library-card-thumb-placeholder status-${d.status}`}>
                        {d.status === 'processing' ? (
                          <ProcessingWave progress={d.progress} size="lg" />
                        ) : (
                          <span className="library-card-thumb-icon">
                            {d.status === 'failed' ? '⚠' : '🎬'}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="library-card-thumb-overlay">
                      <span className={`library-card-status status-${d.status}`}>
                        {isDone ? '✓ Done' : d.status}
                      </span>
                    </div>
                    {durationLabel && (
                      <span className="library-card-duration" title="Trailer length">
                        {durationLabel}
                      </span>
                    )}
                    {/* Date moved out of the top-right overlay (where
                        bare white text sat on the brightest band of
                        the thumbnail) to a solid pill bottom-right on
                        the duration badge's baseline. Completed tiles
                        keep the play circle in that corner — CSS
                        offsets the pill beside it. */}
                    <span className="library-card-date" title="Created">
                      {formatDemoDate(d.createdAt)}
                    </span>
                    {isDone && (
                      <div className="library-card-thumb-play" aria-hidden="true">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {/* Body wrapper holds the card's text content
                      + actions. Padding lives here so the
                      thumbnail above can go edge-to-edge without
                      needing overflow:hidden on the card itself
                      (which would clip the kebab dropdown). */}
                  <div className="library-card-body">
                    <div className="library-card-title">{demoTitle(d)}</div>
                    {d.prospectName && d.filename && (
                      <div className="library-card-filename">{d.filename}</div>
                    )}
                    <div className="library-card-meta">
                      {d.moments && d.moments.length > 0 ? `${d.moments.length} clips` : '—'}
                      {libraryScope === 'team' && creatorName && (
                        <>
                          <span className="library-card-sep">·</span>
                          <span>by {creatorName}</span>
                        </>
                      )}
                      {d.sourceAvailable && (
                        <>
                          <span className="library-card-sep">·</span>
                          <span className="library-card-source-badge" title="The original, unedited recording is available to re-watch">
                            Full recording
                          </span>
                        </>
                      )}
                    </div>
                    {isDone && (
                      <DealStatusPill demo={d} compact />
                    )}
                    {renderDemoActions(d, 'default', { reclassify: true })}
                  </div>
                </div>
              );
            };

            const renderRow = (d) => {
              const isDone = d.status === 'completed';
              const canOpen = isDone || d.status === 'analyzed';
              const creatorName = demoCreatorName(d);
              const durationLabel = formatTrailerDuration(trailerDurationSeconds(d));
              return (
                <div
                  key={d.id}
                  className={`library-list-row status-${d.status} ${canOpen ? 'clickable' : ''}`}
                  onClick={() => canOpen && loadCompletedDemo(d.id)}
                  role={canOpen ? 'button' : undefined}
                  tabIndex={canOpen ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (canOpen && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      loadCompletedDemo(d.id);
                    }
                  }}
                >
                  <div className="library-list-thumb" aria-hidden="true">
                    {isDone ? (
                      <ReelThumb
                        demoId={d.id}
                        hasSprite={d.hasThumbnailSprite}
                        posterFrame={d.posterFrameIndex}
                        className="library-list-thumb-video"
                      />
                    ) : (
                      <div className={`library-card-thumb-placeholder status-${d.status}`}>
                        <span className="library-card-thumb-icon">
                          {d.status === 'failed' ? '⚠' : '🎬'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="library-list-main">
                    <div className="library-list-title">{demoTitle(d)}</div>
                    {d.prospectName && d.filename && (
                      <div className="library-list-sub">{d.filename}</div>
                    )}
                  </div>
                  <div className="library-list-cell library-list-duration">
                    {durationLabel || '—'}
                  </div>
                  <div className="library-list-cell library-list-created">
                    {formatDemoDate(d.createdAt) || '—'}
                  </div>
                  <div className="library-list-cell library-list-creator">
                    {creatorName || '—'}
                  </div>
                  <div className="library-list-cell library-list-status">
                    <span className={`library-card-status status-${d.status}`}>
                      {isDone ? '✓ Done' : d.status}
                    </span>
                  </div>
                  <div className="library-list-cell library-list-actions">
                    {renderDemoActions(d, 'default', { reclassify: true })}
                  </div>
                </div>
              );
            };

            return (
              <div className="library-page">
                <div className="library-page-header">
                  <div>
                    {/* "Trailers" and not "Trailer Library": the sidebar
                        has a separate top-level "Library" (the Sales
                        Library of decks and PDFs), and this heading was
                        the reason support answers had to open with
                        "which library do you mean". */}
                    <h2>Trailers</h2>
                    <p>{activeTab.subtitle}</p>
                  </div>
                  {/* Action cluster — back-to-dashboard tile + primary
                      CTA. Both right-aligned to match the Decision Rooms
                      header pattern. The CTA stays the gradient pill;
                      back tile is the standard secondary look. */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <button
                      type="button"
                      className="back-to-dashboard"
                      onClick={() => setPhase('dashboard')}
                    >
                      ← Back to dashboard
                    </button>
                    {onReusableTab ? (
                      /* Reusable content starts with a recording. Until
                         now the recorder's only route was a dropdown
                         whose only affordance was a ▾ at 11px, then the
                         second item below a divider — so the feature was
                         effectively unshipped. On this tab it is the
                         primary button, and upload becomes the aside. */
                      <>
                        {/* One upload button, not two: a separate
                            "Upload video" beside this opened the same
                            screen with the switch flipped, which read
                            as two buttons doing the same thing. The
                            AI-vs-as-is choice lives on the screen and
                            remembers the last pick; this only files the
                            result under Reusable. */}
                        <button
                          type="button"
                          className="library-page-secondary-cta"
                          onClick={() => { setUploadAsIsScope('reusable'); setPhase('upload'); }}
                        >
                          Upload existing
                        </button>
                        <button
                          type="button"
                          className="dashboard-cta-pill library-page-cta"
                          onClick={openRecorderTab}
                        >
                          <span className="dashboard-cta-pill-icon" aria-hidden="true">●</span>
                          <span className="dashboard-cta-pill-label">Record</span>
                        </button>
                      </>
                    ) : (
                      /* Deal tab keeps upload as the primary path — a
                         deal trailer is built from a call that already
                         happened. Recording stays reachable in the menu. */
                      <div style={{ position: 'relative' }}>
                        <button
                          type="button"
                          className="dashboard-cta-pill library-page-cta"
                          onClick={() => setNewTrailerMenuOpen((v) => !v)}
                          aria-haspopup="menu"
                          aria-expanded={newTrailerMenuOpen}
                        >
                          <span className="dashboard-cta-pill-icon" aria-hidden="true">+</span>
                          <span className="dashboard-cta-pill-label">New trailer</span>
                          <span aria-hidden="true" style={{ marginLeft: 6, fontSize: 11, opacity: 0.85 }}>▾</span>
                        </button>
                        {newTrailerMenuOpen && (
                          <>
                            {/* Click-catcher to close on outside click. Sits
                                behind the menu so clicks anywhere else dismiss it. */}
                            <div
                              onClick={() => setNewTrailerMenuOpen(false)}
                              style={{ position: 'fixed', inset: 0, zIndex: 90 }}
                            />
                            <div
                              role="menu"
                              style={{
                                position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                                background: 'var(--room-card, #fff)', borderRadius: 14,
                                border: '1px solid var(--room-border, #e2e8f0)',
                                boxShadow: '0 18px 48px rgba(15, 23, 42, 0.14), 0 4px 12px rgba(15, 23, 42, 0.06)',
                                minWidth: 320, padding: 8, zIndex: 100,
                                animation: 'tcMenuIn 0.14s ease-out',
                              }}
                            >
                              <div style={{
                                padding: '6px 12px 10px',
                                fontSize: 11, fontWeight: 600,
                                color: 'var(--room-text-subtle, #94a3b8)',
                                textTransform: 'uppercase', letterSpacing: '0.06em',
                              }}>
                                Create a trailer
                              </div>
                              <button
                                type="button"
                                role="menuitem"
                                onClick={() => { setNewTrailerMenuOpen(false); setUploadAsIs(false); setPhase('upload'); }}
                                style={menuItemStyle}
                                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--room-accent-soft, #faf5ff)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              >
                                <div style={{ fontWeight: 600, color: 'var(--room-text-strong, #0f172a)', fontSize: 14, marginBottom: 3 }}>
                                  Upload existing recording
                                </div>
                                <div style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)', lineHeight: 1.45 }}>
                                  MP4, MOV, or WebM. The AI auto-builds a polished trailer with the best moments.
                                </div>
                              </button>
                              {finishedUploadEnabled && (
                                <button
                                  type="button"
                                  role="menuitem"
                                  onClick={() => {
                                    setNewTrailerMenuOpen(false);
                                    setUploadAsIs(true);
                                    setUploadAsIsScope('deal');
                                    setPhase('upload');
                                  }}
                                  style={menuItemStyle}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--room-accent-soft, #faf5ff)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                >
                                  <div style={{ fontWeight: 600, color: 'var(--room-text-strong, #0f172a)', fontSize: 14, marginBottom: 3 }}>
                                    Upload a finished video
                                  </div>
                                  <div style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)', lineHeight: 1.45 }}>
                                    Already cut and ready to send. Stored as-is — no AI rebuild — and shareable the moment it lands.
                                  </div>
                                </button>
                              )}
                              <div style={{ height: 1, background: 'var(--room-border-soft, #f1f5f9)', margin: '4px 8px' }} />
                              <button
                                type="button"
                                role="menuitem"
                                onClick={openRecorderTab}
                                style={menuItemStyle}
                                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--room-accent-soft, #faf5ff)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              >
                                <div style={{ fontWeight: 600, color: 'var(--room-text-strong, #0f172a)', fontSize: 14, marginBottom: 3 }}>
                                  Record in the browser
                                </div>
                                <div style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)', lineHeight: 1.45 }}>
                                  Screen, camera, or both. Up to 15 minutes. Opens in a new tab so it's out of your way while you record.
                                </div>
                              </button>
                              <style>{`
                                @keyframes tcMenuIn {
                                  from { opacity: 0; transform: translateY(-4px); }
                                  to   { opacity: 1; transform: translateY(0); }
                                }
                              `}</style>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Deal / Reusable — the primary split. Sits above the
                    scope + filter row because it changes what the rest
                    of the controls are filtering. */}
                <div className="library-segmented" role="tablist" aria-label="Trailer type">
                  {TRAILER_TABS.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      role="tab"
                      aria-selected={libraryContentScope === t.value}
                      className={`library-segment ${libraryContentScope === t.value ? 'active' : ''}`}
                      onClick={() => setLibraryContentScope(t.value)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                <div className="library-page-controls">
                  <div className="library-page-tabs">
                    <button
                      type="button"
                      className={`library-tab ${libraryScope === 'personal' ? 'active' : ''}`}
                      onClick={() => setLibraryScope('personal')}
                    >
                      {canSeeTeamLibrary ? 'Mine' : 'Library'}
                    </button>
                    {canSeeTeamLibrary && (
                      <button
                        type="button"
                        className={`library-tab ${libraryScope === 'team' ? 'active' : ''}`}
                        onClick={() => setLibraryScope('team')}
                      >
                        Team
                      </button>
                    )}
                  </div>

                  <input
                    type="search"
                    className="library-page-search"
                    placeholder="Search by filename or prospect…"
                    value={libraryQuery}
                    onChange={(e) => setLibraryQuery(e.target.value)}
                  />

                  <select
                    className="library-page-status"
                    value={libraryStatusFilter}
                    onChange={(e) => setLibraryStatusFilter(e.target.value)}
                  >
                    <option value="all">All statuses</option>
                    <option value="completed">Completed</option>
                    <option value="processing">Processing</option>
                    <option value="failed">Failed</option>
                  </select>

                  <select
                    className="library-page-status"
                    value={librarySort}
                    onChange={(e) => setLibrarySort(e.target.value)}
                    aria-label="Sort trailers"
                  >
                    {TRAILER_SORTS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>

                  {/* Folder filter (2026-08-24). A select rather than a
                      left rail: the Trailers screen already has a tab
                      row, a scope row and this toolbar, and a fourth
                      navigational surface would compete with all three.
                      Counts ride in the labels so the rail's job — "how
                      much is in there" — is still answered.

                      "Unfiled" is always offered, even at zero, because
                      its absence is what would make someone think
                      filing is mandatory. */}
                  <select
                    className="library-page-status"
                    value={libraryFolderId}
                    onChange={(e) => setLibraryFolderId(e.target.value)}
                    aria-label="Filter by folder"
                    disabled={foldersLoading && trailerFolders.length === 0}
                  >
                    <option value="all">All folders</option>
                    <option value="none">Unfiled</option>
                    {trailerFolders.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}{f.trailerCount ? ` (${f.trailerCount})` : ''}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    className="library-view-btn"
                    onClick={async () => {
                      const name = window.prompt('New folder name');
                      if (name === null) return;
                      const folder = await createTrailerFolder(name);
                      // Drop the user into what they just made. Creating
                      // a folder and staying where you were makes it
                      // look like nothing happened.
                      if (folder && folder.id) setLibraryFolderId(folder.id);
                    }}
                    title="Create a folder"
                  >
                    + Folder
                  </button>

                  {/* Rename/delete act on the folder currently being
                      viewed, so they only appear when one is. The
                      server still enforces creator-or-admin; this is
                      just not offering what most people cannot do. */}
                  {libraryFolderId !== 'all' && libraryFolderId !== 'none' && (() => {
                    const active = trailerFolders.find((f) => f.id === libraryFolderId);
                    if (!active) return null;
                    return (
                      <>
                        <button
                          type="button"
                          className="library-view-btn"
                          onClick={() => renameTrailerFolder(active)}
                          title={`Rename “${active.name}”`}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          className="library-view-btn"
                          onClick={() => deleteTrailerFolder(active)}
                          title={`Delete “${active.name}”`}
                        >
                          Delete folder
                        </button>
                      </>
                    );
                  })()}

                  {/* Creator filter — only meaningful on team scope where
                      multiple sellers contribute. Defaults to "All team
                      members" + pulls the list from teamMembers (auto-
                      loaded when the phase mounts on team scope). */}
                  {libraryScope === 'team' && teamMembers.length > 0 && (
                    <select
                      className="library-page-status"
                      value={filterCreatorId}
                      onChange={(e) => setFilterCreatorId(e.target.value)}
                      aria-label="Filter by team member"
                    >
                      <option value="all">All team members</option>
                      {teamMembers.map((m) => (
                        <option key={m.id} value={String(m.id)}>
                          {m.fullName || m.email}
                        </option>
                      ))}
                    </select>
                  )}

                  {/* Date-range filter — inclusive on both ends.
                      Matches the conversations-phase filter UX so the
                      app's filter idioms stay consistent. */}
                  <label className="library-page-date">
                    <span>From</span>
                    <input
                      type="date"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                    />
                  </label>
                  <label className="library-page-date">
                    <span>To</span>
                    <input
                      type="date"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                    />
                  </label>

                  {(filterCreatorId !== 'all' || filterDateFrom || filterDateTo) && (
                    <button
                      type="button"
                      className="library-page-filter-clear"
                      onClick={() => {
                        setFilterCreatorId('all');
                        setFilterDateFrom('');
                        setFilterDateTo('');
                      }}
                    >
                      Clear filters
                    </button>
                  )}

                  {/* Grid is the default: this is video, and a poster
                      frame is how you recognise one. List is for when
                      you know what you are looking for and want density. */}
                  <div className="library-view-toggle" role="group" aria-label="View mode">
                    <button
                      type="button"
                      className={`library-view-btn ${libraryViewMode === 'grid' ? 'active' : ''}`}
                      onClick={() => setLibraryViewMode('grid')}
                      aria-pressed={libraryViewMode === 'grid'}
                      title="Grid view"
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z" />
                      </svg>
                      <span className="library-view-btn-label">Grid</span>
                    </button>
                    <button
                      type="button"
                      className={`library-view-btn ${libraryViewMode === 'list' ? 'active' : ''}`}
                      onClick={() => setLibraryViewMode('list')}
                      aria-pressed={libraryViewMode === 'list'}
                      title="List view"
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M3 5h18v2H3V5zm0 6h18v2H3v-2zm0 6h18v2H3v-2z" />
                      </svg>
                      <span className="library-view-btn-label">List</span>
                    </button>
                  </div>
                </div>

                {/* The honest count. The old screen showed the length of
                    a client-filtered 50-row window and implied that was
                    everything. */}
                {!isFirstLoad && !libraryError && rows.length > 0 && (
                  <p className="library-count">
                    Showing {rows.length} of {libraryTotal} {libraryTotal === 1 ? 'trailer' : 'trailers'}
                    {hasActiveFilters ? ' matching your filters' : ''}
                  </p>
                )}

                {isFirstLoad ? (
                  <div className="library-page-grid" aria-busy="true" aria-label="Loading trailers">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="library-card library-card--skeleton" aria-hidden="true">
                        <div className="library-card-thumb library-skeleton-block" />
                        <div className="library-card-body">
                          <div className="library-skeleton-line" style={{ width: '70%' }} />
                          <div className="library-skeleton-line" style={{ width: '40%' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : libraryError && rows.length === 0 ? (
                  <p className="library-page-empty loader-error">
                    {libraryError}
                    <button
                      type="button"
                      className="loader-retry"
                      onClick={() => loadLibraryDemos({ ...trailersQueryRef.current, offset: 0 })}
                    >
                      Retry
                    </button>
                  </p>
                ) : rows.length === 0 ? (
                  <div className="library-empty-state">
                    {hasActiveFilters || (libraryQuery || '').trim() ? (
                      <>
                        <p className="library-empty-title">No {activeTab.label.toLowerCase()} trailers match those filters</p>
                        <p className="library-empty-body">
                          Try a different search term or widen the date range.
                        </p>
                        <button
                          type="button"
                          className="library-page-filter-clear"
                          onClick={() => {
                            setLibraryQuery('');
                            setLibraryStatusFilter('all');
                            setFilterCreatorId('all');
                            setFilterDateFrom('');
                            setFilterDateTo('');
                          }}
                        >
                          Clear all filters
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="library-empty-title">{activeTab.emptyTitle}</p>
                        <p className="library-empty-body">{activeTab.emptyBody}</p>
                        {/* The Reusable tab is empty or near-empty for
                            most workspaces on day one, so its empty
                            state is where the recorder gets sold. */}
                        {onReusableTab ? (
                          <div className="library-empty-actions">
                            <button
                              type="button"
                              className="dashboard-cta-pill"
                              onClick={openRecorderTab}
                            >
                              <span className="dashboard-cta-pill-icon" aria-hidden="true">●</span>
                              <span className="dashboard-cta-pill-label">Record a micro demo</span>
                            </button>
                            <button
                              type="button"
                              className="library-page-secondary-cta"
                              onClick={() => setPhase('upload')}
                            >
                              Upload one instead
                            </button>
                          </div>
                        ) : (
                          <div className="library-empty-actions">
                            <button
                              type="button"
                              className="dashboard-cta-pill"
                              onClick={() => setPhase('upload')}
                            >
                              <span className="dashboard-cta-pill-icon" aria-hidden="true">+</span>
                              <span className="dashboard-cta-pill-label">Upload a recording</span>
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    {libraryViewMode === 'list' ? (
                      <div className="library-list">
                        <div className="library-list-head" aria-hidden="true">
                          <div className="library-list-thumb" />
                          <div className="library-list-main">Trailer</div>
                          <div className="library-list-cell library-list-duration">Length</div>
                          <div className="library-list-cell library-list-created">Created</div>
                          <div className="library-list-cell library-list-creator">Creator</div>
                          <div className="library-list-cell library-list-status">Status</div>
                          <div className="library-list-cell library-list-actions" />
                        </div>
                        {rows.map(renderRow)}
                      </div>
                    ) : (
                      <div className="library-page-grid">
                        {rows.map(renderCard)}
                      </div>
                    )}

                    {/* A refresh that lands while rows are already on
                        screen shouldn't blank them out — say so inline. */}
                    {libraryError && (
                      <p className="library-page-empty loader-error">
                        {libraryError}
                        <button
                          type="button"
                          className="loader-retry"
                          onClick={() => loadLibraryDemos({ ...trailersQueryRef.current, offset: 0 })}
                        >
                          Retry
                        </button>
                      </p>
                    )}

                    {hasMore && (
                      <div className="library-loadmore">
                        <button
                          type="button"
                          className="library-loadmore-btn"
                          onClick={loadMoreTrailers}
                          disabled={libraryLoadingMore}
                        >
                          {libraryLoadingMore
                            ? 'Loading…'
                            : `Load ${Math.min(TRAILER_PAGE_SIZE, libraryTotal - rows.length)} more`}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* RecordTrailerModal removed 2026-05-10. Browser recording
              now lives in its own tab (RecordTrailerPage at
              #/record-trailer) — see the "+ New trailer ▾" dropdown
              handler above. The standalone-tab approach kills the
              modal-on-top problems (captured in screen-share, z-index
              containing-block bugs, popup juggling) at once. */}

          {/* BUYER SPACES PHASE — Phase 1 scaffold (2026-04-26).
              Lists every Decision Room the rep owns. Detail view, MAP,
              docs, and the public /space/<slug> surface land in
              subsequent commits. For now this gives the new nav item
              a real (data-backed) destination instead of a 404. */}
          {!bootstrapping && phase === 'buyer-spaces' && authUser && (
            <BuyerSpacesPhase
              authToken={authToken}
              setPhase={setPhase}
              onOpenDemo={loadCompletedDemo}
              sellerName={(authUser && (authUser.fullName || authUser.username)) || ''}
              currentUserId={authUser?.id || null}
              isCompanyAdmin={!!(authUser?.company?.isAdmin)}
              uiFlags={authUser?.company?.featureFlags || {}}
            />
          )}

          {/* FORMS PHASE (2026-09-07, flag forms) — forms & surveys
              list, builder and responses. Nav item is flag-gated in
              LeftNav; this render is gated too so a stale deep link
              never shows the page to a workspace without the flag. */}
          {!bootstrapping && phase === 'forms' && authUser && authUser?.company?.featureFlags?.forms && (
            <FormsPhase authToken={authToken} onToast={showToast} />
          )}

          {/* SALES LIBRARY PHASE — company-wide pool of reusable
              assets (decks, PDFs, case studies, pricing, etc.).
              Reps browse + attach to Decision Rooms; admins curate.
              Distinct from `phase === 'library'` above which is
              the per-rep trailer list. */}
          {!bootstrapping && phase === 'sales-library' && authUser && (
            <SalesLibraryPhase
              authToken={authToken}
              isCompanyAdmin={!!(authUser?.company?.isAdmin)}
              currentUserName={
                (authUser && (
                  [authUser.firstName, authUser.lastName].filter(Boolean).join(' ')
                  || authUser.fullName
                  || authUser.username
                )) || ''
              }
            />
          )}

          {/* QBR BRIEFS PHASE — per-rep quarterly review prep.
              Sellers generate a brief over a date range and present
              from the AI-synthesized rollup. Admins / managers can
              read teammates' briefs (cross-rep audit-logged on the
              backend). Spec: backend/utils/generateQbrBrief.js. */}
          {!bootstrapping && phase === 'qbr' && authUser && (
            <QbrBriefsPhase
              authToken={authToken}
              currentUserId={authUser?.id || null}
              isCompanyAdmin={!!(authUser?.company?.isAdmin)}
            />
          )}

          {/* COACHING PHASE (2026-08-06, whistle icon) — rep call
              habits from diarized transcripts + QBR briefs' new home.
              Nav item + backend route both dark behind coaching_tab;
              rendering isn't flag-checked here so a direct #/coaching
              hash on a flagged-off company just shows the endpoint's
              404 empty-state rather than a blank screen. */}
          {!bootstrapping && phase === 'coaching' && authUser && (
            <CoachingPhase
              authToken={authToken}
              authFetch={authFetch}
              authUser={authUser}
              onOpenCall={loadCompletedDemo}
            />
          )}

          {/* QUALIFYING CALLS PHASE — pre-Buyer-Space review surface
              (2026-04-27). AE confirms the auto-detected recording
              kind on upcoming calls + reviews completed qualifying
              calls (transcript + AI summary) and decides Create
              Space vs Mark Not Qualified. See
              project_buyer_spaces.md for full spec. */}
          {!bootstrapping && phase === 'calls' && authUser && (
            <CallsPhase
              authToken={authToken}
              setPhase={setPhase}
              onOpenDemo={loadCompletedDemo}
              authUser={authUser}
            />
          )}

          {/* CONVERSATIONS PHASE — inbox of every prospect thread across
              every trailer the user owns. Each row opens the signed share
              URL (the "communication hub") in a new tab. Matches what
              the prospect-message email deep-link takes the seller to,
              so the two flows converge on the same surface. */}
          {!bootstrapping && phase === 'conversations' && authUser && (() => {
            // Apply filters. Each is an AND — narrowing one doesn't unset
            // the others. Date bounds are inclusive: end-of-day on the
            // upper bound so "to: 2026-04-21" includes messages sent at
            // 2026-04-21 23:59:59.
            // Client-context row upgrade (2026-08-31) — dark behind the
            // conversations_client_context release flag until Friday.
            const clientContextOn = !!authUser?.company?.featureFlags?.conversations_client_context;
            const qDemo = convFilterDemo.trim().toLowerCase();
            const qProspect = convFilterProspect.trim().toLowerCase();
            const fromTs = convFilterDateFrom ? new Date(`${convFilterDateFrom}T00:00:00`).getTime() : null;
            const toTs = convFilterDateTo ? new Date(`${convFilterDateTo}T23:59:59.999`).getTime() : null;
            const filtered = conversations.filter((c) => {
              if (qDemo) {
                const hay = (c.demoTitle || '').toLowerCase();
                if (!hay.includes(qDemo)) return false;
              }
              if (qProspect) {
                const hay = `${c.prospectName || ''} ${c.prospectEmail || ''}${clientContextOn ? ` ${c.clientName || ''}` : ''}`.toLowerCase();
                if (!hay.includes(qProspect)) return false;
              }
              if (fromTs || toTs) {
                const ts = c.lastMessageAt ? new Date(c.lastMessageAt).getTime() : null;
                if (!ts) return false;
                if (fromTs && ts < fromTs) return false;
                if (toTs && ts > toTs) return false;
              }
              return true;
            });
            const anyFilter = qDemo || qProspect || fromTs || toTs;
            const clearFilters = () => {
              setConvFilterDemo('');
              setConvFilterProspect('');
              setConvFilterDateFrom('');
              setConvFilterDateTo('');
            };
            return (
            <div className="reports-phase">
              <div className="phase-header-row">
                <div className="reports-header">
                  <h1>Conversations</h1>
                  <p className="reports-subtitle">
                    Every prospect thread across all your trailers — most recent activity first.
                  </p>
                </div>
                <button className="back-to-dashboard" onClick={() => setPhase('dashboard')}>
                  ← Back to dashboard
                </button>
              </div>

              {/* Filter bar — demo title / prospect (name + email) / date
                  range. All client-side; hidden on the empty state. CRM
                  integration will slot a "prospect company" filter in
                  here once that lands. */}
              {conversations.length > 0 && (
                <div className="conversations-filter-bar">
                  <input
                    type="search"
                    className="conversations-filter-input"
                    placeholder="Filter by demo name…"
                    value={convFilterDemo}
                    onChange={(e) => setConvFilterDemo(e.target.value)}
                  />
                  <input
                    type="search"
                    className="conversations-filter-input"
                    placeholder={clientContextOn ? 'Filter by prospect or client…' : 'Filter by prospect…'}
                    value={convFilterProspect}
                    onChange={(e) => setConvFilterProspect(e.target.value)}
                  />
                  <div className="conversations-filter-date-group">
                    <label className="conversations-filter-date">
                      <span>From</span>
                      <input
                        type="date"
                        value={convFilterDateFrom}
                        onChange={(e) => setConvFilterDateFrom(e.target.value)}
                      />
                    </label>
                    <label className="conversations-filter-date">
                      <span>To</span>
                      <input
                        type="date"
                        value={convFilterDateTo}
                        onChange={(e) => setConvFilterDateTo(e.target.value)}
                      />
                    </label>
                  </div>
                  {anyFilter && (
                    <button
                      type="button"
                      className="conversations-filter-clear"
                      onClick={clearFilters}
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              )}

              {conversationsLoading && conversations.length === 0 ? (
                <div className="conversations-empty">Loading…</div>
              ) : conversations.length === 0 ? (
                <div className="conversations-empty">
                  <h3>No conversations yet</h3>
                  <p>
                    When a prospect sends a question, comment, or follow-up
                    request from one of your share links, it'll show up here.
                  </p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="conversations-empty">
                  <p>No conversations match your filters.</p>
                  <button type="button" className="conversations-filter-clear" onClick={clearFilters}>
                    Clear filters
                  </button>
                </div>
              ) : (
                <ul className="conversations-list">
                  {filtered.map((c) => {
                    const isMapComment = c.kind === 'map_comment';
                    // Kind label: "MAP comment" for action-thread rows so
                    // the inbox row tells you at a glance you're looking
                    // at MAP-thread chatter, not share-page Q&A. Other
                    // values stay 1:1 with the share-message kinds.
                    const kindLabel = isMapComment ? 'MAP comment'
                                    : c.lastMessageKind === 'question' ? 'Question'
                                    : c.lastMessageKind === 'followup_request' ? 'Follow-up request'
                                    : 'Comment';
                    // With client context on, the head shows NAME +
                    // separate muted email instead of one
                    // "Name <email>" string — a mismatched pair (someone
                    // testing a link under another identity) reads as
                    // what it is instead of as one confusing identity.
                    const senderDisplay = isMapComment
                      ? (c.lastAuthorDisplayName || c.prospectName || 'Activity')
                      : clientContextOn
                        ? (c.prospectName || c.prospectEmail || 'Anonymous viewer')
                        : c.prospectName
                          ? (c.prospectEmail ? `${c.prospectName} <${c.prospectEmail}>` : c.prospectName)
                          : (c.prospectEmail || 'Anonymous viewer');
                    const senderEmailSub = clientContextOn && !isMapComment
                      && c.prospectName && c.prospectEmail ? c.prospectEmail : null;
                    const avatarInitials = (String(senderDisplay || '?')
                      .split(/\s+/).map((w) => w[0]).filter(Boolean)
                      .slice(0, 2).join('').toUpperCase()) || '?';
                    return (
                      <li key={c.threadKey} className="conversations-row">
                        {clientContextOn && (
                          <div
                            className={`conversations-row-avatar${c.prospectIsInternal ? ' internal' : ''}`}
                            aria-hidden="true"
                          >
                            {avatarInitials}
                          </div>
                        )}
                        <div className="conversations-row-main">
                          <div className="conversations-row-head">
                            <span className={`prospect-message-kind kind-${c.lastMessageKind}`}>
                              {kindLabel}
                            </span>
                            <span className="conversations-row-sender">{senderDisplay}</span>
                            {clientContextOn && c.clientName && (
                              // The client this thread belongs to — the
                              // page's headline gap. Solid chip when a
                              // Decision Room names the company; muted
                              // when all we know is the email domain.
                              <span
                                className={`conversations-row-client${c.clientKind === 'domain' ? ' domain-only' : ''}`}
                                title={c.clientKind === 'domain'
                                  ? 'Inferred from the prospect’s email domain'
                                  : 'Client on this thread'}
                              >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <path d="M3 21h18" />
                                  <path d="M5 21V7l8-4v18" />
                                  <path d="M19 21V11l-6-4" />
                                </svg>
                                <span className="conversations-row-client-name">{c.clientName}</span>
                              </span>
                            )}
                            {clientContextOn && c.prospectIsInternal && (
                              <span
                                className="conversations-row-internal"
                                title="Sent from your own company's domain — you or a teammate testing a share link, not a prospect"
                              >
                                Internal
                              </span>
                            )}
                            {senderEmailSub && (
                              <span className="conversations-row-email">{senderEmailSub}</span>
                            )}
                            {c.awaitingSellerReply && (
                              <span className="prospect-thread-awaiting-pill">Awaiting reply</span>
                            )}
                            {/* Clip reference — when the prospect tagged
                                their message with a specific moment, show
                                "Re: <feature>" so the seller knows what
                                part of the trailer the conversation is about
                                without having to click in. */}
                            {typeof c.lastMessageClipIndex === 'number' && (
                              <span
                                title={`The latest message refers to: ${c.lastMessageClipFeature || `clip ${c.lastMessageClipIndex + 1}`}`}
                                style={{
                                  // inline-block (not flex) so a long
                                  // moment label ellipsizes instead of
                                  // swallowing the row header.
                                  display: 'inline-block', maxWidth: 240,
                                  overflow: 'hidden', textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap', verticalAlign: 'middle',
                                  padding: '2px 8px', borderRadius: 100,
                                  fontSize: 11, fontWeight: 600,
                                  background: 'rgba(99, 102, 241, 0.10)',
                                  color: '#4f46e5',
                                  border: '1px solid rgba(99, 102, 241, 0.22)',
                                }}
                              >
                                Re: {c.lastMessageClipFeature || `Clip ${c.lastMessageClipIndex + 1}`}
                              </span>
                            )}
                          </div>
                          <div className="conversations-row-demo">
                            {isMapComment ? <>on MAP — <strong>{c.actionTitle}</strong></> : <>on <strong>{c.demoTitle}</strong></>}
                            {c.buyerSpaceId && c.buyerSpaceName && (
                              // Space chip — at-a-glance signal that this
                              // thread is deal-attached. Click anywhere on
                              // the chip routes into the Space (same
                              // destination as "Open conversation"); the
                              // stopPropagation keeps the row click (which
                              // currently does nothing) from firing.
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const targetHash = `#/buyer-spaces/${c.buyerSpaceId}`;
                                  if (typeof window !== 'undefined') {
                                    window.history.pushState(null, '', window.location.pathname + window.location.search + targetHash);
                                  }
                                  setPhase('buyer-spaces');
                                }}
                                title={`Open ${c.buyerSpaceName} Decision Room`}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 4,
                                  marginLeft: 8, padding: '2px 8px',
                                  borderRadius: 100, fontSize: 11, fontWeight: 600,
                                  background: 'rgba(168, 85, 247, 0.12)',
                                  color: '#7c3aed', cursor: 'pointer',
                                  border: '1px solid rgba(168, 85, 247, 0.25)',
                                }}
                              >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                  <polyline points="9 22 9 12 15 12 15 22" />
                                </svg>
                                {c.buyerSpaceName}
                              </span>
                            )}
                          </div>
                          {c.lastMessagePreview && (
                            <div className="conversations-row-preview">
                              <span className="conversations-row-who">
                                {c.lastMessageSenderKind === 'seller' ? 'You: ' : ''}
                              </span>
                              {c.lastMessagePreview}
                              {Array.isArray(c.lastMessageMentions) && c.lastMessageMentions.length > 0 && (
                                <span style={{ marginLeft: 6, color: '#4f46e5', fontWeight: 600 }}>
                                  · mentions {c.lastMessageMentions.map((m) => `@${m.displayName}`).join(', ')}
                                </span>
                              )}
                            </div>
                          )}
                          <div className="conversations-row-meta">
                            {formatDemoDate(c.lastMessageAt)} · {c.messageCount} {c.messageCount === 1 ? 'message' : 'messages'}
                          </div>
                        </div>
                        <div className="conversations-row-actions">
                          {isMapComment ? (
                            // MAP comment thread — route into the Space's
                            // MAP section with the action ID in the hash so
                            // BuyerSpaceDetail can auto-expand the right
                            // action's comments thread.
                            <button
                              type="button"
                              className="prospect-message-reply"
                              onClick={() => {
                                const targetHash = `#/buyer-spaces/${c.buyerSpaceId}/map/${c.actionId}`;
                                if (typeof window !== 'undefined') {
                                  window.history.pushState(null, '', window.location.pathname + window.location.search + targetHash);
                                }
                                setPhase('buyer-spaces');
                              }}
                            >
                              Open conversation
                            </button>
                          ) : c.buyerSpaceId ? (
                            // Share-message thread attached to a Decision Room —
                            // route the seller into the Space's Conversations
                            // section in the same tab. That's where they
                            // reply alongside the rest of the deal context
                            // (MAP, docs, people). Hash-routing is owned
                            // by BuyerSpaceDetail and survives refresh.
                            <button
                              type="button"
                              className="prospect-message-reply"
                              onClick={() => {
                                const targetHash = `#/buyer-spaces/${c.buyerSpaceId}/conversations`;
                                if (typeof window !== 'undefined') {
                                  window.history.pushState(null, '', window.location.pathname + window.location.search + targetHash);
                                }
                                setPhase('buyer-spaces');
                              }}
                            >
                              Open conversation
                            </button>
                          ) : c.threadUrl ? (
                            // Orphan thread (legacy demo, webinar, lead-magnet)
                            // — open the prospect-facing share URL in a new
                            // tab. Same fallback the email deep-link uses.
                            <a
                              href={c.threadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="prospect-message-reply"
                            >
                              Open conversation
                            </a>
                          ) : null}
                          {/* "Open in trailers" only makes sense for
                              share-message threads (MAP comments aren't
                              attached to a specific demo). */}
                          {!isMapComment && (
                            <button
                              type="button"
                              className="prospect-thread-preview-btn"
                              onClick={() => {
                                // Jump into the completed-demo view — that's
                                // where the trailer plays + the other threads
                                // for the same demo live. Labeled "Open in
                                // trailers" because that's the
                                // user-visible name of the surface now.
                                if (c.demoId) loadCompletedDemo(c.demoId);
                              }}
                            >
                              Open in trailers
                            </button>
                          )}
                          {/* Delete is only wired for share-message threads
                              (the DELETE endpoint operates on share_messages
                              specifically; MAP comments have their own
                              delete flow inside the MAP editor). Hide on
                              map_comment rows. */}
                          {!isMapComment && (
                          <button
                            type="button"
                            className="conversations-row-delete"
                            onClick={() => deleteConversationThread(c)}
                            title="Delete this entire conversation"
                            aria-label="Delete conversation"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                              <path d="M10 11v6" />
                              <path d="M14 11v6" />
                              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            );
          })()}

          {/* REPORTS PHASE — engagement analytics dashboard.
              Three tabs: Top Demos (leaderboard), By User (team roster
              with per-user numbers), Activity (recent viewer feed).
              Backend aggregates from distribution_views. Team scope
              is universal under the unified TrailerCast tier — the
              Pro+ gate that lived here pre-2026-05-15 is gone. */}
          {/* SECURITY & PERMISSIONS PHASE — team roster, invites, role
              management. Admin-only (non-admins fall through to the
              member read-only view rendered by renderSecurityContent). */}
          {!bootstrapping && phase === 'security' && authUser && (
            <div className="reports-phase admin-phase">
              <div className="phase-header-row">
                <div className="reports-header">
                  <h1>Security &amp; Permissions</h1>
                  <p className="reports-subtitle">
                    Team members, roles, and invites.
                  </p>
                </div>
                <button className="back-to-dashboard" onClick={() => setPhase('dashboard')}>
                  ← Back to dashboard
                </button>
              </div>
              <div className="admin-phase-body">
                {renderSecurityContent()}
              </div>
            </div>
          )}

          {/* INTEGRATIONS PHASE — calendar OAuth, consent jurisdiction,
              bot display name, manual bot scheduling, recent bot runs. */}
          {!bootstrapping && phase === 'integrations' && authUser && (
            <div className="reports-phase admin-phase">
              <div className="phase-header-row">
                <div className="reports-header">
                  <h1>Integrations</h1>
                  <p className="reports-subtitle">
                    Connect your calendar and schedule bots on specific meetings.
                  </p>
                </div>
                <button className="back-to-dashboard" onClick={() => setPhase('dashboard')}>
                  ← Back to dashboard
                </button>
              </div>
              <div className="admin-phase-body">
                {renderIntegrationsContent()}
              </div>
            </div>
          )}

          {/* SETTINGS PHASE — company-wide admin config with internal tabs:
              Brand / AI Personalization / Bot Display Name / Recording
              Consent. ADMIN-ONLY surface. Non-admins never see this
              phase: LeftNav hides it (adminOnly: true), useEffect
              redirects away if they somehow land here. Least-privilege
              UI — if you can't do it, you don't see it. */}
          {!bootstrapping && phase === 'settings' && authUser && authUser?.company?.isAdmin && (() => {
            const isCompanyAdmin = true;
            // Company-wide settings only. Per-user Notifications moved
            // to the user's profile modal (Account → Notifications) where
            // every teammate can control their own channels — this
            // phase is admin-gated and isn't the right surface for a
            // per-user preference.
            const SETTINGS_TABS = [
              { id: 'brand',           label: 'Brand',             render: renderBrandContent },
              { id: 'personalization', label: 'AI Personalization', render: renderPersonalizationContent },
              { id: 'bot',             label: 'Bot Display Name',  render: renderBotNameContent },
              { id: 'consent',         label: 'Recording Consent', render: renderConsentContent },
              { id: 'billing',         label: 'Billing',           render: renderBillingContent },
              { id: 'deliverability',  label: 'Email Delivery',    render: renderDeliverabilityContent },
              // Products (2026-09-07) rides the proposals flag: no
              // proposals, no catalogue to manage.
              ...(authUser?.company?.featureFlags?.proposals
                ? [{ id: 'products', label: 'Products', render: renderProductsContent }]
                : []),
              // Security & Permissions has its own dedicated phase
              // (phase === 'security') with the team roster, role
              // changes, and granular permission editor — not a tab
              // here. The earlier 2026-05-07 add of a "Security" tab
              // inside Settings was redundant and got removed when the
              // GA + permission editor merged into the existing
              // Security & Permissions page.
            ];
            const activeTab = SETTINGS_TABS.find((t) => t.id === settingsPhaseTab) || SETTINGS_TABS[0];
            return (
              <div className="reports-phase admin-phase">
                <div className="phase-header-row">
                  <div className="reports-header">
                    <h1>Settings</h1>
                    <p className="reports-subtitle">
                      Company-wide configuration — brand, AI tone, how the bot shows up in meetings, and your recording-consent regime.
                    </p>
                  </div>
                  <button className="back-to-dashboard" onClick={() => setPhase('dashboard')}>
                    ← Back to dashboard
                  </button>
                </div>

                {/* Tab strip — horizontal on desktop, scrollable on mobile. */}
                <div className="settings-phase-tabs" role="tablist">
                  {SETTINGS_TABS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      role="tab"
                      className={`settings-phase-tab ${settingsPhaseTab === t.id ? 'is-active' : ''}`}
                      aria-selected={settingsPhaseTab === t.id}
                      onClick={() => {
                        setSettingsPhaseTab(t.id);
                        // Seed the Consent form's local state when the
                        // Consent tab becomes active, mirroring what
                        // integrations used to do on first open.
                        if (t.id === 'consent') {
                          setJurisdictionForm({
                            jurisdiction: authUser?.company?.consentJurisdiction || '',
                            privacyPolicyUrl: authUser?.company?.privacyPolicyUrl || '',
                          });
                          setJurisdictionError(null);
                        }
                        if (t.id === 'bot') {
                          setBotNameInput(authUser?.company?.botDisplayName || '');
                        }
                        // Lazy-load Notifications data on first click.
                        // Refetch each activation so a user who just
                        // connected Slack in another tab sees the new
                        // state without a page reload.
                        if (t.id === 'notifications') {
                          loadNotificationSettings();
                          setNotifFormOpen(null);
                          setNotifFormUrl('');
                        }
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                <div className="admin-phase-body">
                  {activeTab.render()}
                </div>
              </div>
            );
          })()}

          {/* AUDIT LOG PHASE — admin-only read of security-relevant events
              (logins, invites, role changes, config updates, bot
              dispatches, demo deletes, share revokes). The component
              self-handles fetch + pagination + filters; App just hands
              it the API base + bearer token. Non-admins see a friendly
              "admins only" panel inside the component (the backend 403
              drives that state). */}
          {!bootstrapping && phase === 'audit-log' && authUser && (
            <div className="reports-phase admin-phase">
              <div className="phase-header-row">
                <div className="reports-header">
                  <h1>Audit Log</h1>
                  <p className="reports-subtitle">
                    Security-relevant actions across your workspace — logins, team changes,
                    config updates, bot dispatches, and shared-link lifecycle events.
                  </p>
                </div>
                <button className="back-to-dashboard" onClick={() => setPhase('dashboard')}>
                  ← Back to dashboard
                </button>
              </div>
              <div className="admin-phase-body">
                <AuditLog apiBase={API_BASE} authToken={authToken} />
              </div>
            </div>
          )}

          {!bootstrapping && reportsActive && authUser && (() => {
            const reportsTier = (authUser && authUser.tier) || 'free';
            const canSeeTeamReports = ['pro', 'enterprise', 'beta'].includes(reportsTier);

            // Sortable column helpers — clicking a header toggles
            // asc/desc; clicking a different column sets desc fresh.
            const setSort = (col) => {
              setReportsSort((prev) => {
                if (prev.col === col) {
                  return { col, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
                }
                return { col, dir: 'desc' };
              });
            };
            const sortArrow = (col) => {
              if (reportsSort.col !== col) return '';
              return reportsSort.dir === 'desc' ? ' ▼' : ' ▲';
            };

            // Shared client-side filters: creator (team member) + date
            // range. Date filter anchors on firstViewAt for top-demos
            // (when the demo first got traffic; createdAt fallback for
            // view-less rows) and viewedAt on activity rows. Creator
            // filter matches creator.userId. It only applies where its
            // dropdown is actually visible (team scope / By User) —
            // filterCreatorId is shared with Library, and an invisible
            // filter silently blanking personal-scope tables read as
            // data loss.
            const fromTs = filterDateFrom ? new Date(`${filterDateFrom}T00:00:00`).getTime() : null;
            const toTs = filterDateTo ? new Date(`${filterDateTo}T23:59:59.999`).getTime() : null;
            const creatorFilterVisible = reportsScope === 'team' || reportsTab === 'by-user';
            const creatorIdNum = creatorFilterVisible && filterCreatorId !== 'all'
              ? Number(filterCreatorId)
              : null;
            const passesFilters = (row, dateField = 'createdAt') => {
              if (creatorIdNum != null) {
                const cid = row.userId ?? row.creatorUserId ?? row.creator?.userId ?? null;
                if (Number(cid) !== creatorIdNum) return false;
              }
              if (fromTs || toTs) {
                const d = row[dateField] || row.createdAt || row.at;
                const ts = d ? new Date(d).getTime() : null;
                if (!ts) return false;
                if (fromTs && ts < fromTs) return false;
                if (toTs && ts > toTs) return false;
              }
              return true;
            };
            const topDemosFiltered = reportsTopDemos.filter((r) => passesFilters(r, 'firstViewAt'));
            const activityFiltered = (reportsActivity || []).filter((r) => passesFilters(r, 'viewedAt'));
            const filtersActive = creatorIdNum != null || fromTs != null || toTs != null;
            const topDemosSorted = sortReportRows(topDemosFiltered, reportsSort.col, reportsSort.dir);
            // By User roster — apply the creator filter (narrows to a
            // single seller) and compute a date-window scale factor
            // that the render loop below applies to the fake deal-
            // outcome counts. The window default is 90 days (matches
            // the dashboard sparkline) — a fully open range is treated
            // as 1x so the default view shows the full roster.
            const byUserFiltered = creatorIdNum != null
              ? reportsByUser.filter((u) => Number(u.id) === creatorIdNum)
              : reportsByUser;
            const byUserSorted = sortReportRows(byUserFiltered, reportsSort.col, reportsSort.dir);

            const formatRelative = (when) => {
              if (!when) return '';
              const diffMs = Date.now() - new Date(when).getTime();
              if (diffMs < 60000) return 'just now';
              const mins = Math.round(diffMs / 60000);
              if (mins < 60) return `${mins}m ago`;
              const hrs = Math.round(mins / 60);
              if (hrs < 24) return `${hrs}h ago`;
              const days = Math.round(hrs / 24);
              if (days < 30) return `${days}d ago`;
              return new Date(when).toLocaleDateString();
            };

            const formatWatchTime = (secs) => {
              if (!secs || secs < 1) return '';
              const m = Math.floor(secs / 60);
              const s = Math.round(secs % 60);
              return m > 0 ? `${m}m ${s}s` : `${s}s`;
            };

            return (
              <div className="reports-page">
                <div className="phase-header-row">
                  <div className="reports-page-header">
                    <h2>Reports</h2>
                    <p>See how your trailers are performing, who's watching, and which demos are getting the most engagement. Click any row to drill in.</p>
                  </div>
                  <button className="back-to-dashboard" onClick={() => setPhase('dashboard')}>
                    ← Back to dashboard
                  </button>
                </div>

                <div className="reports-page-controls">
                  <div className="reports-tabs">
                    {[
                      { id: 'top-demos', label: 'Top demos' },
                      { id: 'by-user',   label: 'By user',  teamOnly: true },
                      { id: 'activity',  label: 'Activity' },
                    ]
                      .filter((t) => !t.teamOnly || canSeeTeamReports)
                      .map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          className={`reports-tab ${reportsTab === t.id ? 'active' : ''}`}
                          onClick={() => {
                            setReportsSort({ col: null, dir: 'desc' });
                            setReportsTab(t.id);
                          }}
                        >
                          {t.label}
                        </button>
                      ))}
                  </div>
                  {(reportsTab === 'top-demos' || reportsTab === 'by-user') && (
                    <button
                      type="button"
                      onClick={exportReportsCsv}
                      title="Download this table as CSV"
                      style={{
                        marginLeft: 'auto', padding: '7px 12px', borderRadius: 8,
                        border: '1px solid var(--room-border, #e2e8f0)', background: 'transparent',
                        color: 'var(--room-text-muted, #64748b)', fontSize: 12, fontWeight: 600,
                        fontFamily: 'inherit', cursor: 'pointer',
                      }}
                    >
                      ⤓ Export CSV
                    </button>
                  )}

                  {/* Scope toggle — hidden on by-user since that tab has
                      its own admin-vs-self logic on the backend. */}
                  {reportsTab !== 'by-user' && (
                    <div className="reports-scope">
                      <button
                        type="button"
                        className={`reports-scope-btn ${reportsScope === 'personal' ? 'active' : ''}`}
                        onClick={() => setReportsScope('personal')}
                      >
                        {canSeeTeamReports ? 'Mine' : 'Reports'}
                      </button>
                      {canSeeTeamReports && (
                        <button
                          type="button"
                          className={`reports-scope-btn ${reportsScope === 'team' ? 'active' : ''}`}
                          onClick={() => setReportsScope('team')}
                        >
                          Team
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Creator + date-range filters (client-side). Shared
                    state with Library so switching between the two
                    phases preserves the filters. Creator dropdown is
                    only meaningful on team scope — hidden on personal
                    since there's only one creator (you). Hidden on
                    by-user since that tab IS the creator breakdown. */}
                {/* Filter bar — shown on every tab now. On By User the
                    creator dropdown narrows the roster to a single row
                    and the date range proportionally scales the fake
                    deal-outcome counts (90d = full window). Real CRM
                    data will swap both in without changing the UI. */}
                <div className="reports-filter-bar">
                  {/* Creator dropdown — always useful to filter to one
                      user on team scope. On By User it applies to the
                      roster directly. */}
                  {(reportsScope === 'team' || reportsTab === 'by-user') && teamMembers.length > 0 && (
                    <select
                      className="reports-filter-select"
                      value={filterCreatorId}
                      onChange={(e) => setFilterCreatorId(e.target.value)}
                      aria-label="Filter by team member"
                    >
                      <option value="all">All team members</option>
                      {teamMembers.map((m) => (
                        <option key={m.id} value={String(m.id)}>
                          {m.fullName || m.email}
                        </option>
                      ))}
                    </select>
                  )}
                  <label className="reports-filter-date">
                    <span>From</span>
                    <input
                      type="date"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                    />
                  </label>
                  <label className="reports-filter-date">
                    <span>To</span>
                    <input
                      type="date"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                    />
                  </label>
                  {(filterCreatorId !== 'all' || filterDateFrom || filterDateTo) && (
                    <button
                      type="button"
                      className="reports-filter-clear"
                      onClick={() => {
                        setFilterCreatorId('all');
                        setFilterDateFrom('');
                        setFilterDateTo('');
                      }}
                    >
                      Clear filters
                    </button>
                  )}
                </div>

                {/* Load-failure banner — a 500 used to fall through to
                    the "No demos with views yet" empty state, which
                    reads as data loss. Now it's explicit + retryable. */}
                {reportsError && !reportsLoading && (
                  <div className="reports-error-banner" role="alert">
                    <span>{reportsError}</span>
                    <button
                      type="button"
                      onClick={() => loadReportsTab(reportsTab, reportsScope)}
                    >
                      Retry
                    </button>
                  </div>
                )}

                {/* ─── Top Demos tab ───────────────────────────── */}
                {reportsTab === 'top-demos' && !reportsError && (
                  reportsLoading && topDemosSorted.length === 0 ? (
                    <p className="reports-empty">Loading…</p>
                  ) : topDemosSorted.length === 0 ? (
                    <p className="reports-empty">
                      {filtersActive && reportsTopDemos.length > 0
                        ? 'No demos match your filters.'
                        : 'No demos with views yet. Once recipients watch your shared trailers, they\'ll appear here.'}
                    </p>
                  ) : (
                    <div className="reports-table-wrap">
                      <table className="reports-table">
                        <thead>
                          <tr>
                            <th>Demo</th>
                            {reportsScope === 'team' && <th>Creator</th>}
                            <th>Status</th>
                            <th className="reports-num" onClick={() => setSort('views')}>Views{sortArrow('views')}</th>
                            <th className="reports-num" onClick={() => setSort('uniqueViewers')}>Unique{sortArrow('uniqueViewers')}</th>
                            <th className="reports-num" onClick={() => setSort('avgCompletionPct')}>Avg %{sortArrow('avgCompletionPct')}</th>
                            <th className="reports-num" onClick={() => setSort('completions')}>Completions{sortArrow('completions')}</th>
                            <th className="reports-num" onClick={() => setSort('sends')}>Sends{sortArrow('sends')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topDemosSorted.map((d) => (
                            <tr key={d.id} className="reports-row-clickable" onClick={() => loadCompletedDemo(d.id)}>
                              <td>
                                <div className="reports-cell-title">{d.title}</div>
                                <div className="reports-cell-sub">{formatDemoDate(d.createdAt)}</div>
                              </td>
                              {reportsScope === 'team' && (
                                <td>{`${d.creator.firstName || ''} ${d.creator.lastName || ''}`.trim() || d.creator.email}</td>
                              )}
                              <td>
                                <DealStatusPill demo={d} compact />
                              </td>
                              <td className="reports-num">{d.views}</td>
                              <td className="reports-num">{d.uniqueViewers}</td>
                              <td className="reports-num">{d.avgCompletionPct}%</td>
                              <td className="reports-num">{d.completions}</td>
                              <td className="reports-num">{d.sends}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                )}

                {/* ─── By User tab ────────────────────────────── */}
                {reportsTab === 'by-user' && !reportsError && (
                  reportsLoading && byUserSorted.length === 0 ? (
                    <p className="reports-empty">Loading…</p>
                  ) : byUserSorted.length === 0 ? (
                    <p className="reports-empty">
                      {filtersActive && reportsByUser.length > 0
                        ? 'No team members match your filters.'
                        : 'No team data yet.'}
                    </p>
                  ) : (
                    <div className="reports-table-wrap">
                      <table className="reports-table">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Role</th>
                            <th className="reports-num" onClick={() => setSort('demosCreated')}>Demos{sortArrow('demosCreated')}</th>
                            <th className="reports-num" onClick={() => setSort('viewsGot')}>Views got{sortArrow('viewsGot')}</th>
                            <th className="reports-num" onClick={() => setSort('completions')}>Completions{sortArrow('completions')}</th>
                            <th className="reports-num" onClick={() => setSort('avgCompletionPct')}>Avg %{sortArrow('avgCompletionPct')}</th>
                            {/* Real reach columns (2026-07-26 design audit):
                                replaced the fabricated Won/Lost/Pending +
                                outcome-mix bar (djb2-seeded sample data) —
                                a leader sorting reps by invented win rates
                                is worse than no data. Sends + unique
                                viewers come straight from distributions +
                                distribution_views. */}
                            <th className="reports-num" onClick={() => setSort('sends')}>Sends{sortArrow('sends')}</th>
                            <th className="reports-num" onClick={() => setSort('uniqueViewers')}>Unique viewers{sortArrow('uniqueViewers')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {byUserSorted.map((u) => {
                            return (
                              <tr
                                key={u.id}
                                className="reports-row-clickable"
                                onClick={() => {
                                  // Drill into this seller's demos: jump
                                  // to Top Demos pre-filtered to them.
                                  setFilterCreatorId(String(u.id));
                                  setReportsSort({ col: null, dir: 'desc' });
                                  setReportsTab('top-demos');
                                  if (canSeeTeamReports) setReportsScope('team');
                                }}
                              >
                                <td>
                                  <div className="reports-cell-title">{`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}</div>
                                  <div className="reports-cell-sub">{u.email}</div>
                                </td>
                                <td>
                                  <span className={`role-badge role-${u.role || 'member'}`}>{u.role === 'global_admin' ? 'Global Admin' : u.role === 'admin' ? 'Admin' : 'Member'}</span>
                                </td>
                                <td className="reports-num">{u.demosCreated}</td>
                                <td className="reports-num">{u.viewsGot}</td>
                                <td className="reports-num">{u.completions}</td>
                                <td className="reports-num">{u.avgCompletionPct}%</td>
                                <td className="reports-num">{u.sends ?? 0}</td>
                                <td className="reports-num">{u.uniqueViewers ?? 0}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )
                )}

                {/* ─── Activity tab ───────────────────────────── */}
                {reportsTab === 'activity' && !reportsError && (
                  reportsLoading && reportsActivity.length === 0 ? (
                    <p className="reports-empty">Loading…</p>
                  ) : activityFiltered.length === 0 ? (
                    <p className="reports-empty">
                      {reportsActivity.length === 0
                        ? 'No activity yet. When recipients watch your shared trailers, you\'ll see a real-time feed here.'
                        : 'No activity matches your filters.'}
                    </p>
                  ) : (
                    <ul className="reports-activity-feed">
                      {activityFiltered.map((e) => {
                        const verb = e.eventType === 'view' ? 'opened'
                          : e.eventType === 'complete' ? 'finished'
                          : e.eventType === 'cta_click' ? 'clicked the CTA on'
                          : 'progressed through';
                        const viewerLabel = e.viewer
                          ? (e.viewer.name || e.viewer.email)
                          : 'Someone (via a shared link)';
                        return (
                          <li key={e.eventId} className="reports-activity-row" onClick={() => loadCompletedDemo(e.demo.id)}>
                            <div className="reports-activity-dot" data-type={e.eventType} />
                            <div className="reports-activity-body">
                              <div className="reports-activity-title">
                                <strong>{viewerLabel}</strong> {verb} <strong>{e.demo.title}</strong>
                              </div>
                              <div className="reports-activity-meta">
                                {formatRelative(e.viewedAt)}
                                {e.watchSeconds != null && e.watchSeconds > 0 && (
                                  <> · {formatWatchTime(e.watchSeconds)} watched</>
                                )}
                                {e.completionPct != null && (
                                  <> · {e.completionPct}% complete</>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )
                )}
              </div>
            );
          })()}

          {/* UPLOAD PHASE */}
          {!bootstrapping && phase === 'upload' && (
            <>
              {/* Back to dashboard */}
              <button className="back-to-dashboard" onClick={() => setPhase('dashboard')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                Dashboard
              </button>

              {uploadAsIs ? (
                <div className="hero">
                  <h2>Upload a Finished Video</h2>
                  <h2 className="gradient-text">Share It As-Is</h2>
                  <p>Stored exactly as you cut it — no AI rebuild, no narration, no slides. Ready to share the moment the upload finishes.</p>
                </div>
              ) : (
                <div className="hero">
                  <h2>Transform Your Demo Into</h2>
                  <h2 className="gradient-text">Cinematic Highlights</h2>
                  <p>Upload your demo recording and let AI craft a compelling trailer with professional narration</p>
                </div>
              )}

              {/* AI build vs. store as-is (finished_video_upload). A
                  personalized demo cut in an editor is already the thing
                  to send; pushing it through the pipeline re-cuts it. */}
              {finishedUploadEnabled && (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <div className="library-segmented" role="tablist" aria-label="What should happen to this file">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={!uploadAsIs}
                      className={`library-segment ${!uploadAsIs ? 'active' : ''}`}
                      onClick={() => { setUploadAsIs(false); setError(null); }}
                    >
                      Build a trailer with AI
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={uploadAsIs}
                      className={`library-segment ${uploadAsIs ? 'active' : ''}`}
                      onClick={() => {
                        setUploadAsIs(true);
                        setError(null);
                        // An audio file picked for the AI path has no
                        // meaning here — clear it rather than fail later.
                        if (selectedFile && !isVideoFile(selectedFile)) setSelectedFile(null);
                      }}
                    >
                      Upload a finished video
                    </button>
                  </div>
                </div>
              )}

              {/* Voice Settings — opens as a modal overlay so it doesn't
                  push the upload form down when the user is just checking
                  or tweaking their narrator pick. Preferences (voice +
                  language) are auto-saved to the user profile so next
                  upload defaults to whatever they picked last time. */}
              {!uploadAsIs && (
              <div className="brand-settings-section">
                <button
                  className="voice-trigger"
                  onClick={openVoiceModal}
                  type="button"
                >
                  <span className="voice-trigger-label">
                    <span className="voice-trigger-icon"></span>
                    <span>
                      <strong>Narration voice</strong>
                      <span className="voice-trigger-sub">
                        {(() => {
                          const v = availableVoices.find(x => x.id === selectedVoiceId);
                          const voiceName = v ? v.name : (userTier === 'free' ? 'Standard voice' : 'Default');
                          const lang = availableLanguages.find(l => l.code === selectedLanguage);
                          const langLabel = selectedLanguage === 'en' || !lang ? '' : ` · ${lang.name}`;
                          return `${voiceName}${langLabel}`;
                        })()}
                      </span>
                    </span>
                  </span>
                  <span className="voice-trigger-change">Change →</span>
                </button>

                {showVoiceSettings && (
                  <div
                    className="voice-modal-overlay"
                    onClick={(e) => {
                      // Clicking the backdrop cancels (reverts). Users
                      // expecting "click outside to save" can still hit
                      // the Save button in the footer. Matches the
                      // Cancel/Save pattern users asked for.
                      if (e.target === e.currentTarget) cancelVoiceModal();
                    }}
                    role="dialog"
                    aria-modal="true"
                  >
                    <div className="voice-modal">
                      <div className="voice-modal-header">
                        <h3>Narration voice</h3>
                        <button
                          type="button"
                          className="voice-modal-close"
                          onClick={cancelVoiceModal}
                          aria-label="Cancel narration changes"
                          title="Cancel"
                        >
                          ×
                        </button>
                      </div>
                      <div className="voice-modal-scroll">
                      <div className="voice-modal-body voice-panel">
                    {/* Language picker — only shown if the user has more than
                        just English (i.e. Pro+). Placed above the voice grid
                        so users pick language first. */}
                    {availableLanguages.length > 1 && (
                      <div className="language-field">
                        <label htmlFor="narration-language">Narration Language</label>
                        <select
                          id="narration-language"
                          className="language-select"
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                        >
                          {availableLanguages.map((l) => (
                            <option key={l.code} value={l.code}>
                              {l.name}{l.nativeName && l.nativeName !== l.name ? ` — ${l.nativeName}` : ''}
                            </option>
                          ))}
                        </select>
                        <span className="language-hint">
                          {selectedLanguage === 'en'
                            ? 'Default — narration is written in English.'
                            : 'Narration will be translated before voice synthesis.'}
                        </span>
                      </div>
                    )}

                    {/* Voice cloning — available on every tier.
                        Collapsed by default since the file-upload UI
                        for cloning shouldn't dominate the picker for
                        users who just want to browse the catalogue.
                        The header acts as the toggle; open state
                        reveals the Active voice summary + upload form. */}
                    <div className={`voice-clone-section ${showCloneSection ? 'open' : 'collapsed'}`}>
                        <button
                          type="button"
                          className="voice-clone-header voice-clone-toggle"
                          onClick={() => setShowCloneSection(!showCloneSection)}
                          aria-expanded={showCloneSection}
                        >
                          <span className="voice-clone-icon"></span>
                          <div className="voice-clone-header-text">
                            <strong>Clone your own voice</strong>
                            <span className="voice-clone-subtitle">
                              {authUser && authUser.clonedVoiceId
                                ? `Active: ${authUser.clonedVoiceName || 'Your voice'}`
                                : 'Upload a 30–60 second audio sample of clear speech.'}
                            </span>
                          </div>
                          <span className="voice-clone-arrow">{showCloneSection ? '▲' : '▼'}</span>
                        </button>

                        {showCloneSection && <>

                        {authUser && authUser.clonedVoiceId ? (
                          <div className="voice-clone-active">
                            <div className="voice-clone-active-info">
                              <span className="voice-clone-badge">Active</span>
                              <span className="voice-clone-active-name">{authUser.clonedVoiceName || 'Your voice'}</span>
                            </div>
                            <div className="voice-clone-active-actions">
                              <button
                                type="button"
                                className="voice-clone-replace"
                                onClick={() => { setCloneFile(null); setCloneName(''); setCloneError(null); cloneInputRef.current?.click(); }}
                                disabled={cloneLoading}
                              >
                                Replace
                              </button>
                              <button
                                type="button"
                                className="voice-clone-delete"
                                onClick={deleteClone}
                                disabled={cloneLoading}
                              >
                                {cloneLoading ? 'Working…' : 'Delete'}
                              </button>
                            </div>
                          </div>
                        ) : null}

                        <form className="voice-clone-form" onSubmit={submitClone}>
                          <div className="voice-clone-field">
                            <label>Voice name</label>
                            <input
                              type="text"
                              placeholder="e.g. Daniel Fellows — sales narrator"
                              value={cloneName}
                              onChange={(e) => setCloneName(e.target.value)}
                              maxLength={100}
                            />
                          </div>

                          <div className="voice-clone-field">
                            <label>Audio sample</label>
                            <div className="voice-clone-file-row">
                              <button
                                type="button"
                                className="voice-clone-file-btn"
                                onClick={() => cloneInputRef.current?.click()}
                              >
                                {cloneFile ? 'Change file' : 'Choose file…'}
                              </button>
                              <input
                                ref={cloneInputRef}
                                type="file"
                                accept="audio/*,.mp3,.wav,.m4a,.webm"
                                onChange={(e) => handleCloneFileSelect(e.target.files[0])}
                                style={{ display: 'none' }}
                              />
                              <span className="voice-clone-file-name">
                                {cloneFile
                                  ? `${cloneFile.name} · ${formatFileSize(cloneFile.size)}`
                                  : 'MP3, WAV, M4A, or WebM (up to 15 MB)'}
                              </span>
                            </div>
                          </div>

                          {cloneError && (
                            <div className="error-message"><span></span> {cloneError}</div>
                          )}

                          <button
                            type="submit"
                            className="voice-clone-submit"
                            disabled={cloneLoading || !cloneFile || !cloneName.trim()}
                          >
                            {cloneLoading ? (
                              <><span className="spinner"></span> Cloning…</>
                            ) : (
                              <> {authUser && authUser.clonedVoiceId ? 'Replace my voice' : 'Create my voice'}</>
                            )}
                          </button>
                        </form>

                        </>}
                      </div>

                    {/* Voice catalogue — every tier gets the full grid. */}
                    {voicesLoading && (
                      <p className="profile-muted">Loading voices…</p>
                    )}

                    {!voicesLoading && voicesError && availableVoices.length === 0 && (
                      <p className="loader-error">
                        {voicesError}
                        <button type="button" className="loader-retry" onClick={() => loadAvailableVoices()}>Retry</button>
                      </p>
                    )}

                    {!voicesLoading && !voicesError && availableVoices.length === 0 && (
                      <p className="profile-muted">
                        No voices configured yet. Your trailer will use the default voice.
                      </p>
                    )}

                    {!voicesLoading && availableVoices.length > 0 && (
                      <>
                        <p className="voice-panel-intro">
                          Pick the voice that will narrate your trailer.
                        </p>
                        <div className="voice-grid">
                          {availableVoices.map((voice) => {
                            const isActive = selectedVoiceId === voice.id;
                            const genderIcon = voice.gender === 'female' ? '♀' : voice.gender === 'male' ? '♂' : '•';
                            return (
                              <button
                                type="button"
                                key={voice.id}
                                className={`voice-card ${isActive ? 'active' : ''} ${voice.isCloned ? 'is-cloned' : ''}`}
                                onClick={() => setSelectedVoiceId(voice.id)}
                              >
                                <div className="voice-card-header">
                                  <span className="voice-card-name">
                                    {voice.name}
                                    {voice.isCloned && (
                                      <span className="voice-card-your-badge">Your voice</span>
                                    )}
                                  </span>
                                  <span className={`voice-card-gender gender-${voice.gender || 'other'}`}>
                                    {genderIcon}
                                  </span>
                                </div>
                                {voice.description && (
                                  <p className="voice-card-desc">{voice.description}</p>
                                )}
                                {isActive && (
                                  <span className="voice-card-check" aria-hidden="true">✓</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                      </div>{/* /voice-modal-body */}
                      </div>{/* /voice-modal-scroll */}

                      {/* Explicit footer — users were confused by having
                          only a backdrop/× to dismiss. Save commits the
                          preference via PATCH + closes; Cancel reverts
                          to the pre-open selection. */}
                      <div className="voice-modal-footer">
                        <button
                          type="button"
                          className="voice-modal-cancel"
                          onClick={cancelVoiceModal}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="voice-modal-save"
                          onClick={saveVoiceAndClose}
                        >
                          Save
                        </button>
                      </div>
                    </div>{/* /voice-modal */}
                  </div>
                )}
              </div>
              )}

              <div
                className={`upload-zone ${dragActive ? 'drag-active' : ''} ${selectedFile ? 'has-file' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={uploadAsIs ? 'video/mp4,video/webm,video/quicktime,.mp4,.mov,.webm' : 'video/*,audio/mpeg,.mp3,.mp4,.mov,.avi,.mkv'}
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                  style={{ display: 'none' }}
                />
                {!selectedFile ? (
                  <div className="upload-content">
                    <div className="upload-icon">
                      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M32 44V20M32 20L22 30M32 20L42 30" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M52 44V50C52 52.2091 50.2091 54 48 54H16C13.7909 54 12 52.2091 12 50V44" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      </svg>
                    </div>
                    <h3>{uploadAsIs ? 'Drop your finished video here' : 'Drop your demo here'}</h3>
                    <p>or click to browse</p>
                    <span className="file-types">{uploadAsIs ? 'MP4, MOV, or WebM — up to 2 GB' : 'MP4, MOV, AVI, MP3 — up to 2 GB'}</span>
                  </div>
                ) : (
                  <div className="file-preview">
                    <div className="file-icon">{isVideoFile(selectedFile) ? '' : ''}</div>
                    <div className="file-info">
                      <h4>{selectedFile.name}</h4>
                      <span>{formatFileSize(selectedFile.size)} · {uploadAsIs ? 'Finished video — stored as-is' : isVideoFile(selectedFile) ? 'Demo Highlights' : 'Podcast Trailer'}</span>
                    </div>
                    <button className="change-file" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}>✕</button>
                  </div>
                )}
              </div>

              {/* Large-file warning — upload time depends on connection
                  speed. A 1 GB file on a 25 Mbps hotel wifi is ~5 min;
                  a 2 GB file is 10+ min and may hit proxy timeouts. The
                  threshold (500 MB) picks up the slow-connection tail
                  without spamming every typical 60-min Zoom recording.
                  Direct-to-R2 uploads are queued as a post-launch fix;
                  this warning is the stopgap. */}
              {selectedFile && selectedFile.size > 500 * 1024 * 1024 && (
                <div className="large-file-warning">
                  <strong>Heads up: this is a large file ({formatFileSize(selectedFile.size)}).</strong>
                  <span>
                    Upload time depends on your connection speed — expect ~5 min
                    per 500 MB on a 25 Mbps wifi. On a slower connection, the
                    upload may time out. If you can, compress the recording first
                    (HandBrake, Clipchamp, or QuickTime export at 720p) before
                    uploading.
                  </span>
                </div>
              )}

              {uploadAsIs && (
                <div className="prospect-field">
                  <label htmlFor="finished-title">Title</label>
                  <input
                    id="finished-title"
                    type="text"
                    placeholder={selectedFile ? selectedFile.name.replace(/\.[^.]+$/, '') : 'e.g. Acme Corp — personalized demo'}
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    maxLength={255}
                  />
                  <span className="prospect-field-hint">
                    Optional. Names the card on Trailers; defaults to the file name.
                  </span>
                </div>
              )}

              {/* Prospect name — drives the intro slide's "Prepared for X" headline */}
              <div className="prospect-field">
                <label htmlFor="prospect-name">{uploadAsIs ? 'Who is this video for?' : 'Who is this demo for?'}</label>
                <input
                  id="prospect-name"
                  type="text"
                  placeholder="e.g. Acme Corp"
                  value={prospectName}
                  onChange={(e) => {
                    setProspectName(e.target.value);
                    // Naming a prospect is the tell that this is deal
                    // content; the explicit choice below still wins if
                    // the user flips it afterwards.
                    if (uploadAsIs) setUploadAsIsScope(e.target.value.trim() ? 'deal' : 'reusable');
                  }}
                  maxLength={120}
                />
                <span className="prospect-field-hint">
                  {uploadAsIs
                    ? 'Optional. Files the video under Deal and shows the prospect on the card.'
                    : 'Optional. Appears on the intro slide as "Prepared for…".'}
                </span>
              </div>

              {uploadAsIs && (
                <div className="prospect-field">
                  <label id="finished-scope-label">Where should it live?</label>
                  <div className="library-segmented" role="radiogroup" aria-labelledby="finished-scope-label" style={{ margin: 0 }}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={uploadAsIsScope === 'deal'}
                      className={`library-segment ${uploadAsIsScope === 'deal' ? 'active' : ''}`}
                      onClick={() => setUploadAsIsScope('deal')}
                    >
                      Deal
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={uploadAsIsScope === 'reusable'}
                      className={`library-segment ${uploadAsIsScope === 'reusable' ? 'active' : ''}`}
                      onClick={() => setUploadAsIsScope('reusable')}
                    >
                      Reusable
                    </button>
                  </div>
                  <span className="prospect-field-hint">
                    {uploadAsIsScope === 'deal'
                      ? 'Deal: made for one prospect, kept with that deal.'
                      : 'Reusable: a micro demo or explainer you will send to many people.'}
                    {' '}You can move it later from the card's ⋮ menu.
                  </span>
                </div>
              )}

              {error && (
                <div className="error-message">
                  <span></span> {error}
                </div>
              )}

              <button
                className={`generate-btn ${selectedFile ? 'ready' : ''}`}
                onClick={handleUpload}
                disabled={!selectedFile || loading}
              >
                <span className="btn-icon"></span>
                <span className="btn-text">{uploadAsIs ? 'Upload video' : 'Upload & Analyze'}</span>
              </button>

              {/* Feature cards */}
              {uploadAsIs ? (
                <div className="feature-cards">
                  <div className="feature-card">
                    <div className="feature-card-icon"></div>
                    <h4>Stored exactly as uploaded</h4>
                    <p>No transcription, moment detection, narration, or slides. What you cut is what gets sent.</p>
                  </div>
                  <div className="feature-card">
                    <div className="feature-card-icon"></div>
                    <h4>Ready the moment it lands</h4>
                    <p>Shows as completed on Trailers as soon as the upload finishes — no processing wait.</p>
                  </div>
                  <div className="feature-card">
                    <div className="feature-card-icon"></div>
                    <h4>Shares like any trailer</h4>
                    <p>Per-recipient share links with engagement tracking, Decision Room attach, and folders all apply.</p>
                  </div>
                </div>
              ) : (
              <div className="feature-cards">
                <div className="feature-card">
                  <div className="feature-card-icon"></div>
                  <h4>AI Moment Detection</h4>
                  <p>Finds customer reactions, aha moments, and quantified outcomes using Opus-powered analysis</p>
                </div>
                <div className="feature-card">
                  <div className="feature-card-icon"></div>
                  <h4>Professional Narration</h4>
                  <p>AI-generated voiceover ties each clip together with context and transitions</p>
                </div>
                <div className="feature-card">
                  <div className="feature-card-icon"></div>
                  <h4>Branded Slides</h4>
                  <p>Intro, agenda, and outro slides with your logo, colors, and custom text</p>
                </div>
              </div>
              )}
            </>
          )}

          {/* ANALYZING PHASE — hidden when the user has minimized the
              upload; the floating widget (rendered near the end of the
              App tree) takes over in that mode. */}
          {phase === 'analyzing' && !uploadMinimized && (
            <div className="processing-view">
              <div className="processing-view-actions">
                {/* The floater below keys on a pipeline jobId, which an
                    as-is upload never has — so minimizing would hide
                    the only progress indicator. Keep the view up. */}
                {!(uploadAsIs && status === 'uploading') && (
                <button
                  type="button"
                  className="processing-minimize-btn"
                  onClick={() => {
                    setUploadMinimized(true);
                    // Drop them on the dashboard so the widget has a
                    // populated page behind it rather than an empty
                    // card; they can left-nav anywhere from there.
                    setPhase('dashboard');
                  }}
                  title="Minimize — keep working while this processes"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14" />
                  </svg>
                  Minimize
                </button>
                )}
                <button className="cancel-btn cancel-btn-top" onClick={cancelUpload}>
                  <span>✕</span> Cancel Upload
                </button>
              </div>
              <div className="processing-animation">
                <div className="wave-container">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="wave-bar" style={{ animationDelay: `${i * 0.1}s` }} />
                  ))}
                </div>
              </div>
              <h2>{uploadAsIs && status === 'uploading' ? 'Uploading Your Video' : 'Analyzing Your Demo'}</h2>
              <div className="status-badge" style={{ '--status-color': getStatusInfo(status).color }}>
                <span>{getStatusInfo(status).icon}</span>
                <span>{getStatusInfo(status).text}</span>
              </div>
              <div className="progress-container">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
                <span className="progress-text">{progress}%</span>
              </div>
              <p className="processing-tip">
                {uploadAsIs && status === 'uploading'
                  ? 'Storing the file as-is — it will be ready to share the moment the upload finishes.'
                  : 'AI is transcribing and detecting the best moments…'}
              </p>
            </div>
          )}

          {/* CLIP SELECTION PHASE */}
          {phase === 'select' && (
            <div className="select-view">
              <div className="select-header">
                <h2>Review Detected Moments</h2>
                <p>
                  AI found <strong>{moments.length}</strong> high-value moments.
                  {canCustomizeClips
                    ? ' Preview each one, then check the boxes for the clips you want in your trailer.'
                    : ' Preview each one — your full trailer will include them all.'}
                </p>
              </div>

              {/* Shared preview player — used by all clip "Preview" buttons. */}
              {getSourceUrl() && (
                <div className={`clip-preview-player ${sourceIsVideo() ? 'is-video' : 'is-audio'}`}>
                  {sourceIsVideo() ? (
                    <video
                      ref={previewPlayerRef}
                      src={getSourceUrl()}
                      className="clip-preview-media"
                      controls
                      preload="metadata"
                      onTimeUpdate={handlePreviewTimeUpdate}
                      onEnded={stopPreview}
                    />
                  ) : (
                    <audio
                      ref={previewPlayerRef}
                      src={getSourceUrl()}
                      className="clip-preview-media"
                      controls
                      preload="metadata"
                      onTimeUpdate={handlePreviewTimeUpdate}
                      onEnded={stopPreview}
                    />
                  )}
                  <div className="clip-preview-hint">
                    {previewingMomentIndex != null
                      ? `Previewing clip #${previewingMomentIndex + 1}`
                      : 'Click ▶ Preview on any clip below to scrub through just that segment.'}
                  </div>
                </div>
              )}

              <div className="select-actions">
                <button className="select-action-btn" onClick={selectAll}>Select All</button>
                <button className="select-action-btn" onClick={deselectAll}>Deselect All</button>
                <span className="select-count">{selectedMoments.length} of {moments.length} selected</span>
              </div>

              <div className="moments-list">
                {moments.map((moment, index) => {
                  const isSelected = selectedMoments.includes(index);
                  const isPreviewing = previewingMomentIndex === index;
                  return (
                    <div
                      key={index}
                      className={`moment-card ${isSelected ? 'selected' : ''} ${isPreviewing ? 'previewing' : ''}`}
                      onClick={() => toggleMoment(index)}
                    >
                      <div className="moment-checkbox">
                        {isSelected ? (
                          <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <div className="checkbox-empty" />
                        )}
                      </div>
                      <div className="moment-body">
                        <div className="moment-top">
                          <span className="moment-number">#{index + 1}</span>
                          <span className="moment-time">{formatTime(moment.startSeconds)} – {formatTime(moment.endSeconds)}</span>
                          {moment.duration && (
                            <span
                              className="moment-duration"
                              title={moment.clipLengthJustification || undefined}
                            >{moment.duration}s</span>
                          )}
                          {/* Explainability (2026-08-12 AI-engine audit):
                              the detector already produces momentClass,
                              impactScore, and a written rationale for
                              every pick — surfacing them is what turns
                              "the AI chose this" into a reviewable
                              editorial decision. */}
                          {moment.momentClass === 'deal' && (
                            <span className="moment-class-chip moment-class-deal" title="A deal-defining exchange: objection handled, pricing landing, or a buying signal">DEAL MOMENT</span>
                          )}
                          {moment.impactScore != null && (
                            <span
                              className={`moment-impact ${moment.impactScore >= 80 ? 'impact-high' : moment.impactScore >= 60 ? 'impact-mid' : 'impact-low'}`}
                              title="AI-scored persuasive impact on a stakeholder who wasn't on the call (0–100)"
                            >
                              impact {moment.impactScore}
                            </span>
                          )}
                        </div>
                        <h4 className="moment-feature">{moment.feature || moment.title || `Moment ${index + 1}`}</h4>
                        {moment.benefit && <p className="moment-benefit">{moment.benefit}</p>}
                        {moment.criterion && <span className="moment-criterion">{moment.criterion}</span>}
                        {moment.reason && (
                          <p className="moment-reason">
                            <span className="moment-reason-label">Why this clip:</span> {moment.reason}
                          </p>
                        )}
                        {moment.transcript && <p className="moment-transcript">"{moment.transcript.substring(0, 120)}{moment.transcript.length > 120 ? '…' : ''}"</p>}
                      </div>
                      <div className="moment-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className={`preview-clip-btn ${isPreviewing ? 'playing' : ''}`}
                          onClick={() => isPreviewing ? stopPreview() : playClip(index)}
                          title={isPreviewing ? 'Stop preview' : 'Preview this clip'}
                        >
                          {isPreviewing ? (
                            <><span className="preview-icon">⏸</span><span>Stop</span></>
                          ) : (
                            <><span className="preview-icon">▶</span><span>Preview</span></>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {error && <div className="error-message"><span></span> {error}</div>}

              <button
                className={`generate-btn ${selectedMoments.length > 0 ? 'ready' : ''}`}
                onClick={handleGenerate}
                disabled={selectedMoments.length === 0 || loading}
              >
                <span className="btn-icon"></span>
                <span className="btn-text">
                  {canCustomizeClips
                    ? `Generate Trailer (${selectedMoments.length} clip${selectedMoments.length !== 1 ? 's' : ''})`
                    : `Generate Trailer (all ${moments.length} clips)`}
                </span>
              </button>

              <button className="back-btn" onClick={resetApp}>← Start Over</button>
            </div>
          )}

          {/* GENERATING PHASE — same minimize affordance as ANALYZING. */}
          {phase === 'generating' && !uploadMinimized && (
            <div className="processing-view">
              <div className="processing-view-actions">
                <button
                  type="button"
                  className="processing-minimize-btn"
                  onClick={() => {
                    setUploadMinimized(true);
                    setPhase('dashboard');
                  }}
                  title="Minimize — keep working while this renders"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14" />
                  </svg>
                  Minimize
                </button>
                <button className="cancel-btn cancel-btn-top" onClick={cancelUpload}>
                  <span>✕</span> Cancel
                </button>
              </div>
              <div className="processing-animation">
                <div className="wave-container">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="wave-bar" style={{ animationDelay: `${i * 0.1}s` }} />
                  ))}
                </div>
              </div>
              <h2>Creating Your Trailer</h2>
              <div className="status-badge" style={{ '--status-color': getStatusInfo(status).color }}>
                <span>{getStatusInfo(status).icon}</span>
                <span>{getStatusInfo(status).text}</span>
              </div>
              <div className="progress-container">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
                <span className="progress-text">{progress}%</span>
              </div>
              <p className="processing-tip">Narration, transitions, and composition — this takes 1–3 minutes</p>
            </div>
          )}

          {/* COMPLETED PHASE */}
          {phase === 'completed' && (
            <div className="success-view">
              {/* Phase header — title block on the left, back-to-library
                  tile right-aligned to match the Trailer Library / Buyer
                  Spaces / Reports header pattern. Reps clicking through
                  from the Library expect to go back THERE, not to the
                  dashboard. */}
              <div className="phase-header-row" style={{ marginBottom: 12 }}>
                <div>
                  <div className="success-icon"></div>
                  {/* Page headline is always the generic label — the
                      specific demo name moves above the trailer player
                      (see .reel-video-title below) where it reads as a
                      caption for the video itself instead of a page header. */}
                  <h2>AI-Generated Trailer</h2>
                  <p>Review the clips, tailor the chapters, and make this trailer exactly what your prospect needs to see.</p>
                </div>
                <button className="back-to-dashboard" onClick={() => setPhase('library')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                  Back to trailer library
                </button>
              </div>

              {/* Two-column desktop grid. Left column holds the trailer
                  video + its chapter list (video-centric). Right column
                  holds Demo stats (engagement) + Generate follow-up
                  (action-centric). On narrow viewports the grid
                  collapses to a single stacked column via CSS. */}
              <div className="reel-desktop-grid">
                <div className="reel-desktop-left">
              {trailerPath && !videoLoadError && (
                <>
                  {/* Demo-specific title. Shown right above the player
                      so it reads as a caption for THIS trailer (prospect
                      name preferred, filename stripped of extension as
                      fallback). Hidden entirely when neither is set to
                      avoid a blank line. */}
                  {(prospectName || currentDemoFilename) && (
                    <h3 className="reel-video-title">
                      {prospectName
                        || (currentDemoFilename && currentDemoFilename.replace(/\.[^.]+$/, ''))}
                    </h3>
                  )}
                <div className="video-preview">
                  <video
                    key={jobId}
                    ref={reelVideoRef}
                    controls
                    className="video-player"
                    preload="metadata"
                    onError={() => setVideoLoadError(true)}
                    onLoadedMetadata={(e) => {
                      // Capture the duration — drives chapter-tick
                      // positioning (tick left% = offset/duration).
                      setReelVideoDuration(e.target.duration || 0);
                      // Metadata loaded — the video is streamable. Clear
                      // any stuck-watch timeout so we don't flip into
                      // the "processing" panel by mistake.
                      if (videoWatchdogRef.current) {
                        clearTimeout(videoWatchdogRef.current);
                        videoWatchdogRef.current = null;
                      }
                    }}
                    onLoadStart={() => {
                      // Belt-and-suspenders watchdog: if the browser hasn't
                      // fired loadedmetadata within 20s, the stream has
                      // stalled (connection hung / Railway cold start /
                      // R2 object unreachable). Treat as a load failure
                      // so the user sees the "still processing" panel
                      // with a Retry button instead of an infinite
                      // spinner with no escape hatch.
                      if (videoWatchdogRef.current) {
                        clearTimeout(videoWatchdogRef.current);
                      }
                      videoWatchdogRef.current = setTimeout(() => {
                        setVideoLoadError(true);
                      }, 20000);
                    }}
                    onStalled={() => {
                      // Browser has given up getting more data. Usually
                      // means the source is unreachable. Trigger the
                      // error panel so the user isn't stuck.
                      setVideoLoadError(true);
                    }}
                  >
                    <source src={getVideoUrl()} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  {/* ─── Color-coded chapter strip (2026-05-08) ──────
                      Sits directly under the trailer player. Each
                      colored block corresponds to one moment in the
                      composed trailer; clicking jumps the player to
                      that moment. Built on top of moments[] (the
                      same array that drives the chapter list below),
                      mapped onto the trailer's timeline via
                      reelOffsetSeconds. Self-renders null when
                      duration / moments aren't ready, so we mount
                      it unconditionally. */}
                  {reelVideoDuration > 0 && Array.isArray(moments) && moments.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <TopicChapterStrip
                        segments={moments
                          .filter((m) => typeof m.reelOffsetSeconds === 'number')
                          .map((m) => {
                            // Each clip's trailer-timeline duration is
                            // its source duration (endSeconds - startSeconds).
                            // Falls back to 8s if either is missing — gives
                            // the block enough width to be clickable but
                            // not so much that it overlaps the next chapter.
                            const dur = (typeof m.endSeconds === 'number'
                                && typeof m.startSeconds === 'number')
                              ? Math.max(2, m.endSeconds - m.startSeconds)
                              : 8;
                            return {
                              startSeconds: m.reelOffsetSeconds,
                              endSeconds: m.reelOffsetSeconds + dur,
                              label: m.feature || 'Moment',
                            };
                          })}
                        totalDurationSeconds={reelVideoDuration}
                        onSeek={(seconds) => reelJumpTo({ reelOffsetSeconds: seconds })}
                        videoRef={reelVideoRef /* active-segment highlight + [/] keyboard nav */}
                        showLegend={false /* the chapter list below is the legend */}
                      />
                    </div>
                  )}
                  {/* Action bar — grouped by intent (2026-05-29 polish):
                      SHARE group (send it to the prospect) is visually
                      separated from the EDIT/SOURCE group (change it / see
                      the original) by a divider, and every button now
                      carries an icon (the slots were previously empty
                      <span>s). Copy-share stays the sole primary. */}
                  <div className="video-actions">
                    <div className="video-actions__group">
                      <button
                        className="video-action-btn video-action-btn--primary"
                        onClick={() => copyShareLinkForJob(jobId)}
                        title="Create and copy a public share link"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                        Copy share link
                      </button>
                      <button className="video-action-btn" onClick={handleDownload} title="Download the trailer MP4">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        Download
                      </button>
                      <button
                        className="video-action-btn"
                        onClick={() => setInternalShareJobId(jobId)}
                        title="Create a colleague-facing link — includes AI reasoning, never counts toward buyer engagement"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                        Share internally
                        <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 999, padding: '1px 7px', fontSize: 9, fontWeight: 700, marginLeft: 6, letterSpacing: '0.05em' }}>INTERNAL</span>
                      </button>
                    </div>

                    <span className="video-actions__divider" aria-hidden="true" />

                    <div className="video-actions__group">
                      <button
                        className="video-action-btn"
                        onClick={openRebuildModal}
                        title="Swap which clips are in the trailer without re-uploading"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                        Rebuild with different clips
                      </button>
                      <button
                        className="video-action-btn"
                        onClick={() => {
                          // Build a minimal "demo" object for the custom-trailer
                          // modal. We pass the current jobId + duration off the
                          // current state so validateClips can bound-check.
                          openCustomTrailer({
                            id: jobId,
                            duration_seconds: fullRecordingDuration || null,
                          });
                        }}
                        title="Hand-pick clips and let AI compose a fresh trailer around them"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 3v4M3 5h4M6 17v4M4 19h4"/><path d="M13 3l2.5 6.5L22 12l-6.5 2.5L13 21l-2.5-6.5L4 12l6.5-2.5z"/></svg>
                        Build your own trailer
                      </button>
                      <button
                        className="video-action-btn"
                        onClick={() => openFullRecording(jobId)}
                        title="Watch the original, unedited demo recording"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
                        Watch full recording
                      </button>
                    </div>
                  </div>
                </div>
                </>
              )}

              {rebuilding && (
                <div className="video-processing-panel">
                  <h3>Rebuilding your trailer…</h3>
                  <p>
                    Re-composing with your new clip selection. The page will
                    refresh automatically when it's ready — usually 1–3 minutes.
                  </p>
                  <div className="progress-container" style={{ marginTop: 12 }}>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${progress || 0}%` }}></div>
                    </div>
                    <span className="progress-text">{progress || 0}%</span>
                  </div>
                </div>
              )}

              {/* Custom-trailer build in progress. Same shape as the
                  rebuild panel — but the new trailer is a separate demo
                  row, so we navigate to it (loadCompletedDemo) when the
                  poller sees status=completed. The user's view doesn't
                  go blank like before; they see this panel until the
                  new trailer's ready. */}
              {customTrailerBuilding && (
                <div className="video-processing-panel">
                  <h3>Building your custom trailer…</h3>
                  <p>
                    Your hand-picked clips are running through the AI pipeline:
                    titling each section, generating slides, and narrating with
                    your voice. The page will switch to the new trailer
                    automatically when it's ready — usually 2–4 minutes.
                  </p>
                  <div className="progress-container" style={{ marginTop: 12 }}>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${customTrailerBuildingProgress || 0}%` }}
                      />
                    </div>
                    <span className="progress-text">{customTrailerBuildingProgress || 0}%</span>
                  </div>
                  <p className="profile-muted" style={{ marginTop: 10, fontSize: 12 }}>
                    Your original AI trailer + source recording stay intact. The
                    new trailer appears in your library when done.
                  </p>
                </div>
              )}

              {trailerPath && videoLoadError && (
                <div className="video-processing-panel">
                  <h3>Can't load this trailer right now</h3>
                  <p>
                    The video stream stalled. This usually means the backend
                    is waking up or the trailer is still being published.
                    Wait a moment and retry — if it keeps failing, you can
                    rebuild the trailer from the original recording below.
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      className="video-action-btn video-action-btn--primary"
                      onClick={() => {
                        // Force the <video> to re-request by reloading
                        // the demo (which re-keys the element via jobId
                        // and triggers a fresh loadStart). Simpler than
                        // imperatively calling video.load().
                        setVideoLoadError(false);
                        if (jobId) loadCompletedDemo(jobId);
                      }}
                    >
                      Retry
                    </button>
                    {/* Recovery actions that depend on the SOURCE recording
                        (still in R2), NOT the missing/stalled trailer object.
                        Without these the failed player is a dead-end: a
                        completed demo whose trailer never persisted (e.g. its
                        R2 upload failed during an outage) has no way to
                        regenerate. Rebuild re-composes from source; Watch
                        full recording confirms the source is reachable. */}
                    {Array.isArray(moments) && moments.length > 0 && (
                      <button
                        className="video-action-btn"
                        onClick={openRebuildModal}
                        title="Re-compose the trailer from the original recording — fixes a trailer that won't load"
                      >
                        <span></span> Rebuild trailer
                      </button>
                    )}
                    <button
                      className="video-action-btn"
                      onClick={() => openFullRecording(jobId)}
                      title="Watch the original, unedited recording"
                    >
                      <span></span> Watch full recording
                    </button>
                    <button
                      className="video-action-btn"
                      onClick={() => setPhase('dashboard')}
                    >
                      Back to dashboard
                    </button>
                  </div>
                </div>
              )}

              {/* Engagement heat-strip — visualizes which seconds of the
                  trailer prospects actually watched. Renders under the
                  video + action row so it reads as an extension of the
                  scrubber. Hidden for trailers with no views yet. */}
              {trailerPath && !videoLoadError && (
                <>
                  <EngagementHeatStrip heatmap={currentDemoHeatmap} />
                  {/* Attention density + top re-watched ranges (2026-08-05
                      ROI pack). Self-hides below 2 viewing sessions. */}
                  <AttentionCurve authToken={authToken} jobId={jobId} />
                  {/* Deal outcome marking — feeds the Impact report's
                      win-rate and won-value numbers. CRM-linked deals
                      sync automatically and overwrite manual marks. */}
                  <OutcomeControl authToken={authToken} jobId={jobId} />
                </>
              )}

              {/* Follow-Up Generator CTA — moved here from the right
                  column so the primary post-demo action sits directly
                  above the "In this trailer" chapter list. Modal-launching
                  pattern unchanged. */}
              {trailerPath && !videoLoadError && (
                <div className="followup-launcher">
                  <button
                    type="button"
                    className="followup-launcher-btn"
                    onClick={() => setShowFollowUp(true)}
                  >
                    <span className="followup-launcher-icon" aria-hidden="true">✉️</span>
                    <span className="followup-launcher-label">Generate Follow-Up Message</span>
                    <span className="followup-launcher-hint">Compose with AI → send to attendees</span>
                  </button>
                </div>
              )}

              {/* ─── Chapters list — table of contents for the trailer ───
                  Mirrors ShareViewer's "In this trailer" section. Clicking
                  a chapter seeks the trailer player to that clip's offset;
                  the "Watch full segment" pill (when the source-recording
                  is available) opens the full-recording modal at the
                  clip's end-point in the original meeting. */}
              {Array.isArray(moments) && moments.length > 0 && trailerPath && !videoLoadError && (
                <section className="reel-chapters">
                  <div className="reel-chapters-header">
                    <h3>In this trailer</h3>
                    <span className="reel-chapters-count">
                      {moments.length} {moments.length === 1 ? 'clip' : 'clips'}
                    </span>
                  </div>
                  {/* Timeline-tick strip — positioned by reelOffsetSeconds /
                      duration so clicks map 1:1 to the player scrubber.
                      Only rendered once we know duration AND at least
                      one moment has an offset (pre-Phase-2D demos lack
                      this; rendering without it would mis-position). */}
                  {reelVideoDuration > 0 && moments.some((m) => typeof m.reelOffsetSeconds === 'number') && (
                    <div className="reel-chapters-timeline" aria-hidden="true">
                      <div className="reel-chapters-timeline-track" />
                      {moments
                        .filter((m) => typeof m.reelOffsetSeconds === 'number')
                        .map((m, i) => {
                          const leftPct = Math.max(0, Math.min(100,
                            (m.reelOffsetSeconds / reelVideoDuration) * 100
                          ));
                          return (
                            <button
                              key={i}
                              type="button"
                              className="reel-chapters-timeline-tick"
                              style={{ left: `${leftPct}%` }}
                              onClick={() => reelJumpTo(m)}
                              title={`${m.feature} — ${formatClipTime(m.reelOffsetSeconds)}`}
                              aria-label={`Jump to ${m.feature} at ${formatClipTime(m.reelOffsetSeconds)}`}
                            />
                          );
                        })}
                    </div>
                  )}
                  <ol className="reel-chapters-list">
                    {moments.map((m, i) => {
                      // Two distinct seek targets:
                      //   - reelClipStart: where this clip starts inside
                      //     the trailer (what the chapter ROW
                      //     button jumps to — scrubs the trailer player).
                      //   - sourceJumpTo: where this clip STARTS in the
                      //     ORIGINAL recording (what the "Watch full
                      //     segment" pill opens — pops the full-recording
                      //     modal so the seller can watch the segment in
                      //     full context from its true start point).
                      //
                      // Regression history (resolved 2026-05-08):
                      //   - 488ff1df pointed the pill at the reel offset,
                      //     making it identical to the row's own button.
                      //   - 596c8d0b restored a source-recording deep-link
                      //     but used m.endSeconds (end of clip), so
                      //     clicking "Watch full segment" landed AFTER
                      //     the segment instead of AT IT — a label /
                      //     behavior mismatch the user flagged in
                      //     project_open_bugs.md. Switched back to
                      //     m.startSeconds so the action matches the
                      //     label: you watch THE SEGMENT, in full, from
                      //     the start.
                      const reelClipStart = typeof m.reelOffsetSeconds === 'number'
                        ? m.reelOffsetSeconds
                        : (typeof m.startSeconds === 'number' ? m.startSeconds : 0);
                      const sourceJumpTo = typeof m.startSeconds === 'number'
                        ? m.startSeconds
                        : 0;
                      // Rewatch badge — how many times prospects
                      // played this moment's segment. Hidden for 0
                      // (too noisy on a cold trailer), for legacy demos
                      // where the heatmap doesn't know about this
                      // moment, AND for synthetic heatmaps. The
                      // heat-strip itself shows phantom data with a
                      // "Sample engagement" label, but per-moment
                      // counts shouldn't lie about playback numbers.
                      const rewatchCount = (() => {
                        if (!currentDemoHeatmap || !Array.isArray(currentDemoHeatmap.moments)) return 0;
                        if (currentDemoHeatmap.synthetic) return 0;
                        const hit = currentDemoHeatmap.moments.find((hm) => hm.momentIndex === i);
                        return hit ? hit.watches : 0;
                      })();
                      return (
                        <li key={i} className="reel-chapter">
                          <button
                            type="button"
                            className="reel-chapter-button"
                            onClick={() => reelJumpTo(m)}
                          >
                            <span className="reel-chapter-index">
                              {String(i + 1).padStart(2, '0')}
                            </span>
                            <span className="reel-chapter-body">
                              <span className="reel-chapter-feature">
                                {m.feature}
                                {rewatchCount > 0 && (
                                  <span
                                    className={`reel-chapter-watches ${rewatchCount >= 3 ? 'is-hot' : ''}`}
                                    title={`${rewatchCount} playback${rewatchCount === 1 ? '' : 's'} of this moment`}
                                  >
                                    {rewatchCount}× watched
                                  </span>
                                )}
                              </span>
                              {m.benefit && (
                                <span className="reel-chapter-benefit">{m.benefit}</span>
                              )}
                            </span>
                            <span className="reel-chapter-time">
                              {formatClipTime(
                                typeof m.reelOffsetSeconds === 'number'
                                  ? m.reelOffsetSeconds
                                  : m.startSeconds
                              )}
                            </span>
                          </button>
                          {currentDemoSourceAvailable && (
                            <button
                              type="button"
                              className="reel-chapter-source"
                              onClick={(e) => {
                                e.stopPropagation();
                                openFullRecording(jobId, sourceJumpTo);
                              }}
                              title={`Open the full recording at ${formatClipTime(sourceJumpTo)} — where this clip starts`}
                            >
                              Watch full segment
                              <span
                                className="reel-chapter-source-time"
                                title="Timestamp in the original recording (different timeline from the trailer time on the left)"
                              >
                                {formatClipTime(sourceJumpTo)}
                              </span>
                            </button>
                          )}
                          {/* Segment regen (2026-08-12): rewrite THIS
                              clip's narration and recompose — the rest
                              of the reel stays untouched. Gated on
                              narrationTexts (trailers built after the
                              feature) + not already recomposing. */}
                          {Array.isArray(demoNarrationTexts) && demoNarrationTexts.length === moments.length && (
                            <button
                              type="button"
                              className="reel-chapter-regen"
                              disabled={regenNarrationBusy != null}
                              onClick={(e) => { e.stopPropagation(); handleRegenerateNarration(i); }}
                              title={demoNarrationTexts[i]
                                ? `Rewrite this clip's narration and re-render the trailer.\n\nCurrent: "${demoNarrationTexts[i]}"`
                                : "Rewrite this clip's narration and re-render the trailer."}
                            >
                              {regenNarrationBusy === i ? 'Rewriting…' : '↻ Narration'}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </section>
              )}

              {/* ─── Talking Timeline (always visible) ─────────────────
                  2026-05-15 refactor: removed the 3-pill switcher
                  (Talking timeline / AI Deal Brief / Transcript). Now:
                    - Talking timeline is ALWAYS visible here in a
                      card, matching the qualifying-call detail
                      screen layout pattern.
                    - AI Deal Brief is DELETED — the Decision Room owns
                      that view, so it was redundant on the trailer
                      review surface.
                    - Transcript moved to the right rail as a new
                      tab (sits alongside Engagement / Highlights /
                      Comments / Ask Winter — same pattern as the
                      qualifying-call screen). See the right rail
                      block further down.
                  Talk-time timestamps live on the FULL recording's
                  timeline; clicks open the existing full-recording
                  modal at the timestamp, mirroring how "Watch full
                  segment" already works on chapter rows above. */}
              {trailerPath && !videoLoadError && (
                (() => {
                  const ptt = currentDemoParticipantTalkTime;
                  const transcript = currentDemoTranscript;
                  const segments = Array.isArray(transcript?.segments) ? transcript.segments : [];
                  const baselineLabels = (transcript && transcript.speakerLabels) || {};
                  // Augment diarized speaker IDs with multi-track names
                  // by rank-aligning top-talker → top-talker. Same trick
                  // used in QualifyingCallsPhase.
                  let labels = baselineLabels;
                  if (ptt && Array.isArray(ptt.participants) && ptt.participants.length) {
                    const totals = new Map();
                    for (const s of segments) {
                      if (s.speaker == null) continue;
                      const dur = Math.max(0, (s.end || 0) - (s.start || 0));
                      totals.set(s.speaker, (totals.get(s.speaker) || 0) + dur);
                    }
                    const ranked = [...totals.entries()]
                      .sort((a, b) => b[1] - a[1])
                      .map(([id]) => id);
                    labels = { ...baselineLabels };
                    ranked.forEach((spId, idx) => {
                      const p = ptt.participants[idx];
                      if (p && p.name) labels[spId] = p.name;
                    });
                  }
                  // Stats: prefer multi-track totals when present (richer + accurate).
                  let stats;
                  if (ptt && Array.isArray(ptt.participants) && ptt.participants.length) {
                    stats = {
                      totalSeconds: ptt.totalSpeakerSeconds || 0,
                      bySpeaker: ptt.participants.map((p, idx) => ({
                        speaker: p.participantId || `mt_${idx}`,
                        seconds: p.totalSeconds || 0,
                        pct: p.pct || 0,
                        label: p.name || `Participant ${idx + 1}`,
                        sublabel: p.email || (p.isHost ? 'Host' : null),
                        color: reviewGetSpeakerColor(idx, idx),
                        segments: Array.isArray(p.segments) ? p.segments : [],
                      })),
                    };
                  } else {
                    stats = reviewComputeSpeakerStats(segments, labels);
                  }
                  // Layered name resolver — applies user overrides,
                  // calendar attendees, NER, AND the demo-attendees
                  // list (which doubles as our "email signature" data
                  // source for titles) on top of whichever base path
                  // (multi-track or diarized) produced stats. Same
                  // logic as in CallsPhase, deduped via the shared
                  // CallReviewSections export.
                  stats = {
                    ...stats,
                    bySpeaker: reviewApplySpeakerNameLayers(stats.bySpeaker, {
                      speakerNameOverrides: currentDemoSpeakerNameOverrides,
                      calendarAttendees: currentDemoCalendarAttendees,
                      extractedNames: currentDemoExtractedNames,
                      demoAttendees: attendees,
                    }),
                  };
                  const hasTimeline = segments.length > 0 || (ptt && ptt.participants && ptt.participants.length);
                  // No timeline? Render nothing — there's nothing to show
                  // here. (Transcript availability is checked separately
                  // by the right-rail Transcript tab.)
                  if (!hasTimeline) return null;

                  const seekFullRecording = (seconds) => {
                    if (!jobId) return;
                    openFullRecording(jobId, seconds);
                  };

                  // Inline rename handler for the talking-timeline.
                  // Updates local state optimistically so the rename
                  // shows immediately, then waits for the server
                  // confirmation. On error, reverts.
                  const handleSpeakerRename = async (speakerId, newName) => {
                    if (!jobId) throw new Error('No demo loaded');
                    const previous = currentDemoSpeakerNameOverrides;
                    // Optimistic update: merge into local state first.
                    const next = { ...(previous || {}) };
                    if (newName) next[String(speakerId)] = newName;
                    else delete next[String(speakerId)];
                    setCurrentDemoSpeakerNameOverrides(Object.keys(next).length ? next : null);
                    try {
                      const r = await authFetch(
                        `${API_BASE}/api/demos/${encodeURIComponent(jobId)}/speakers/${encodeURIComponent(String(speakerId))}/name`,
                        {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ name: newName }),
                        }
                      );
                      if (!r.ok) {
                        const body = await r.json().catch(() => ({}));
                        throw new Error(body.error || `Server returned ${r.status}`);
                      }
                    } catch (err) {
                      // Revert the optimistic update on failure.
                      setCurrentDemoSpeakerNameOverrides(previous);
                      throw err;
                    }
                  };

                  // Always-visible Talking Timeline in a clean card,
                  // matching the qualifying-call detail screen's
                  // pattern. No pill switcher, no Deal Brief — the
                  // single most important post-demo review surface
                  // sits right here under the video.
                  return (
                    <div style={{
                      marginTop: 24,
                      background: 'var(--room-card, #fff)',
                      borderRadius: 12,
                      border: '1px solid var(--room-border, #e2e8f0)',
                      padding: 16,
                    }}>
                      <div style={{
                        fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
                        textTransform: 'uppercase', color: 'var(--room-text-muted, #64748b)',
                        marginBottom: 10,
                      }}>
                        Talk time
                      </div>
                      <ReviewTalkingTimeline
                        segments={segments}
                        speakerLabels={labels}
                        speakerStats={stats}
                        onSeek={seekFullRecording}
                        onRename={handleSpeakerRename}
                        emptyMessage="No talk-time data — this recording predates the multi-track ingest, or it was a manual upload without a Recall bot."
                      />
                    </div>
                  );
                })()
              )}

                </div>{/* /.reel-desktop-left */}

                {/* ─── Right column — tabbed reference panel ───────
                    Tabs (2026-05-08 rebuild):
                      Engagement (default) — prospect threads + stats
                      Highlights            — AI feature/benefit summary
                      Comments              — internal team chat with @mentions
                      Ask Winter            — AI Q&A grounded on this demo
                    Tab state lives in `demoRightTab` (App-level) and
                    resets to 'engagement' on demo change. */}
                <div className="reel-desktop-right">

              {/* Tab bar — sits above all tab panels. Sticky so it
                  stays visible as the user scrolls long content
                  (e.g. a 50-message conversation thread or a long
                  AI Q&A session). */}
              <div style={{
                display: 'inline-flex', flexWrap: 'wrap', gap: 4,
                padding: 4, borderRadius: 12,
                background: 'var(--room-card-hover, #f1f5f9)',
                border: '1px solid var(--room-border, #e2e8f0)',
                marginBottom: 16, alignSelf: 'flex-start',
              }}>
                {[
                  { key: 'engagement',  label: 'Engagement' },
                  { key: 'highlights',  label: 'Highlights' },
                  { key: 'transcript',  label: 'Transcript' },
                  { key: 'comments',    label: 'Comments' },
                  { key: 'ask-winter',  label: 'Ask Winter' },
                ].map((t) => {
                  const active = demoRightTab === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => { reelTabUserPickedRef.current = true; setDemoRightTab(t.key); }}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 8,
                        border: 'none',
                        background: active ? 'var(--room-card, #ffffff)' : 'transparent',
                        color: active ? 'var(--room-text-strong, #0f172a)' : 'var(--room-text-muted, #64748b)',
                        fontSize: 13, fontWeight: active ? 600 : 500,
                        fontFamily: 'inherit', cursor: 'pointer',
                        boxShadow: active ? '0 1px 3px rgba(15, 23, 42, 0.08)' : 'none',
                        transition: 'all 120ms ease',
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      {t.label}
                      {/* Comments tab count badge — same pattern as
                          the call-detail tab strip. */}
                      {t.key === 'comments' && demoCommentCount > 0 && (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          minWidth: 18, height: 18, padding: '0 5px',
                          background: active ? '#0891b2' : 'var(--room-border, #cbd5e1)',
                          color: 'white', borderRadius: 10,
                          fontSize: 11, fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          {demoCommentCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Prospect conversation threads — V2. Each thread groups
                  messages between one prospect (by email) and the seller
                  on a specific distribution. Click a thread to expand the
                  full conversation + reply inline. Reply sends an email
                  to the prospect with a signed resume link back to the
                  share page. Now gated to the Engagement tab. */}
              {demoRightTab === 'engagement' && demoThreads.length > 0 && (
                <div className="prospect-messages-section">
                  <div className="prospect-messages-header">
                    <h3>Conversations</h3>
                    <span className="prospect-messages-count">
                      {demoThreads.length} {demoThreads.length === 1 ? 'thread' : 'threads'}
                    </span>
                  </div>
                  <ul className="prospect-threads-list">
                    {demoThreads.map((t) => {
                      const open = openThreadKey === t.threadKey;
                      const latest = t.messages[0];
                      const latestKindLabel = latest && (
                        latest.kind === 'question' ? 'Question'
                        : latest.kind === 'followup_request' ? 'Follow-up request'
                        : 'Comment'
                      );
                      const prospectDisplay = t.prospectName
                        ? (t.prospectEmail ? `${t.prospectName} <${t.prospectEmail}>` : t.prospectName)
                        : (t.prospectEmail || 'Anonymous viewer');
                      return (
                        <li
                          key={t.threadKey}
                          className={`prospect-thread-item ${open ? 'is-open' : ''} ${t.awaitingSellerReply ? 'awaiting' : ''}`}
                        >
                          <button
                            type="button"
                            className="prospect-thread-head"
                            onClick={() => toggleThreadOpen(t.threadKey)}
                            aria-expanded={open}
                          >
                            <span className="prospect-thread-head-main">
                              <span className="prospect-thread-sender">{prospectDisplay}</span>
                              <span className="prospect-thread-meta">
                                <span className={`prospect-message-kind kind-${latest?.kind || 'comment'}`}>
                                  {latestKindLabel || 'Thread'}
                                </span>
                                <span>·</span>
                                <span>{t.messageCount} {t.messageCount === 1 ? 'message' : 'messages'}</span>
                                <span>·</span>
                                <span>{formatDemoDate(t.lastMessageAt)}</span>
                                {t.awaitingSellerReply && (
                                  <span className="prospect-thread-awaiting-pill">Awaiting reply</span>
                                )}
                              </span>
                            </span>
                            <span className="prospect-thread-chevron">{open ? '▲' : '▼'}</span>
                          </button>
                          {open && (
                            <div className="prospect-thread-body">
                              <ol className="prospect-thread-messages">
                                {[...t.messages].reverse().map((m) => (
                                  <li
                                    key={m.id}
                                    className={`prospect-thread-msg is-${m.senderKind}`}
                                  >
                                    <div className="prospect-thread-msg-head">
                                      <strong>
                                        {m.senderKind === 'seller'
                                          ? (m.senderName || 'You')
                                          : (m.senderName || m.senderEmail || 'Prospect')}
                                      </strong>
                                      <span className="prospect-thread-msg-date">
                                        {formatDemoDate(m.createdAt)}
                                      </span>
                                    </div>
                                    <div className="prospect-thread-msg-body">{m.body}</div>
                                  </li>
                                ))}
                              </ol>
                              {t.prospectEmail ? (
                                <div className="prospect-thread-reply">
                                  <textarea
                                    className="prospect-thread-reply-input"
                                    placeholder={`Reply to ${t.prospectName || t.prospectEmail}…`}
                                    value={open ? threadReplyBody : ''}
                                    onChange={(e) => setThreadReplyBody(e.target.value)}
                                    rows={3}
                                    maxLength={4000}
                                  />
                                  {threadReplyError && (
                                    <div className="prospect-thread-reply-error">{threadReplyError}</div>
                                  )}
                                  <div className="prospect-thread-reply-footer">
                                    <span className="prospect-thread-reply-hint">
                                      They'll get an email with a link back to this conversation.
                                    </span>
                                    <div className="prospect-thread-reply-actions">
                                      <button
                                        type="button"
                                        className="prospect-thread-preview-btn"
                                        onClick={() => previewThreadAsProspect(t.threadKey)}
                                        title="Open the share page the way your prospect sees it"
                                      >
                                        Preview as prospect
                                      </button>
                                      <button
                                        type="button"
                                        className="prospect-message-reply"
                                        onClick={() => submitThreadReply(t.threadKey)}
                                        disabled={threadReplySubmitting || !threadReplyBody.trim()}
                                      >
                                        {threadReplySubmitting ? 'Sending…' : 'Send reply'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="prospect-thread-noemail">
                                  This prospect didn't leave an email address, so there's
                                  no way to continue the conversation with them here.
                                </div>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* ─── Highlight Summary — AI-derived overview ─────
                  Synthesized from the moments the AI already picked —
                  feature + benefit pairs drove clip selection, so
                  reading them back gives the seller a quick "what
                  this trailer is about" brief without a second API
                  call. Gated to the Highlights tab in the rebuilt
                  right rail (2026-05-08). */}
              {demoRightTab === 'highlights' && Array.isArray(moments) && moments.length > 0 && trailerPath && !videoLoadError && (() => {
                const features = moments
                  .map((m) => m.feature)
                  .filter((f) => typeof f === 'string' && f.trim());
                if (features.length === 0) return null;
                const lead = features.slice(0, 3).join(' · ');
                const topMoments = moments.slice(0, 3).filter(
                  (m) => typeof m.feature === 'string' && typeof m.benefit === 'string'
                );
                return (
                  <section className={`reel-summary ${summaryOpen ? 'is-open' : 'is-collapsed'}`}>
                    <button
                      type="button"
                      className="reel-summary-head"
                      onClick={() => setSummaryOpen((v) => !v)}
                      aria-expanded={summaryOpen}
                    >
                      <h3>Highlight Summary</h3>
                      <span className="reel-summary-meta">
                        <span className="reel-summary-count">
                          {moments.length} moment{moments.length === 1 ? '' : 's'}
                        </span>
                        <span className="reel-summary-chevron" aria-hidden="true">
                          {summaryOpen ? '▲' : '▼'}
                        </span>
                      </span>
                    </button>
                    {summaryOpen && (
                      <div className="reel-summary-body">
                        <p className="reel-summary-lead">{lead}</p>
                        {topMoments.length > 0 && (
                          <ul className="reel-summary-points">
                            {topMoments.map((m, i) => (
                              <li key={i}>
                                <strong>{m.feature}</strong>
                                {m.benefit && <> — {m.benefit}</>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </section>
                );
              })()}

              {/* Demo stats — always-visible engagement panel.
                  Previously hidden behind a toggle that loaded on expand;
                  now loads on phase mount (see the useEffect near the
                  loadEngagementForDemo call site) so sellers see their
                  view/completion numbers without an extra click.
                  Gated to the Engagement tab (2026-05-08 rebuild). */}
              {demoRightTab === 'engagement' && (
              <div className="engagement-card">
                <div className="engagement-card-head">
                  <h3>
                    Demo stats
                    {engagementData && engagementData.aggregate && engagementData.aggregate.views > 0 && (
                      <span className="engagement-quick-stat">
                        {' '}· {engagementData.aggregate.views} view{engagementData.aggregate.views === 1 ? '' : 's'}
                      </span>
                    )}
                  </h3>
                </div>

                  <div className="engagement-panel">
                    {/* Refresh bar — manual + auto-poll every 20s. A
                        seller copy-pasting the link into a separate
                        tab to test would otherwise stare at stale
                        numbers until they close+reopen this panel. */}
                    <div className="engagement-refresh-bar">
                      <span className="engagement-refresh-hint">
                        {engagementLoading ? 'Updating…' : 'Click refresh to update'}
                      </span>
                      <button
                        type="button"
                        className="engagement-refresh-btn"
                        onClick={() => jobId && loadEngagementForDemo(jobId)}
                        disabled={engagementLoading}
                        title="Refresh now"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="23 4 23 10 17 10" />
                          <polyline points="1 20 1 14 7 14" />
                          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                        </svg>
                        Refresh
                      </button>
                    </div>

                    {engagementLoading && !engagementData && (
                      <p className="engagement-empty">Loading…</p>
                    )}
                    {!engagementLoading && !engagementData && engagementError && (
                      <p className="engagement-empty loader-error">
                        {engagementError}
                        <button type="button" className="loader-retry" onClick={() => loadEngagementForDemo(jobId)}>Retry</button>
                      </p>
                    )}
                    {!engagementLoading && engagementData && (
                      <>
                        {/* Adaptive top block (2026-05-29): the fixed 4-zero
                            grid read as "nothing here" on a fresh trailer.
                            Now the panel meets the trailer's lifecycle:
                              • views > 0        → the real stat cards (hero)
                              • shared, 0 views  → "waiting for first open"
                              • not shared yet   → a share CTA that IS the
                                                   next action. */}
                        {engagementData.aggregate.views > 0 ? (
                          <div className="engagement-stat-grid">
                            <div className="engagement-stat">
                              <span className="engagement-stat-value">{engagementData.aggregate.views}</span>
                              <span className="engagement-stat-label">Views</span>
                            </div>
                            <div className="engagement-stat">
                              <span className="engagement-stat-value">{engagementData.aggregate.uniqueViewers}</span>
                              <span className="engagement-stat-label">Unique viewers</span>
                            </div>
                            <div className="engagement-stat">
                              <span className="engagement-stat-value">{engagementData.aggregate.avgCompletionPct}%</span>
                              <span className="engagement-stat-label">Avg watched</span>
                            </div>
                            <div className="engagement-stat">
                              <span className="engagement-stat-value">{engagementData.aggregate.completions}</span>
                              <span className="engagement-stat-label">Completed</span>
                            </div>
                          </div>
                        ) : engagementData.recipients.length > 0 ? (
                          <div className="engagement-awaiting">
                            <div className="engagement-awaiting-dot" aria-hidden="true" />
                            <div>
                              <div className="engagement-awaiting-title">Shared — waiting for the first open</div>
                              <div className="engagement-awaiting-sub">
                                Views, watch-time, drop-off, and who-forwarded-to-whom will appear here the moment {engagementData.recipients.length === 1 ? 'your recipient presses' : 'someone presses'} play.
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="engagement-cta">
                            <div className="engagement-cta-title">Share it to start tracking</div>
                            <div className="engagement-cta-sub">
                              This trailer's ready. Send it and you'll see who watches, for how long, where they drop off, and every stakeholder they forward it to — live.
                            </div>
                            <button
                              type="button"
                              className="engagement-cta-btn"
                              onClick={() => jobId && copyShareLinkForJob(jobId)}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                              Copy share link
                            </button>
                          </div>
                        )}

                        {/* Per-recipient breakdown — only once there ARE
                            recipients (the CTA above covers the empty case). */}
                        {engagementData.recipients.length > 0 && (
                        <div className="engagement-recipients-header">
                          <h4>By recipient</h4>
                          <span>{engagementData.recipients.length} share link{engagementData.recipients.length === 1 ? '' : 's'}</span>
                        </div>
                        )}
                        {engagementData.recipients.length > 0 && (
                          <ul className="engagement-recipient-list">
                            {engagementData.recipients.map((r) => {
                              const label = r.name || r.email || 'Shared link (no recipient)';
                              const statusChip = !r.hasViewed
                                ? { cls: 'neutral', label: 'Not yet opened' }
                                : r.completions > 0
                                  ? { cls: 'ok', label: '✓ Finished' }
                                  : { cls: 'watching', label: `Watched ${r.avgCompletionPct}%` };
                              // Champion-forwarded rows render indented with a
                              // provenance badge so the seller can read the
                              // engagement tree at a glance — each indent is a
                              // new branch of the buying committee that the
                              // demo spread into, which is the whole point of
                              // the multi-threaded sales-engagement model.
                              const isForwarded = Boolean(r.parentDistributionId);
                              return (
                                <li
                                  key={r.distributionId}
                                  className={`engagement-recipient-row${isForwarded ? ' engagement-recipient-row--forwarded' : ''}`}
                                  style={isForwarded ? { paddingLeft: 24 } : undefined}
                                >
                                  <div className="engagement-recipient-info">
                                    <span className="engagement-recipient-name">
                                      {isForwarded && (
                                        <span
                                          className="engagement-recipient-forward-arrow"
                                          aria-hidden="true"
                                          style={{ marginRight: 6, opacity: 0.55 }}
                                        >
                                          ↳
                                        </span>
                                      )}
                                      {label}
                                    </span>
                                    {r.email && r.name && (
                                      <span className="engagement-recipient-email">{r.email}</span>
                                    )}
                                    {isForwarded && r.forwardedFrom && r.forwardedFrom.name && (
                                      <span
                                        className="engagement-recipient-meta engagement-recipient-forward-badge"
                                        style={{ fontStyle: 'italic', opacity: 0.75 }}
                                      >
                                        forwarded from {r.forwardedFrom.name}
                                      </span>
                                    )}
                                    {r.lastViewAt && (
                                      <span className="engagement-recipient-meta">
                                        Last viewed {formatDemoDate(r.lastViewAt)}
                                      </span>
                                    )}
                                  </div>
                                  <span className={`engagement-chip engagement-chip--${statusChip.cls}`}>
                                    {statusChip.label}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        )}

                        {/* Unified activity timeline (2026-07-20) — every
                            engagement type across every surface, newest
                            first: views, completions, CTA + chapter
                            clicks, full-recording opens, messages,
                            forwards, Decision Room activity. */}
                        {Array.isArray(engagementData.activity) && engagementData.activity.length > 0 && (
                          <>
                            <div className="engagement-recipients-header" style={{ marginTop: 18 }}>
                              <h4>Recent activity</h4>
                              <span>{engagementData.activity.length} event{engagementData.activity.length === 1 ? '' : 's'}</span>
                            </div>
                            <ul className="engagement-activity-list">
                              {engagementData.activity.slice(0, 20).map((ev, i) => {
                                const who = ev.actorName || ev.actorEmail || 'Someone';
                                const ICONS = {
                                  trailer_view: '▶', trailer_complete: '✓', cta_click: '🎯',
                                  chapter_click: '❯', source_open: '🎞', space_visit: '👀',
                                  doc_download: '📄', doc_upload: '📎', map_checkoff: '☑',
                                  map_comment: '💬', message: '✉', forward: '↪',
                                };
                                const LABELS = {
                                  trailer_view: 'opened the trailer',
                                  trailer_complete: 'watched to the end',
                                  cta_click: 'clicked the CTA',
                                  chapter_click: ev.metadata && ev.metadata.chapterTitle
                                    ? `jumped to "${ev.metadata.chapterTitle}"`
                                    : 'jumped to a chapter',
                                  source_open: 'opened the full recording',
                                  space_visit: 'visited the Decision Room',
                                  doc_download: ev.metadata && ev.metadata.docTitle
                                    ? `downloaded "${ev.metadata.docTitle}"`
                                    : 'downloaded a document',
                                  doc_upload: 'uploaded a document',
                                  map_checkoff: 'checked off an action item',
                                  map_comment: 'commented on the action plan',
                                  message: 'sent a message',
                                  forward: ev.metadata && ev.metadata.recipientName
                                    ? `forwarded to ${ev.metadata.recipientName}`
                                    : 'forwarded to a teammate',
                                };
                                return (
                                  <li key={i} className="engagement-activity-row">
                                    <span className="engagement-activity-icon" aria-hidden="true">
                                      {ICONS[ev.type] || '•'}
                                    </span>
                                    <span className="engagement-activity-text">
                                      <strong>{who}</strong> {LABELS[ev.type] || ev.type}
                                    </span>
                                    <span className="engagement-activity-time">
                                      {formatDemoDate(ev.occurredAt)}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          </>
                        )}
                      </>
                    )}
                  </div>
              </div>
              )}

              {/* ─── Transcript tab (2026-05-15) ──────────────────────
                  Moved here from the now-deleted left-column pill
                  switcher. Re-computes labels + stats inline because
                  the transcript needs the resolved speaker names
                  (override → multi-track → calendar → NER → email
                  signature → generic). Renders the seek-to-timestamp
                  TranscriptPanel — clicks open the full-recording
                  modal at the picked moment, same as the talking
                  timeline below the video. */}
              {demoRightTab === 'transcript' && trailerPath && !videoLoadError && (() => {
                const transcript = currentDemoTranscript;
                const segments = Array.isArray(transcript?.segments) ? transcript.segments : [];
                if (!segments.length) {
                  return (
                    <div style={{
                      background: 'var(--room-card, #ffffff)', borderRadius: 12,
                      border: '1px solid var(--room-border, #e2e8f0)',
                      padding: 28, textAlign: 'center', color: 'var(--room-text-muted, #64748b)', fontSize: 14,
                    }}>
                      No transcript on this demo yet.
                    </div>
                  );
                }

                // Same label resolver as the left-column TalkingTimeline.
                // Kept inline (not lifted) because the right-rail tab only
                // mounts when the user clicks Transcript, so computing it
                // here doesn't cost anything on the default Engagement tab.
                const ptt = currentDemoParticipantTalkTime;
                const baselineLabels = (transcript && transcript.speakerLabels) || {};
                let labels = baselineLabels;
                if (ptt && Array.isArray(ptt.participants) && ptt.participants.length) {
                  const totals = new Map();
                  for (const s of segments) {
                    if (s.speaker == null) continue;
                    const dur = Math.max(0, (s.end || 0) - (s.start || 0));
                    totals.set(s.speaker, (totals.get(s.speaker) || 0) + dur);
                  }
                  const ranked = [...totals.entries()]
                    .sort((a, b) => b[1] - a[1])
                    .map(([id]) => id);
                  labels = { ...baselineLabels };
                  ranked.forEach((spId, idx) => {
                    const p = ptt.participants[idx];
                    if (p && p.name) labels[spId] = p.name;
                  });
                }
                // Layer overrides → calendar → NER → demo-attendees on top.
                let stats;
                if (ptt && Array.isArray(ptt.participants) && ptt.participants.length) {
                  stats = {
                    totalSeconds: ptt.totalSpeakerSeconds || 0,
                    bySpeaker: ptt.participants.map((p, idx) => ({
                      speaker: p.participantId || `mt_${idx}`,
                      seconds: p.totalSeconds || 0,
                      pct: p.pct || 0,
                      label: p.name || `Participant ${idx + 1}`,
                      sublabel: p.email || (p.isHost ? 'Host' : null),
                      color: reviewGetSpeakerColor(idx, idx),
                      segments: Array.isArray(p.segments) ? p.segments : [],
                    })),
                  };
                } else {
                  stats = reviewComputeSpeakerStats(segments, labels);
                }
                stats = {
                  ...stats,
                  bySpeaker: reviewApplySpeakerNameLayers(stats.bySpeaker, {
                    speakerNameOverrides: currentDemoSpeakerNameOverrides,
                    calendarAttendees: currentDemoCalendarAttendees,
                    extractedNames: currentDemoExtractedNames,
                    demoAttendees: attendees,
                  }),
                };
                // Mirror the resolved bySpeaker.label back into labels
                // so the TranscriptPanel header rows pick up the
                // user-override / calendar names.
                const resolvedLabels = { ...labels };
                for (const s of stats.bySpeaker) {
                  if (s.speaker != null && s.label) resolvedLabels[s.speaker] = s.label;
                }

                const seekFullRecording = (seconds) => {
                  if (!jobId) return;
                  openFullRecording(jobId, seconds);
                };

                return (
                  <ReviewTranscriptPanel
                    transcript={{ ...transcript, speakerLabels: resolvedLabels }}
                    onSeek={seekFullRecording}
                  />
                );
              })()}

              {/* ─── Comments tab — internal team chat with @mentions ─
                  Reuses the same CommentsThread component the call
                  detail uses. demoId is the same primary key as
                  callId since calls + demos share the demos table. */}
              {demoRightTab === 'comments' && jobId && (
                <div style={{
                  background: 'var(--room-card, #ffffff)', borderRadius: 12,
                  border: '1px solid var(--room-border, #e2e8f0)',
                  padding: 16, minHeight: 380,
                }}>
                  <CommentsThread
                    demoId={jobId}
                    authToken={authToken}
                    currentUser={authUser}
                    onCountChange={setDemoCommentCount}
                  />
                </div>
              )}

              {/* ─── Ask Winter tab — AI Q&A grounded on this demo ──
                  Reuses AskWinterCallTab but in demo-mode (passes
                  demoId instead of callId). Backend (askWinter.js)
                  resolves both through the same loader since demos
                  + calls share the underlying table. */}
              {demoRightTab === 'ask-winter' && jobId && (
                <div style={{
                  background: 'var(--room-card, #ffffff)', borderRadius: 12,
                  border: '1px solid var(--room-border, #e2e8f0)',
                  padding: 16, minHeight: 380,
                  display: 'flex', flexDirection: 'column',
                }}>
                  <AskWinterCallTab
                    demoId={jobId}
                    authToken={authToken}
                  />
                </div>
              )}

              {/* Empty-state nudge for Engagement tab when there's
                  truly nothing to show (no threads yet AND no view
                  signal). The engagement-card itself has its own
                  internal empty state, so we only need a nudge when
                  the user opens this tab on a brand-new demo. */}
              {demoRightTab === 'engagement' && demoThreads.length === 0 && (!engagementData || !engagementData.aggregate || engagementData.aggregate.views === 0) && (
                <div style={{
                  marginTop: 12, padding: '20px 16px',
                  background: 'var(--room-card, #ffffff)', borderRadius: 12,
                  border: '1px dashed var(--room-border, #e2e8f0)',
                  fontSize: 13, color: 'var(--room-text-muted, #64748b)',
                  textAlign: 'center', lineHeight: 1.6,
                }}>
                  Once you share this trailer, prospect threads + view stats will appear here.
                </div>
              )}

              {/* Empty-state nudge for Highlights tab when there are
                  no moments to summarize. Happens on demos where
                  the moment-detection pipeline returned nothing
                  (rare — usually ~6-15 moments per demo) or where
                  trailerPath isn't ready (still processing). The
                  Highlights conditional above renders nothing in
                  those cases, so the tab would otherwise look broken. */}
              {demoRightTab === 'highlights' && (!Array.isArray(moments) || moments.length === 0 || !trailerPath || videoLoadError) && (
                <div style={{
                  marginTop: 12, padding: '20px 16px',
                  background: 'var(--room-card, #ffffff)', borderRadius: 12,
                  border: '1px dashed var(--room-border, #e2e8f0)',
                  fontSize: 13, color: 'var(--room-text-muted, #64748b)',
                  textAlign: 'center', lineHeight: 1.6,
                }}>
                  Highlights appear once the trailer is composed. Check back when this demo finishes processing.
                </div>
              )}

              {/* Follow-Up Generator CTA lives in the LEFT column
                  now (above "In this trailer"). The modal itself still
                  mounts here so the click from anywhere on the page
                  opens the same dialog without layout shift. */}
              {showFollowUp && (
                <div
                  className="rebuild-modal-overlay"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="followup-modal-title"
                  onClick={() => setShowFollowUp(false)}
                >
                  <div
                    className="followup-modal"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="followup-modal-head">
                      <h3 id="followup-modal-title">Generate Follow-Up Message</h3>
                      <button
                        type="button"
                        className="rebuild-modal-close"
                        onClick={() => setShowFollowUp(false)}
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>
                  <div className="followup-panel followup-panel--modal">
                    <div className="followup-options">
                      <div className="followup-field">
                        <label>Tone</label>
                        <div className="tone-buttons">
                          {['professional', 'friendly', 'enthusiastic', 'concise'].map(tone => (
                            <button
                              key={tone}
                              className={`tone-btn ${followUpTone === tone ? 'active' : ''}`}
                              onClick={() => setFollowUpTone(tone)}
                            >
                              {tone.charAt(0).toUpperCase() + tone.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      <div className="followup-field">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={includeTimestamps}
                            onChange={(e) => setIncludeTimestamps(e.target.checked)}
                          />
                          <span>Include timestamps</span>
                        </label>
                      </div>
                      
                      <div className="followup-field">
                        <label>Additional Context (optional)</label>
                        <textarea
                          placeholder="e.g., Follow-up to our call on Tuesday, they were interested in the reporting features..."
                          value={followUpContext}
                          onChange={(e) => setFollowUpContext(e.target.value)}
                          rows={3}
                        />
                      </div>
                      
                      <button 
                        className="generate-followup-btn"
                        onClick={generateFollowUp}
                        disabled={generatingFollowUp}
                      >
                        {generatingFollowUp ? (
                          <><span className="spinner"></span><span>Generating...</span></>
                        ) : (
                          <><span></span><span>Generate with AI</span></>
                        )}
                      </button>
                    </div>
                    
                    {followUpMessage && (
                      <div className="followup-output">
                        <div className="followup-output-header">
                          <span>Generated Message</span>
                          <button className="copy-btn" onClick={copyFollowUp}> Copy</button>
                        </div>

                        {/* Editable message body — keep the raw {{first_name}}
                            token here. Preview below shows it substituted. */}
                        <textarea
                          className="followup-textarea"
                          value={followUpMessage}
                          onChange={(e) => setFollowUpMessage(e.target.value)}
                          rows={10}
                        />

                        {/* Embed chips — three tokens to confirm. Each gets
                            substituted per-recipient/per-sender at send time.
                            Missing tokens = missing personalization. */}
                        <div className="followup-chips">
                          <span
                            className={`followup-chip ${followUpMessage.includes('{{first_name}}') ? 'ok' : 'warn'}`}
                            title="Substituted with each recipient's first name at send time"
                          >
                             {followUpMessage.includes('{{first_name}}')
                              ? '{{first_name}} token present'
                              : '{{first_name}} token missing'}
                          </span>
                          <span
                            className={`followup-chip ${followUpMessage.includes('{{share_link}}') ? 'ok' : 'warn'}`}
                            title={followUpShareUrl || 'Share link will be substituted here'}
                          >
                             {followUpMessage.includes('{{share_link}}')
                              ? '{{share_link}} token present'
                              : '{{share_link}} token missing'}
                          </span>
                          <span
                            className={`followup-chip ${followUpMessage.includes('{{sender_name}}') ? 'ok' : 'warn'}`}
                            title="Substituted with your name in the sign-off"
                          >
                             {followUpMessage.includes('{{sender_name}}')
                              ? '{{sender_name}} token present'
                              : '{{sender_name}} token missing'}
                          </span>
                        </div>

                        {/* Preview-as — type a first name, see how the email
                            will render for that recipient. Useful to sanity-
                            check the {{first_name}} substitution before
                            dispatching. */}
                        <div className="followup-preview">
                          <div className="followup-preview-header">
                            <label htmlFor="followUpPreviewName">Preview as</label>
                            <input
                              id="followUpPreviewName"
                              type="text"
                              placeholder="Type a first name (e.g., David)"
                              value={followUpPreviewName}
                              onChange={(e) => setFollowUpPreviewName(e.target.value)}
                            />
                          </div>
                          <div className="followup-preview-body">
                            {substituteRecipientTokens(followUpMessage, followUpPreviewName)}
                          </div>
                          <p className="followup-preview-note">
                            Preview only. Tokens like {`{{first_name}}`}, {`{{share_link}}`},
                            and {`{{sender_name}}`} stay in the message above so each recipient
                            gets their own personalized version at send time. In the HTML version
                            of the email, the share link is a styled "
                            {(followUpPreviewName && followUpPreviewName.trim()) || 'their first name'}
                            's demo trailer" hyperlink rather than a raw URL.
                          </p>
                        </div>

                        {/* ─── Recipients ───────────────────────────── */}
                        <div className="followup-recipients">
                          <div className="followup-recipients-header">
                            <h4>Recipients</h4>
                            <span className="followup-recipients-count">
                              {attendees.length === 0
                                ? 'No recipients yet'
                                : `${attendees.length} recipient${attendees.length === 1 ? '' : 's'}`}
                            </span>
                          </div>

                          {attendees.length > 0 && (
                            <ul className="followup-recipient-list">
                              {attendees.map((att) => {
                                // Map last_send_status to a chip style.
                                const statusChip = att.lastSendStatus === 'sent'
                                  ? { cls: 'ok', label: '✓ Sent' }
                                  : att.lastSendStatus === 'failed'
                                    ? { cls: 'warn', label: 'Failed' }
                                    : null;
                                return (
                                  <li key={att.id} className="followup-recipient-row">
                                    <div className="followup-recipient-info">
                                      <span className="followup-recipient-name">
                                        {att.firstName || att.email.split('@')[0]}
                                      </span>
                                      <span className="followup-recipient-email">{att.email}</span>
                                    </div>
                                    {statusChip && (
                                      <span
                                        className={`followup-chip ${statusChip.cls}`}
                                        title={att.lastSendError || ''}
                                      >
                                        {statusChip.label}
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      className="followup-recipient-remove"
                                      onClick={() => removeAttendee(att.id)}
                                      title="Remove recipient"
                                      aria-label={`Remove ${att.email}`}
                                    >
                                      ×
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}

                          <div className="followup-recipient-add">
                            <input
                              type="text"
                              placeholder="First name (optional)"
                              value={newAttendee.firstName}
                              onChange={(e) =>
                                setNewAttendee({ ...newAttendee, firstName: e.target.value })
                              }
                              className="followup-recipient-input followup-recipient-input--small"
                            />
                            <input
                              type="email"
                              placeholder="email@example.com"
                              value={newAttendee.email}
                              onChange={(e) =>
                                setNewAttendee({ ...newAttendee, email: e.target.value })
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  addAttendee();
                                }
                              }}
                              className="followup-recipient-input"
                            />
                            <button
                              type="button"
                              className="followup-recipient-add-btn"
                              onClick={addAttendee}
                            >
                              + Add
                            </button>
                          </div>

                          <button
                            type="button"
                            className="followup-send-btn"
                            onClick={sendFollowUpToAttendees}
                            disabled={sendingFollowUp || attendees.length === 0 || !followUpMessage}
                          >
                            {sendingFollowUp ? (
                              <>
                                <span className="spinner"></span>
                                Sending…
                              </>
                            ) : (
                              <>
                                 Send to {attendees.length || 0} recipient{attendees.length === 1 ? '' : 's'}
                              </>
                            )}
                          </button>

                          {lastSendResult && lastSendResult.failed.length > 0 && (
                            <p className="followup-send-error">
                              {lastSendResult.failed.length} send{lastSendResult.failed.length === 1 ? '' : 's'} failed —
                              check the chips next to each recipient for details.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  </div>{/* /.followup-modal */}
                </div>
              )}

                </div>{/* /.reel-desktop-right */}
              </div>{/* /.reel-desktop-grid */}
            </div>
          )}

          {/* FAILED PHASE */}
          {phase === 'failed' && (
            <div className="error-view">
              <div className="error-icon-large">⚠️</div>
              <h2>Something went wrong with this demo</h2>
              <p className="error-message-body">
                {error || 'An unexpected error occurred while processing your demo.'}
              </p>
              <div className="error-actions">
                <button className="retry-btn" onClick={resetApp}>Try again</button>
                <button
                  type="button"
                  className="error-secondary-btn"
                  onClick={() => setPhase('dashboard')}
                >
                  Back to dashboard
                </button>
              </div>
              <p className="error-support-hint">
                If this keeps happening, use the feedback button at the bottom-right to
                send us the error details — we'll look into it.
              </p>
            </div>
          )}
        </main>

        <footer className="footer">
          <p>Powered by AI • Built for Sales Teams</p>
        </footer>
      </div>

      {/* ─── Floating upload-progress widget ──────────────────────────
          Fixed-position pill in the bottom-right showing live % progress
          when an upload is in flight but the seller has either
          minimized the hero processing view OR navigated away from the
          analyzing/generating phases. Click to restore the full view.
          Hidden on terminal states (completed/failed/analyzed) so it
          doesn't linger after the pipeline finishes.
          Phases where it shows up:
            - uploadMinimized + (analyzing|generating): seller
              explicitly minimized
            - Any other phase while loading + jobId are truthy:
              seller navigated away with left nav mid-pipeline */}
      {loading && jobId && (
        // Two render paths:
        //   1. uploadMinimized === true: ALWAYS render the floater while
        //      a job is in flight. This used to be gated on phase, but
        //      that hid the floater the moment polling advanced phase
        //      to 'select' — silently breaking the user's minimize
        //      intent. Now uploadMinimized is the durable signal.
        //   2. !uploadMinimized: the seller hasn't actively minimized,
        //      but they may have navigated away from the analyzing/
        //      generating hero view. Show the floater so they don't
        //      lose track of the in-flight job. Skip terminal states.
        uploadMinimized || (
          phase !== 'analyzing'
          && phase !== 'generating'
          && phase !== 'completed'
          && phase !== 'failed'
          && phase !== 'select'
        )
      ) && (
        <div
          className="upload-floater"
          role="button"
          tabIndex={0}
          aria-label={`Upload ${progress || 0}% complete — click to expand`}
          onClick={() => {
            // Drop the minimize flag + return to whichever phase the
            // pipeline is actually in. Polling sets the `status` field
            // to 'analyzed' / 'composing' / etc., which we use to pick
            // the right destination phase: 'analyzed' → select view,
            // composing/voicing/etc. → generating, otherwise → analyzing.
            setUploadMinimized(false);
            const nextPhase = status === 'analyzed'
              ? 'select'
              : (phase === 'generating' || (status && ['narrating', 'voicing', 'composing'].includes(status))
                ? 'generating'
                : 'analyzing');
            setPhase(nextPhase);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setUploadMinimized(false);
              const nextPhase = status === 'analyzed'
                ? 'select'
                : (phase === 'generating' || (status && ['narrating', 'voicing', 'composing'].includes(status))
                  ? 'generating'
                  : 'analyzing');
              setPhase(nextPhase);
            }
          }}
        >
          <div className="upload-floater-bars">
            {[...Array(3)].map((_, i) => (
              <span
                key={i}
                className="upload-floater-bar"
                style={{ animationDelay: `${i * 0.12}s` }}
              />
            ))}
          </div>
          <div className="upload-floater-body">
            <div className="upload-floater-label">
              {getStatusInfo(status).text || 'Processing…'}
            </div>
            <div className="upload-floater-progress">
              <div className="upload-floater-progress-track">
                <div
                  className="upload-floater-progress-fill"
                  style={{ width: `${Math.max(0, Math.min(100, progress || 0))}%` }}
                />
              </div>
              <span className="upload-floater-pct">{progress || 0}%</span>
            </div>
          </div>
          <button
            type="button"
            className="upload-floater-cancel"
            onClick={(e) => {
              e.stopPropagation();
              cancelUpload();
            }}
            title="Cancel upload"
            aria-label="Cancel upload"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* Full-recording modal — sellers watching their own original
          demo recording (persisted to R2 via Phase 2A). Opens from the
          completed-view button, from any demo kebab menu, or from the
          library card. Duration surfaced in the header to prove it's
          the raw recording, not the trailer. */}
      {fullRecordingDemoId && (
        <div
          className="rebuild-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="full-recording-title"
          onClick={closeFullRecording}
        >
          <div
            className="full-recording-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="full-recording-modal-head">
              <div className="full-recording-modal-titles">
                <h3 id="full-recording-title">Full Demo Recording</h3>
                <span className="full-recording-modal-sub">
                  The original, unedited meeting
                  {fullRecordingDuration > 0 && (
                    <> — {Math.floor(fullRecordingDuration / 60)}m {String(Math.floor(fullRecordingDuration % 60)).padStart(2, '0')}s</>
                  )}
                </span>
              </div>
              <button
                type="button"
                className="rebuild-modal-close"
                onClick={closeFullRecording}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <video
              ref={fullRecordingVideoRef}
              className="full-recording-modal-video"
              controls
              playsInline
              preload="metadata"
              autoPlay
              src={withPlaybackToken(`${API_BASE}/api/source-stream/${fullRecordingDemoId}`, fullRecordingToken)}
              onLoadedMetadata={(e) => {
                setFullRecordingDuration(e.target.duration || 0);
                // Honor the per-chapter "Watch full segment" deep-link
                // by seeking once the browser has enough metadata to
                // do so without bouncing off the end.
                if (typeof fullRecordingSeekAt === 'number' && fullRecordingSeekAt > 0) {
                  try {
                    const v = e.target;
                    v.currentTime = Math.min(fullRecordingSeekAt, (v.duration || fullRecordingSeekAt) - 0.25);
                  } catch (_) { /* noop */ }
                }
              }}
            >
              Your browser doesn't support HTML5 video.
            </video>
          </div>
        </div>
      )}

      {/* Rebuild-with-different-clips modal — shown on the completed demo
          view when the user wants to reshape the trailer without re-uploading.
          Mirrors the select-phase checkbox UX but scoped tight (no preview
          player yet — feature TBD once we have usage data). */}
      {/* Slide-template zone editor — Phase 2. Click "Edit zones" on
          a Settings → Brand custom slide tile to open. Drag rectangles
          on the seller's uploaded image to define where AI text appears
          in the rendered trailer. Per-zone styling controls in the
          right panel. The image+overlay IS the live preview — sample
          text shows in each zone using ZONE_SAMPLE_TEXT. */}
      {zoneEditorOpen && zoneEditorTemplate && (
        <div
          className="rebuild-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="zone-editor-title"
          onClick={closeZoneEditor}
        >
          <div className="zone-editor-modal" onClick={(e) => e.stopPropagation()}>
            <div className="zone-editor-head">
              <div>
                <h2 id="zone-editor-title">
                  Define text zones — {SLIDE_TYPE_LABELS[zoneEditorTemplate.slideType] || zoneEditorTemplate.slideType}
                </h2>
                <p className="zone-editor-sub">
                  Drag rectangles to position AI text on your background. Each zone shows
                  sample text live so you can preview how the slide will render.
                </p>
              </div>
              <button
                type="button"
                className="rebuild-modal-close"
                onClick={closeZoneEditor}
                disabled={zoneEditorSaving}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="zone-editor-body">
              {/* Left — image canvas with zone overlays. The image is
                  shown at its natural aspect ratio (16:9), zones are
                  positioned in slide-coord space (1920×1080) and
                  scaled by CSS percentages. */}
              <div className="zone-editor-canvas-wrap">
                <div className="zone-editor-canvas">
                  {/* Authed-fetch the image as a blob so the browser
                      doesn't drop the Authorization header on the img
                      request. ZoneEditorBg is the same pattern as
                      AuthedSlideImage, just adapted for the editor's
                      ref + className needs. */}
                  <ZoneEditorBg
                    template={zoneEditorTemplate}
                    innerRef={zoneEditorImageRef}
                  />
                  {zoneEditorZones.map((zone) => {
                    const isSelected = zoneEditorSelectedId === zone.id;
                    const sampleText = ZONE_SAMPLE_TEXT[zone.id] || zone.id.toUpperCase();
                    return (
                      <div
                        key={zone.id}
                        className={`zone-rect ${isSelected ? 'is-selected' : ''}`}
                        style={{
                          left: `${(zone.x / 1920) * 100}%`,
                          top: `${(zone.y / 1080) * 100}%`,
                          width: `${(zone.width / 1920) * 100}%`,
                          height: `${(zone.height / 1080) * 100}%`,
                        }}
                        onMouseDown={startZoneDrag(zone.id, 'move')}
                        onClick={(e) => { e.stopPropagation(); setZoneEditorSelectedId(zone.id); }}
                      >
                        <div
                          className="zone-rect-text"
                          style={{
                            // Scale font down for the preview based on
                            // canvas size — actual render uses the
                            // zone.fontSize at 1920×1080. Preview is
                            // typically ~600px wide, so divide by ~3.
                            fontSize: `${(zone.fontSize / 1920) * 100}cqw`,
                            color: zone.fontColor,
                            fontWeight: zone.fontWeight,
                            textAlign: zone.alignment,
                            justifyContent: zone.alignment === 'left' ? 'flex-start'
                                          : zone.alignment === 'right' ? 'flex-end'
                                          : 'center',
                            fontFamily: zone.fontFamily,
                          }}
                        >
                          {sampleText}
                        </div>
                        <span className="zone-rect-label">{zone.id}</span>
                        {isSelected && (
                          <span
                            className="zone-rect-resize"
                            onMouseDown={startZoneDrag(zone.id, 'resize')}
                            title="Drag to resize"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right — zone list + per-zone properties + add-zone */}
              <div className="zone-editor-side">
                <div className="zone-editor-side-section">
                  <div className="zone-editor-side-title">Zones</div>
                  <ul className="zone-editor-list">
                    {zoneEditorZones.map((zone) => (
                      <li
                        key={zone.id}
                        className={`zone-editor-list-item ${zoneEditorSelectedId === zone.id ? 'is-selected' : ''}`}
                        onClick={() => setZoneEditorSelectedId(zone.id)}
                      >
                        <span className="zone-editor-list-id">{zone.id}</span>
                        <button
                          type="button"
                          className="zone-editor-list-remove"
                          onClick={(e) => { e.stopPropagation(); removeZone(zone.id); }}
                          title="Remove this zone"
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                  {/* Add-zone — only show preset ids that aren't already used */}
                  {(() => {
                    const used = new Set(zoneEditorZones.map((z) => z.id));
                    const available = (ZONE_PRESETS[zoneEditorTemplate.slideType] || []).filter((id) => !used.has(id));
                    if (available.length === 0) return null;
                    return (
                      <div className="zone-editor-add">
                        <span className="zone-editor-add-label">Add zone:</span>
                        {available.map((id) => (
                          <button
                            key={id}
                            type="button"
                            className="zone-editor-add-chip"
                            onClick={() => addZone(id)}
                          >
                            + {id}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Per-zone properties */}
                {(() => {
                  const sel = zoneEditorZones.find((z) => z.id === zoneEditorSelectedId);
                  if (!sel) {
                    return (
                      <div className="zone-editor-side-section">
                        <p className="zone-editor-empty">Select a zone to edit its style.</p>
                      </div>
                    );
                  }
                  return (
                    <div className="zone-editor-side-section">
                      <div className="zone-editor-side-title">Style — <code>{sel.id}</code></div>
                      <label className="zone-prop">
                        <span>Font size</span>
                        <input
                          type="number"
                          min={8}
                          max={200}
                          value={sel.fontSize}
                          onChange={(e) => updateZone(sel.id, { fontSize: Number(e.target.value) || 32 })}
                        />
                      </label>
                      <label className="zone-prop">
                        <span>Color</span>
                        <input
                          type="color"
                          value={sel.fontColor}
                          onChange={(e) => updateZone(sel.id, { fontColor: e.target.value })}
                        />
                      </label>
                      <label className="zone-prop">
                        <span>Weight</span>
                        <select
                          value={sel.fontWeight}
                          onChange={(e) => updateZone(sel.id, { fontWeight: Number(e.target.value) })}
                        >
                          {/* Space Grotesk ships only 500/700 TTFs in the
                              renderer — offering 400/600 made Satori
                              silently snap to a different weight than the
                              seller picked. Menu now matches the loaded
                              fonts exactly (2026-07-25). */}
                          {(sel.fontFamily === 'Space Grotesk'
                            ? [[500, '500 — Medium'], [700, '700 — Bold']]
                            : [[400, '400 — Regular'], [500, '500 — Medium'], [600, '600 — Semibold'], [700, '700 — Bold']]
                          ).map(([v, label]) => (
                            <option key={v} value={v}>{label}</option>
                          ))}
                        </select>
                      </label>
                      <label className="zone-prop">
                        <span>Alignment</span>
                        <select
                          value={sel.alignment}
                          onChange={(e) => updateZone(sel.id, { alignment: e.target.value })}
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                        </select>
                      </label>
                      <label className="zone-prop">
                        <span>Font</span>
                        <select
                          value={sel.fontFamily}
                          onChange={(e) => updateZone(sel.id, { fontFamily: e.target.value })}
                        >
                          <option value="Inter">Inter</option>
                          <option value="Space Grotesk">Space Grotesk</option>
                        </select>
                      </label>
                      <div className="zone-prop-coords">
                        <span>Position: {sel.x},{sel.y}</span>
                        <span>Size: {sel.width}×{sel.height}</span>
                      </div>
                    </div>
                  );
                })()}

                {zoneEditorError && (
                  <div className="zone-editor-error">{zoneEditorError}</div>
                )}

                <div className="zone-editor-actions">
                  <button
                    type="button"
                    className="zone-editor-btn zone-editor-btn--ghost"
                    onClick={clearAllZones}
                    disabled={zoneEditorSaving}
                  >
                    Clear all
                  </button>
                  <button
                    type="button"
                    className="zone-editor-btn"
                    onClick={closeZoneEditor}
                    disabled={zoneEditorSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="zone-editor-btn zone-editor-btn--primary"
                    onClick={saveZones}
                    disabled={zoneEditorSaving}
                  >
                    {zoneEditorSaving ? 'Saving…' : 'Save zones'}
                  </button>
                </div>
                <p className="zone-editor-note">
                  When you save, the next trailer this company generates will use your
                  zones to place AI text. The image overlay you see here is the live
                  preview — it&apos;s exactly what the slide will look like.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom-trailer modal — seller hand-picks clips from the source
          recording. The video player at the top syncs with the mark-in /
          mark-out controls; the right column lists clips with reorder +
          label-override + delete. Submit creates a NEW demo (parent_demo_id
          set to source) so the original AI trailer stays intact. */}
      {customTrailerOpen && customTrailerParent && (
        <div
          className="rebuild-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="custom-trailer-title"
          onClick={closeCustomTrailer}
        >
          <div
            className="custom-trailer-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="custom-trailer-head">
              <div>
                <h2 id="custom-trailer-title">Build your own trailer</h2>
                <p className="custom-trailer-sub">
                  Pick the moments you want. AI handles the slides, titles,
                  and voice-cloned narration around them. Min 5 sec / max 2
                  min per clip, up to 12 clips.
                </p>
              </div>
              <button
                type="button"
                className="rebuild-modal-close"
                onClick={closeCustomTrailer}
                aria-label="Close"
                disabled={customTrailerSubmitting}
              >
                ×
              </button>
            </div>

            <div className="custom-trailer-body">
              {/* Left column — player + timeline + controls + transcript + suggestions */}
              <div className="custom-trailer-player-col">
                <video
                  ref={customTrailerVideoRef}
                  className="custom-trailer-video"
                  controls
                  playsInline
                  preload="metadata"
                  src={withPlaybackToken(`${API_BASE}/api/source-stream/${customTrailerParent.id}`, customTrailerToken)}
                  onTimeUpdate={(e) => setCustomTrailerCurrentMs(Math.floor(e.target.currentTime * 1000))}
                  onLoadedMetadata={(e) => {
                    if (!customTrailerSourceDuration && e.target.duration) {
                      setCustomTrailerSourceDuration(e.target.duration);
                    }
                  }}
                />

                {/* Visual timeline scrubber. Renders the full source duration as
                    a horizontal bar; selected clips appear as colored boxes
                    with drag-handles on each edge for fine-tuning. Click
                    anywhere on the empty bar to scrub the player to that
                    timestamp. */}
                {customTrailerSourceDuration > 0 && (
                  <div className="custom-trailer-timeline-wrap">
                    <div
                      className="custom-trailer-timeline"
                      ref={customTrailerTimelineRef}
                      onMouseDown={handleTimelineScrub}
                      title="Click to scrub the player"
                    >
                      {/* Playhead indicator */}
                      <div
                        className="custom-trailer-timeline-playhead"
                        style={{
                          left: `${Math.min(100, (customTrailerCurrentMs / 1000 / customTrailerSourceDuration) * 100)}%`,
                        }}
                      />
                      {/* Clip boxes */}
                      {customTrailerClips.map((clip, i) => {
                        const totalMs = customTrailerSourceDuration * 1000;
                        const leftPct = (clip.startMs / totalMs) * 100;
                        const widthPct = ((clip.endMs - clip.startMs) / totalMs) * 100;
                        return (
                          <div
                            key={i}
                            className={`custom-trailer-timeline-clip ${customTrailerDragging?.index === i ? 'is-dragging' : ''}`}
                            style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                            title={`Clip ${i + 1}: ${formatTime(clip.startMs / 1000)} – ${formatTime(clip.endMs / 1000)}`}
                            onClick={(e) => { e.stopPropagation(); previewCustomClip(clip); }}
                          >
                            <span
                              className="custom-trailer-timeline-handle custom-trailer-timeline-handle--start"
                              onMouseDown={handleTimelineMouseDown(i, 'start')}
                              onClick={(e) => e.stopPropagation()}
                              aria-label="Drag to adjust clip start"
                            />
                            <span className="custom-trailer-timeline-clip-num">{i + 1}</span>
                            <span
                              className="custom-trailer-timeline-handle custom-trailer-timeline-handle--end"
                              onMouseDown={handleTimelineMouseDown(i, 'end')}
                              onClick={(e) => e.stopPropagation()}
                              aria-label="Drag to adjust clip end"
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className="custom-trailer-timeline-axis">
                      <span>0:00</span>
                      <span>{formatTime(customTrailerSourceDuration / 2)}</span>
                      <span>{formatTime(customTrailerSourceDuration)}</span>
                    </div>
                  </div>
                )}

                <div className="custom-trailer-controls">
                  <div className="custom-trailer-playhead">
                    Current: <strong>{formatTime(customTrailerCurrentMs / 1000)}</strong>
                    {customTrailerInPoint != null && (
                      <span className="custom-trailer-in-pill">
                        In: {formatTime(customTrailerInPoint / 1000)}
                      </span>
                    )}
                    {customTrailerSelStart != null && customTrailerTranscript && (
                      <span className="custom-trailer-in-pill custom-trailer-in-pill--transcript">
                        Transcript: from {formatTime(customTrailerTranscript.segments[customTrailerSelStart]?.start || 0)} — click another sentence to finish
                      </span>
                    )}
                  </div>
                  <div className="custom-trailer-control-buttons">
                    <button
                      type="button"
                      className="custom-trailer-btn"
                      onClick={markCustomIn}
                      disabled={customTrailerSubmitting}
                      title="Set the start of the next clip to the current playhead"
                    >
                      [ Mark In
                    </button>
                    <button
                      type="button"
                      className="custom-trailer-btn custom-trailer-btn--primary"
                      onClick={markCustomOutAndAdd}
                      disabled={customTrailerSubmitting || customTrailerInPoint == null}
                      title="Use the current playhead as the end-point and add this clip"
                    >
                      Add Clip ]
                    </button>
                    {customTrailerInPoint != null && (
                      <button
                        type="button"
                        className="custom-trailer-btn custom-trailer-btn--ghost"
                        onClick={() => setCustomTrailerInPoint(null)}
                        disabled={customTrailerSubmitting}
                      >
                        Clear In
                      </button>
                    )}
                    {customTrailerSelStart != null && (
                      <button
                        type="button"
                        className="custom-trailer-btn custom-trailer-btn--ghost"
                        onClick={() => setCustomTrailerSelStart(null)}
                        disabled={customTrailerSubmitting}
                      >
                        Cancel transcript
                      </button>
                    )}
                  </div>
                  <div className="custom-trailer-help">
                    Three ways to pick clips: <strong>scrub-and-mark</strong> using the buttons,
                    <strong> drag</strong> the edges of clip boxes on the timeline, or
                    <strong> click two sentences</strong> in the transcript below.
                  </div>
                </div>

                {/* AI suggestions row — one-click adds from the parent's
                    auto-detected moments. Hidden if the parent has none. */}
                {customTrailerSuggestions.length > 0 && (
                  <div className="custom-trailer-suggestions">
                    <div className="custom-trailer-suggestions-head">
                      <strong>AI suggestions</strong>
                      <span className="custom-trailer-suggestions-meta">
                        Detected moments from the original AI trailer — one click to add
                      </span>
                    </div>
                    <div className="custom-trailer-suggestions-list">
                      {customTrailerSuggestions.map((s, i) => {
                        const startMs = Math.floor((s.startSeconds || 0) * 1000);
                        const endMs = Math.floor((s.endSeconds || 0) * 1000);
                        const alreadyAdded = customTrailerClips.some(
                          (c) => startMs < c.endMs && endMs > c.startMs
                        );
                        // Out-of-bounds: suggestion's start is past the
                        // source's actual duration. Disable + label so
                        // the seller doesn't try to add a moment that
                        // would render as silent black frames.
                        const sourceDurationMs = customTrailerSourceDuration > 0
                          ? Math.floor(customTrailerSourceDuration * 1000) : null;
                        const outOfBounds = sourceDurationMs != null && startMs >= sourceDurationMs;
                        const isDisabled = alreadyAdded || outOfBounds || customTrailerSubmitting;
                        return (
                          <button
                            key={i}
                            type="button"
                            className={`custom-trailer-suggestion-chip ${alreadyAdded ? 'is-added' : ''} ${outOfBounds ? 'is-out-of-bounds' : ''}`}
                            onClick={() => addSuggestion(s)}
                            disabled={isDisabled}
                            title={
                              outOfBounds ? `Out of bounds — this moment's timestamp (${formatTime(s.startSeconds || 0)}) is past the recording's end (${formatTime(customTrailerSourceDuration)})`
                              : alreadyAdded ? 'This suggestion is already in your clip list'
                              : 'Add this AI-suggested moment as a clip'
                            }
                          >
                            <span className="custom-trailer-suggestion-title">
                              {s.feature || s.title || `Moment ${i + 1}`}
                            </span>
                            <span className="custom-trailer-suggestion-meta">
                              {formatTime(s.startSeconds || 0)} – {formatTime(s.endSeconds || 0)}
                            </span>
                            {alreadyAdded ? (
                              <span className="custom-trailer-suggestion-added">✓</span>
                            ) : outOfBounds ? (
                              <span className="custom-trailer-suggestion-meta">⚠</span>
                            ) : (
                              <span className="custom-trailer-suggestion-add">+</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Transcript browser — click a sentence to mark it as
                    the start of a range, click another to finalize as a
                    clip. Shows under the suggestions, scrolls within
                    the modal. */}
                {customTrailerTranscript && customTrailerTranscript.segments && customTrailerTranscript.segments.length > 0 ? (
                  <div className="custom-trailer-transcript">
                    <div className="custom-trailer-transcript-head">
                      <strong>Transcript</strong>
                      <span className="custom-trailer-transcript-meta">
                        Click a sentence to start a range, click another to finalize
                      </span>
                    </div>
                    <div className="custom-trailer-transcript-list" ref={customTrailerTranscriptListRef}>
                      {customTrailerTranscript.segments.map((seg, i) => {
                        const isAnchor = customTrailerSelStart === i;
                        // Highlight all segments that fall inside any existing clip
                        const inAClip = customTrailerClips.some((c) => {
                          const segStartMs = Math.floor(seg.start * 1000);
                          const segEndMs = Math.floor(seg.end * 1000);
                          return segStartMs < c.endMs && segEndMs > c.startMs;
                        });
                        // V4.1: highlight + ref the segment that's
                        // currently playing so the auto-scroll effect
                        // can scrollIntoView when the playhead moves.
                        const isPlaying = customTrailerActiveSegIdx === i;
                        return (
                          <button
                            key={i}
                            type="button"
                            ref={isPlaying ? customTrailerActiveSegRef : null}
                            className={`custom-trailer-transcript-seg ${isAnchor ? 'is-anchor' : ''} ${inAClip ? 'is-in-clip' : ''} ${isPlaying ? 'is-playing' : ''}`}
                            onClick={() => handleTranscriptSegmentClick(i)}
                            disabled={customTrailerSubmitting}
                            title={`${formatTime(seg.start)} – ${formatTime(seg.end)}`}
                          >
                            <span className="custom-trailer-transcript-seg-time">
                              {formatTime(seg.start)}
                            </span>
                            <span className="custom-trailer-transcript-seg-text">
                              {seg.text}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : customTrailerLoadingDetails ? (
                  <div className="custom-trailer-transcript-loading">Loading transcript…</div>
                ) : null}
              </div>

              {/* Right column — clips list + submit */}
              <div className="custom-trailer-clips-col">
                <div className="custom-trailer-clips-head">
                  <strong>Your clips ({customTrailerClips.length}/12)</strong>
                  {customTrailerClips.length > 0 && (
                    <span className="custom-trailer-clips-meta">
                      Total: {formatTime(customTrailerClips.reduce((a, c) => a + (c.endMs - c.startMs) / 1000, 0))}
                    </span>
                  )}
                </div>

                {customTrailerClips.length === 0 ? (
                  <div className="custom-trailer-empty">
                    <p>No clips yet.</p>
                    <p className="custom-trailer-empty-hint">
                      Use Mark In + Add Clip to capture moments. Each clip becomes
                      a section in your final trailer with an AI-generated title.
                    </p>
                  </div>
                ) : (
                  <ul className="custom-trailer-clips-list">
                    {customTrailerClips.map((clip, i) => (
                      <li key={i} className="custom-trailer-clip-row">
                        <div className="custom-trailer-clip-num">{i + 1}</div>
                        <div className="custom-trailer-clip-body">
                          <div className="custom-trailer-clip-meta">
                            <span>{formatTime(clip.startMs / 1000)} – {formatTime(clip.endMs / 1000)}</span>
                            <span className="custom-trailer-clip-sep">·</span>
                            <span>{Math.round((clip.endMs - clip.startMs) / 1000)}s</span>
                            <button
                              type="button"
                              className="custom-trailer-preview-btn"
                              onClick={() => previewCustomClip(clip)}
                              title="Jump to this clip in the player"
                              disabled={customTrailerSubmitting}
                            >
                              Preview
                            </button>
                          </div>
                          <input
                            type="text"
                            className="custom-trailer-label-input"
                            placeholder="Section title (optional — AI will pick one)"
                            value={clip.customLabel || ''}
                            onChange={(e) => updateCustomLabel(i, e.target.value)}
                            disabled={customTrailerSubmitting}
                            maxLength={50}
                          />
                        </div>
                        <div className="custom-trailer-clip-actions">
                          <button
                            type="button"
                            className="custom-trailer-icon-btn"
                            onClick={() => moveCustomClip(i, -1)}
                            disabled={i === 0 || customTrailerSubmitting}
                            title="Move up"
                            aria-label="Move clip up"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="custom-trailer-icon-btn"
                            onClick={() => moveCustomClip(i, 1)}
                            disabled={i === customTrailerClips.length - 1 || customTrailerSubmitting}
                            title="Move down"
                            aria-label="Move clip down"
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            className="custom-trailer-icon-btn custom-trailer-icon-btn--danger"
                            onClick={() => removeCustomClip(i)}
                            disabled={customTrailerSubmitting}
                            title="Remove clip"
                            aria-label="Remove clip"
                          >
                            ×
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {/* V4.2: Undo affordance for the most-recently-deleted
                    clip. Auto-expires after 6 seconds (controlled in the
                    state effect). Sits above the error region so a
                    legit error doesn't get crowded out. */}
                {customTrailerUndo && (
                  <div className="custom-trailer-undo">
                    <span className="custom-trailer-undo-text">
                      Removed clip <strong>{customTrailerUndo.index + 1}</strong>
                      {customTrailerUndo.clip.customLabel ? ` · "${customTrailerUndo.clip.customLabel}"` : ''}
                    </span>
                    <button
                      type="button"
                      className="custom-trailer-undo-btn"
                      onClick={undoRemoveCustomClip}
                    >
                      Undo
                    </button>
                  </div>
                )}

                {customTrailerError && (
                  <div className="custom-trailer-error">{customTrailerError}</div>
                )}

                <button
                  type="button"
                  className="custom-trailer-submit"
                  onClick={submitCustomTrailer}
                  disabled={customTrailerSubmitting || customTrailerClips.length === 0}
                >
                  {customTrailerSubmitting
                    ? 'Building…'
                    : `Build trailer with ${customTrailerClips.length} clip${customTrailerClips.length === 1 ? '' : 's'}`}
                </button>
                <p className="custom-trailer-submit-note">
                  Creates a new trailer alongside your existing one. The original
                  AI trailer + source recording stay intact.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {rebuildModalOpen && (
        <div
          className="rebuild-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rebuild-modal-title"
          onClick={closeRebuildModal}
        >
          <div className="rebuild-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rebuild-modal-head">
              <h2 id="rebuild-modal-title">Rebuild with different clips</h2>
              <button
                type="button"
                className="rebuild-modal-close"
                onClick={closeRebuildModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="rebuild-modal-sub">
              {rebuildMode === 'new'
                ? 'Pick which moments to include. The rebuilt version is saved as a separate trailer — this one stays exactly as it is.'
                : 'Pick which moments to include. Your current trailer will be replaced with a new one — the source recording stays intact.'}
            </p>
            <div className="rebuild-modal-list">
              {rebuildPool.map((m, i) => {
                const checked = rebuildSelected.has(i);
                return (
                  <label
                    key={i}
                    className={`rebuild-clip-row ${checked ? 'is-checked' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleRebuildClip(i)}
                    />
                    <div className="rebuild-clip-body">
                      <div className="rebuild-clip-title">
                        {m.feature || m.title || `Clip ${i + 1}`}
                      </div>
                      <div className="rebuild-clip-meta">
                        {typeof m.startSeconds === 'number' && typeof m.endSeconds === 'number' && (
                          <>
                            <span>{formatTime(m.startSeconds)} – {formatTime(m.endSeconds)}</span>
                            <span className="rebuild-clip-sep">·</span>
                          </>
                        )}
                        <span>{Math.max(0, Math.round((m.endSeconds || 0) - (m.startSeconds || 0)))}s</span>
                      </div>
                      {m.benefit && (
                        <div className="rebuild-clip-benefit">{m.benefit}</div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
            {/* Replace vs save-as-new choice. Radio pair matching the
                modal's checkbox-row styling; 'replace' preserves the
                long-standing default behavior. */}
            <div className="rebuild-mode-row" role="radiogroup" aria-label="What happens to the current trailer">
              <label className={`rebuild-clip-row rebuild-mode-option ${rebuildMode === 'replace' ? 'is-checked' : ''}`}>
                <input
                  type="radio"
                  name="rebuild-mode"
                  checked={rebuildMode === 'replace'}
                  onChange={() => setRebuildMode('replace')}
                />
                <div className="rebuild-clip-body">
                  <div className="rebuild-clip-title">Replace this trailer</div>
                  <div className="rebuild-clip-meta">The current version is overwritten. Share links keep working and pick up the new cut.</div>
                </div>
              </label>
              <label className={`rebuild-clip-row rebuild-mode-option ${rebuildMode === 'new' ? 'is-checked' : ''}`}>
                <input
                  type="radio"
                  name="rebuild-mode"
                  checked={rebuildMode === 'new'}
                  onChange={() => setRebuildMode('new')}
                />
                <div className="rebuild-clip-body">
                  <div className="rebuild-clip-title">Save as a new trailer</div>
                  <div className="rebuild-clip-meta">Keeps this trailer untouched and adds the rebuilt version to your library as "{currentDemoFilename || 'Trailer'} (rebuilt)". Counts as a new trailer.</div>
                </div>
              </label>
            </div>
            {rebuildError && (
              <div className="error-message rebuild-modal-error">
                <span></span> {rebuildError}
              </div>
            )}
            <div className="rebuild-modal-footer">
              <span className="rebuild-modal-count">
                {rebuildSelected.size} of {rebuildPool.length} clips selected
              </span>
              <div className="rebuild-modal-buttons">
                <button
                  type="button"
                  className="rebuild-modal-cancel"
                  onClick={closeRebuildModal}
                  disabled={rebuildSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rebuild-modal-submit"
                  onClick={submitRebuild}
                  disabled={rebuildSubmitting || rebuildSelected.size === 0}
                >
                  {rebuildSubmitting
                    ? 'Starting…'
                    : rebuildMode === 'new' ? 'Save as new trailer' : 'Rebuild trailer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Feedback button — visible on every authed screen.
          Bottom-right fixed position so it's discoverable without
          competing with the in-page CTAs. Renders only when authed
          so the logged-out landing page doesn't get a stray button. */}
      {authUser && (
        <button
          type="button"
          className="feedback-fab"
          onClick={() => setFeedbackModalOpen(true)}
          title="Send feedback"
          aria-label="Send feedback"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <span>Feedback</span>
        </button>
      )}

      {/* Feedback modal — category + message + submit. Uses the same
          .modal-overlay / .modal-panel primitives as the onboarding
          modal for consistency. POST /api/feedback handles the
          insert + email notification. */}

      {/* ─── Plan modal ─────────────────────────────────────────────
          Contact-us space (2026-09-17). Replaced the self-serve pricing
          modal: no prices, no Stripe Checkout. Points the customer at
          the team; subscribers can still open the Stripe portal. */}
      {pricingModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => { if (!pricingBusy) setPricingModalOpen(false); }}
        >
          <div className="modal-panel pricing-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pricing-modal-header">
              <h2>Talk to us about your plan</h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => { if (!pricingBusy) setPricingModalOpen(false); }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {pricingError && (
              <div className="pricing-modal-error">{pricingError}</div>
            )}

            <p className="pricing-modal-sub">
              Plans are tailored to your team. Tell us a little about how you
              work and we&apos;ll get you set up on the right one, adjust seats,
              or answer any billing question — usually the same day.
            </p>

            <div className="pricing-contact-actions">
              <a
                className="pricing-card-cta pricing-card-cta--featured pricing-contact-cta"
                href={planContactHref()}
              >
                Contact us
              </a>
              {!!authUser?.company?.hasStripeCustomer && (
                <button
                  type="button"
                  className="pricing-card-cta"
                  onClick={() => openBillingPortal('portal')}
                  disabled={pricingBusy === 'portal'}
                >
                  {pricingBusy === 'portal' ? 'Opening…' : 'Manage billing'}
                </button>
              )}
            </div>

            <div className="pricing-modal-enterprise">
              <p>
                Every workspace gets every feature. Need SSO, SLA, white-label,
                custom retention, or CRM sync? Mention it in your note.
              </p>
            </div>
          </div>
        </div>
      )}

      {feedbackModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => { if (!feedbackSubmitting) setFeedbackModalOpen(false); }}
        >
          <div className="modal-panel feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <div>
                <h2>Send feedback</h2>
                <p>
                  Tell us what's working, what isn't, or what you wish TrailerCast
                  could do. Every message lands in the founder's inbox and gets a
                  real reply.
                </p>
              </div>
              <button
                type="button"
                className="onboarding-modal-close"
                onClick={() => { if (!feedbackSubmitting) setFeedbackModalOpen(false); }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form className="feedback-form" onSubmit={submitFeedback}>
              <div className="auth-field">
                <label>Category</label>
                <div className="feedback-category-row">
                  {[
                    { id: 'bug',             label: 'Bug' },
                    { id: 'feature_request', label: 'Feature' },
                    { id: 'question',        label: 'Question' },
                    { id: 'praise',          label: 'Praise' },
                    { id: 'other',           label: 'Other' },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={`feedback-category-chip ${feedbackForm.category === opt.id ? 'active' : ''}`}
                      onClick={() => setFeedbackForm({ ...feedbackForm, category: opt.id })}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="auth-field">
                <label>What's on your mind?</label>
                <textarea
                  rows={6}
                  maxLength={4000}
                  autoFocus
                  placeholder={
                    feedbackForm.category === 'bug'
                      ? "What went wrong? What did you expect instead? Steps to reproduce if you have them."
                      : feedbackForm.category === 'feature_request'
                        ? "What would make TrailerCast better for you? The more specific, the better."
                        : feedbackForm.category === 'question'
                          ? "What would you like to know?"
                          : feedbackForm.category === 'praise'
                            ? "What's working well? What do you love? (Made our day just now.)"
                            : "Whatever's on your mind."
                  }
                  value={feedbackForm.message}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, message: e.target.value })}
                />
                <span className="auth-field-hint">
                  {feedbackForm.message.length} / 4000 characters
                </span>
              </div>

              {feedbackError && (
                <div className="error-message">{feedbackError}</div>
              )}

              <div className="onboarding-actions">
                <button
                  type="button"
                  className="onboarding-skip"
                  onClick={() => { if (!feedbackSubmitting) setFeedbackModalOpen(false); }}
                  disabled={feedbackSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="generate-btn ready"
                  disabled={feedbackSubmitting || !feedbackForm.message.trim()}
                >
                  <span className="btn-text">{feedbackSubmitting ? 'Sending…' : 'Send feedback'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Recording-consent acknowledgment modal — opened when the
          seller tries to dispatch a bot without having acknowledged
          (or after the admin changed jurisdictions). Three checkboxes
          must all be ticked before Submit unlocks. POST
          /api/auth/consent/acknowledge persists the acknowledgment +
          jurisdiction snapshot. */}
      {consentModalOpen && (() => {
        const currentJurisdiction = authUser?.company?.consentJurisdiction || null;
        const currentJurisdictionLabel = currentJurisdiction
          ? (CONSENT_JURISDICTION_LABEL[currentJurisdiction] || currentJurisdiction)
          : null;
        const needsAffirmativeConsent = ['us_two_party', 'eu_uk', 'other'].includes(currentJurisdiction);
        const allChecked = consentCheckboxes.authority && consentCheckboxes.inform && consentCheckboxes.jurisdiction;
        return (
          <div className="modal-overlay" onClick={() => { if (!consentSubmitting) setConsentModalOpen(false); }}>
            <div className="modal-panel consent-modal" onClick={(e) => e.stopPropagation()}>
              <div className="onboarding-modal-header">
                <div>
                  <h2>Acknowledge recording consent</h2>
                  <p>
                    Before TrailerCast dispatches a bot to a meeting on your
                    behalf, confirm you understand how recording works
                    {currentJurisdictionLabel ? ` in your selected jurisdiction (${currentJurisdictionLabel})` : ''}.
                  </p>
                </div>
                <button
                  type="button"
                  className="onboarding-modal-close"
                  onClick={() => { if (!consentSubmitting) setConsentModalOpen(false); }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className="consent-modal-body">
                <label className="consent-checkbox-row">
                  <input
                    type="checkbox"
                    checked={consentCheckboxes.authority}
                    onChange={(e) => setConsentCheckboxes({ ...consentCheckboxes, authority: e.target.checked })}
                  />
                  <span>
                    <strong>I have authority to record meetings on behalf of my organization.</strong>{' '}
                    I'm authorized to schedule a recording bot and to share
                    recordings externally as part of my sales workflow.
                  </span>
                </label>

                <label className="consent-checkbox-row">
                  <input
                    type="checkbox"
                    checked={consentCheckboxes.inform}
                    onChange={(e) => setConsentCheckboxes({ ...consentCheckboxes, inform: e.target.checked })}
                  />
                  <span>
                    <strong>I will make sure participants know a recording is happening.</strong>{' '}
                    TrailerCast's bot posts an in-meeting notice when it joins and
                    appears in the attendee list under your company name. I'll
                    verbally confirm the recording with participants at the top
                    of each call
                    {needsAffirmativeConsent
                      ? ' and will pause the recording if anyone objects.'
                      : '.'}
                  </span>
                </label>

                <label className="consent-checkbox-row">
                  <input
                    type="checkbox"
                    checked={consentCheckboxes.jurisdiction}
                    onChange={(e) => setConsentCheckboxes({ ...consentCheckboxes, jurisdiction: e.target.checked })}
                  />
                  <span>
                    <strong>I understand my jurisdiction's rules.</strong>{' '}
                    {currentJurisdiction === 'us_two_party' && 'My jurisdiction requires all participants to consent before recording. I will not record anyone who has not affirmatively consented.'}
                    {currentJurisdiction === 'us_one_party' && 'My jurisdiction allows recording when at least one participant (me) consents. I will still notify participants as a courtesy.'}
                    {currentJurisdiction === 'canada' && 'PIPEDA governs my commercial recording. BC has additional PIPA rules. I will comply with whichever is stricter.'}
                    {currentJurisdiction === 'eu_uk' && 'GDPR / UK-GDPR applies. I have a lawful basis (usually explicit consent) before recording each participant.'}
                    {currentJurisdiction === 'other' && 'I treat recording as requiring explicit consent from all participants. I will only record when every participant has confirmed.'}
                    {!currentJurisdiction && 'An admin needs to pick a jurisdiction in Settings > Integrations before I can acknowledge.'}
                  </span>
                </label>

                {consentError && <div className="error-message">{consentError}</div>}
              </div>

              <div className="onboarding-actions">
                <button
                  type="button"
                  className="onboarding-skip"
                  onClick={() => { if (!consentSubmitting) setConsentModalOpen(false); }}
                  disabled={consentSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="generate-btn ready"
                  onClick={acknowledgeConsent}
                  disabled={!allChecked || consentSubmitting || !currentJurisdiction}
                >
                  <span className="btn-text">{consentSubmitting ? 'Saving…' : 'I acknowledge'}</span>
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Onboarding personalization modal — auto-opens on first
          dashboard visit for company admins who haven't completed
          the 5-question form. Answers injected into the AI moment-
          detection prompt server-side so future demos reflect the
          seller's product + ICP. Skippable via "Remind me later";
          dismissed state is session-only so it re-appears next login
          until completed. */}
      {onboardingModalOpen && (
        <div className="modal-overlay" onClick={() => { setOnboardingModalOpen(false); setOnboardingDismissed(true); }}>
          <div className="modal-panel onboarding-modal" onClick={(e) => e.stopPropagation()}>
            <div className="onboarding-modal-header">
              <div>
                <h2>Let's personalize your AI editor</h2>
                <p>
                  5 quick questions. We'll use these to pick better highlight
                  moments, write sharper follow-ups, and narrate in your voice.
                  Takes ~2 minutes. You can always update this later.
                </p>
              </div>
              <button
                type="button"
                className="onboarding-modal-close"
                onClick={() => { setOnboardingModalOpen(false); setOnboardingDismissed(true); }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <OnboardingForm
              initialAnswers={authUser && authUser.company && authUser.company.onboardingAnswers}
              // Show the team-size + tier-routing question only when:
              //   (a) this user is the company admin (i.e. they took the
              //       new-team signup path, are the first of their domain
              //       to land in the system, and will need to pick a plan)
              //   (b) onboarding hasn't been completed yet (so we don't
              //       re-prompt on second open)
              isFirstAdmin={!!(
                authUser?.company?.isAdmin &&
                !authUser?.company?.onboarded
              )}
              onSave={saveOnboardingAnswers}
              onCancel={() => { setOnboardingModalOpen(false); setOnboardingDismissed(true); }}
              cancelLabel="Remind me later"
            />
          </div>
        </div>
      )}

      {/* Internal share modal (2026-08-23) — amber-badged colleague
          links; views never touch buyer engagement. */}
      {internalShareJobId && (
        <InternalSharePanel
          artifactType="demo"
          artifactId={internalShareJobId}
          authToken={authToken}
          onClose={() => setInternalShareJobId(null)}
        />
      )}

      {/* Global toast — fixed-position, shown when clips are ready, etc. */}
      {toast && (
        <div className="toast-container" role="status" aria-live="polite">
          <div className="toast">
            <span className="toast-icon"></span>
            <span className="toast-message">{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                className="toast-action"
                onClick={() => { toast.action.onClick(); dismissToast(); }}
              >
                {toast.action.label}
              </button>
            )}
            <button
              type="button"
              className="toast-close"
              onClick={dismissToast}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Ask Winter — in-app AI help agent. Floating bubble lives
          outside all phase render branches so she's reachable from
          every page the authed user is on. Invisible when logged
          out (component self-guards on authToken). */}
      <AskWinter authToken={authToken} apiBase={API_BASE} />
    </div>
    </HelpProvider>
  );
}

// Root wrapper — decides whether to render a public surface (ShareViewer
// for /share/:token, BuyerSpaceProspectView for /space/:slug) or the
// full auth + dashboard App. Kept here (rather than inside App()) so App
// always mounts its hooks in the same order, satisfying ESLint's
// react-hooks/rules-of-hooks check on Vercel builds. SHARE_TOKEN and
// SPACE_SLUG are computed once at module load from window.location.
// Detect the standalone recording-tab route from the current hash.
// Computed once at module load — same pattern as SHARE_TOKEN /
// SPACE_SLUG above. The App component never mounts when we're in
// recording mode, so its 100+ hooks don't pay any cost on this
// dedicated surface.
const IS_RECORDING_TAB = (typeof window !== 'undefined')
  && /^#\/?record-trailer\b/i.test(window.location.hash || '');

// The two document surfaces are React.lazy, not require(): a synchronous
// require() is resolved at BUILD time and webpack folds the module — and
// everything it imports — into the main chunk, which is how the buyer's
// PDF renderer ended up in every seller's first page load. Dynamic
// import() gives each its own chunk, downloaded only on the route that
// needs it. Declared at module scope; nothing loads until rendered.
const InternalViewerLazy = lazy(() => import('./components/InternalViewer'));
const DocShareViewerLazy = lazy(() => import('./components/DocShareViewer'));
// Third document surface, same rule: the room-document page pulls in
// DocPages (and therefore the pdf.js fallback's import site), so it
// gets its own chunk rather than riding along in main.
const RoomDocViewerLazy = lazy(() => import('./components/RoomDocViewer'));

/** Chunk-loading placeholder. Buyer surfaces are pinned light (a
 *  seller's dark-mode preference must not leak onto a buyer page);
 *  the internal viewer follows the room tokens like the rest of the
 *  in-app UI. */
function RouteChunkLoading({ label, light }) {
  const surface = light ? '#f8fafc' : 'var(--room-surface, #f8fafc)';
  const ink = light ? '#64748b' : 'var(--room-text-muted, #64748b)';
  return (
    <div style={{ minHeight: '100vh', background: surface, fontFamily: 'inherit' }}>
      <div role="status" style={{ textAlign: 'center', paddingTop: '20vh', color: ink, fontSize: 14 }}>
        {label}
      </div>
    </div>
  );
}

export default function Root() {
  if (INTERNAL_TOKEN) {
    // Internal share viewer — before everything else so a colleague
    // clicking a link never touches auth or the app bundle's state.
    return (
      <Suspense fallback={<RouteChunkLoading label="Opening…" />}>
        <InternalViewerLazy token={INTERNAL_TOKEN} />
      </Suspense>
    );
  }
  if (DOC_TOKEN) {
    // Tracked document share viewer — public buyer surface, mounted
    // before auth so a stakeholder clicking a /d/ link never touches
    // auth or the app bundle's state.
    return (
      <Suspense fallback={<RouteChunkLoading label="Opening document…" light />}>
        <DocShareViewerLazy token={DOC_TOKEN} />
      </Suspense>
    );
  }
  if (ROOM_DOC_SLUG && ROOM_DOC_ID) {
    // One Decision Room document on its own page. Checked before
    // SPACE_SLUG so the room view can never claim this path.
    return (
      <Suspense fallback={<RouteChunkLoading label="Opening document…" light />}>
        <RoomDocViewerLazy slug={ROOM_DOC_SLUG} docId={ROOM_DOC_ID} />
      </Suspense>
    );
  }
  if (FORM_TOKEN) {
    // Standalone form fill page — buyer surface, no app chrome.
    const FormPublicPage = require('./components/FormPublicPage').default;
    return <FormPublicPage token={FORM_TOKEN} />;
  }
  if (SHARE_TOKEN) {
    return <ShareViewer token={SHARE_TOKEN} />;
  }
  if (SPACE_SLUG) {
    // Lazy-import via require so the seller-side App bundle isn't
    // affected by changes to the prospect view (and vice versa).
    const BuyerSpaceProspectView = require('./components/BuyerSpaceProspectView').default;
    return <BuyerSpaceProspectView slug={SPACE_SLUG} />;
  }
  if (IS_RECORDING_TAB) {
    // Standalone recording experience — its own tab, no app chrome.
    // Opened from the Trailers "+ New trailer ▾" dropdown via
    // window.open('#/record-trailer'). On save, posts a BroadcastChannel
    // message that the parent App tab listens for, then closes itself.
    const RecordTrailerPage = require('./components/RecordTrailerPage').default;
    return <RecordTrailerPage />;
  }
  return <App />;
}
